import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useHistory } from 'react-router-dom';
import { useFlashMessage, DEFAULT_DURATION } from './FlashMessage';
import { debounce } from 'lodash';
import PortSearchAutocomplete from './PortSearchAutocomplete';
import FreightRecapLinkModal from './FreightRecapLinkModal';
import BillsOfLadingTable from './Deliveries/BillsOfLadingTable';
import SeveralProductsNote from './Deliveries/SeveralProductsNote';
import { hasSeveralProducts } from '../utils/dealProductLines';
import RichTextField, { richTextLength } from './ui/RichTextField';
import { DatePicker, DateRangePicker } from './ui';
import InvoiceDatePicker from './ui/InvoiceDatePicker';
import AmountInput from './ui/AmountInput';
import { canonicalizeAmount, exceedsDecimals } from '../utils/amountFormat';
import {
  PRICE_DECIMALS,
  QUANTITY_DECIMALS,
  formatFigure,
  stepFor,
  subtractFigures,
} from '../utils/precision';
import './DeliveryForm.css';
import { fetchDeliveryDropdowns } from '../services/deliveryService';
import { crmService } from '../services/crmService';
import { mapSanctionStatus, mapKYCStatus } from '../utils/crmStatusMapper';
import { TRANSPORT_MODE_OPTIONS, CURRENCY_OPTIONS, INCOTERMS_OPTIONS } from '../utils/constants';
import { INDUSTRY_OPTIONS } from '../data/industryOptions';
import FlatMapComponent from './FlatMapComponent';
import VesselSearchAutocomplete from './VesselSearchAutocomplete';
import VesselTrackingPanel from './VesselTrackingPanel';
import { globeService } from '../services/globeService';
import { normalizeLng } from '../utils/geo';
// Phase 1 UI Redesign Components
import DeliveryTabs from './DeliveryTabs';
import DeliverySectionNav from './DeliverySectionNav';
import RecapLinkModal from './RecapLinkModal';
import RecapCardFlip from './RecapCardFlip';
import StatusButton from './StatusButton';
import DeliveryPartnerSearch from './DeliveryPartnerSearch';
import RecapProductSearch from './RecapProductSearch';
import InlineProductCreator from './Stock/InlineProductCreator';
import InlineCrmCreator from './CRM/InlineCrmCreator';
import DeliveryPitToPortSection from './Deliveries/DeliveryPitToPortSection';
import DeliveryQualityCertificateModal from './DeliveryQualityCertificateModal';
import { fetchHMSEntities } from '../services/hmsEntityService';
import axiosInstance from '../utils/axiosConfig';
import systemDataService from '../services/systemDataService';
import stockService from '../services/stockService';
import ReportingMonthInput from './Invoicing/ReportingMonthInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faBalanceScale } from '@fortawesome/free-solid-svg-icons';

// The coal quality reading paired with the price basis (e.g. 6000 kcal/kg).
// It is not a price, so it keeps its two decimals and its blur formatting
// while the prices and quantities around it widen to utils/precision.js
// (card: six-decimals-across-the-chain).
const PRICE_BASIS_DECIMALS = 2;

// Keystroke rule for the bespoke number boxes on this screen (the quantities
// and the price basis): digits, one point, at most `decimals` decimals.
export const isTypeableNumber = (value, decimals) => {
  if (!value) return true;
  return new RegExp(`^\\d*\\.?\\d{0,${decimals}}$`).test(value);
};

// Wrap a raw text box's change handler so a keystroke that would carry more
// decimals than the figure keeps is refused (the box keeps its last value)
// instead of reaching the server and coming back as a refused save. Nothing
// else about what the box accepts changes, and nothing is ever shortened.
export const refuseExtraDecimals = (decimals, apply) => (e) => {
  if (exceedsDecimals(e.target.value, decimals)) return;
  apply(e);
};

// HMS brief ("show all linked products here"): a full-width list of
// EVERY product linked to this fuels delivery via its sales recaps --
// not just recaps that are individually multi-product. Five separately
// linked single-product recaps still surface their five product lines.
// Each line captures its own BDN-confirmed tonnage + document id at
// handover, saved per-recap via /set-sales-product-allocations/.
//
// (Replaces the old multi-product-only ``PerProductBDNTable`` and the
// stale Funding/Interest/ROCE financial panel that used to sit here.)
// Exported for test only. Mounting the whole DeliveryForm to assert on one
// table drags in the globe, vessel tracking and a dozen services; the BDN
// seam deserves probes that fail for ITS reasons, not for a map's.
export const LinkedProductsBDNTable = ({ deliveryId, salesRecapsData, purchaseRecapsData, onSavedFlash, onTotalsChange }) => {
  // Sales side: every linked fuels recap with at least one product line.
  // These carry the editable BDN tonnage + document id (the delivered-to-
  // vessel store), saved per-recap via /set-sales-product-allocations/.
  const salesRecaps = useMemo(() => (
    (salesRecapsData || []).filter(
      (r) => r.commodity_type === 'fuels' && Array.isArray(r.recap_products) && r.recap_products.length >= 1
    )
  ), [salesRecapsData]);

  // Grades already shown on the sales side (normalised), used to dedupe the
  // purchase side so the same fuel grade isn't listed twice.
  const salesGrades = useMemo(() => {
    const s = new Set();
    salesRecaps.forEach((r) => r.recap_products.forEach((p) => {
      const g = (p.product_label || '').trim().toLowerCase();
      if (g) s.add(g);
    }));
    return s;
  }, [salesRecaps]);

  // Purchase side: same product shape, but read-only. BDN tonnage is a
  // sales-side concept (no purchase allocation store/endpoint), so we only
  // surface purchase products to complete the "all linked products" picture.
  // Products whose grade already appears on a sales line are deduped out.
  const purchaseRecaps = useMemo(() => (
    (purchaseRecapsData || [])
      .filter((r) => r.commodity_type === 'fuels' && Array.isArray(r.recap_products) && r.recap_products.length >= 1)
      .map((r) => ({
        ...r,
        recap_products: r.recap_products.filter((p) => {
          const g = (p.product_label || '').trim().toLowerCase();
          return !g || !salesGrades.has(g);
        }),
      }))
      .filter((r) => r.recap_products.length >= 1)
  ), [purchaseRecapsData, salesGrades]);

  // Per-recap local edit state (sales only). Initialised from the loaded
  // ``allocations`` array on each recap.
  const [editsByRecap, setEditsByRecap] = useState({});
  const [saving, setSaving] = useState(false);

  // The BDN documents already uploaded to this delivery, and what HMS read
  // off each one at upload time. HMS has extracted the BDN number and date
  // since the naming layer shipped; nothing ever read them back, so this
  // table asked the trader to retype numbers we already held.
  const [bdnDocs, setBdnDocs] = useState([]);
  const [bdnDocsWarning, setBdnDocsWarning] = useState('');
  const [bdnDocsLoaded, setBdnDocsLoaded] = useState(false);
  const [rereadingKey, setRereadingKey] = useState(null);
  // What we OFFERED per row, and where it came from. Never records anything
  // the trader typed themselves -- its whole job is to let them tell the
  // difference between a value they entered and a value a machine read.
  const [offeredByRow, setOfferedByRow] = useState({});

  useEffect(() => {
    const initial = {};
    salesRecaps.forEach((recap) => {
      const allocByProduct = {};
      (recap.allocations || []).forEach((a) => {
        if (a.recap_product != null) {
          allocByProduct[a.recap_product] = a;
        }
      });
      initial[recap.id] = recap.recap_products.map((p) => {
        const existing = allocByProduct[p.id];
        return {
          recap_product: p.id,
          final_tonnage_mt: existing?.final_tonnage_mt ?? '',
          bdn_document_id: existing?.bdn_document_id ?? '',
          bdn_number: existing?.bdn_number ?? '',
          bdn_date: existing?.bdn_date ?? '',
        };
      });
    });
    setEditsByRecap(initial);
  }, [salesRecaps]);

  // One read per delivery. Cheap: the join is local, and the archive call
  // is the same one the document panel already makes.
  useEffect(() => {
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
      setBdnDocsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/api/deliveries/${deliveryId}/bdn-documents/`);
        if (cancelled) return;
        setBdnDocs(Array.isArray(data?.documents) ? data.documents : []);
        setBdnDocsWarning((data?.warnings || [])[0] || '');
      } catch (err) {
        // A failure here must never render as "no BDN uploaded" -- that
        // sends the trader hunting for a document that is sitting right
        // there. Say the list could not be read.
        if (!cancelled) setBdnDocsWarning('Could not load this delivery\'s BDN documents.');
      } finally {
        if (!cancelled) setBdnDocsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [deliveryId]);

  // Report the live running total of per-product BDN tonnages (sales side
  // only) up to the parent so the delivery-level "FINAL Tonnage" field can
  // render it read-only as Σ products. ``count`` lets the parent decide
  // whether any per-product capture exists at all.
  useEffect(() => {
    if (!onTotalsChange) return;
    let count = 0;
    let total = 0;
    let anyFilled = false;
    salesRecaps.forEach((r) => {
      (editsByRecap[r.id] || []).forEach((row) => {
        count += 1;
        const v = parseFloat(row.final_tonnage_mt);
        if (Number.isFinite(v)) { total += v; anyFilled = true; }
      });
    });
    onTotalsChange({ count, total: anyFilled ? total : null });
  }, [editsByRecap, salesRecaps, onTotalsChange]);

  const totalProducts =
    salesRecaps.reduce((n, r) => n + r.recap_products.length, 0) +
    purchaseRecaps.reduce((n, r) => n + r.recap_products.length, 0);

  const handleEdit = (recapId, productIndex, key, value) => {
    setEditsByRecap((prev) => {
      const rows = [...(prev[recapId] || [])];
      rows[productIndex] = { ...rows[productIndex], [key]: value };
      return { ...prev, [recapId]: rows };
    });
  };

  const rowKey = (recapId, idx) => `${recapId}:${idx}`;

  // Fill a row FROM a document, and only where the trader has left a gap.
  //
  // Three rules, and all three are the point of the feature:
  //   1. never overwrite a value a human typed
  //   2. never write anything to the server -- this only stages the inputs,
  //      the existing Save button stays the one and only writer
  //   3. record what was offered, so the row can say so out loud
  //
  // Rule 2 matters more than it looks: a BDN date on every sales line is
  // what moves the customer's credit from pending to actualized. Staging it
  // silently would move money on a machine's say-so.
  const applyDocumentToRow = useCallback((recapId, idx, doc, { onlyIfUnset = false } = {}) => {
    if (!doc) return;
    let offered = [];
    let skipped = false;
    setEditsByRecap((prev) => {
      const rows = [...(prev[recapId] || [])];
      const current = { ...(rows[idx] || {}) };
      const isBlank = (v) => v === '' || v === null || v === undefined;
      // The auto-offer path must not barge in on a row that already names a
      // document -- that would be an overwrite wearing a suggestion's hat.
      if (onlyIfUnset && !isBlank(current.bdn_document_id)) {
        skipped = true;
        return prev;
      }
      const found = [];
      current.bdn_document_id = doc.document_id;
      if (isBlank(current.bdn_number) && doc.bdn_number) {
        current.bdn_number = doc.bdn_number;
        found.push('number');
      }
      if (isBlank(current.bdn_date) && doc.bdn_date) {
        current.bdn_date = doc.bdn_date;
        found.push('date');
      }
      if (isBlank(current.final_tonnage_mt) && doc.quantity_mt != null) {
        current.final_tonnage_mt = String(doc.quantity_mt);
        found.push('tonnage');
      }
      offered = found;
      rows[idx] = current;
      return { ...prev, [recapId]: rows };
    });
    setOfferedByRow((prev) => {
      if (skipped) return prev;
      return {
        ...prev,
        [rowKey(recapId, idx)]: offered.length
          ? { fields: offered, provenance: doc.provenance, source: doc.display_name }
          : undefined,
      };
    });
  }, []);

  // Exactly one BDN on this delivery -> offer it to every sales line that
  // has not already got one, still unsaved. Two or more and we offer
  // NOTHING: quietly attaching one of several notes to the wrong product
  // line is a worse failure than an extra click, and with two notes on one
  // delivery there is no honest way to guess which line each belongs to.
  useEffect(() => {
    if (!bdnDocsLoaded || bdnDocs.length !== 1) return;
    const doc = bdnDocs[0];
    salesRecaps.forEach((recap) => {
      (recap.recap_products || []).forEach((_p, idx) => {
        applyDocumentToRow(recap.id, idx, doc, { onlyIfUnset: true });
      });
    });
  }, [bdnDocsLoaded, bdnDocs, salesRecaps, applyDocumentToRow]);

  if (salesRecaps.length === 0 && purchaseRecaps.length === 0) {
    return null;
  }


  const handlePickDocument = (recapId, idx, rawValue) => {
    if (rawValue === '') {
      handleEdit(recapId, idx, 'bdn_document_id', '');
      setOfferedByRow((prev) => ({ ...prev, [rowKey(recapId, idx)]: undefined }));
      return;
    }
    const doc = bdnDocs.find((d) => String(d.document_id) === String(rawValue));
    if (!doc) {
      handleEdit(recapId, idx, 'bdn_document_id', rawValue);
      return;
    }
    applyDocumentToRow(recapId, idx, doc);
  };

  // Ask HMS to look at an already-uploaded note again. Explicit on purpose:
  // it costs an archive fetch, an OCR pass and an LLM round trip, so it runs
  // on a click and never on render. This is the only route to the back
  // catalogue -- documents uploaded before the naming layer existed have no
  // extracted number, date or tonnage at all.
  const handleReread = async (recapId, idx, documentId) => {
    if (!documentId || !deliveryId) return;
    const key = rowKey(recapId, idx);
    setRereadingKey(key);
    try {
      const { data } = await axiosInstance.post('/api/document-naming/reread/', {
        docuware_document_id: String(documentId),
        module: 'delivery',
        related_entity_id: String(deliveryId),
      });
      const row = data?.document || {};
      const fresh = {
        document_id: Number(documentId),
        display_name: row.display_name || `Document ${documentId}`,
        bdn_number: row.component_doc_number || null,
        bdn_date: row.component_date || null,
        quantity_mt: row.extracted_quantity_mt != null ? Number(row.extracted_quantity_mt) : null,
        provenance: (row.user_overrides || []).includes('component_doc_number')
          ? 'trader_confirmed'
          : (row.component_doc_number ? 'ai_read' : 'unread'),
      };
      setBdnDocs((prev) => prev.map((d) => (
        String(d.document_id) === String(documentId) ? { ...d, ...fresh } : d
      )));
      applyDocumentToRow(recapId, idx, fresh);
      // The server says what THIS read found, and whether it kept a tonnage it
      // could not see again. Counting the stored row here would claim a
      // tonnage was read when it was only kept.
      onSavedFlash?.(data.message, true);
    } catch (err) {
      // A read that found nothing answers 422 with the reason and the promise
      // that nothing stored was changed.
      const detail = err?.response?.data?.error || 'Could not read that BDN.';
      onSavedFlash?.(detail, false);
    } finally {
      setRereadingKey(null);
    }
  };

  // The allocation endpoint is keyed per-recap, so a single "Save all"
  // loops the sales recaps and POSTs one payload each.
  const handleSaveAll = async () => {
    if (!deliveryId) {
      onSavedFlash?.('Save the delivery first before assigning per-product BDN.', false);
      return;
    }
    setSaving(true);
    let creditMoved = 0;
    let creditReleased = 0;
    let creditSkipped = [];
    try {
      for (const recap of salesRecaps) {
        const rows = (editsByRecap[recap.id] || []).map((row) => ({
          recap_product: row.recap_product,
          final_tonnage_mt: row.final_tonnage_mt === '' ? null : Number(row.final_tonnage_mt),
          bdn_document_id: row.bdn_document_id === '' ? null : Number(row.bdn_document_id),
          bdn_number: (row.bdn_number ?? '').trim() === '' ? null : row.bdn_number.trim(),
          bdn_date: row.bdn_date === '' ? null : row.bdn_date,
        }));
        // eslint-disable-next-line no-await-in-loop
        const resp = await axiosInstance.post(
          `/api/deliveries/${deliveryId}/set-sales-product-allocations/`,
          { recap_id: recap.id, rows },
        );
        // The credit move rides back on the save. Reporting it is the whole
        // point: a trader who fills in a BDN date is entitled to see that the
        // customer's exposure moved, and to hear about it when it did not.
        const credit = resp?.data?.credit;
        if (credit?.ok) {
          creditMoved += credit.moved || 0;
          creditReleased += (credit.released_enquiries || []).length;
          creditSkipped = creditSkipped.concat(credit.skipped || []);
        }
      }
      let message = 'BDN saved for all linked products.';
      if (creditReleased > 0) {
        message += ` Credit released on ${creditReleased} paid deal${creditReleased === 1 ? '' : 's'}.`;
      } else if (creditMoved > 0) {
        message += ' Credit moved from Pending to Actualized.';
      }
      if (creditSkipped.length > 0) {
        message += ` Credit not moved for ${creditSkipped.length} line(s): ${creditSkipped[0]}`;
      }
      onSavedFlash?.(message, creditSkipped.length === 0);
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Failed to save per-product BDN.';
      onSavedFlash?.(detail, false);
    } finally {
      setSaving(false);
    }
  };

  // One card per linked recap. ``editable`` sales cards expose the BDN
  // tonnage/doc inputs; read-only purchase cards show the recap quantity
  // and a muted placeholder (BDN is captured on the sales side).
  const renderRecapCard = (recap, side) => {
    const editable = side === 'sales';
    return (
      <div key={`lp-${side}-${recap.id}`} className="linked-products-card">
        <div className="linked-products-card-head">
          <strong>Recap {recap.transaction_number}</strong>
          {recap.display_name && (
            <span className="linked-products-card-name">{recap.display_name}</span>
          )}
          <span className={`linked-products-side linked-products-side--${side}`}>
            {editable ? 'Sales' : 'Purchase'}
          </span>
        </div>
        <table className="linked-products-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Recap qty</th>
              <th>Final tonnage (MT)</th>
              <th>BDN number</th>
              <th>BDN document</th>
              <th>BDN date</th>
            </tr>
          </thead>
          <tbody>
            {recap.recap_products.map((p, idx) => {
              const edit = (editsByRecap[recap.id] || [])[idx] || {};
              const offer = offeredByRow[rowKey(recap.id, idx)];
              const hasDoc = edit.bdn_document_id !== '' && edit.bdn_document_id != null;
              const isRereading = rereadingKey === rowKey(recap.id, idx);
              return (
                <tr key={`lp-row-${side}-${recap.id}-${p.id}`}>
                  <td className="linked-products-name-cell">
                    {p.product_label || recap.product || `Product ${idx + 1}`}
                  </td>
                  <td className="linked-products-qty-cell">
                    {p.sale_quantity != null ? p.sale_quantity : '—'}
                  </td>
                  {editable ? (
                    <>
                      <td>
                        <input
                          type="number"
                          step={stepFor(QUANTITY_DECIMALS)}
                          min="0"
                          className={
                            edit.final_tonnage_mt === '' || edit.final_tonnage_mt == null
                              ? ''
                              : 'bdn-doc-input bdn-doc-input--filled'
                          }
                          value={edit.final_tonnage_mt ?? ''}
                          onChange={refuseExtraDecimals(
                            QUANTITY_DECIMALS,
                            (e) => handleEdit(recap.id, idx, 'final_tonnage_mt', e.target.value),
                          )}
                          placeholder="e.g. 320.123456"
                          title="BDN-confirmed tonnage for this product line. This is what invoicing bills on."
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          maxLength={100}
                          className={
                            edit.bdn_number == null || edit.bdn_number === ''
                              ? 'bdn-doc-input bdn-doc-input--empty'
                              : 'bdn-doc-input bdn-doc-input--filled'
                          }
                          value={edit.bdn_number ?? ''}
                          onChange={(e) => handleEdit(recap.id, idx, 'bdn_number', e.target.value)}
                          placeholder="e.g. SG-2026-0041"
                          title="The number printed on the BDN document"
                        />
                      </td>
                      <td className="bdn-doc-cell">
                        <select
                          className={
                            hasDoc ? 'bdn-doc-input bdn-doc-input--filled' : 'bdn-doc-input bdn-doc-input--empty'
                          }
                          value={edit.bdn_document_id ?? ''}
                          onChange={(e) => handlePickDocument(recap.id, idx, e.target.value)}
                          disabled={!bdnDocsLoaded}
                          title="Pick the bunker delivery note uploaded to this delivery. Its number, date and tonnage are offered to this line — nothing is saved until you press Save."
                        >
                          <option value="">
                            {!bdnDocsLoaded
                              ? 'Loading…'
                              : (bdnDocs.length ? 'Select the BDN…' : 'No BDN uploaded yet')}
                          </option>
                          {bdnDocs.map((d) => (
                            <option key={d.document_id} value={d.document_id}>{d.display_name}</option>
                          ))}
                          {hasDoc
                            && !bdnDocs.some((d) => String(d.document_id) === String(edit.bdn_document_id)) && (
                            <option value={edit.bdn_document_id}>
                              {`Document ${edit.bdn_document_id} (saved earlier, not in this list)`}
                            </option>
                          )}
                        </select>
                        {offer && (
                          <div className={`bdn-offer-note bdn-offer-note--${offer.provenance}`}>
                            {`Read ${offer.fields.join(', ')} from ${offer.source} — `}
                            {offer.provenance === 'trader_confirmed'
                              ? 'confirmed by whoever uploaded it.'
                              : 'read by AI, nobody has checked it.'}
                            {offer.fields.includes('date')
                              && ' Saving the date moves this deal\'s credit to actualized.'}
                            {' Check it, then save.'}
                          </div>
                        )}
                        {hasDoc && (
                          <button
                            type="button"
                            className="bdn-reread-btn"
                            disabled={isRereading}
                            onClick={() => handleReread(recap.id, idx, edit.bdn_document_id)}
                            title="Look at the uploaded note again and read its number, date and tonnage off it."
                          >
                            {isRereading ? 'Reading…' : 'Read from BDN'}
                          </button>
                        )}
                      </td>
                      <td>
                        <input
                          type="date"
                          className={
                            edit.bdn_date == null || edit.bdn_date === ''
                              ? 'bdn-date-input bdn-doc-input--empty'
                              : 'bdn-date-input bdn-doc-input--filled'
                          }
                          value={edit.bdn_date ?? ''}
                          onChange={(e) => handleEdit(recap.id, idx, 'bdn_date', e.target.value)}
                          title="Date stated on the BDN document. Filling this on every sales line moves the customer's credit from Pending to Actualized."
                        />
                      </td>
                    </>
                  ) : (
                    <td colSpan={4} className="linked-products-readonly-note" title="BDN tonnage is captured on the sales side">
                      Captured on the sales side
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="linked-products-section">
      <div className="linked-products-head">
        <h3 className="delivery-subsection-title" style={{ margin: 0 }}>Linked products</h3>
        <span className="linked-products-count">{totalProducts} product{totalProducts === 1 ? '' : 's'}</span>
        {bdnDocsLoaded && bdnDocs.length > 0 && (
          <span
            className="linked-products-bdn-badge"
            title={bdnDocs.map((d) => d.display_name).join('\n')}
          >
            {bdnDocs.length === 1
              ? 'BDN uploaded and linked'
              : `${bdnDocs.length} BDNs uploaded and linked`}
          </span>
        )}
      </div>
      {bdnDocsWarning && (
        <div className="linked-products-bdn-warning">{bdnDocsWarning}</div>
      )}
      <small className="linked-products-hint">
        All products linked to this delivery across its sales and purchase recaps. Capture each sales line&apos;s
        BDN-confirmed tonnage, number and date at handover; purchase lines are shown for reference.
        {' '}<strong>The BDN date is what moves the customer&apos;s credit</strong> from Pending to Actualized —
        once every sales line below carries one, this deal&apos;s credit shifts on save. The document id is an
        optional internal link and does not affect credit.
      </small>

      <div className="linked-products-grid">
        {salesRecaps.map((recap) => renderRecapCard(recap, 'sales'))}
        {purchaseRecaps.map((recap) => renderRecapCard(recap, 'purchase'))}
      </div>

      {salesRecaps.length > 0 && (
        <div className="linked-products-actions">
          <button
            type="button"
            className="linked-products-save-btn"
            onClick={handleSaveAll}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save BDN for all products'}
          </button>
        </div>
      )}
    </div>
  );
};

// A figure as a recap carries it -- a number, or text that may hold spaces
// and thousands commas ("1,000.5") -- as a number; NaN when there is none.
// The one reader for every recap figure this file adds up or compares --
// quantities, prices, tolerances. A bare parseFloat stops at the comma and
// reads "1,000.5" as 1. The comma is always a thousands separator here, the
// house rule in utils/amountFormat.js.
const recapFigureToNumber = (v) => {
  if (v === undefined || v === null || v === '') return NaN;
  const n = parseFloat(v.toString().replace(/\s/g, '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

// The B/L quantity a delivery with Bills of Lading rows carries: their sum,
// added exactly (never through a float) and shaped like the stored figure.
// '' when no row has a quantity yet; null when there are no rows at all. A
// row with no number, date or quantity is not a row -- set-bills-of-lading
// skips it -- and a row without a quantity adds nothing, both exactly as
// DeliverySerializer._derive_bill_of_lading_quantity does on the server.
const billOfLadingRowsSum = (rows) => {
  const hasQuantity = (r) => r.quantity !== '' && r.quantity !== null && r.quantity !== undefined;
  const real = (rows || []).filter((r) => r && (r.bl_number || r.bl_date || hasQuantity(r)));
  if (real.length === 0) return null;
  const quantities = real.filter(hasQuantity).map((r) => String(r.quantity).trim());
  if (quantities.length === 0) return '';
  // a + b, exactly, as a - (-b): precision.js adds nothing but subtracts exactly.
  const total = quantities.reduce(
    (acc, q) => subtractFigures(acc, q.startsWith('-') ? q.slice(1) : `-${q}`, QUANTITY_DECIMALS),
    '0',
  );
  return formatFigure(total, 2, QUANTITY_DECIMALS);
};

/**
 * BunkersRecapProductCard
 * Bunkers (fuels) variant of the WHAT-tab recap card body. Bunkers recaps have
 * no tolerance or price-basis (cargo concepts) and carry their real commercial
 * values PER PRODUCT, so this renders a product picker that drives an editable
 * Quantity + Price per selected product. The fuel spec is read-only (edited on
 * the recap form). Recap-level totals (qty sum, weighted-avg price) show
 * read-only and re-derive on save.
 */
const BunkersRecapProductCard = ({ recap, side, units, currencies, productEdits, onProductFieldChange, onRecapFieldChange }) => {
  const products = (recap && Array.isArray(recap.recap_products)) ? recap.recap_products : [];
  const history = useHistory();
  const [activeIdx, setActiveIdx] = useState(0);
  const [showProductModal, setShowProductModal] = useState(false);
  const idx = products.length ? Math.min(activeIdx, products.length - 1) : 0;
  const product = products[idx] || {};
  const pid = product.id;
  const priceField = side === 'purchase' ? 'buy_price' : 'sale_price';
  const edits = (productEdits && pid != null && productEdits[pid]) || {};
  const qtyVal = edits.sale_quantity ?? (product.sale_quantity != null ? product.sale_quantity : '');
  const priceVal = edits[priceField] ?? (product[priceField] != null ? product[priceField] : '');

  const unitCode = recap ? recap[side === 'purchase' ? 'purchase_contract_quantity_unit' : 'sale_contract_quantity_unit'] : '';
  const unitName = (() => { const u = (units || []).find(x => x.code === unitCode); return u ? u.name : (unitCode || 'MT'); })();
  const unitField = side === 'purchase' ? 'purchase_contract_quantity_unit' : 'sale_contract_quantity_unit';
  const unitSelectValue = (() => {
    if (!unitCode) return '';
    const m = (units || []).find(u => u.code === unitCode || String(u.id) === String(unitCode));
    return m ? m.id : '';
  })();
  const currencyField = side === 'purchase' ? 'purchase_contract_price_currency' : 'sale_contract_price_currency';
  const currencyVal = (recap && recap[currencyField]) || '';

  // Derived recap totals, reflecting in-progress edits.
  const num = recapFigureToNumber;
  const merged = products.map(p => ({ ...p, ...((productEdits && p.id != null && productEdits[p.id]) || {}) }));
  const totalQty = merged.reduce((a, p) => a + (Number.isFinite(num(p.sale_quantity)) ? num(p.sale_quantity) : 0), 0);
  let wnum = 0, wden = 0;
  merged.forEach(p => { const q = num(p.sale_quantity), v = num(p[priceField]); if (q > 0 && Number.isFinite(v)) { wnum += v * q; wden += q; } });
  const avgPrice = wden > 0 ? (wnum / wden) : null;

  const qtyLabel = side === 'purchase' ? 'Purchase Quantity' : 'Sales Quantity';
  const priceLabel = side === 'purchase' ? 'Purchase Price per Unit (contract)' : 'Sales Price per Unit (contract)';

  return (
    <div className="recap-editable-container">
      <h3 className="delivery-subsection-title">Product</h3>
      {products.length > 1 && (
        <div className="recap-nav-numbers" style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {products.map((p, i) => (
            <button key={p.id ?? i} type="button"
              className={`recap-number-btn ${i === idx ? 'active' : ''}`}
              onClick={() => setActiveIdx(i)}>
              Product {i + 1}
            </button>
          ))}
        </div>
      )}
      <div className="form-group">
        <input type="text" value={product.product_label || (recap && recap.product) || ''} readOnly
          onClick={() => setShowProductModal(true)}
          title="Click to view — product is edited on the recap screen"
          style={{ backgroundColor: '#fff', cursor: 'pointer' }} />
      </div>

      <h3 className="delivery-subsection-title">{qtyLabel}</h3>
      <div className="form-group">
        <div style={{ display: 'flex', gap: '8px' }}>
          <input type="text" value={qtyVal}
            onChange={refuseExtraDecimals(QUANTITY_DECIMALS, (e) => onProductFieldChange(pid, 'sale_quantity', e.target.value))}
            placeholder="Quantity" style={{ flex: 1 }} />
          <select value={unitSelectValue}
            onChange={(e) => {
              const u = (units || []).find(x => String(x.id) === String(e.target.value));
              onRecapFieldChange(unitField, u ? u.code : e.target.value);
            }}
            style={{ width: '140px' }}>
            <option value="">Unit</option>
            {(units || []).map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
          </select>
        </div>
      </div>

      <h3 className="delivery-subsection-title">{priceLabel}</h3>
      <div className="form-group">
        <div style={{ display: 'flex', gap: '8px' }}>
          <input type="text" value={priceVal}
            onChange={refuseExtraDecimals(PRICE_DECIMALS, (e) => onProductFieldChange(pid, priceField, e.target.value))}
            placeholder="Price" style={{ flex: 1 }} />
          <select value={currencyVal}
            onChange={(e) => onRecapFieldChange(currencyField, e.target.value)}
            style={{ width: '140px' }}>
            <option value="">Currency</option>
            {(currencies || []).map(o => (<option key={o.value} value={o.value}>{o.label || o.value}</option>))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
        Recap total: <strong>{totalQty}</strong> {unitName} &middot; weighted avg{' '}
        <strong>{avgPrice != null ? formatFigure(avgPrice, 2) : '—'}</strong> {currencyVal}
      </div>

      {showProductModal && ReactDOM.createPortal((
        <div onClick={() => setShowProductModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '10px', width: '440px', maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eef1f5', fontWeight: 600, fontSize: '16px', color: '#1f2d3d' }}>
              Product is read-only here
            </div>
            <div style={{ padding: '18px 20px', fontSize: '14px', color: '#4a5568', lineHeight: 1.5 }}>
              These product details — fuel spec, grade and ISO — can only be edited on the
              recap screen. Quantity and price stay editable here on the delivery.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '14px 20px', borderTop: '1px solid #eef1f5' }}>
              <button type="button" onClick={() => setShowProductModal(false)}
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #cbd5e0', background: '#fff', color: '#4a5568', cursor: 'pointer' }}>
                Close
              </button>
              <button type="button"
                onClick={() => { setShowProductModal(false); history.push(`/trade-recap/${recap && recap.id}`); }}
                style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', background: '#3182ce', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                View Recap
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

const DeliveryForm = forwardRef(({ formData, handleInputChange, deliveryId, markAsChanged }, ref) => {
  // Use flash message hook
  const { showError, showSuccess } = useFlashMessage();
  const location = useLocation();
  
  // Keep reference to original handleInputChange (needed by some existing code)
  const originalHandleInputChange = handleInputChange;
  
  // === PHASE 1: Tab and Section Navigation State ===
  const [activeTab, setActiveTab] = useState('what');
  const [activeSection, setActiveSection] = useState('business-partners');

  // Live per-product BDN summary reported by LinkedProductsBDNTable.
  // { count, total } — count > 0 means per-product capture exists, so the
  // delivery-level FINAL Tonnage renders read-only as the sum (option B).
  // null total = no tonnages entered yet. Stable setter + value-compare so
  // the child's reporting effect can't drive a render loop.
  const [linkedBdnSummary, setLinkedBdnSummary] = useState(null);
  const handleLinkedBdnTotals = useCallback((summary) => {
    setLinkedBdnSummary((prev) => {
      if (prev && prev.count === summary.count && prev.total === summary.total) {
        return prev;
      }
      return summary;
    });
  }, []);
  
  // === PHASE 1: Recap Modal State ===
  const [showPurchaseRecapModal, setShowPurchaseRecapModal] = useState(false);
  const [showSalesRecapModal, setShowSalesRecapModal] = useState(false);
  const [showFreightRecapModal, setShowFreightRecapModal] = useState(false);
  const [availableRecaps, setAvailableRecaps] = useState({ purchase_recaps: [], sales_recaps: [] });
  
  // === BLANK RECAPS: Simple state for blank recaps (unsaved, being edited) ===
  const [blankPurchaseRecaps, setBlankPurchaseRecaps] = useState([]);
  const [blankSalesRecaps, setBlankSalesRecaps] = useState([]);
  const [activeBlankPurchaseId, setActiveBlankPurchaseId] = useState(null);
  const [activeBlankSalesId, setActiveBlankSalesId] = useState(null);
  
  // === BLANK RECAP DATA: Storage for preserving values during navigation ===
  const [blankRecapValues, setBlankRecapValues] = useState({});
  // Structure: { 'blank-purchase-12345': { product: 'Coal', quantity: '1000', ... }, ... }

  // === SAVED RECAP EDITS: In-flight edits to already-saved TradeRecaps ===
  // Buffered until the user clicks Save on the delivery, at which point each
  // entry is PATCHed to /api/offer/trade-recaps/{recapId}/. Edit keys use the
  // TradeRecap column names directly (e.g. purchase_contract_quantity,
  // sale_cv_basis_value) so they can be both spread over the active recap
  // for rendering and sent straight through to the PATCH payload.
  const [savedRecapEdits, setSavedRecapEdits] = useState({});
  // Per-product edit buffer for bunkers recaps:
  // { [recapId]: { [productId]: { sale_quantity?, buy_price?, sale_price? } } }
  const [savedRecapProductEdits, setSavedRecapProductEdits] = useState({});

  // === PENDING LINKED RECAPS: Saved recaps selected before delivery save ===
  const [pendingLinkedPurchaseRecaps, setPendingLinkedPurchaseRecaps] = useState([]);
  const [pendingLinkedSalesRecaps, setPendingLinkedSalesRecaps] = useState([]);
  const [pendingLinkedFreightRecaps, setPendingLinkedFreightRecaps] = useState([]);

  // === State for Multiple Contractual Partners ===
  // Initialize from formData or create default structure
  const [sellerContractualList, setSellerContractualList] = useState(() => {
    if (formData.seller_contractual_list && Array.isArray(formData.seller_contractual_list) && formData.seller_contractual_list.length > 0) {
      // Ensure each item has status fields with defaults
      return formData.seller_contractual_list.map(item => ({
        name: item.name || '',
        crm_id: item.crm_id || null,
        reporting_as: item.reporting_as || false,
        sanction_status: item.sanction_status || 'pending',
        kyc_status: item.kyc_status || 'pending'
      }));
    } else if (formData.seller_contractual || formData.seller_contractual_crm_id) {
      return [{
        name: formData.seller_contractual || '',
        crm_id: formData.seller_contractual_crm_id || null,
        reporting_as: formData.seller_contractual_reporting_as_tickbox || false,
        sanction_status: 'pending',
        kyc_status: 'pending'
      }];
    }
    return [{ name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' }];
  });
  
  const [buyerContractualList, setBuyerContractualList] = useState(() => {
    if (formData.buyer_contractual_list && Array.isArray(formData.buyer_contractual_list) && formData.buyer_contractual_list.length > 0) {
      // Ensure each item has status fields with defaults
      return formData.buyer_contractual_list.map(item => ({
        name: item.name || '',
        crm_id: item.crm_id || null,
        reporting_as: item.reporting_as || false,
        sanction_status: item.sanction_status || 'pending',
        kyc_status: item.kyc_status || 'pending'
      }));
    } else if (formData.buyer_contractual || formData.buyer_contractual_crm_id) {
      return [{
        name: formData.buyer_contractual || '',
        crm_id: formData.buyer_contractual_crm_id || null,
        reporting_as: formData.buyer_contractual_reporting_as_tickbox || false,
        sanction_status: 'pending',
        kyc_status: 'pending'
      }];
    }
    return [{ name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' }];
  });
  
  // CRITICAL FIX: Sync contractual lists when formData changes (e.g., after loading delivery from backend)
  useEffect(() => {
    const blankEntry = { name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' };
    
    // Sync seller_contractual_list
    if (formData.seller_contractual_list && Array.isArray(formData.seller_contractual_list) && formData.seller_contractual_list.length > 0) {
      const hasData = formData.seller_contractual_list.some(item => item.name || item.crm_id);
      if (hasData) {
        const syncedList = formData.seller_contractual_list.map(item => ({
          name: item.name || '',
          crm_id: item.crm_id || null,
          reporting_as: item.reporting_as || false,
          sanction_status: item.sanction_status || 'pending',
          kyc_status: item.kyc_status || 'pending'
        }));
        setSellerContractualList(syncedList);
      }
    } else if (formData.seller_contractual || formData.seller_contractual_crm_id) {
      const migratedList = [{
        name: formData.seller_contractual || '',
        crm_id: formData.seller_contractual_crm_id || null,
        reporting_as: formData.seller_contractual_reporting_as_tickbox || false,
        sanction_status: 'pending',
        kyc_status: 'pending'
      }];
      setSellerContractualList(migratedList);
    } else if (Array.isArray(formData.seller_contractual_list) && formData.seller_contractual_list.length === 0) {
      setSellerContractualList([blankEntry]);
    }
    
    // Sync buyer_contractual_list
    if (formData.buyer_contractual_list && Array.isArray(formData.buyer_contractual_list) && formData.buyer_contractual_list.length > 0) {
      const hasData = formData.buyer_contractual_list.some(item => item.name || item.crm_id);
      if (hasData) {
        const syncedList = formData.buyer_contractual_list.map(item => ({
          name: item.name || '',
          crm_id: item.crm_id || null,
          reporting_as: item.reporting_as || false,
          sanction_status: item.sanction_status || 'pending',
          kyc_status: item.kyc_status || 'pending'
        }));
        setBuyerContractualList(syncedList);
      }
    } else if (formData.buyer_contractual || formData.buyer_contractual_crm_id) {
      const migratedList = [{
        name: formData.buyer_contractual || '',
        crm_id: formData.buyer_contractual_crm_id || null,
        reporting_as: formData.buyer_contractual_reporting_as_tickbox || false,
        sanction_status: 'pending',
        kyc_status: 'pending'
      }];
      setBuyerContractualList(migratedList);
    } else if (Array.isArray(formData.buyer_contractual_list) && formData.buyer_contractual_list.length === 0) {
      setBuyerContractualList([blankEntry]);
    }
  }, [
    formData.seller_contractual_list, 
    formData.buyer_contractual_list,
    formData.seller_contractual,
    formData.seller_contractual_crm_id,
    formData.seller_contractual_reporting_as_tickbox,
    formData.buyer_contractual,
    formData.buyer_contractual_crm_id,
    formData.buyer_contractual_reporting_as_tickbox
  ]);
  
  // These are full recap objects from the modal that will be linked when delivery saves
  
  // Log availableRecaps changes
  // useEffect(() => {
  //   console.log('📊 RECAP STATE CHANGED:', {
  //     purchase_count: availableRecaps.purchase_recaps?.length || 0,
  //     sales_count: availableRecaps.sales_recaps?.length || 0,
  //     purchase_recaps: availableRecaps.purchase_recaps,
  //     sales_recaps: availableRecaps.sales_recaps
  //   });
  // }, [availableRecaps]);
  
  // NavAPI vessel tracking state
  const [confirmedVesselImoNumber, setConfirmedVesselImoNumber] = useState('');
  const [isTrackingVessel, setIsTrackingVessel] = useState(false);
  const [vesselPositionData, setVesselPositionData] = useState(null);
  const [vesselDetailsData, setVesselDetailsData] = useState(null);
  const [resolvedPortMarkers, setResolvedPortMarkers] = useState([]);
  const [routeWaypoints, setRouteWaypoints] = useState([]);  // planned sea route [lat, lon]
  const [sailedRouteWaypoints, setSailedRouteWaypoints] = useState([]);  // estimated sailed route: loading port -> live position [lat, lon]

  // "AIS suggests" chip state, keyed by port field. Each field tracks whether a
  // click-resolution is in flight and a quiet inline note when it couldn't match
  // a navigable port. Suggestions are DISPLAY-ONLY until the trader clicks.
  const [aisSuggestState, setAisSuggestState] = useState({
    loading_port_location: { resolving: false, note: '' },
    discharging_port_location: { resolving: false, note: '' },
  });

  const autoTrackedImoNumbers = useRef(new Set());
  const previousImoNumber = useRef('');
  const debounceTimer = useRef(null);
  const activeTrackingImo = useRef('');
  const trackInFlightRef = useRef(false);
  const portCoordCache = useRef({});
  
  // Reset tracking state when vessel data is loaded
  useEffect(() => {
    if (confirmedVesselImoNumber && isTrackingVessel) {
      const timer = setTimeout(() => {
        setIsTrackingVessel(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [confirmedVesselImoNumber, isTrackingVessel]);
  
  // Auto-track vessel when IMO or MMSI number is loaded from database (with debouncing)
  useEffect(() => {
    const imoNumber = formData.vessel_imo_number;
    const mmsiNumber = formData.vessel_mmsi_number;
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const trimmedImo = (imoNumber || '').trim();
    const trimmedMmsi = (mmsiNumber || '').trim();
    const isCompleteImo = /^\d{7}$/.test(trimmedImo);
    const isCompleteMmsi = /^\d{9}$/.test(trimmedMmsi);

    const trackingKey = trimmedImo || trimmedMmsi;
    if (!trackingKey) {
      previousImoNumber.current = '';
      return;
    }

    const isSubstantialChange = previousImoNumber.current === '' && trackingKey.length > 5;

    if ((isCompleteImo || isCompleteMmsi || isSubstantialChange) && 
        !autoTrackedImoNumbers.current.has(trackingKey) &&
        !trackInFlightRef.current &&
        activeTrackingImo.current !== trackingKey) {
      
      debounceTimer.current = setTimeout(() => {
        autoTrackedImoNumbers.current.add(trackingKey);
        handleTrackVessel(
          isCompleteImo ? trimmedImo : null,
          isCompleteMmsi ? trimmedMmsi : null,
          { silent: true }
        );
      }, 500);
    }
    
    previousImoNumber.current = trackingKey;
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vessel_imo_number, formData.vessel_mmsi_number]);

  // Port marker resolution: dots derive ONLY from the deal's port fields.
  // AIS-declared origin/destination is machine data about the VESSEL, not the
  // deal, so it never plots a load/discharge dot. An empty port field -> no dot.
  // (AIS origin/dest still surfaces read-only in the Voyage Info panel and the
  // "AIS suggests" chips beneath the pickers — the sanctioned display of it.)
  useEffect(() => {
    let cancelled = false;

    // Extract a resolvable UN/LOCODE from a loading/discharge port field, however
    // it was set: the PORTS dropdown stores "1sp 1sb NAME, COUNTRY, UNLOCODE", but
    // a recap or import may leave "NAME, COUNTRY, UNLOCODE" or a bare UN/LOCODE.
    // Always take the trailing UN/LOCODE token (robust to commas in the name).
    const UNLOCODE_RE = /^[A-Z]{2}[A-Z0-9]{3}$/i;
    const parseManualPort = (val) => {
      if (!val) return null;
      const s = String(val).trim();
      if (!s) return null;
      const body = s.startsWith('1sp 1sb ') ? s.replace('1sp 1sb ', '') : s;
      if (body.includes(',')) {
        const last = body.split(',').pop().trim();
        if (UNLOCODE_RE.test(last)) return last;
      }
      if (UNLOCODE_RE.test(s)) return s;  // bare UN/LOCODE
      return null;  // a plain name with no code -> leave to the AIS fallback
    };

    const manualLoadingCode = parseManualPort(formData.loading_port_location);
    const manualDischargingCode = parseManualPort(formData.discharging_port_location);

    const hasAny = manualLoadingCode || manualDischargingCode;
    if (!hasAny) {
      setResolvedPortMarkers([]);
      return;
    }

    // Resolve a UN/LOCODE to a plot dot. Passes ?routing=1 so the backend only
    // returns a genuine navigable port (real port_code from the local DB or
    // SeaPortSearch); a Mapbox / country-centroid guess yields 404 -> null -> no
    // dot. This keeps the mini-map port DOTS under the same "real port only" rule
    // as the fleet map, so AIS free-text / typo'd codes never plot a mislocated
    // dot. The routing=1 result also carries port_code (== code) for a genuine
    // hit, so the no-live route fallback below can route by those codes.
    const resolvePort = async (code) => {
      if (!code) return null;
      const cacheKey = `port_${code}`;
      if (portCoordCache.current[cacheKey]) return portCoordCache.current[cacheKey];
      try {
        const resp = await axiosInstance.get('/api/globe/resolve-port/', { params: { code, routing: 1 } });
        const d = resp.data;
        const result = {
          lat: d.lat, lon: d.lon, name: d.name,
          country: d.country || '',
          country_iso: d.country_iso || '',
          country_dropdown_value: d.country_dropdown_value || '',
          country_level_only: d.country_level_only || false,
          code: d.code || code,
          // Canonical navigable port code for the routing engine ('' when the
          // backend returned a non-port hit — never happens under routing=1).
          routeCode: d.port_code || '',
        };
        portCoordCache.current[cacheKey] = result;
        return result;
      } catch (err) {
        console.warn(`Port resolution failed for ${code}:`, err.message || err);
        return null;
      }
    };

    const buildMarkers = async () => {
      const markers = [];

      // Loading port dot: only from the deal's loading_port_location field.
      if (manualLoadingCode) {
        const resolved = await resolvePort(manualLoadingCode);
        if (resolved) markers.push({ ...resolved, type: 'loading', manualOverride: true });
      }

      // Discharging port dot: only from the deal's discharging_port_location field.
      if (manualDischargingCode) {
        const resolved = await resolvePort(manualDischargingCode);
        if (resolved) markers.push({ ...resolved, type: 'discharge', manualOverride: true });
      }

      if (cancelled) return;

      setResolvedPortMarkers(markers);

      // NOTE: dots plot ONLY from the deal's port fields. AIS-declared
      // origin/destination is machine data about the VESSEL, so it never plots a
      // load/discharge dot (and never auto-writes a port or its country into the
      // form). AIS still surfaces read-only in the Voyage Info panel and the "AIS
      // suggests" chips beneath the pickers; a trader populates a port input only
      // by an explicit click (chip) or picking a dropdown suggestion. (Was:
      // AIS origin/dest fell back to plot dots + auto-populated the port fields —
      // both removed by design.)
    };

    buildMarkers();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.loading_port_location, formData.discharging_port_location]);

  // Plot the navigable sea route, re-anchored to mirror the fleet map's
  // semantics (FleetMapComponent / ShipmentOverviewTab). Two cases:
  //   1. LIVE position known  -> line runs from the vessel's LIVE lat/lon to the
  //      discharge PORT CODE (getSeaRoute endPort = canonical UN/LOCODE). Routing
  //      by code lets NavAPI use its own navigable discharge coords, so the line
  //      tracks the ship instead of the stale planned loading->discharge leg.
  //      Drawn only when the discharge resolved to a real port (routeCode set).
  //   2. NO live position     -> fall back to the planned loading->discharge line
  //      ONLY if BOTH ports resolved to genuine port codes (routing=1), so the
  //      form stays useful pre-voyage without ever plotting a Mapbox guess.
  // Cached server-side, so repeat fetches for the same leg are token-free.
  // Best-effort: hides on failure. total_distance/seca_distance now reflect
  // live->discharge when live, planned when not — intended.
  useEffect(() => {
    const loading = resolvedPortMarkers.find(p => p.type === 'loading');
    const discharge = resolvedPortMarkers.find(p => p.type === 'discharge');

    const liveLat = vesselPositionData?.latitude;
    const liveLon = vesselPositionData?.longitude;
    const hasLive = liveLat != null && liveLon != null;
    const dischargeCode = discharge?.routeCode || '';

    let routeArgs = null;
    if (hasLive && dischargeCode) {
      // Live -> discharge port code (re-anchored, mirrors the fleet map).
      routeArgs = { startLat: liveLat, startLon: liveLon, endPort: dischargeCode };
    } else if (!hasLive
        && loading?.lat != null && loading?.lon != null && loading?.routeCode
        && discharge?.lat != null && discharge?.lon != null && discharge?.routeCode) {
      // Pre-voyage fallback: planned loading -> discharge, both genuine ports.
      routeArgs = {
        startLat: loading.lat, startLon: loading.lon,
        endLat: discharge.lat, endLon: discharge.lon,
      };
    }

    // Estimated sailed leg (loading port -> live position), fetched ONLY in the
    // live-position case. Prefer the loading port CODE (routeCode) so NavAPI
    // routes from its own navigable coords; fall back to the resolved loading
    // lat/lon. NOT fetched in the pre-voyage fallback — there the planned line
    // already IS loading -> discharge, so a sailed line would be meaningless.
    // Suppressed while the vessel is still at/near the load berth (same 0.03-deg
    // guard as the fleet map's sailedRouteLeg) so a moored vessel never spends a
    // routing token on — or draws a degenerate stub of — a zero-length sailed leg.
    let sailedArgs = null;
    if (hasLive && loading?.lat != null && loading?.lon != null) {
      const dLat = Math.abs(loading.lat - liveLat);
      const dLon = Math.abs(normalizeLng(loading.lon - liveLon));
      const atBerth = dLat < 0.03 && dLon < 0.03;
      if (!atBerth) {
        sailedArgs = loading.routeCode
          ? { startPort: loading.routeCode, endLat: liveLat, endLon: liveLon }
          : { startLat: loading.lat, startLon: loading.lon, endLat: liveLat, endLon: liveLon };
      }
    }

    if (!routeArgs) {
      setRouteWaypoints([]);
      setSailedRouteWaypoints([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const route = await globeService.getSeaRoute(routeArgs);
        if (!cancelled) setRouteWaypoints(route?.configured ? (route.data?.waypoints || []) : []);
      } catch (e) {
        if (!cancelled) setRouteWaypoints([]);
      }
    })();
    // Best-effort sailed line: same config-gated / hide-on-failure contract as
    // the planned line, but never blocks or clears the planned line.
    (async () => {
      if (!sailedArgs) {
        if (!cancelled) setSailedRouteWaypoints([]);
        return;
      }
      try {
        const sailed = await globeService.getSeaRoute(sailedArgs);
        if (!cancelled) setSailedRouteWaypoints(sailed?.configured ? (sailed.data?.waypoints || []) : []);
      } catch (e) {
        if (!cancelled) setSailedRouteWaypoints([]);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPortMarkers, vesselPositionData?.latitude, vesselPositionData?.longitude]);

  // Auto-populate delivery form fields from vessel API data (no toast -- silent update)
  const handleVesselDataUpdate = (vesselData) => {
    if (!vesselData) return;
    const pos = vesselData.position || vesselData;
    const det = vesselData.details || null;

    setVesselPositionData(pos);
    setVesselDetailsData(det);

    const updates = {};
    if (pos.latitude != null && pos.longitude != null) {
      updates.vessel_position = `${pos.latitude.toFixed(4)}, ${pos.longitude.toFixed(4)}`;
    }
    if (pos.ship_name && !formData.vessel_name) {
      updates.vessel_name = pos.ship_name;
    }
    if (pos.imo) updates.vessel_imo_number = pos.imo;
    if (pos.mmsi) updates.vessel_mmsi_number = pos.mmsi;
    if (pos.call_sign) updates.vessel_call_sign = pos.call_sign;
    if (pos.flag) updates.vessel_flag = pos.flag;
    if (pos.ship_type) updates.vessel_type = pos.ship_type;
    if (pos.nav_status) updates.vessel_navigation_status = pos.nav_status;
    if (pos.speed != null) updates.vessel_speed = pos.speed;
    if (pos.course != null) updates.vessel_course = pos.course;
    if (pos.draught != null) updates.vessel_draught = pos.draught;
    if (pos.destination) updates.vessel_destination = pos.destination;
    if (pos.origin) updates.vessel_origin = pos.origin;
    if (pos.loa) updates.vessel_loa = pos.loa;
    if (pos.beam) updates.vessel_beam = pos.beam;
    if (pos.position_updated) updates.vessel_position_updated_at = pos.position_updated;
    if (pos.eta && !formData.delivery_estimated_time_of_arrival) {
      try {
        const etaDate = new Date(pos.eta).toISOString().split('T')[0];
        updates.delivery_estimated_time_of_arrival = etaDate;
      } catch { /* ignore invalid date */ }
    }

    if (det) {
      if (det.deadweight) updates.vessel_deadweight = det.deadweight;
      if (det.gross_tonnage) updates.vessel_gross_tonnage = det.gross_tonnage;
      if (det.year_built) updates.vessel_year_built = det.year_built;
      if (det.owner_name) updates.vessel_owner_name = det.owner_name;
      if (det.manager_name) updates.vessel_manager_name = det.manager_name;
      if (det.ship_type && !updates.vessel_type) updates.vessel_type = det.ship_type;
    }

    Object.entries(updates).forEach(([name, value]) => {
      handleInputChange({ target: { name, value } });
    });
  };
  
  // Enrich vessel spec fields from the richer vessel-particulars feed. Only
  // applies when the intelligence provider is configured (otherwise the live
  // ship-details from getVesselFullInfo already cover these). Best-effort:
  // never disrupts the tracking flow.
  const applyVesselParticulars = async (imo) => {
    if (!imo) return;
    try {
      const res = await globeService.getVesselParticulars(imo);
      if (!res || res.configured !== true || !res.data) return;
      const p = res.data;
      const updates = {};
      if (p.ship_type) updates.vessel_type = p.ship_type;
      if (p.dwt != null) updates.vessel_deadweight = p.dwt;
      if (p.gross_tonnage != null) updates.vessel_gross_tonnage = p.gross_tonnage;
      if (p.year_built != null) updates.vessel_year_built = p.year_built;
      if (p.owner) updates.vessel_owner_name = p.owner;
      if (p.manager) updates.vessel_manager_name = p.manager;
      if (p.loa != null) updates.vessel_loa = p.loa;
      if (p.beam != null) updates.vessel_beam = p.beam;
      if (p.draught != null) updates.vessel_draught = p.draught;
      if (p.flag && !formData.vessel_flag) updates.vessel_flag = p.flag;
      Object.entries(updates).forEach(([name, value]) => {
        handleInputChange({ target: { name, value } });
      });
    } catch (e) {
      // Enrichment is advisory; swallow errors so tracking still succeeds.
    }
  };

  const handleTrackVessel = async (imo, mmsi, { silent = false } = {}) => {
    const effectiveImo = imo || formData.vessel_imo_number?.trim();
    const effectiveMmsi = mmsi || formData.vessel_mmsi_number?.trim();
    if (!effectiveImo && !effectiveMmsi) {
      showError('Please search and select a vessel first');
      return;
    }
    
    // Prevent concurrent or duplicate tracking calls for the same vessel
    if (trackInFlightRef.current) return;
    trackInFlightRef.current = true;
    activeTrackingImo.current = effectiveImo || effectiveMmsi || '';

    setIsTrackingVessel(true);
    setConfirmedVesselImoNumber(effectiveImo || effectiveMmsi || '');
    try {
      const fullInfo = await globeService.getVesselFullInfo(effectiveImo, effectiveMmsi);
      handleVesselDataUpdate(fullInfo);

      // Enrich with richer particulars (DWT/GT/year/owner/dimensions) when the
      // intelligence provider is configured. Best-effort, runs after the live
      // ship-details so it only adds/refines spec fields.
      if (effectiveImo) {
        await applyVesselParticulars(effectiveImo);
      }

      if (!silent) {
        showSuccess('Vessel data updated successfully');
      }

    } catch (err) {
      showError(err.message || 'Failed to track vessel');
    } finally {
      setIsTrackingVessel(false);
      trackInFlightRef.current = false;
    }
  };

  const handleVesselSearchSelect = (vessel) => {
    // Reset tracking guard so the new vessel can be tracked
    activeTrackingImo.current = '';
    trackInFlightRef.current = false;
    
    handleInputChange({ target: { name: 'vessel_name', value: vessel.ship_name || '' } });
    handleInputChange({ target: { name: 'vessel_imo_number', value: vessel.imo || '' } });
    handleInputChange({ target: { name: 'vessel_mmsi_number', value: vessel.mmsi || '' } });
    handleInputChange({ target: { name: 'vessel_call_sign', value: vessel.call_sign || '' } });
    handleInputChange({ target: { name: 'vessel_flag', value: vessel.flag || '' } });

    // Clear port fields when a different vessel is picked so stale deal ports
    // don't linger. The new vessel's AIS origin/destination is only SUGGESTED
    // (via the "AIS suggests" chips under the port pickers) — never auto-written.
    handleInputChange({ target: { name: 'loading_port_location', value: '' } });
    handleInputChange({ target: { name: 'discharging_port_location', value: '' } });
    handleInputChange({ target: { name: 'product_origin', value: '' } });
    handleInputChange({ target: { name: 'delivery_destination', value: '' } });
    setResolvedPortMarkers([]);

    handleTrackVessel(vessel.imo, vessel.mmsi);
  };

  // --- "AIS suggests" chips -------------------------------------------------
  // AIS declares a raw origin/destination string (e.g. "PAPCN", "SINGAPORE",
  // "CN PAP >BERTH 3"). It is a FACT ABOUT THE VESSEL, not the deal, so it is
  // only ever DISPLAYED as a suggestion — a trader must click to adopt it. On
  // click we resolve the raw text to a canonical navigable port via the same
  // backend the picker uses, then write the port EXACTLY as an onSelect pick
  // would (canonical "Name, Country, Code" string + validation flag).

  // Strip berth junk / trailing free-text so "CN PAP >BERTH 3" -> "CN PAP".
  // NavAPI's copyrighted ports DB must never be bulk-queried; this runs only on
  // an explicit chip click (one interactive search), same billing model as the
  // picker. Keeps letters, digits and single spaces.
  const cleanAisPortValue = (raw) => {
    if (!raw) return '';
    return String(raw)
      .split(/[>|/(]/)[0]          // drop berth/notes after >, |, /, (
      .replace(/[^A-Za-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // A clean 5-char UN/LOCODE (2 alpha country + 3 alnum) with no embedded space.
  const AIS_UNLOCODE_RE = /^[A-Z]{2}[A-Z0-9]{3}$/;

  // Adopt an AIS-declared origin/destination into a port field on explicit
  // click. field is 'loading_port_location' | 'discharging_port_location'.
  const applyAisSuggestion = async (field, rawAisValue) => {
    const cleaned = cleanAisPortValue(rawAisValue);
    if (!cleaned) return;

    setAisSuggestState(prev => ({
      ...prev,
      [field]: { resolving: true, note: '' },
    }));

    // Normalize whatever backend shape we get into the picker's port object
    // ({ port_name, country, port_code }) so buildCanonicalPortString matches
    // the dropdown-pick format exactly.
    const writePort = (port) => {
      const canonical = buildCanonicalPortString(port);
      if (!canonical) return false;
      handleInputChange({ target: { name: field, value: canonical } });
      handlePortValidationChange(field, true);
      return true;
    };

    try {
      const compact = cleaned.replace(/\s+/g, '').toUpperCase();
      let resolved = false;

      // A clean UN/LOCODE: prefer the routing resolver (real navigable port only).
      if (AIS_UNLOCODE_RE.test(compact)) {
        try {
          const resp = await axiosInstance.get('/api/globe/resolve-port/', {
            params: { code: compact, routing: 1 },
          });
          const d = resp.data || {};
          resolved = writePort({
            port_name: d.name,
            country: d.country,
            port_code: d.port_code || d.code || compact,
          });
        } catch (codeErr) {
          // Fall through to the name search below.
        }
      }

      // Free-text name (or code that didn't resolve): SeaPortSearch top hit.
      if (!resolved) {
        const search = await globeService.portSearch(cleaned);
        const top = (search.results || [])[0];
        if (top) resolved = writePort(top);
      }

      if (resolved) {
        setAisSuggestState(prev => ({
          ...prev,
          [field]: { resolving: false, note: '' },
        }));
      } else {
        setAisSuggestState(prev => ({
          ...prev,
          [field]: { resolving: false, note: "couldn't match to a navigable port" },
        }));
      }
    } catch (err) {
      setAisSuggestState(prev => ({
        ...prev,
        [field]: { resolving: false, note: "couldn't match to a navigable port" },
      }));
    }
  };

  // Render the suggestion chip for a port field. Shown ONLY when a live vessel
  // position exists, the AIS field is non-empty, and the port input is empty or
  // differs from the raw AIS text (so it hides once the trader has filled it).
  const renderAisSuggestChip = (field, aisRaw) => {
    const raw = (aisRaw || '').trim();
    if (!vesselPositionData || !raw) return null;

    const current = (formData[field] || '').trim();
    // Hide once the field already reflects this suggestion (filled or matching).
    if (current && current.toUpperCase().includes(raw.toUpperCase())) return null;
    if (current) return null;  // any explicit value present -> chip dismissed

    const st = aisSuggestState[field] || { resolving: false, note: '' };
    return (
      <div className="ais-suggest">
        <button
          type="button"
          className="ais-suggest-chip"
          onClick={() => applyAisSuggestion(field, raw)}
          disabled={st.resolving}
          title="Adopt the vessel's AIS-declared port. AIS data is advisory — verify before use."
        >
          <span className="ais-suggest-label">AIS declares:</span>
          <span className="ais-suggest-value">{raw}</span>
          <span className="ais-suggest-action">{st.resolving ? 'resolving…' : 'use?'}</span>
        </button>
        {st.note && <span className="ais-suggest-note">{st.note}</span>}
      </div>
    );
  };

  // === DEBUG: Watch formData recap fields for changes ===
  // useEffect(() => {
  //   console.log('=== FORMDATA RECAP FIELDS CHANGED ===');
  //   console.log('purchase_recaps_data:', formData.purchase_recaps_data);
  //   console.log('active_purchase_recap_id:', formData.active_purchase_recap_id);
  //   console.log('sales_recaps_data:', formData.sales_recaps_data);
  //   console.log('active_sales_recap_id:', formData.active_sales_recap_id);
  // }, [formData.purchase_recaps_data, formData.active_purchase_recap_id, formData.sales_recaps_data, formData.active_sales_recap_id]);
  
  // === PHASE 1: Fetch available recaps for linking ===
  // Scoped to the delivery's Trade Ops flavour so the Cargo picker doesn't
  // list the (many) fuels ENQ recap legs. Router state is the pre-save
  // fallback; the loaded delivery's own commodity_type is authoritative.
  const effectiveCommodityType = (formData.commodity_type === 'fuels' || formData.commodity_type === 'solids')
    ? formData.commodity_type
    : (location.state?.commodityType === 'fuels' ? 'fuels' : 'solids');

  const fetchAvailableRecaps = async () => {
    try {
      const response = await axiosInstance.get(`/api/deliveries/available-recaps/`, {
        params: { commodity_type: effectiveCommodityType }
      });
      setAvailableRecaps(response.data);
    } catch (error) {
      console.error('Error fetching available recaps:', error);
    }
  };

  // Fetch available recaps on mount, and again if the flavour resolves to a
  // different value once an existing delivery finishes loading.
  useEffect(() => {
    fetchAvailableRecaps();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCommodityType]);

  // === Freight recap linking (Jonas feedback 19.08) ===
  // Freight recaps could only be attached while creating a delivery; the
  // backend assign_recap/unassign_recap endpoints always handled the F
  // prefix, so this is purely a reachability fix on the form.
  const isExistingDelivery = deliveryId && deliveryId !== 'new' && deliveryId !== 'undefined';

  const linkedFreightNumbers = React.useMemo(() => {
    const nums = formData.associated_trade_recap_numbers?.trade_recap_numbers || [];
    return nums.filter((n) => typeof n === 'string' && n.startsWith('F'));
  }, [formData.associated_trade_recap_numbers]);

  const handleLinkFreightRecaps = async (selectedRecaps) => {
    if (!isExistingDelivery) {
      // New delivery: queue them; linkPendingLinkedRecaps posts after save.
      setPendingLinkedFreightRecaps((prev) => {
        const have = new Set(prev.map((r) => r.transaction_number));
        return [...prev, ...selectedRecaps.filter((r) => !have.has(r.transaction_number))];
      });
      showSuccess('Freight recap marked for linking - will link when delivery is saved');
      if (markAsChanged) markAsChanged();
      return;
    }

    const linked = [];
    for (const recap of selectedRecaps) {
      try {
        await axiosInstance.post(`/api/deliveries/${deliveryId}/assign_recap/`, {
          recap_transaction_number: recap.transaction_number,
        });
        linked.push(recap.transaction_number);
      } catch (error) {
        console.error(`Error linking freight recap ${recap.transaction_number}:`, error);
        showError(error.response?.data?.error || `Failed to link ${recap.transaction_number}`);
      }
    }
    if (linked.length > 0) {
      const current = formData.associated_trade_recap_numbers?.trade_recap_numbers || [];
      handleInputChange({
        target: {
          name: 'associated_trade_recap_numbers',
          value: { trade_recap_numbers: [...current, ...linked].sort() },
        },
      });
      // Feed the Linked Freight Recap strip immediately (its rows come from
      // freight_recaps_data; a full reload would also rebuild it).
      const newRows = selectedRecaps.filter((r) => linked.includes(r.transaction_number));
      handleInputChange({
        target: {
          name: 'freight_recaps_data',
          value: [...(formData.freight_recaps_data || []), ...newRows],
        },
      });
      fetchAvailableRecaps();
      showSuccess(`Linked ${linked.length} freight recap${linked.length > 1 ? 's' : ''}`);
    }
  };

  const handleUnlinkFreightRecap = async (transactionNumber) => {
    if (!isExistingDelivery) {
      setPendingLinkedFreightRecaps((prev) =>
        prev.filter((r) => r.transaction_number !== transactionNumber)
      );
      return;
    }
    try {
      await axiosInstance.post(`/api/deliveries/${deliveryId}/unassign_recap/`, {
        recap_transaction_number: transactionNumber,
      });
      const current = formData.associated_trade_recap_numbers?.trade_recap_numbers || [];
      handleInputChange({
        target: {
          name: 'associated_trade_recap_numbers',
          value: { trade_recap_numbers: current.filter((n) => n !== transactionNumber) },
        },
      });
      fetchAvailableRecaps();
      showSuccess(`Freight recap ${transactionNumber} unlinked`);
    } catch (error) {
      console.error(`Error unlinking freight recap ${transactionNumber}:`, error);
      showError(error.response?.data?.error || 'Failed to unlink freight recap');
    }
  };

  useEffect(() => {
    // Check if we have pre-linked recaps from TradeOperationsLayout
    if (location.state?.preLinkedRecaps) {
      const { purchase = [], sales = [], freight = [] } = location.state.preLinkedRecaps;
      
      if (purchase.length > 0) {
        setPendingLinkedPurchaseRecaps(purchase);
        handleInputChange({ 
          target: { name: 'active_purchase_recap_id', value: purchase[0].id }
        });
      }
      
      if (sales.length > 0) {
        setPendingLinkedSalesRecaps(sales);
        handleInputChange({ 
          target: { name: 'active_sales_recap_id', value: sales[0].id }
        });
      }

      if (freight.length > 0) {
        setPendingLinkedFreightRecaps(freight);
      }
      
      if (markAsChanged) {
        markAsChanged();
      }
    }
  }, []);

  // Hydrate pre-linked recaps to FULL detail. Recaps arriving from the deals
  // grid (Assign Delivery -> Create New) come through the LIGHTWEIGHT list
  // serializer, which strips every FUELS_ONLY field: `recap_products` (the
  // bunkers WHAT card renders qty/price from these), the trader-authored
  // `purchase_date`/`sale_date` (the WHO/CONTRACTS confirmation-date auto-fill
  // reads these), vessel, ship-details and funding. That's why those sections
  // sat empty until the first save round-tripped the full recap back. Fetch
  // each pre-linked recap's full detail once and upgrade the pending
  // objects in place, preserving the deals-grid allocation markers, so the
  // form previews the same data pre-save. Guarded to run a single fetch pass.
  const preLinkedHydratedRef = useRef(false);
  const [preLinkedHydrationDone, setPreLinkedHydrationDone] = useState(false);
  useEffect(() => {
    if (preLinkedHydratedRef.current) return;
    const pending = [...pendingLinkedPurchaseRecaps, ...pendingLinkedSalesRecaps];
    if (!pending.length) return;
    // The list serializer drops `recap_products` for every row, so a cargo
    // recap with several products would show as one editable product until
    // the first save. A present `recap_products` array means hydrated.
    const needs = pending.filter(
      r => r && !Array.isArray(r.recap_products)
    );
    preLinkedHydratedRef.current = true;
    if (!needs.length) { setPreLinkedHydrationDone(true); return; }

    (async () => {
      const fullById = {};
      try {
        await Promise.all(needs.map(async (r) => {
          try {
            const resp = await axiosInstance.get(`/api/offer/trade-recaps/${r.id}/`);
            fullById[r.id] = resp.data;
          } catch (e) {
            console.error(`Failed to hydrate pre-linked recap ${r.id}:`, e);
          }
        }));
        if (Object.keys(fullById).length) {
          const merge = (list) => list.map(r => {
            const full = fullById[r.id];
            if (!full) return r;
            // Preserve pending-only markers the link/save path relies on.
            return {
              ...full,
              is_partial_allocation: r.is_partial_allocation,
              allocated_quantity: r.allocated_quantity,
            };
          });
          setPendingLinkedPurchaseRecaps(prev => merge(prev));
          setPendingLinkedSalesRecaps(prev => merge(prev));
        }
      } finally {
        // Unblock the auto-populate pass even if the fetch failed: it then runs
        // off whatever data we have (numbers still fill from the list payload).
        setPreLinkedHydrationDone(true);
      }
    })();
  }, [pendingLinkedPurchaseRecaps, pendingLinkedSalesRecaps]);

  // Shared WHO/contract auto-population from a recap (entity, partner,
  // incoterm, contract number, price basis, unit). Used by the manual link
  // path (handleLinkSavedRecap) and the pre-linked recaps path so both fill
  // in the same fields during editing. Only fills fields that are blank.
  const applyRecapAutoPopulate = (recapType, selectedRecap) => {
    if (!selectedRecap) return;

    // Ship Details / delivery window are trade-level fields the FuelEnquiry
    // captures up-front and the conversion service mirrors onto BOTH recap
    // legs (vessel, IMO, bunkering port, laycan, ETA). They're identical on the
    // sale and purchase leg, so map them once here, blank-only, regardless of
    // side. This is the pre-save mirror of the backend's
    // ``_pull_fuels_recap_details`` (deliveries/views.py) so the HOW tab shows
    // the same data before the first save instead of only after the link
    // endpoint copies it. ``expected_delivery_start`` is a YYYY-MM-DD date.
    if (selectedRecap.commodity_type === 'fuels') {
      const shipMap = [
        ['vessel_imo_number', selectedRecap.trade_recap_imo_number],
        ['loading_port_location', selectedRecap.loading_port],
        ['discharging_port_location', selectedRecap.discharging_port],
        ['delivery_laycan', selectedRecap.laycan],
        ['delivery_estimated_time_of_arrival',
         selectedRecap.expected_delivery_start
           ? String(selectedRecap.expected_delivery_start).slice(0, 10)
           : ''],
      ];
      shipMap.forEach(([field, value]) => {
        if (value && !formData[field]) {
          handleInputChange({ target: { name: field, value } });
        }
      });
    }

    if (recapType === 'purchase') {
      if (selectedRecap.entity && !formData.hms_entity) {
        const matchedEntity = dropdownOptions.hms_entities.find(
          e => e.label === selectedRecap.entity || String(e.value) === String(selectedRecap.entity)
        );
        const entityValue = matchedEntity ? matchedEntity.value : selectedRecap.entity;
        handleInputChange({ target: { name: 'hms_entity', value: entityValue } });
      }

      // Vessel name displays in the header and flows to save. IMO / ports /
      // laycan / ETA are handled by the shared Ship Details block above.
      if (selectedRecap.vessel_name && !formData.vessel_name) {
        handleInputChange({ target: { name: 'vessel_name', value: selectedRecap.vessel_name } });
      }

      if (selectedRecap.purchase_seller || selectedRecap.purchase_seller_crm_id) {
        const sellerExists = sellerContractualList.some(s =>
          (selectedRecap.purchase_seller_crm_id && s.crm_id === selectedRecap.purchase_seller_crm_id) ||
          (selectedRecap.purchase_seller && s.name === selectedRecap.purchase_seller)
        );
        if (!sellerExists) {
          const newSeller = {
            name: selectedRecap.purchase_seller || '',
            crm_id: selectedRecap.purchase_seller_crm_id || null,
            reporting_as: false,
            sanction_status: 'pending',
            kyc_status: 'pending'
          };
          setSellerContractualList(prev => {
            const blankIdx = prev.findIndex(s => !s.name && !s.crm_id);
            if (blankIdx !== -1) {
              const updated = [...prev];
              updated[blankIdx] = newSeller;
              return updated;
            }
            return [...prev, newSeller];
          });
        }
      }

      if (selectedRecap.purchase_incoterm && !formData.incoterms_purchase) {
        handleInputChange({ target: { name: 'incoterms_purchase', value: selectedRecap.purchase_incoterm } });
      }

      if (selectedRecap.purchase_contract_number && !formData.buyer_contract_number) {
        handleInputChange({ target: { name: 'buyer_contract_number', value: selectedRecap.purchase_contract_number } });
      }

      if (!formData.price_basis_purchase_quality && (selectedRecap.purchase_price_basis_option || selectedRecap.purchase_price_basis_value)) {
        const parts = [];
        if (selectedRecap.purchase_price_basis_option) parts.push(selectedRecap.purchase_price_basis_option);
        if (selectedRecap.purchase_price_basis_value) parts.push(`${selectedRecap.purchase_price_basis_value} kcal/kg`);
        handleInputChange({ target: { name: 'price_basis_purchase_quality', value: parts.join(' ') } });
      }

      // Contract quality basis mirrors the purchase CV/price basis (same source
      // as price_basis_purchase_quality); editable for trader override.
      if (!formData.purchase_contract_quality_basis && (selectedRecap.purchase_price_basis_option || selectedRecap.purchase_price_basis_value)) {
        const qualityParts = [];
        if (selectedRecap.purchase_price_basis_option) qualityParts.push(selectedRecap.purchase_price_basis_option);
        if (selectedRecap.purchase_price_basis_value) qualityParts.push(`${selectedRecap.purchase_price_basis_value} kcal/kg`);
        handleInputChange({ target: { name: 'purchase_contract_quality_basis', value: qualityParts.join(' ') } });
      }

      // Confirmation date: trader-authored purchase_date (fuels) first, else the
      // legacy deal-confirmation timestamp. Slice to YYYY-MM-DD so both date and
      // datetime ISO strings feed the picker cleanly.
      if (!formData.purchase_confirmation_date && (selectedRecap.purchase_date || selectedRecap.purchase_deal_confirmation_date)) {
        const confirmationDate = String(selectedRecap.purchase_date || selectedRecap.purchase_deal_confirmation_date).slice(0, 10);
        handleInputChange({ target: { name: 'purchase_confirmation_date', value: confirmationDate } });
      }

      if (selectedRecap.purchase_contract_quantity_unit) {
        const matchingUnit = units.find(u => u.code === selectedRecap.purchase_contract_quantity_unit);
        if (matchingUnit) {
          handleInputChange({ target: { name: 'delivery_product_unit', value: matchingUnit.id } });
        } else {
          console.error('Unit code not found:', selectedRecap.purchase_contract_quantity_unit, 'Available units:', units.map(u => u.code));
        }
      }
    } else {
      if (selectedRecap.entity && !formData.hms_entity) {
        const matchedEntity = dropdownOptions.hms_entities.find(
          e => e.label === selectedRecap.entity || String(e.value) === String(selectedRecap.entity)
        );
        const entityValue = matchedEntity ? matchedEntity.value : selectedRecap.entity;
        handleInputChange({ target: { name: 'hms_entity', value: entityValue } });
      }

      // Vessel name displays in the header and flows to save. IMO / ports /
      // laycan / ETA are handled by the shared Ship Details block above.
      if (selectedRecap.vessel_name && !formData.vessel_name) {
        handleInputChange({ target: { name: 'vessel_name', value: selectedRecap.vessel_name } });
      }

      if (selectedRecap.sale_buyer || selectedRecap.sale_buyer_crm_id) {
        const buyerExists = buyerContractualList.some(b =>
          (selectedRecap.sale_buyer_crm_id && b.crm_id === selectedRecap.sale_buyer_crm_id) ||
          (selectedRecap.sale_buyer && b.name === selectedRecap.sale_buyer)
        );
        if (!buyerExists) {
          const newBuyer = {
            name: selectedRecap.sale_buyer || '',
            crm_id: selectedRecap.sale_buyer_crm_id || null,
            reporting_as: false,
            sanction_status: 'pending',
            kyc_status: 'pending'
          };
          setBuyerContractualList(prev => {
            const blankIdx = prev.findIndex(b => !b.name && !b.crm_id);
            if (blankIdx !== -1) {
              const updated = [...prev];
              updated[blankIdx] = newBuyer;
              return updated;
            }
            return [...prev, newBuyer];
          });
        }
      }

      if (selectedRecap.sale_incoterm && !formData.incoterms_sale) {
        handleInputChange({ target: { name: 'incoterms_sale', value: selectedRecap.sale_incoterm } });
      }

      if (selectedRecap.sale_contract_number && !formData.seller_contract_number) {
        handleInputChange({ target: { name: 'seller_contract_number', value: selectedRecap.sale_contract_number } });
      }

      if (!formData.price_basis_sale_quality && (selectedRecap.sale_cv_basis_option || selectedRecap.sale_cv_basis_value)) {
        const parts = [];
        if (selectedRecap.sale_cv_basis_option) parts.push(selectedRecap.sale_cv_basis_option);
        if (selectedRecap.sale_cv_basis_value) parts.push(`${selectedRecap.sale_cv_basis_value} kcal/kg`);
        handleInputChange({ target: { name: 'price_basis_sale_quality', value: parts.join(' ') } });
      }

      // Contract quality basis mirrors the sale CV basis (same source as
      // price_basis_sale_quality); editable for trader override.
      if (!formData.sales_contract_quality_basis && (selectedRecap.sale_cv_basis_option || selectedRecap.sale_cv_basis_value)) {
        const qualityParts = [];
        if (selectedRecap.sale_cv_basis_option) qualityParts.push(selectedRecap.sale_cv_basis_option);
        if (selectedRecap.sale_cv_basis_value) qualityParts.push(`${selectedRecap.sale_cv_basis_value} kcal/kg`);
        handleInputChange({ target: { name: 'sales_contract_quality_basis', value: qualityParts.join(' ') } });
      }

      // Confirmation date: trader-authored sale_date (fuels) first, else the
      // legacy deal-confirmation timestamp. Slice to YYYY-MM-DD so both date and
      // datetime ISO strings feed the picker cleanly.
      if (!formData.sale_confirmation_date && (selectedRecap.sale_date || selectedRecap.sale_deal_confirmation_date)) {
        const confirmationDate = String(selectedRecap.sale_date || selectedRecap.sale_deal_confirmation_date).slice(0, 10);
        handleInputChange({ target: { name: 'sale_confirmation_date', value: confirmationDate } });
      }

      if (selectedRecap.sale_contract_quantity_unit) {
        const matchingUnit = units.find(u => u.code === selectedRecap.sale_contract_quantity_unit);
        if (matchingUnit) {
          handleInputChange({ target: { name: 'sales_product_unit', value: matchingUnit.id } });
        } else {
          console.error('Unit code not found:', selectedRecap.sale_contract_quantity_unit, 'Available units:', units.map(u => u.code));
        }
      }
    }
  };

  // After a delivery's products are saved (DeliveryProductsTable), its recap
  // figures and totals are the server's again, so the next Save never sends
  // the old ones (HMS review r2f1bd5).
  const refreshRecapFigures = async () => {
    if (!isExistingDelivery) return;
    try {
      const { data } = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
      // The recap figures only: the delivery's totals are re-derived on its Save.
      ['sales_recaps_data', 'purchase_recaps_data'].forEach((name) => {
        if (name in data) handleInputChange({ target: { name, value: data[name] } });
      });
    } catch (err) {
      // The products table already shows what was saved; the next load reads the rest.
      console.error('Could not re-read the delivery after saving its products:', err);
    }
  };

  // === IMPROVED: Link saved recap to delivery (purchase or sales) ===
  const handleLinkSavedRecap = async (recapType, recapId, allocatedQuantity = null) => {
    // UPDATED: Now accepts optional allocatedQuantity parameter for partial allocation
    // If allocatedQuantity is null/undefined, uses full recap quantity (backward compatible)
    
    // Find the recap in available recaps
    const recapList = recapType === 'purchase' 
      ? availableRecaps.purchase_recaps 
      : availableRecaps.sales_recaps;
    const selectedRecap = recapList.find(r => r.id === recapId);
    
    if (!selectedRecap) {
      showError('Recap not found');
      return;
    }
    
    // If delivery NOT saved yet → add to pending linked recaps
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
      console.log('===== UNSAVED DELIVERY - PENDING RECAP LINKING =====');
      console.log('Recap type:', recapType);
      console.log('Selected recap:', selectedRecap);
      console.log('Units array length:', units.length);
      
      if (recapType === 'purchase') {
        console.log('🎯 handleLinkSavedRecap - Purchase - allocatedQuantity received:', allocatedQuantity, 'Type:', typeof allocatedQuantity);
        
        // Store recap with allocated quantity if partial
        const recapToAdd = {
          ...selectedRecap,
          allocated_quantity: allocatedQuantity !== null && allocatedQuantity !== undefined 
            ? allocatedQuantity 
            : selectedRecap.purchase_contract_quantity,
          is_partial_allocation: allocatedQuantity !== null && allocatedQuantity !== undefined,
          total_quantity: selectedRecap.purchase_contract_quantity
        };
        
        console.log('🎯 recapToAdd object:', {
          allocated_quantity: recapToAdd.allocated_quantity,
          is_partial_allocation: recapToAdd.is_partial_allocation,
          total_quantity: recapToAdd.total_quantity
        });
        
        setPendingLinkedPurchaseRecaps(prev => [...prev, recapToAdd]);
        // Mark as changed when linking recaps
        if (markAsChanged) {
          markAsChanged();
        }
        // Set as active if it's the first one
        if (pendingLinkedPurchaseRecaps.length === 0) {
          handleInputChange({ 
            target: { 
              name: 'active_purchase_recap_id', 
              value: recapId 
            } 
          });
        }
        
        // Auto-populate WHO / contract fields from the recap (shared with
        // the pre-linked recaps path so they fill in during editing).
        applyRecapAutoPopulate('purchase', selectedRecap);
      } else {
        console.log('🎯 handleLinkSavedRecap - Sales - allocatedQuantity received:', allocatedQuantity, 'Type:', typeof allocatedQuantity);
        
        // Store recap with allocated quantity if partial
        const recapToAdd = {
          ...selectedRecap,
          allocated_quantity: allocatedQuantity !== null && allocatedQuantity !== undefined 
            ? allocatedQuantity 
            : selectedRecap.sale_contract_quantity,
          is_partial_allocation: allocatedQuantity !== null && allocatedQuantity !== undefined,
          total_quantity: selectedRecap.sale_contract_quantity
        };
        
        console.log('🎯 recapToAdd object:', {
          allocated_quantity: recapToAdd.allocated_quantity,
          is_partial_allocation: recapToAdd.is_partial_allocation,
          total_quantity: recapToAdd.total_quantity
        });
        
        setPendingLinkedSalesRecaps(prev => [...prev, recapToAdd]);
        // Mark as changed when linking recaps
        if (markAsChanged) {
          markAsChanged();
        }
        if (pendingLinkedSalesRecaps.length === 0) {
          handleInputChange({ 
            target: { 
              name: 'active_sales_recap_id', 
              value: recapId 
            } 
          });
        }
        
        // Auto-populate WHO / contract fields from the recap (shared with
        // the pre-linked recaps path so they fill in during editing).
        applyRecapAutoPopulate('sales', selectedRecap);
      }
      
      console.log('===== END PENDING RECAP DEBUG =====');
      
      showSuccess(`${recapType === 'purchase' ? 'Purchase' : 'Sales'} recap marked for linking - will link when delivery is saved`);
      setShowPurchaseRecapModal(false);
      setShowSalesRecapModal(false);
      return;
    }
    
    // Delivery IS saved → link immediately via API
    try {
      const endpoint = recapType === 'purchase' 
        ? 'link-purchase-recap' 
        : 'link-sales-recap';
      
      // UPDATED: Pass allocated_quantity if provided (partial allocation)
      const requestBody = { recap_id: recapId };
      if (allocatedQuantity !== null && allocatedQuantity !== undefined) {
        requestBody.allocated_quantity = allocatedQuantity;
      }
      
      const response = await axiosInstance.post(
        `/api/deliveries/${deliveryId}/${endpoint}/`,
        requestBody
      );
      
      // Update formData with new recap arrays
      handleInputChange({ 
        target: { 
          name: `${recapType}_recaps_data`, 
          value: response.data[`${recapType}_recaps_data`] 
        } 
      });
      handleInputChange({ 
        target: { 
          name: `active_${recapType}_recap_id`, 
          value: response.data[`active_${recapType}_recap_id`] 
        } 
      });
      
      // Populate unit field from the active recap (matching blank recap behavior)
      // CRITICAL DEBUG: Check what's in the response
      console.log('===== SAVED RECAP LINKING DEBUG =====');
      console.log('Recap type:', recapType);
      console.log('Response data:', response.data);
      console.log('Units array:', units);
      console.log('Units array length:', units.length);
      
      const activeRecap = response.data[`${recapType}_recaps_data`]?.find(
        r => r.id === response.data[`active_${recapType}_recap_id`]
      );
      
      console.log('Active recap found:', activeRecap);
      
      if (activeRecap) {
        if (recapType === 'purchase') {
          const unitCode = activeRecap.purchase_contract_quantity_unit;
          console.log('Purchase unit code from recap:', unitCode);
          
          if (unitCode) {
            // Try to find matching unit by code
            const matchingUnit = units.find(u => u.code === unitCode);
            console.log('Matching unit found:', matchingUnit);
            
            if (matchingUnit) {
              console.log('Setting purchase unit to ID:', matchingUnit.id);
              handleInputChange({
                target: {
                  name: 'delivery_product_unit',
                  value: matchingUnit.id
                }
              });
            } else {
              console.error('FAILED: Unit code not found in units array:', unitCode, 'Available units:', units.map(u => u.code));
            }
          } else {
            console.warn('No purchase_contract_quantity_unit in recap');
          }
        } else {
          const unitCode = activeRecap.sale_contract_quantity_unit;
          console.log('Sales unit code from recap:', unitCode);
          
          if (unitCode) {
            const matchingUnit = units.find(u => u.code === unitCode);
            console.log('Matching unit found:', matchingUnit);
            
            if (matchingUnit) {
              console.log('Setting sales unit to ID:', matchingUnit.id);
              handleInputChange({
                target: {
                  name: 'sales_product_unit',
                  value: matchingUnit.id
                }
              });
            } else {
              console.error('FAILED: Unit code not found in units array:', unitCode, 'Available units:', units.map(u => u.code));
            }
          } else {
            console.warn('No sale_contract_quantity_unit in recap');
          }
        }
      } else {
        console.error('FAILED: Active recap not found in response data');
      }
      console.log('===== END DEBUG =====');
      
      // Refresh hms_entity from backend (auto-populated from recap on link)
      if (response.data.hms_entity && !formData.hms_entity) {
        // Backend returns entity name string from recap, but dropdown may use integer IDs as values.
        // Match by label (name) to find the correct dropdown value.
        const matchedEntity = dropdownOptions.hms_entities.find(
          e => e.label === response.data.hms_entity || String(e.value) === String(response.data.hms_entity)
        );
        const entityValue = matchedEntity ? matchedEntity.value : response.data.hms_entity;
        handleInputChange({ target: { name: 'hms_entity', value: entityValue } });
      }
      
      // Refresh seller/buyer lists from backend (auto-populated from recap on link)
      if (response.data.seller_contractual_list) {
        handleInputChange({ target: { name: 'seller_contractual_list', value: response.data.seller_contractual_list } });
      }
      if (response.data.buyer_contractual_list) {
        handleInputChange({ target: { name: 'buyer_contractual_list', value: response.data.buyer_contractual_list } });
      }

      // Refresh incoterms from backend (auto-populated from recap on link)
      if (response.data.incoterms_purchase !== undefined) {
        handleInputChange({ target: { name: 'incoterms_purchase', value: response.data.incoterms_purchase || '' } });
      }
      if (response.data.incoterms_sale !== undefined) {
        handleInputChange({ target: { name: 'incoterms_sale', value: response.data.incoterms_sale || '' } });
      }

      // Refresh contract numbers from backend (auto-populated from recap on link)
      if (response.data.seller_contract_number && !formData.seller_contract_number) {
        handleInputChange({ target: { name: 'seller_contract_number', value: response.data.seller_contract_number } });
      }
      if (response.data.buyer_contract_number && !formData.buyer_contract_number) {
        handleInputChange({ target: { name: 'buyer_contract_number', value: response.data.buyer_contract_number } });
      }

      // Refresh price basis quality from backend (auto-populated from recap on link)
      if (response.data.price_basis_purchase_quality && !formData.price_basis_purchase_quality) {
        handleInputChange({ target: { name: 'price_basis_purchase_quality', value: response.data.price_basis_purchase_quality } });
      }
      if (response.data.price_basis_sale_quality && !formData.price_basis_sale_quality) {
        handleInputChange({ target: { name: 'price_basis_sale_quality', value: response.data.price_basis_sale_quality } });
      }

      // Refresh contract quality basis + confirmation dates from backend
      // (auto-populated from recap on link). Dates arrive as YYYY-MM-DD.
      if (response.data.purchase_contract_quality_basis && !formData.purchase_contract_quality_basis) {
        handleInputChange({ target: { name: 'purchase_contract_quality_basis', value: response.data.purchase_contract_quality_basis } });
      }
      if (response.data.sales_contract_quality_basis && !formData.sales_contract_quality_basis) {
        handleInputChange({ target: { name: 'sales_contract_quality_basis', value: response.data.sales_contract_quality_basis } });
      }
      if (response.data.purchase_confirmation_date && !formData.purchase_confirmation_date) {
        handleInputChange({ target: { name: 'purchase_confirmation_date', value: response.data.purchase_confirmation_date } });
      }
      if (response.data.sale_confirmation_date && !formData.sale_confirmation_date) {
        handleInputChange({ target: { name: 'sale_confirmation_date', value: response.data.sale_confirmation_date } });
      }

      // Refresh intercompany status from backend
      if (response.data.is_intercompany !== undefined) {
        handleInputChange({ target: { name: 'is_intercompany', value: response.data.is_intercompany } });
      }
      if (response.data.intercompany_entities !== undefined) {
        handleInputChange({ target: { name: 'intercompany_entities', value: response.data.intercompany_entities } });
      }

      // Reload the full delivery so HOW/vessel fields the backend auto-populated
      // on link (vessel, IMO, loading port, laycan, ETA, origin, WHO-chain)
      // appear in the form immediately, before any save. Mirrors the unlink
      // handler's reload. Skips null/undefined so it never clears a field
      // the trader is mid-editing.
      try {
        const refreshed = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
        Object.keys(refreshed.data).forEach((key) => {
          if (key === 'active_purchase_recap_id' || key === 'active_sales_recap_id') {
            handleInputChange({ target: { name: key, value: refreshed.data[key] } });
          } else if (refreshed.data[key] !== null && refreshed.data[key] !== undefined) {
            handleInputChange({ target: { name: key, value: refreshed.data[key] } });
          }
        });
      } catch (reloadErr) {
        console.warn('Post-link delivery reload failed (non-fatal):', reloadErr);
      }

      showSuccess('Recap linked successfully');
      setShowPurchaseRecapModal(false);
      setShowSalesRecapModal(false);
      fetchAvailableRecaps();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to link recap');
    }
  };
  
  // Compatibility wrappers for existing modal calls
  const handleLinkPurchaseRecap = async (recapId, allocatedQuantity = null) => {
    return handleLinkSavedRecap('purchase', recapId, allocatedQuantity);
  };
  
  const handleLinkSalesRecap = async (recapId, allocatedQuantity = null) => {
    return handleLinkSavedRecap('sales', recapId, allocatedQuantity);
  };
  
  // OLD COMPLEX FUNCTIONS - MARKED FOR DELETION
  // === OLD COMPLEX VERSION DELETED ===
  // === NEW SIMPLIFIED FUNCTIONS (Moved above for organization) ===
  // Simplified blank recap creation (ALREADY DEFINED ABOVE)
  // const handleCreateBlankRecap - ALREADY EXISTS ABOVE
  
  // === DUPLICATE FUNCTIONS REMOVED (Already defined above) ===
  
  // === OLD COMPLEX FUNCTIONS DELETED (270+ lines removed) ===
  
  // === PHASE 2: Unlink recap from delivery ===
  // Unlink a FREIGHT recap (F-number). Freight has no M2M/allocation row —
  // its link is the F-number in associated_trade_recap_numbers — so it goes
  // through the unassign_recap action (which resets the recap to ACTIVE),
  // not the purchase/sales unlink endpoints.
  const handleUnlinkFreight = async (recap) => {
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') return;
    try {
      await axiosInstance.post(`/api/deliveries/${deliveryId}/unassign_recap/`, {
        recap_transaction_number: recap.transaction_number,
      });
      handleInputChange({
        target: {
          name: 'freight_recaps_data',
          value: (formData.freight_recaps_data || []).filter(r => r.id !== recap.id),
        },
      });
      showSuccess(`Freight recap ${recap.transaction_number} unlinked.`);
    } catch (error) {
      console.error('Error unlinking freight recap:', error);
      showError('Could not unlink the freight recap. Please try again.');
    }
  };

  const handleUnlinkRecap = async (recapType, recapId) => {
//    console.log('=== UNLINK RECAP STARTED ===');
//    console.log('Recap Type:', recapType);
//    console.log('Recap ID to unlink:', recapId);
//    console.log('Delivery ID:', deliveryId);
    
    // If delivery not saved yet, remove from pending state (blank or pending linked)
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
//      console.log('Delivery not saved yet - removing recap from pending state');
      
      // Mark as changed when removing recaps
      if (markAsChanged) {
        markAsChanged();
      }
      
      if (recapType === 'purchase') {
        const removedRecap = pendingLinkedPurchaseRecaps.find(r => r.id === recapId);
        
        setBlankPurchaseRecaps(prev => prev.filter(r => r.id !== recapId));
        setPendingLinkedPurchaseRecaps(prev => prev.filter(r => r.id !== recapId));
        
        if (activeBlankPurchaseId === recapId) {
          setActiveBlankPurchaseId(null);
        }
        
        // Remove seller if no other purchase recaps reference it
        if (removedRecap && (removedRecap.purchase_seller || removedRecap.purchase_seller_crm_id)) {
          const remainingRecaps = pendingLinkedPurchaseRecaps.filter(r => r.id !== recapId);
          const sellerStillUsed = remainingRecaps.some(r =>
            (removedRecap.purchase_seller_crm_id && r.purchase_seller_crm_id === removedRecap.purchase_seller_crm_id) ||
            (removedRecap.purchase_seller && r.purchase_seller === removedRecap.purchase_seller)
          );
          if (!sellerStillUsed) {
            setSellerContractualList(prev => {
              const filtered = prev.filter(s => !(
                (removedRecap.purchase_seller_crm_id && s.crm_id === removedRecap.purchase_seller_crm_id) ||
                (removedRecap.purchase_seller && s.name === removedRecap.purchase_seller)
              ));
              return filtered.length > 0 ? filtered : [{ name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' }];
            });
          }
        }
        
        // Clear entity if ALL recaps (purchase + sales) are gone
        const allPurchaseGone = pendingLinkedPurchaseRecaps.filter(r => r.id !== recapId).length === 0 && blankPurchaseRecaps.filter(r => r.id !== recapId).length === 0;
        const allSalesGone = pendingLinkedSalesRecaps.length === 0 && blankSalesRecaps.length === 0;
        if (allPurchaseGone && allSalesGone) {
          handleInputChange({ target: { name: 'hms_entity', value: '' } });
        }
      } else {
        const removedRecap = pendingLinkedSalesRecaps.find(r => r.id === recapId);
        
        setBlankSalesRecaps(prev => prev.filter(r => r.id !== recapId));
        setPendingLinkedSalesRecaps(prev => prev.filter(r => r.id !== recapId));
        
        if (activeBlankSalesId === recapId) {
          setActiveBlankSalesId(null);
        }
        
        // Remove buyer if no other sales recaps reference it
        if (removedRecap && (removedRecap.sale_buyer || removedRecap.sale_buyer_crm_id)) {
          const remainingRecaps = pendingLinkedSalesRecaps.filter(r => r.id !== recapId);
          const buyerStillUsed = remainingRecaps.some(r =>
            (removedRecap.sale_buyer_crm_id && r.sale_buyer_crm_id === removedRecap.sale_buyer_crm_id) ||
            (removedRecap.sale_buyer && r.sale_buyer === removedRecap.sale_buyer)
          );
          if (!buyerStillUsed) {
            setBuyerContractualList(prev => {
              const filtered = prev.filter(b => !(
                (removedRecap.sale_buyer_crm_id && b.crm_id === removedRecap.sale_buyer_crm_id) ||
                (removedRecap.sale_buyer && b.name === removedRecap.sale_buyer)
              ));
              return filtered.length > 0 ? filtered : [{ name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' }];
            });
          }
        }
        
        // Clear entity if ALL recaps (purchase + sales) are gone
        const allPurchaseGone = pendingLinkedPurchaseRecaps.length === 0 && blankPurchaseRecaps.length === 0;
        const allSalesGone = pendingLinkedSalesRecaps.filter(r => r.id !== recapId).length === 0 && blankSalesRecaps.filter(r => r.id !== recapId).length === 0;
        if (allPurchaseGone && allSalesGone) {
          handleInputChange({ target: { name: 'hms_entity', value: '' } });
        }
      }
      
      showSuccess(`${recapType === 'purchase' ? 'Purchase' : 'Sales'} recap removed`);
      return;
    }
    
    try {
      const endpoint = recapType === 'purchase'
        ? 'unlink-purchase-recap'
        : 'unlink-sales-recap';
      
//      console.log('Calling endpoint:', `/api/deliveries/${deliveryId}/${endpoint}/`);
      const response = await axiosInstance.post(
        `/api/deliveries/${deliveryId}/${endpoint}/`,
        { recap_id: recapId }
      );
      
//      console.log('Unlink recap response:', response.data);
//      console.log(`${recapType}_recaps_data after unlink:`, response.data[`${recapType}_recaps_data`]);
      
      // CRITICAL FIX: Define which fields belong to which side to prevent cross-contamination
      const purchaseOnlyFields = [
        'purchase_product_name',
        'delivery_product_quantity',
        'delivery_product_unit',
        'delivery_product_tolerance',
        'delivery_product_tolerance_lower_value',
        'delivery_product_tolerance_lower_unit',
        'delivery_product_tolerance_upper_value',
        'delivery_product_tolerance_upper_unit',
        'purchase_price_per_ton_contract',
        'purchase_price_per_ton_contract_currency',
        'purchase_price_per_ton_adjusted',
        'purchase_price_per_ton_adjusted_currency',
        'purchase_price_basis_type',
        'purchase_price_basis_value',
        'delivery_producer',
        'seller_contractual',
        'active_purchase_recap_id',
        'purchase_recaps_data'
      ];
      
      const salesOnlyFields = [
        'sales_product_name',
        'sales_product_quantity',
        'sales_product_unit',
        'sales_tolerance',
        'sales_tolerance_lower_value',
        'sales_tolerance_lower_unit',
        'sales_tolerance_upper_value',
        'sales_tolerance_upper_unit',
        'sales_price_per_ton_contract',
        'sale_price_per_ton_contract_currency',
        'sales_price_per_ton_adjusted',
        'sale_price_per_ton_adjusted_currency',
        'sales_price_basis_type',
        'sales_price_basis_value',
        'buyer_contractual',
        'delivery_end_user',
        'active_sales_recap_id',
        'sales_recaps_data'
      ];
      
      // Backend returns lightweight response - we reload full delivery data below
      // CRITICAL: Reload delivery data to get fresh recap arrays with complete data
      const deliveryResponse = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
      
      // Update formData with fresh data to ensure recap arrays are current
      // CRITICAL FIX: Allow null values for active_recap_id fields to properly clear stale state
      Object.keys(deliveryResponse.data).forEach(key => {
        // For active_recap_id fields, explicitly allow null to clear stale IDs
        if (key === 'active_purchase_recap_id' || key === 'active_sales_recap_id') {
          handleInputChange({ target: { name: key, value: deliveryResponse.data[key] } });
        } else if (deliveryResponse.data[key] !== null && deliveryResponse.data[key] !== undefined) {
          handleInputChange({ target: { name: key, value: deliveryResponse.data[key] } });
        }
      });
      
      // After unlinking, check if there's a NEW active recap or if all recaps are gone
      const recapsDataKey = recapType === 'purchase' ? 'purchase_recaps_data' : 'sales_recaps_data';
      const activeRecapIdKey = recapType === 'purchase' ? 'active_purchase_recap_id' : 'active_sales_recap_id';
      
      if (deliveryResponse.data[activeRecapIdKey]) {
        // There's a new active recap - populate form fields from it
        console.log(`Populating form fields from new active ${recapType} recap:`, deliveryResponse.data[activeRecapIdKey]);
        
        const activeRecap = deliveryResponse.data[recapsDataKey].find(
          r => r.id === deliveryResponse.data[activeRecapIdKey]
        );
        
        if (activeRecap) {
          const parsedTolerance = parseToleranceString(activeRecap.tolerance);
          
          if (recapType === 'purchase') {
            const fieldMappings = {
              'purchase_product_name': activeRecap.product,
              'delivery_product_quantity': activeRecap.quantity,
              'delivery_product_unit': activeRecap.quantity_unit,
              'purchase_price_per_ton_contract': activeRecap.price_value,
              'purchase_price_per_ton_contract_currency': activeRecap.price_currency,
              'delivery_product_tolerance': activeRecap.tolerance,
              'delivery_product_tolerance_lower_value': parsedTolerance.lowerValue,
              'delivery_product_tolerance_lower_unit': parsedTolerance.lowerUnit,
              'delivery_product_tolerance_upper_value': parsedTolerance.upperValue,
              'delivery_product_tolerance_upper_unit': parsedTolerance.upperUnit,
              'purchase_price_basis_type': activeRecap.price_basis_option,
              'purchase_price_basis_value': activeRecap.price_basis_value,
            };
            Object.entries(fieldMappings).forEach(([fieldName, fieldValue]) => {
              if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
                handleInputChange({ target: { name: fieldName, value: fieldValue } });
              }
            });
            if (activeRecap.product) {
              setProductSearchTerm(activeRecap.product);
            }
          } else {
            const fieldMappings = {
              'sales_product_name': activeRecap.product,
              'sales_product_quantity': activeRecap.quantity,
              'sales_product_unit': activeRecap.quantity_unit,
              'sales_price_per_ton_contract': activeRecap.price_value,
              'sale_price_per_ton_contract_currency': activeRecap.price_currency,
              'sales_tolerance': activeRecap.tolerance,
              'sales_tolerance_lower_value': parsedTolerance.lowerValue,
              'sales_tolerance_lower_unit': parsedTolerance.lowerUnit,
              'sales_tolerance_upper_value': parsedTolerance.upperValue,
              'sales_tolerance_upper_unit': parsedTolerance.upperUnit,
              'sales_price_basis_type': activeRecap.cv_basis_option,
              'sales_price_basis_value': activeRecap.cv_basis_value,
            };
            Object.entries(fieldMappings).forEach(([fieldName, fieldValue]) => {
              if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
                handleInputChange({ target: { name: fieldName, value: fieldValue } });
              }
            });
            if (activeRecap.product) {
              setSalesProductSearchTerm(activeRecap.product);
            }
          }
        }
      } else if (deliveryResponse.data[recapsDataKey] && deliveryResponse.data[recapsDataKey].length === 0) {
        // No recaps left - clear the fields
        console.log(`No ${recapType} recaps remaining - clearing fields`);
        
        // CRITICAL FIX: Explicitly clear the active recap ID to prevent stale state
        console.log(`Explicitly clearing ${activeRecapIdKey} to null`);
        originalHandleInputChange({ target: { name: activeRecapIdKey, value: null } });
        
        if (recapType === 'purchase') {
          const fieldsToClear = [
            'purchase_product_name',
            'delivery_product_quantity',
            'delivery_product_unit',
            'delivery_product_tolerance',
            'delivery_product_tolerance_lower_value',
            'delivery_product_tolerance_lower_unit',
            'delivery_product_tolerance_upper_value',
            'delivery_product_tolerance_upper_unit',
            'purchase_price_per_ton_contract',
            'purchase_price_per_ton_contract_currency',
            'purchase_price_per_ton_adjusted',
            'purchase_price_per_ton_adjusted_currency',
            'purchase_price_basis_type',
            'purchase_price_basis_value',
          ];
          fieldsToClear.forEach(field => {
            originalHandleInputChange({ target: { name: field, value: '' } });
          });
          setProductSearchTerm('');
        } else {
          const fieldsToClear = [
            'sales_product_name',
            'sales_product_quantity',
            'sales_product_unit',
            'sales_tolerance',
            'sales_tolerance_lower_value',
            'sales_tolerance_lower_unit',
            'sales_tolerance_upper_value',
            'sales_tolerance_upper_unit',
            'sales_price_per_ton_contract',
            'sale_price_per_ton_contract_currency',
            'sales_price_per_ton_adjusted',
            'sale_price_per_ton_adjusted_currency',
            'sales_price_basis_type',
            'sales_price_basis_value',
          ];
          fieldsToClear.forEach(field => {
            originalHandleInputChange({ target: { name: field, value: '' } });
          });
          setSalesProductSearchTerm('');
        }
      }
      
      showSuccess('Recap unlinked successfully');
      fetchAvailableRecaps(); // Refresh available recaps
//      console.log('=== UNLINK RECAP COMPLETED ===');
    } catch (error) {
      console.error('=== UNLINK RECAP ERROR ===');
      console.error('Error unlinking recap:', error);
      console.error('Error response:', error.response);
      showError('Failed to unlink recap');
    }
  };
  
  // === NEW SIMPLIFIED FUNCTIONS ===
  
  // Navigate between recaps (both saved and blank)
  const handleRecapNavigation = async (recapType, recapId) => {
    const isBlankRecap = typeof recapId === 'string' && recapId.startsWith('blank-');
    
    if (isBlankRecap) {
      // Just switch active blank ID - values already in state
      if (recapType === 'purchase') {
        setActiveBlankPurchaseId(recapId);
      } else {
        setActiveBlankSalesId(recapId);
      }
      return;
    }
    
    // Check if it's a pending linked recap (selected but not yet saved to delivery)
    const isPendingLinked = recapType === 'purchase'
      ? pendingLinkedPurchaseRecaps.some(r => r.id === recapId)
      : pendingLinkedSalesRecaps.some(r => r.id === recapId);
    
    if (isPendingLinked) {
      // Clear blank active ID so carousel shows the pending linked recap
      if (recapType === 'purchase') {
        setActiveBlankPurchaseId(null);
      } else {
        setActiveBlankSalesId(null);
      }
      
      // Just update active recap ID - no API call needed (not linked yet)
      handleInputChange({
        target: {
          name: `active_${recapType}_recap_id`,
          value: recapId
        }
      });
      return;
    }
    
    // Actually saved and linked recap - needs API call
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
      showError('Please save delivery first');
      return;
    }
    
    // Clear blank active ID so carousel shows the saved linked recap
    if (recapType === 'purchase') {
      setActiveBlankPurchaseId(null);
    } else {
      setActiveBlankSalesId(null);
    }
    
    try {
      const endpoint = recapType === 'purchase'
        ? 'set-active-purchase-recap'
        : 'set-active-sales-recap';
      
      await axiosInstance.post(
        `/api/deliveries/${deliveryId}/${endpoint}/`,
        { recap_id: recapId }
      );
      
      // Just update active ID - no field syncing needed
      handleInputChange({
        target: {
          name: `active_${recapType}_recap_id`,
          value: recapId
        }
      });
      
      showSuccess('Switched recap successfully');
    } catch (error) {
      showError('Failed to switch recap');
    }
  };
  
  // Handle field changes in blank recaps
  const handleBlankRecapFieldChange = (blankId, fieldName, value) => {
    // Mark as changed when blank recap values are updated
    if (markAsChanged) {
      markAsChanged();
    }
    
    // Determine if this is a purchase or sales blank recap
    const isPurchaseBlank = blankPurchaseRecaps.some(b => b.id === blankId);
    const recapType = isPurchaseBlank ? 'purchase' : 'sales';
    
    // Validate critical fields (product, unit, currency) against existing recaps
    if (['product', 'quantity_unit', 'price_currency', 'cv_basis_option'].includes(fieldName)) {
      // Get ALL existing recaps (saved, pending linked, and other filled blank recaps)
      const allExistingRecaps = [
        ...pendingLinkedPurchaseRecaps,
        ...pendingLinkedSalesRecaps,
        ...(formData.purchase_recaps_data || []),
        ...(formData.sales_recaps_data || [])
      ];
      
      // Add other filled blank recaps (not the current one being edited)
      blankPurchaseRecaps.forEach(blank => {
        if (blank.id !== blankId) {
          const values = blankRecapValues[blank.id];
          if (values?.product && values?.quantity_unit && values?.price_currency) {
            allExistingRecaps.push({
              ...blank,
              product: values.product,
              purchase_contract_quantity_unit: values.quantity_unit,
              purchase_contract_price_currency: values.price_currency
            });
          }
        }
      });
      
      blankSalesRecaps.forEach(blank => {
        if (blank.id !== blankId) {
          const values = blankRecapValues[blank.id];
          if (values?.product && values?.quantity_unit && values?.price_currency) {
            allExistingRecaps.push({
              ...blank,
              product: values.product,
              sale_contract_quantity_unit: values.quantity_unit,
              sale_contract_price_currency: values.price_currency
            });
          }
        }
      });
      
      // Validate if there are existing recaps
      if (allExistingRecaps.length > 0) {
        const firstRecap = allExistingRecaps[0];
        
        // Validate unit
        if (fieldName === 'quantity_unit' && value) {
          const firstUnit = firstRecap.purchase_contract_quantity_unit || firstRecap.sale_contract_quantity_unit;
          const normalizedFirst = normalizeUnit(firstUnit);
          const normalizedNew = normalizeUnit(value);
          if (normalizedFirst && normalizedNew && normalizedFirst !== normalizedNew) {
            const firstUnitName = units.find(u => String(u.id) === String(firstUnit))?.code || firstUnit;
            const newUnitName = units.find(u => String(u.id) === String(value))?.code || value;
            showError(`Unit mismatch: All recaps must use "${firstUnitName}"`);
            return;
          }
        }
        
        // Validate currency
        if (fieldName === 'price_currency' && value) {
          const firstCurrency = firstRecap.purchase_contract_price_currency || firstRecap.sale_contract_price_currency;
          if (firstCurrency && value && firstCurrency !== value) {
            showError(`Currency mismatch: All recaps must use "${firstCurrency}"`);
            return;
          }
        }
      }
    }
    
    setBlankRecapValues(prev => ({
      ...prev,
      [blankId]: {
        ...prev[blankId],
        [fieldName]: value
      }
    }));
  };

  // === SAVED RECAP EDIT HELPERS ===
  // Buffer a single field edit against a saved (already-persisted) TradeRecap.
  // fieldName MUST be a real TradeRecap column name (e.g. purchase_contract_quantity).
  const handleSavedRecapFieldChange = (recapId, fieldName, value) => {
    if (markAsChanged) markAsChanged();
    setSavedRecapEdits(prev => ({
      ...prev,
      [recapId]: {
        ...(prev[recapId] || {}),
        [fieldName]: value
      }
    }));
  };

  // Overlay any pending edits on top of the recap data fetched from the
  // backend, so the user sees their in-flight changes immediately.
  const getEditedRecap = (recap) => {
    if (!recap || !recap.id) return recap;
    const edits = savedRecapEdits[recap.id];
    if (!edits) return recap;
    return { ...recap, ...edits };
  };

  // Buffer a per-product field edit for a bunkers recap. Flushed on delivery
  // save via persistSavedRecapEdits as a products[] array on the recap PATCH.
  const handleProductFieldChange = (recapId, productId, field, value) => {
    if (recapId == null || productId == null) return;
    setSavedRecapProductEdits(prev => ({
      ...prev,
      [recapId]: {
        ...(prev[recapId] || {}),
        [productId]: { ...((prev[recapId] || {})[productId] || {}), [field]: value },
      },
    }));
    if (markAsChanged) markAsChanged();
  };

  // Format the 4 tolerance components into the legacy "5% to 10%" string the
  // backend also stores on the recap. Mirrors the local helper used elsewhere
  // in this file (see line ~1630) so behaviour is identical.
  const formatToleranceComponents = (lowerValue, lowerUnit, upperValue, upperUnit) => {
    const lower = lowerValue ? `${lowerValue}${lowerUnit || '%'}` : '';
    const upper = upperValue ? `${upperValue}${upperUnit || '%'}` : '';
    if (lower && upper) return `${lower} to ${upper}`;
    if (lower) return lower;
    if (upper) return upper;
    return '';
  };

  // Apply a tolerance component change to the saved-recap edit buffer.
  // Writes the component column + the legacy formatted string column so both
  // representations stay in sync on the backend.
  // sideCols = {
  //   lowerValueCol, lowerUnitCol, upperValueCol, upperUnitCol, stringCol
  // } — the actual TradeRecap column names for this side (purchase or sale).
  const updateSavedRecapTolerance = (recapId, currentRecap, sideCols, componentKey, value) => {
    const merged = {
      [sideCols.lowerValueCol]: currentRecap?.[sideCols.lowerValueCol] || '',
      [sideCols.lowerUnitCol]: currentRecap?.[sideCols.lowerUnitCol] || '%',
      [sideCols.upperValueCol]: currentRecap?.[sideCols.upperValueCol] || '',
      [sideCols.upperUnitCol]: currentRecap?.[sideCols.upperUnitCol] || '%',
      [componentKey]: value,
    };
    const stringValue = formatToleranceComponents(
      merged[sideCols.lowerValueCol],
      merged[sideCols.lowerUnitCol],
      merged[sideCols.upperValueCol],
      merged[sideCols.upperUnitCol]
    );
    if (markAsChanged) markAsChanged();
    setSavedRecapEdits(prev => ({
      ...prev,
      [recapId]: {
        ...(prev[recapId] || {}),
        [componentKey]: value,
        [sideCols.stringCol]: stringValue,
      }
    }));
  };

  // Create new blank recap with seller/buyer info from modal
  const handleCreateBlankRecap = (recapType, partnerName, partnerId) => {
    // Mark as changed when creating blank recaps
    if (markAsChanged) {
      markAsChanged();
    }
    
    const counter = recapType === 'purchase' 
      ? blankPurchaseRecaps.length + 1 
      : blankSalesRecaps.length + 1;
    
    const blankId = `blank-${recapType}-${Date.now()}`;
    
    const blankRecap = {
      id: blankId,
      transaction_number: `Blank ${recapType} ${counter}`,
      isBlank: true
    };
    
    if (recapType === 'purchase') {
      setBlankPurchaseRecaps(prev => [...prev, blankRecap]);
      setActiveBlankPurchaseId(blankId);
      // Initialize empty values with seller info
      setBlankRecapValues(prev => ({
        ...prev,
        [blankId]: {
          product: '',
          quantity: '',
          quantity_unit: '',
          tolerance_lower_value: '',
          tolerance_lower_unit: '%',
          tolerance_upper_value: '',
          tolerance_upper_unit: '%',
          price_value: '',
          price_currency: '',
          price_basis_option: '',
          price_basis_value: '',
          seller: partnerName || '',
          seller_crm_id: partnerId || null
        }
      }));
    } else {
      // Same for sales with buyer info
      setBlankSalesRecaps(prev => [...prev, blankRecap]);
      setActiveBlankSalesId(blankId);
      setBlankRecapValues(prev => ({
        ...prev,
        [blankId]: {
          product: '',
          quantity: '',
          quantity_unit: '',
          tolerance_lower_value: '',
          tolerance_lower_unit: '%',
          tolerance_upper_value: '',
          tolerance_upper_unit: '%',
          price_value: '',
          price_currency: '',
          cv_basis_option: '',
          cv_basis_value: '',
          buyer: partnerName || '',
          buyer_crm_id: partnerId || null
        }
      }));
    }
    
    showSuccess(`Blank recap created with ${recapType === 'purchase' ? 'seller' : 'buyer'}: ${partnerName}`);
  };
  
  // === PHASE 2: Bidirectional sync - debounced ===
  //
  // This fires as the trader types (debounced a second) and pushes the field
  // straight onto the trade recap behind the delivery. Every failure used to
  // land in the browser console and nowhere else, so a change the server
  // refused looked exactly like a change that was kept: the trader typed a
  // price, saw nothing, and the contract still said the old number.
  //
  // The server now refuses two of these outright — re-pricing a contract
  // without Trade Operations access, and touching the price or quantity behind
  // an invoice the customer already holds — so silence is no longer an option.
  // The refusal is shown once per distinct message: an unsent keystroke retries
  // on the next character and a toast per character would bury the sentence
  // that matters.
  const lastSyncFailureRef = useRef(null);
  const syncFieldToActiveRecap = useCallback(
    debounce(async (fieldName, fieldValue, recapType) => {
      if (!deliveryId) return;

      try {
        await axiosInstance.post(
          `/api/deliveries/${deliveryId}/sync-to-active-recap/`,
          {
            recap_type: recapType,
            fields: { [fieldName]: fieldValue }
          }
        );
        lastSyncFailureRef.current = null;
//        console.log(`Synced ${fieldName} to active ${recapType} recap`);
      } catch (error) {
        console.error('Failed to sync field to recap:', error);
        // Read the server's own sentence when it sent one. A refusal we author
        // arrives as `detail`; older handlers in this app use `error`. Anything
        // else (a dropped connection, a 500) has no sentence worth showing, so
        // it falls back to plain words rather than a status code.
        const body = error?.response?.data;
        const detail =
          typeof body === 'string' ? body
            : [body?.detail, body?.error, body?.message].find(v => typeof v === 'string' && v.trim());
        const message = detail
          || 'Your change could not be saved onto the trade recap behind this delivery.';
        if (lastSyncFailureRef.current !== message) {
          lastSyncFailureRef.current = message;
          showError(message, 10000);
        }
      }
    }, 1000),
    [deliveryId, showError]
  );
  
  // === OLD SYNC FUNCTIONS DELETED (starts here) ===
  const OLD_syncPendingRecapFields = (fieldName, fieldValue, currentToleranceValues = null) => {
    // Only sync if delivery not saved yet
    if (deliveryId && deliveryId !== 'new' && deliveryId !== 'undefined') return;
    
    // console.log('=== SYNC PENDING RECAP FIELDS ===');
    // console.log('fieldName:', fieldName, 'fieldValue:', fieldValue);
    // console.log('activeBlankPurchaseId:', activeBlankPurchaseId);
    
    // Handle tolerance component fields - format and sync the combined value
    if (fieldName && fieldName.includes('delivery_product_tolerance_')) {
      // Use passed values if available (for immediate updates), otherwise fall back to formData
      const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
        const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
        const upper = upperValue ? `${upperValue}${upperUnit}` : '';
        if (lower && upper) return `${lower} to ${upper}`;
        else if (lower) return lower;
        else if (upper) return upper;
        return '';
      };
      
      // Get current values - use passed values for the changed field, formData for others
      let lowerValue, lowerUnit, upperValue, upperUnit;
      
      if (currentToleranceValues) {
        lowerValue = currentToleranceValues.lowerValue;
        lowerUnit = currentToleranceValues.lowerUnit;
        upperValue = currentToleranceValues.upperValue;
        upperUnit = currentToleranceValues.upperUnit;
      } else {
        lowerValue = formData.delivery_product_tolerance_lower_value;
        lowerUnit = formData.delivery_product_tolerance_lower_unit;
        upperValue = formData.delivery_product_tolerance_upper_value;
        upperUnit = formData.delivery_product_tolerance_upper_unit;
      }
      
      const formatted = formatToleranceField(lowerValue, lowerUnit, upperValue, upperUnit);
      
      if (activeBlankPurchaseId) {
        setBlankPurchaseRecaps(prev => 
          prev.map(recap => 
            recap.id === activeBlankPurchaseId 
              ? { ...recap, purchase_shipping_tolerance: formatted }
              : recap
          )
        );
      }
      return;
    }
    
    if (fieldName && fieldName.includes('sales_tolerance_') && !fieldName.includes('total_')) {
      // Use passed values if available (for immediate updates), otherwise fall back to formData
      const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
        const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
        const upper = upperValue ? `${upperValue}${upperUnit}` : '';
        if (lower && upper) return `${lower} to ${upper}`;
        else if (lower) return lower;
        else if (upper) return upper;
        return '';
      };
      
      // Get current values - use passed values for the changed field, formData for others
      let lowerValue, lowerUnit, upperValue, upperUnit;
      
      if (currentToleranceValues) {
        lowerValue = currentToleranceValues.lowerValue;
        lowerUnit = currentToleranceValues.lowerUnit;
        upperValue = currentToleranceValues.upperValue;
        upperUnit = currentToleranceValues.upperUnit;
      } else {
        lowerValue = formData.sales_tolerance_lower_value;
        lowerUnit = formData.sales_tolerance_lower_unit;
        upperValue = formData.sales_tolerance_upper_value;
        upperUnit = formData.sales_tolerance_upper_unit;
      }
      
      const formatted = formatToleranceField(lowerValue, lowerUnit, upperValue, upperUnit);
      
      if (activeBlankSalesId) {
        setBlankSalesRecaps(prev => 
          prev.map(recap => 
            recap.id === activeBlankSalesId 
              ? { ...recap, sale_shipping_tolerance: formatted }
              : recap
          )
        );
      }
      return;
    }
    
    // Map delivery form fields to purchase recap fields
    const purchaseFieldMap = {
      'purchase_product_name': 'product',
      'delivery_product_quantity': 'purchase_contract_quantity',
      'delivery_product_unit': 'purchase_contract_quantity_unit',
      'purchase_price_per_ton_contract': 'purchase_contract_price_value',
      'purchase_price_per_ton_adjusted': 'purchase_adjusted_price_value',
      'purchase_price_currency': 'purchase_contract_price_currency',
      'purchase_price_per_ton_contract_currency': 'purchase_contract_price_currency',
      'purchase_price_per_ton_adjusted_currency': 'purchase_adjusted_price_currency',
      'delivery_product_tolerance': 'purchase_shipping_tolerance',
      'purchase_price_basis_type': 'purchase_price_basis_option',
      'purchase_price_basis_value': 'purchase_price_basis_value',
      'delivery_producer': 'purchase_producer',
      'seller_contractual': 'purchase_seller'
    };
    
    // Map delivery form fields to sales recap fields
    const salesFieldMap = {
      'sales_product_name': 'product',
      'sales_product_quantity': 'sale_contract_quantity',
      'sales_product_unit': 'sale_contract_quantity_unit',
      'sales_price_per_ton_contract': 'sale_contract_price_value',
      'sales_price_per_ton_adjusted': 'sale_adjusted_price_value',
      'sales_price_currency': 'sale_contract_price_currency',
      'sale_price_per_ton_contract_currency': 'sale_contract_price_currency',
      'sale_price_per_ton_adjusted_currency': 'sale_adjusted_price_currency',
      'sales_tolerance': 'sale_shipping_tolerance',
      'sales_price_basis_type': 'sale_cv_basis_option',
      'sales_price_basis_value': 'sale_cv_basis_value',
      'buyer_contractual': 'sale_buyer',
      'delivery_end_user': 'sale_enduser'
    };
    
    // Update purchase recap if field matches
    if (purchaseFieldMap[fieldName] && activeBlankPurchaseId) {
      const recapField = purchaseFieldMap[fieldName];
//      console.log('Updating purchase recap field:', recapField, '=', fieldValue);
      setBlankPurchaseRecaps(prev => {
        const updated = prev.map(recap => 
          recap.id === activeBlankPurchaseId 
            ? { ...recap, [recapField]: fieldValue }
            : recap
        );
//        console.log('Updated blankPurchaseRecaps:', updated);
        return updated;
      });
    }
    
    // Update sales recap if field matches
    if (salesFieldMap[fieldName] && activeBlankSalesId) {
      const recapField = salesFieldMap[fieldName];
      setBlankSalesRecaps(prev => 
        prev.map(recap => 
          recap.id === activeBlankSalesId 
            ? { ...recap, [recapField]: fieldValue }
            : recap
        )
      );
    }
  };
  
  // === PHASE 2: Wrap handleInputChange to include recap sync ===
  // originalHandleInputChange already defined at top of component
  const OLD_handleInputChangeWithSync = (e) => {
    // Call original handler
    originalHandleInputChange(e);
    
    // Determine if this field should sync to a recap
    const fieldName = e.target ? e.target.name : e.name;
    const fieldValue = e.target ? e.target.value : e.value;
    
    // For tolerance fields, build current values object to pass fresh data
    let currentToleranceValues = null;
    
    if (fieldName && fieldName.includes('delivery_product_tolerance_')) {
      currentToleranceValues = {
        lowerValue: fieldName === 'delivery_product_tolerance_lower_value' 
          ? fieldValue 
          : formData.delivery_product_tolerance_lower_value,
        lowerUnit: fieldName === 'delivery_product_tolerance_lower_unit' 
          ? fieldValue 
          : (formData.delivery_product_tolerance_lower_unit || '%'),
        upperValue: fieldName === 'delivery_product_tolerance_upper_value' 
          ? fieldValue 
          : formData.delivery_product_tolerance_upper_value,
        upperUnit: fieldName === 'delivery_product_tolerance_upper_unit' 
          ? fieldValue 
          : (formData.delivery_product_tolerance_upper_unit || '%')
      };
    }
    
    if (fieldName && fieldName.includes('sales_tolerance_') && !fieldName.includes('total_')) {
      currentToleranceValues = {
        lowerValue: fieldName === 'sales_tolerance_lower_value' 
          ? fieldValue 
          : formData.sales_tolerance_lower_value,
        lowerUnit: fieldName === 'sales_tolerance_lower_unit' 
          ? fieldValue 
          : (formData.sales_tolerance_lower_unit || '%'),
        upperValue: fieldName === 'sales_tolerance_upper_value' 
          ? fieldValue 
          : formData.sales_tolerance_upper_value,
        upperUnit: fieldName === 'sales_tolerance_upper_unit' 
          ? fieldValue 
          : (formData.sales_tolerance_upper_unit || '%')
      };
    }
    
    // Sync to pending recaps (for blank recaps before delivery is saved)
    // NOTE: syncPendingRecapFields removed - now handled by handleBlankRecapFieldChange
    // syncPendingRecapFields(fieldName, fieldValue, currentToleranceValues);
    
    // Only sync to server if we have an active delivery
    if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') return;
    
    // CRITICAL FIX: Don't sync to server's active recap if we're editing a pending recap
    // This prevents tolerance values from being written to the wrong recap
    if (activeBlankPurchaseId || activeBlankSalesId) {
      console.log('⚠️ Skipping server sync - currently editing a pending recap');
      return;
    }
    
    // Handle tolerance component fields - format and sync the combined value
    if (fieldName && fieldName.includes('delivery_product_tolerance_')) {
      // CRITICAL: Use setTimeout to ensure formData is updated before formatting
      setTimeout(() => {
        const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
          const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
          const upper = upperValue ? `${upperValue}${upperUnit}` : '';
          if (lower && upper) return `${lower} to ${upper}`;
          else if (lower) return lower;
          else if (upper) return upper;
          return '';
        };
        
        // Log values being formatted to catch digit loss
        console.log(`🔍 Formatting tolerance - Field changed: ${fieldName}, Value: ${fieldValue}`);
        console.log(`   Lower: [${formData.delivery_product_tolerance_lower_value}]${formData.delivery_product_tolerance_lower_unit}`);
        console.log(`   Upper: [${formData.delivery_product_tolerance_upper_value}]${formData.delivery_product_tolerance_upper_unit}`);
        
        // Now formData is guaranteed to be updated
        const formatted = formatToleranceField(
          formData.delivery_product_tolerance_lower_value,
          formData.delivery_product_tolerance_lower_unit,
          formData.delivery_product_tolerance_upper_value,
          formData.delivery_product_tolerance_upper_unit
        );
        
        console.log(`   Formatted result: "${formatted}"`);
        syncFieldToActiveRecap('delivery_product_tolerance', formatted, 'purchase');
      }, 0);
      return; // Don't sync the individual component field
    }
    
    if (fieldName && fieldName.includes('sales_tolerance_') && !fieldName.includes('total_')) {
      // CRITICAL: Use setTimeout to ensure formData is updated before formatting
      setTimeout(() => {
        const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
          const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
          const upper = upperValue ? `${upperValue}${upperUnit}` : '';
          if (lower && upper) return `${lower} to ${upper}`;
          else if (lower) return lower;
          else if (upper) return upper;
          return '';
        };
        
        // Now formData is guaranteed to be updated
        const formatted = formatToleranceField(
          formData.sales_tolerance_lower_value,
          formData.sales_tolerance_lower_unit,
          formData.sales_tolerance_upper_value,
          formData.sales_tolerance_upper_unit
        );
        
        syncFieldToActiveRecap('sales_tolerance', formatted, 'sales');
      }, 0);
      return; // Don't sync the individual component field
    }
    
    // Sync purchase-related fields to active purchase recap
    if (fieldName && (
      fieldName.includes('purchase_') || 
      fieldName === 'delivery_product_quantity' ||
      fieldName === 'delivery_product_tolerance' ||
      fieldName === 'delivery_producer' ||
      fieldName === 'seller_contractual'
    )) {
      syncFieldToActiveRecap(fieldName, fieldValue, 'purchase');
    }
    
    // Sync sales-related fields to active sales recap
    if (fieldName && (
      fieldName.includes('sales_') || 
      fieldName.includes('sale_') ||
      fieldName === 'buyer_contractual' ||
      fieldName === 'delivery_end_user'
    )) {
      syncFieldToActiveRecap(fieldName, fieldValue, 'sales');
    }
  };
  
  // === SHADOWING REMOVED - No longer needed with simplified approach ===
  // handleInputChange used directly from props (no syncing needed)
  
  // === PHASE 1: Handle section navigation click ===
  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    // No scrolling needed - we're toggling visibility instead
  };
  
  // Custom styles for currency selects to prevent overflow
  const currencySelectStyle = { 
    flex: 1.5, 
    minWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 10 // Ensure dropdown appears above other elements
  };
  
  // Port validation states
  const [portValidation, setPortValidation] = useState({
    loading_port_location: true,
    discharging_port_location: true
  });
  
  // === CREATE BLANK RECAPS IN DATABASE ===
  // OLD FUNCTION - No longer used (replaced by createAndLinkBlankRecaps in useImperativeHandle)
  const OLD_createBlankRecapsInDB = async () => {
    console.log('=== OLD CREATE BLANK RECAPS IN DATABASE ===');
    console.log('📊 Active pending purchase recap ID:', activeBlankPurchaseId);
    console.log('📊 Active pending sales recap ID:', activeBlankSalesId);
    console.log('📊 Current form tolerance values:', {
      purchase_lower: formData.delivery_product_tolerance_lower_value,
      purchase_lower_unit: formData.delivery_product_tolerance_lower_unit,
      purchase_upper: formData.delivery_product_tolerance_upper_value,
      purchase_upper_unit: formData.delivery_product_tolerance_upper_unit,
      sales_lower: formData.sales_tolerance_lower_value,
      sales_lower_unit: formData.sales_tolerance_lower_unit,
      sales_upper: formData.sales_tolerance_upper_value,
      sales_upper_unit: formData.sales_tolerance_upper_unit
    });
    
    // CRITICAL: Save current form values to active pending recap BEFORE creating in DB
    const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
      const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
      const upper = upperValue ? `${upperValue}${upperUnit}` : '';
      const result = lower && upper ? `${lower} to ${upper}` : (lower || upper || '');
      console.log(`   🔄 Format tolerance: [${lowerValue}${lowerUnit}] to [${upperValue}${upperUnit}] => "${result}"`);
      return result;
    };
    
    // Update purchase recaps with current form values if viewing a temp recap
    let updatedPurchaseRecaps = [...blankPurchaseRecaps];
    if (activeBlankPurchaseId && (typeof activeBlankPurchaseId === 'string' && activeBlankPurchaseId.startsWith('temp-'))) {
      console.log(`📝 Saving form values to active pending purchase recap: ${activeBlankPurchaseId}`);
      
      const formattedTolerance = formatToleranceField(
        formData.delivery_product_tolerance_lower_value,
        formData.delivery_product_tolerance_lower_unit,
        formData.delivery_product_tolerance_upper_value,
        formData.delivery_product_tolerance_upper_unit
      );
      
      console.log(`   ✅ Formatted purchase tolerance: "${formattedTolerance}"`);
      console.log(`   Product: ${formData.purchase_product_name}`);
      
      updatedPurchaseRecaps = updatedPurchaseRecaps.map(r => {
        if (r.id === activeBlankPurchaseId) {
          const updated = {
            ...r,
            product: formData.purchase_product_name,
            purchase_contract_quantity: formData.delivery_product_quantity,
            purchase_contract_quantity_unit: formData.delivery_product_unit,
            purchase_contract_price_value: formData.purchase_price_per_ton_contract,
            purchase_contract_price_currency: formData.purchase_price_per_ton_contract_currency,
            purchase_adjusted_price_value: formData.purchase_price_per_ton_adjusted,
            purchase_adjusted_price_currency: formData.purchase_price_per_ton_adjusted_currency,
            purchase_shipping_tolerance: formattedTolerance,
            purchase_price_basis_option: formData.purchase_price_basis_type,
            purchase_price_basis_value: formData.purchase_price_basis_value,
            purchase_producer: formData.delivery_producer,
            purchase_seller: formData.seller_contractual,
          };
          console.log(`   📦 Updated recap:`, updated);
          return updated;
        }
        return r;
      });
      
      // Update state
      setBlankPurchaseRecaps(updatedPurchaseRecaps);
    }
    
    // Update sales recaps with current form values if viewing a temp recap
    let updatedSalesRecaps = [...blankSalesRecaps];
    if (activeBlankSalesId && (typeof activeBlankSalesId === 'string' && activeBlankSalesId.startsWith('temp-'))) {
      console.log(`📝 Saving form values to active pending sales recap: ${activeBlankSalesId}`);
      
      const formattedTolerance = formatToleranceField(
        formData.sales_tolerance_lower_value,
        formData.sales_tolerance_lower_unit,
        formData.sales_tolerance_upper_value,
        formData.sales_tolerance_upper_unit
      );
      
      console.log(`   ✅ Formatted sales tolerance: "${formattedTolerance}"`);
      console.log(`   Product: ${formData.sales_product_name}`);
      
      updatedSalesRecaps = updatedSalesRecaps.map(r => {
        if (r.id === activeBlankSalesId) {
          const updated = {
            ...r,
            product: formData.sales_product_name,
            sale_contract_quantity: formData.sales_product_quantity,
            sale_contract_quantity_unit: formData.sales_product_unit,
            sale_contract_price_value: formData.sales_price_per_ton_contract,
            sale_contract_price_currency: formData.sale_price_per_ton_contract_currency,
            sale_adjusted_price_value: formData.sales_price_per_ton_adjusted,
            sale_adjusted_price_currency: formData.sale_price_per_ton_adjusted_currency,
            sale_shipping_tolerance: formattedTolerance,
            sale_cv_basis_option: formData.sales_price_basis_type,
            sale_cv_basis_value: formData.sales_price_basis_value,
            sale_buyer: formData.buyer_contractual,
            sale_enduser: formData.delivery_end_user,
          };
          console.log(`   📦 Updated recap:`, updated);
          return updated;
        }
        return r;
      });
      
      // Update state
      setBlankSalesRecaps(updatedSalesRecaps);
    }
    
    // Use updated arrays (not state) for filtering
    const blankPurchaseRecaps = updatedPurchaseRecaps.filter(r => typeof r.id === 'string' && r.id.startsWith('temp-'));
    const blankSalesRecaps = updatedSalesRecaps.filter(r => typeof r.id === 'string' && r.id.startsWith('temp-'));
    
//    console.log('Blank purchase recaps to create:', blankPurchaseRecaps.length);
//    console.log('Blank sales recaps to create:', blankSalesRecaps.length);
    
    const createdRecaps = {
      purchase: [],
      sales: []
    };
    
    try {
      // Create purchase recaps
      for (const blankRecap of blankPurchaseRecaps) {
        console.log(`🎯 Creating blank purchase recap: ${blankRecap.id}`);
        console.log(`   Tolerance: "${blankRecap.purchase_shipping_tolerance}"`);
        console.log(`   Product: "${blankRecap.product}"`);
        
        const payload = {
          recap_type: 'PURCHASE',
          // Common fields from delivery form
          vessel_name: formData.vessel_name || '',
          trade_recap_imo_number: formData.vessel_imo_number || '',
          entity: formData.business_name || '',
          product: blankRecap.product || '',
          loading_port: formData.loading_port_location || '',
          discharging_port: formData.discharging_port_location || '',
          // Purchase-specific fields from individual recap
          purchase_contract_quantity: blankRecap.purchase_contract_quantity || '',
          purchase_contract_quantity_unit: blankRecap.purchase_contract_quantity_unit || '',
          purchase_contract_price_value: blankRecap.purchase_contract_price_value || '',
          purchase_contract_price_currency: blankRecap.purchase_contract_price_currency || '',
          purchase_adjusted_price_value: blankRecap.purchase_adjusted_price_value || '',
          purchase_adjusted_price_currency: blankRecap.purchase_adjusted_price_currency || '',
          purchase_producer: blankRecap.purchase_producer || '',
          purchase_seller: blankRecap.purchase_seller || '',
          purchase_shipping_tolerance: blankRecap.purchase_shipping_tolerance || '',
          purchase_price_basis_option: blankRecap.purchase_price_basis_option || '',
          purchase_price_basis_value: blankRecap.purchase_price_basis_value || ''
        };
        
        // Remove empty strings and null values
        Object.keys(payload).forEach(key => {
          if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
            delete payload[key];
          }
        });
        
        console.log(`📤 Purchase recap payload:`, {
          product: payload.product,
          purchase_shipping_tolerance: payload.purchase_shipping_tolerance,
          purchase_contract_quantity: payload.purchase_contract_quantity
        });
        
        const response = await axiosInstance.post('/api/offer/trade-recaps/', payload);
        console.log(`✅ Created purchase recap:`, {
          id: response.data.id,
          transaction_number: response.data.transaction_number,
          tolerance: response.data.purchase_shipping_tolerance,
          product: response.data.product
        });
        
        createdRecaps.purchase.push({
          tempId: blankRecap.id,
          realId: response.data.id,
          recap: response.data
        });
      }
      
      // Create sales recaps
      for (const blankRecap of blankSalesRecaps) {
        console.log(`🎯 Creating blank sales recap: ${blankRecap.id}`);
        console.log(`   Tolerance: "${blankRecap.sale_shipping_tolerance}"`);
        console.log(`   Product: "${blankRecap.product}"`);
        
        const payload = {
          recap_type: 'SALE',
          // Common fields from delivery form
          vessel_name: formData.vessel_name || '',
          trade_recap_imo_number: formData.vessel_imo_number || '',
          entity: formData.business_name || '',
          product: blankRecap.product || '',
          loading_port: formData.loading_port_location || '',
          discharging_port: formData.discharging_port_location || '',
          // Sales-specific fields from individual recap
          sale_contract_quantity: blankRecap.sale_contract_quantity || '',
          sale_contract_quantity_unit: blankRecap.sale_contract_quantity_unit || '',
          sale_contract_price_value: blankRecap.sale_contract_price_value || '',
          sale_contract_price_currency: blankRecap.sale_contract_price_currency || '',
          sale_adjusted_price_value: blankRecap.sale_adjusted_price_value || '',
          sale_adjusted_price_currency: blankRecap.sale_adjusted_price_currency || '',
          sale_buyer: blankRecap.sale_buyer || '',
          sale_enduser: blankRecap.sale_enduser || '',
          sale_shipping_tolerance: blankRecap.sale_shipping_tolerance || '',
          sale_cv_basis_option: blankRecap.sale_cv_basis_option || '',
          sale_cv_basis_value: blankRecap.sale_cv_basis_value || ''
        };
        
        // Remove empty strings and null values
        Object.keys(payload).forEach(key => {
          if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
            delete payload[key];
          }
        });
        
        console.log(`📤 Sales recap payload:`, {
          product: payload.product,
          sale_shipping_tolerance: payload.sale_shipping_tolerance,
          sale_contract_quantity: payload.sale_contract_quantity
        });
        
        const response = await axiosInstance.post('/api/offer/trade-recaps/', payload);
        console.log(`✅ Created sales recap:`, {
          id: response.data.id,
          transaction_number: response.data.transaction_number,
          tolerance: response.data.sale_shipping_tolerance,
          product: response.data.product
        });
        
        createdRecaps.sales.push({
          tempId: blankRecap.id,
          realId: response.data.id,
          recap: response.data
        });
      }
      
      // Update pending recaps with real IDs and data
      if (createdRecaps.purchase.length > 0) {
        setBlankPurchaseRecaps(prev => {
          return prev.map(recap => {
            const created = createdRecaps.purchase.find(c => c.tempId === recap.id);
            if (created) {
              return {
                ...created.recap,
                quantity: created.recap.purchase_contract_quantity,
                price_value: created.recap.purchase_contract_price_value,
                price_currency: created.recap.purchase_contract_price_currency,
                producer: created.recap.purchase_producer,
                seller: created.recap.purchase_seller,
                tolerance: created.recap.purchase_shipping_tolerance,
                price_basis_option: created.recap.purchase_price_basis_option,
                price_basis_value: created.recap.purchase_price_basis_value
              };
            }
            return recap;
          });
        });
        
        // Update active recap ID if it was a blank recap
        const activeWasBlank = createdRecaps.purchase.find(c => c.tempId === activeBlankPurchaseId);
        if (activeWasBlank) {
          setActiveBlankPurchaseId(activeWasBlank.realId);
        }
      }
      
      if (createdRecaps.sales.length > 0) {
        setBlankSalesRecaps(prev => {
          return prev.map(recap => {
            const created = createdRecaps.sales.find(c => c.tempId === recap.id);
            if (created) {
              return {
                ...created.recap,
                quantity: created.recap.sale_contract_quantity,
                price_value: created.recap.sale_contract_price_value,
                price_currency: created.recap.sale_contract_price_currency,
                buyer: created.recap.sale_buyer,
                enduser: created.recap.sale_enduser,
                tolerance: created.recap.sale_shipping_tolerance,
                cv_basis_option: created.recap.sale_cv_basis_option,
                cv_basis_value: created.recap.sale_cv_basis_value
              };
            }
            return recap;
          });
        });
        
        // Update active recap ID if it was a blank recap
        const activeWasBlank = createdRecaps.sales.find(c => c.tempId === activeBlankSalesId);
        if (activeWasBlank) {
          setActiveBlankSalesId(activeWasBlank.realId);
        }
      }
      
      console.log('=== BLANK RECAPS CREATED SUCCESSFULLY ===');
      console.log(`✅ Created ${createdRecaps.purchase.length} purchase recap(s)`);
      console.log(`✅ Created ${createdRecaps.sales.length} sales recap(s)`);
      
      // Log summary of each created recap
      createdRecaps.purchase.forEach((cr, idx) => {
        console.log(`   Purchase #${idx + 1}: ${cr.recap.transaction_number} (ID: ${cr.realId}, was: ${cr.tempId})`);
        console.log(`      Tolerance: "${cr.recap.purchase_shipping_tolerance}"`);
      });
      createdRecaps.sales.forEach((cr, idx) => {
        console.log(`   Sales #${idx + 1}: ${cr.recap.transaction_number} (ID: ${cr.realId}, was: ${cr.tempId})`);
        console.log(`      Tolerance: "${cr.recap.sale_shipping_tolerance}"`);
      });
      
      return createdRecaps;
    } catch (error) {
      console.error('=== ERROR CREATING BLANK RECAPS ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  };
  
  // === HOW MUCH IS ON THIS RECAP? — ONE ANSWER FOR EVERY BOX ON THIS SCREEN ===
  // Marcel, 03.09: the recap card said "Recap total: 660 Metric Ton" while
  // "Total sales quantity" a few centimetres below said 500.00. The card sums
  // the recap's PRODUCT LINES; the totals boxes read the recap's HEADLINE
  // quantity and never looked at the product lines at all.
  //
  // Order of preference, and why:
  //   1. allocated_quantity — the server's own answer for this delivery. It
  //      is per-product-line now, so a 300 MT recap split 200/100 across two
  //      products reports 300 and not 600.
  //   2. recap_products_total_quantity — the server's sum of the product
  //      lines, sent on every linked recap. Used for a recap that is picked
  //      but not yet attached, so the box is right before Save too.
  //   3. the product lines carried on the object, summed here — same
  //      arithmetic the recap card does.
  //   4. the headline quantity — Solids and legacy recaps with no product
  //      lines, whose numbers must not move.
  const recapQuantityForTotals = (recap, isPurchase) => {
    const toNumber = recapFigureToNumber;

    const allocated = toNumber(recap.allocated_quantity);
    if (Number.isFinite(allocated)) return allocated;

    const serverProductsTotal = toNumber(recap.recap_products_total_quantity);
    if (Number.isFinite(serverProductsTotal)) return serverProductsTotal;

    const lines = recap.recap_products || recap.products || [];
    if (Array.isArray(lines) && lines.length > 0) {
      // Only trust the sum when every line carries a tonnage — a partial sum
      // would understate the deal, which is worse than showing the header.
      const quantities = lines.map(p => toNumber(p.sale_quantity));
      if (quantities.every(q => Number.isFinite(q))) {
        return quantities.reduce((a, q) => a + q, 0);
      }
    }

    const header = toNumber(
      isPurchase
        ? (recap.purchase_contract_quantity || recap.quantity)
        : (recap.sale_contract_quantity || recap.quantity)
    );
    return Number.isFinite(header) ? header : 0;
  };

  // === RECAP RANGE CALCULATION HELPER ===
  const calculateRecapRanges = (recaps, recapType) => {
    const isPurchase = recapType === 'purchase';
    
    // Check if ALL recaps have empty tolerance (exact quantity scenario)
    const allTolerancesEmpty = recaps.every(recap => {
      // For blank recaps, check separate tolerance fields
      if (recap.tolerance_lower_value !== undefined || recap.tolerance_upper_value !== undefined) {
        const hasLower = recap.tolerance_lower_value && recap.tolerance_lower_value.toString().trim() !== '';
        const hasUpper = recap.tolerance_upper_value && recap.tolerance_upper_value.toString().trim() !== '';
        return !hasLower && !hasUpper;
      }
      
      // For saved recaps, check tolerance string
      const toleranceStr = isPurchase 
        ? (recap.purchase_shipping_tolerance || recap.tolerance)
        : (recap.sale_shipping_tolerance || recap.tolerance);
      return !toleranceStr || toleranceStr.trim() === '';
    });
    
    // Calculate total quantity.
    // Single source of truth: recapQuantityForTotals() — see its comment.
    const totalQty = recaps.reduce(
      (sum, recap) => sum + recapQuantityForTotals(recap, isPurchase), 0);
    
    // If all tolerances are empty, return exact quantity (no range)
    if (allTolerancesEmpty) {
      return { min: totalQty, max: totalQty, isExact: true };
    }
    
    // Calculate ranges with tolerance
    let totalMin = 0;
    let totalMax = 0;
    
    recaps.forEach((recap) => {
      // Same one answer the total above uses, so the range can never be
      // built on a different tonnage than the total it brackets.
      const baseQty = recapQuantityForTotals(recap, isPurchase);

      // CRITICAL: For blank recaps, use separate tolerance fields directly
      // For saved recaps, parse the tolerance string
      let parsed;
      if (recap.tolerance_lower_value !== undefined || recap.tolerance_upper_value !== undefined) {
        // Blank recap with separate tolerance fields
        parsed = {
          lowerValue: recap.tolerance_lower_value || '',
          lowerUnit: recap.tolerance_lower_unit || '%',
          upperValue: recap.tolerance_upper_value || '',
          upperUnit: recap.tolerance_upper_unit || '%'
        };
      } else {
        // Saved recap with formatted tolerance string
        const toleranceString = isPurchase 
          ? (recap.purchase_shipping_tolerance || recap.tolerance)
          : (recap.sale_shipping_tolerance || recap.tolerance);
        parsed = parseToleranceString(toleranceString);
      }
      
      let min = baseQty;
      let max = baseQty;
      
      // Calculate min - ONLY if value is valid
      if (parsed.lowerValue && parsed.lowerValue.trim() !== '') {
        if (parsed.lowerUnit === '%') {
          const lowerPct = recapFigureToNumber(parsed.lowerValue);
          if (!isNaN(lowerPct)) {
            min = baseQty - (baseQty * lowerPct / 100);
          }
        } else if (parsed.lowerUnit === 'min') {
          const lowerMin = recapFigureToNumber(parsed.lowerValue);
          if (!isNaN(lowerMin)) {
            min = lowerMin;
          }
        }
      }
      
      // Calculate max - ONLY if value is valid
      if (parsed.upperValue && parsed.upperValue.trim() !== '') {
        if (parsed.upperUnit === '%') {
          const upperPct = recapFigureToNumber(parsed.upperValue);
          if (!isNaN(upperPct)) {
            max = baseQty + (baseQty * upperPct / 100);
          }
        } else if (parsed.upperUnit === 'max') {
          const upperMax = recapFigureToNumber(parsed.upperValue);
          if (!isNaN(upperMax)) {
            max = upperMax;
          }
        }
      }
      
      totalMin += min;
      totalMax += max;
    });
    
    return { min: totalMin, max: totalMax, isExact: false };
  };

  // === RECAP TOTALS CALCULATION ===
  const calculateRecapTotals = useCallback(() => {
    // For unsaved deliveries, use pending recaps only
    // For saved deliveries, merge saved recaps from backend with pending blank recaps
    let purchaseRecaps = (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined')
      ? [...blankPurchaseRecaps, ...pendingLinkedPurchaseRecaps]
      : [...(formData.purchase_recaps_data || []), ...blankPurchaseRecaps];
      
    let salesRecaps = (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined')
      ? [...blankSalesRecaps, ...pendingLinkedSalesRecaps]
      : [...(formData.sales_recaps_data || []), ...blankSalesRecaps];
    
    console.log('🔍 CALCULATE TOTALS - Recap Arrays:');
    console.log('Purchase recaps:', purchaseRecaps);
    console.log('Sales recaps:', salesRecaps);
    if (purchaseRecaps.length > 0) {
      console.log('First purchase recap fields:', Object.keys(purchaseRecaps[0]));
      console.log('First purchase recap allocated_quantity:', purchaseRecaps[0].allocated_quantity);
      console.log('First purchase recap is_partial_allocation:', purchaseRecaps[0].is_partial_allocation);
      console.log('First purchase recap total_quantity:', purchaseRecaps[0].total_quantity);
      console.log('First purchase recap purchase_contract_quantity:', purchaseRecaps[0].purchase_contract_quantity);
    }
    if (salesRecaps.length > 0) {
      console.log('First sales recap fields:', Object.keys(salesRecaps[0]));
      console.log('First sales recap quantity field:', salesRecaps[0].sale_contract_quantity);
    }
    
    // CRITICAL FIX: Merge ALL blank recap values from blankRecapValues
    // This ensures typed values are used in calculations even before recap is saved
    purchaseRecaps = purchaseRecaps.map(r => {
      // Check if this is a blank recap (string ID starting with 'blank-purchase-')
      if (typeof r.id === 'string' && r.id.startsWith('blank-purchase-')) {
        const blankValues = blankRecapValues[r.id];
        if (blankValues) {
          console.log('🔧 Merging blank purchase recap values:', r.id, blankValues);
          // Merge the typed values - KEEP the spread simple, let tolerance fields passthrough
          return {
            ...r,
            // Map critical fields for calculation
            purchase_contract_quantity: blankValues.quantity,
            purchase_contract_quantity_unit: blankValues.quantity_unit,
            // Keep tolerance fields AS-IS from blankValues (these are what calculateRecapRanges checks)
            tolerance_lower_value: blankValues.tolerance_lower_value,
            tolerance_lower_unit: blankValues.tolerance_lower_unit,
            tolerance_upper_value: blankValues.tolerance_upper_value,
            tolerance_upper_unit: blankValues.tolerance_upper_unit,
            purchase_adjusted_price_value: blankValues.price_value,
            purchase_adjusted_price_currency: blankValues.price_currency,
            product: blankValues.product
          };
        }
      }
      return r;
    });
    
    salesRecaps = salesRecaps.map(r => {
      // Check if this is a blank recap (string ID starting with 'blank-sales-')
      if (typeof r.id === 'string' && r.id.startsWith('blank-sales-')) {
        const blankValues = blankRecapValues[r.id];
        if (blankValues) {
          console.log('🔧 Merging blank sales recap values:', r.id, blankValues);
          // Merge the typed values - KEEP the spread simple, let tolerance fields passthrough
          return {
            ...r,
            // Map critical fields for calculation
            sale_contract_quantity: blankValues.quantity,
            sale_contract_quantity_unit: blankValues.quantity_unit,
            // Keep tolerance fields AS-IS from blankValues (these are what calculateRecapRanges checks)
            tolerance_lower_value: blankValues.tolerance_lower_value,
            tolerance_lower_unit: blankValues.tolerance_lower_unit,
            tolerance_upper_value: blankValues.tolerance_upper_value,
            tolerance_upper_unit: blankValues.tolerance_upper_unit,
            sale_adjusted_price_value: blankValues.price_value,
            sale_adjusted_price_currency: blankValues.price_currency,
            product: blankValues.product
          };
        }
      }
      return r;
    });

    // Overlay in-flight edits to already-saved recaps so totals/ranges reflect
    // what the user just typed BEFORE the PATCH+reload round-trip on save.
    // Same pattern as blankRecapValues above, but keyed on numeric recap.id.
    purchaseRecaps = purchaseRecaps.map(r => {
      if (r && r.id && savedRecapEdits[r.id]) {
        return { ...r, ...savedRecapEdits[r.id] };
      }
      return r;
    });
    salesRecaps = salesRecaps.map(r => {
      if (r && r.id && savedRecapEdits[r.id]) {
        return { ...r, ...savedRecapEdits[r.id] };
      }
      return r;
    });

    // Purchase totals. The tonnage comes from recapQuantityForTotals() so
    // this box and the recap card above it can never show different numbers.
    const purchaseQuantityTotal = purchaseRecaps.reduce(
      (sum, r) => sum + recapQuantityForTotals(r, true), 0);
    const purchaseToleranceAvg = purchaseRecaps.length > 0
      ? purchaseRecaps.reduce((sum, r) => {
          return sum + (recapFigureToNumber(r.purchase_shipping_tolerance || r.tolerance) || 0);
        }, 0) / purchaseRecaps.length
      : 0;
    // Average of all ADJUSTED purchase prices (average price per unit)
    // Fallback: adjusted -> contract -> 0
    const purchasePriceSum = purchaseRecaps.reduce((sum, r) => {
      const price = recapFigureToNumber(r.purchase_adjusted_price_value || r.adjusted_price_value || r.purchase_contract_price_value);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);
    const purchasePriceTotal = purchaseRecaps.length > 0 ? purchasePriceSum / purchaseRecaps.length : 0;
    const firstPurchaseRecap = purchaseRecaps[0];
    const purchaseCurrency = firstPurchaseRecap?.purchase_adjusted_price_currency || 
                            firstPurchaseRecap?.purchase_contract_price_currency ||
                            firstPurchaseRecap?.price_currency || '';
    const purchaseUnit = firstPurchaseRecap?.purchase_contract_quantity_unit || 
                        firstPurchaseRecap?.quantity_unit || '';
    
    // Sales totals. Same single answer as the purchase leg above -- this is
    // the box Marcel's screenshot showed reading 500 against a 660 MT recap.
    const salesQuantityTotal = salesRecaps.reduce(
      (sum, r) => sum + recapQuantityForTotals(r, false), 0);
    const salesToleranceAvg = salesRecaps.length > 0
      ? salesRecaps.reduce((sum, r) => {
          return sum + (recapFigureToNumber(r.sale_shipping_tolerance || r.tolerance) || 0);
        }, 0) / salesRecaps.length
      : 0;
    // Average of all ADJUSTED sales prices (average price per unit)
    // Fallback: adjusted -> contract -> 0
    const salesPriceSum = salesRecaps.reduce((sum, r) => {
      const price = recapFigureToNumber(r.sale_adjusted_price_value || r.adjusted_price_value || r.sale_contract_price_value);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);
    const salesPriceTotal = salesRecaps.length > 0 ? salesPriceSum / salesRecaps.length : 0;
    const firstSalesRecap = salesRecaps[0];
    const salesCurrency = firstSalesRecap?.sale_adjusted_price_currency ||
                         firstSalesRecap?.sale_contract_price_currency || 
                         firstSalesRecap?.price_currency || '';
    const salesUnit = firstSalesRecap?.sale_contract_quantity_unit || 
                     firstSalesRecap?.quantity_unit || '';
    
    // Calculate quantity ranges with tolerance
    let purchaseRange = calculateRecapRanges(purchaseRecaps, 'purchase');
    let salesRange = calculateRecapRanges(salesRecaps, 'sales');
    
    // CRITICAL: Only use form fields directly when there are NO recaps at all
    // If there are blank recaps, they've already been synced with form values above
    // and their ranges have been calculated by calculateRecapRanges
    if (purchaseRecaps.length === 0) {
      const baseQty = recapFigureToNumber(formData.delivery_product_quantity) || 0;
      const lowerValue = formData.delivery_product_tolerance_lower_value;
      const lowerUnit = formData.delivery_product_tolerance_lower_unit || '%';
      const upperValue = formData.delivery_product_tolerance_upper_value;
      const upperUnit = formData.delivery_product_tolerance_upper_unit || '%';
      
      let min = baseQty;
      let max = baseQty;
      
      if (lowerValue && lowerValue.trim() !== '') {
        const lowerVal = parseFloat(lowerValue);
        if (!isNaN(lowerVal)) {
          if (lowerUnit === '%') {
            min = baseQty - (baseQty * lowerVal / 100);
          } else if (lowerUnit === 'min') {
            min = lowerVal;
          }
        }
      }
      
      if (upperValue && upperValue.trim() !== '') {
        const upperVal = parseFloat(upperValue);
        if (!isNaN(upperVal)) {
          if (upperUnit === '%') {
            max = baseQty + (baseQty * upperVal / 100);
          } else if (upperUnit === 'max') {
            max = upperVal;
          }
        }
      }
      
      purchaseRange = { min, max };
    }
    
    if (salesRecaps.length === 0) {
      const baseQty = recapFigureToNumber(formData.sales_product_quantity) || 0;
      const lowerValue = formData.sales_tolerance_lower_value;
      const lowerUnit = formData.sales_tolerance_lower_unit || '%';
      const upperValue = formData.sales_tolerance_upper_value;
      const upperUnit = formData.sales_tolerance_upper_unit || '%';
      
      let min = baseQty;
      let max = baseQty;
      
      if (lowerValue && lowerValue.trim() !== '') {
        const lowerVal = parseFloat(lowerValue);
        if (!isNaN(lowerVal)) {
          if (lowerUnit === '%') {
            min = baseQty - (baseQty * lowerVal / 100);
          } else if (lowerUnit === 'min') {
            min = lowerVal;
          }
        }
      }
      
      if (upperValue && upperValue.trim() !== '') {
        const upperVal = parseFloat(upperValue);
        if (!isNaN(upperVal)) {
          if (upperUnit === '%') {
            max = baseQty + (baseQty * upperVal / 100);
          } else if (upperUnit === 'max') {
            max = upperVal;
          }
        }
      }
      
      salesRange = { min, max };
    }
    
    const hasRecaps = purchaseRecaps.length > 0 && salesRecaps.length > 0;
    
    // Directional validation: you can buy more than you sell (surplus), but NOT sell more than you buy
    const salesExceedsPurchase = salesRange.max > purchaseRange.max && 
                                  purchaseRange.max > 0 && salesRange.max > 0;
    const purchaseExceedsSales = purchaseRange.min > salesRange.max && 
                                  purchaseRange.min > 0 && salesRange.max > 0;
    const rangesOverlap = purchaseRange.max >= salesRange.min && 
                          salesRange.max >= purchaseRange.min;
    
    // Check for exact quantity mismatch (when tolerance is empty but quantities don't match)
    const exactQuantityMismatch = purchaseRange.isExact && salesRange.isExact && 
                                   purchaseQuantityTotal > 0 && salesQuantityTotal > 0 &&
                                   salesQuantityTotal > purchaseQuantityTotal + 0.01;
    
    // Entity mismatch: check if purchase and sales recaps belong to different HMS entities
    const purchaseEntities = [...new Set(purchaseRecaps.map(r => r.entity).filter(Boolean))];
    const salesEntities = [...new Set(salesRecaps.map(r => r.entity).filter(Boolean))];
    const allEntities = [...new Set([...purchaseEntities, ...salesEntities])];
    const entityMismatch = hasRecaps && allEntities.length > 1;
    const entityMismatchEntities = entityMismatch ? allEntities : [];
    
    // WHO OWNS A LEG'S TOTALS. A leg with recaps: this step, from those
    // recaps (the server re-derives the same columns from the allocations on
    // save). A leg with NO recaps: the trader -- its figures were typed by
    // hand, or loaded as typed, and this step has no opinion on them. So it
    // returns none of that leg's saved columns; it used to return '' for all
    // five, the effect below wrote them into the form on page load, and the
    // next Save sent the blanks. Only the quantity range is returned either
    // way: it is display-only (never saved) and, on a leg with no recaps, is
    // worked out above from that leg's own typed quantity and tolerance.
    const purchaseRangeFields = {
      purchase_quantity_range_min: (purchaseRange.min > 0 || purchaseRange.max > 0) ? formatFigure(purchaseRange.min, 2, QUANTITY_DECIMALS) : '',
      purchase_quantity_range_max: (purchaseRange.min > 0 || purchaseRange.max > 0) ? formatFigure(purchaseRange.max, 2, QUANTITY_DECIMALS) : '',
      purchase_range_is_exact: purchaseRange.isExact || false
    };
    const salesRangeFields = {
      sales_quantity_range_min: (salesRange.min > 0 || salesRange.max > 0) ? formatFigure(salesRange.min, 2, QUANTITY_DECIMALS) : '',
      sales_quantity_range_max: (salesRange.min > 0 || salesRange.max > 0) ? formatFigure(salesRange.max, 2, QUANTITY_DECIMALS) : '',
      sales_range_is_exact: salesRange.isExact || false
    };

    return {
      purchase: purchaseRecaps.length > 0 ? {
        total_purchase_product_quantity: purchaseQuantityTotal > 0 ? formatFigure(purchaseQuantityTotal, 2, QUANTITY_DECIMALS) : '',
        total_purchase_tolerance: purchaseToleranceAvg > 0 ? purchaseToleranceAvg.toFixed(2) + '%' : '',
        total_purchase_product_unit: purchaseUnit,
        total_purchase_price_per_ton_adjusted: purchasePriceTotal > 0 ? formatFigure(purchasePriceTotal, 2, PRICE_DECIMALS) : '',
        total_purchase_price_per_ton_adjusted_currency: purchaseCurrency,
        ...purchaseRangeFields
      } : purchaseRangeFields,
      sales: salesRecaps.length > 0 ? {
        total_sales_product_quantity: salesQuantityTotal > 0 ? formatFigure(salesQuantityTotal, 2, QUANTITY_DECIMALS) : '',
        total_sales_tolerance: salesToleranceAvg > 0 ? salesToleranceAvg.toFixed(2) + '%' : '',
        total_sales_product_unit: salesUnit,
        total_sale_price_per_ton_adjusted: salesPriceTotal > 0 ? formatFigure(salesPriceTotal, 2, PRICE_DECIMALS) : '',
        total_sale_price_per_ton_adjusted_currency: salesCurrency,
        ...salesRangeFields
      } : salesRangeFields,
      validation: {
        purchase_has_recaps: purchaseRecaps.length > 0,
        sales_has_recaps: salesRecaps.length > 0,
        ranges_overlap: rangesOverlap,
        has_recaps: hasRecaps,
        exact_quantity_mismatch: exactQuantityMismatch,
        sales_exceeds_purchase: salesExceedsPurchase,
        purchase_exceeds_sales: purchaseExceedsSales,
        entity_mismatch: entityMismatch,
        entity_mismatch_entities: entityMismatchEntities
      }
    };
  }, [
    blankPurchaseRecaps, 
    blankSalesRecaps,
    blankRecapValues,
    pendingLinkedPurchaseRecaps,
    pendingLinkedSalesRecaps,
    formData.purchase_recaps_data, 
    formData.sales_recaps_data, 
    deliveryId,
    formData.delivery_product_quantity,
    formData.delivery_product_tolerance_lower_value,
    formData.delivery_product_tolerance_lower_unit,
    formData.delivery_product_tolerance_upper_value,
    formData.delivery_product_tolerance_upper_unit,
    formData.sales_product_quantity,
    formData.sales_tolerance_lower_value,
    formData.sales_tolerance_lower_unit,
    formData.sales_tolerance_upper_value,
    formData.sales_tolerance_upper_unit,
    savedRecapEdits
  ]);
  
  // === PRODUCT EXTRACTION HELPER ===
  // Extract base product name (before first "|" or full name)
  const extractBaseProduct = (productString) => {
    if (!productString) return null;
    const parts = productString.split('|').map(p => p.trim());
    return parts[0].toLowerCase(); // "Steam Coal | Australia" → "steam coal"
  };
  
  // === UNIT NORMALIZATION HELPER ===
  // Helper to normalize unit for comparison - converts both ID and CODE to the same format
  const normalizeUnit = (unit) => {
    if (!unit) return null;
    const unitStr = String(unit);
    
    // Find the unit object by ID or CODE
    const unitObj = units.find(u => String(u.id) === unitStr || u.code === unitStr);
    
    if (unitObj) {
      // Return the CODE as the normalized format (more stable than ID)
      return unitObj.code;
    }
    
    // If not found in units array, return as-is for comparison
    // This handles cases where unit might already be a code
    return unitStr;
  };
  
  // Helper: Get all recaps including filled blank recaps
  const getAllRecapsIncludingFilledBlanks = () => {
    const savedPurchase = formData.purchase_recaps_data || [];
    const savedSales = formData.sales_recaps_data || [];
    const pendingLinked = [...pendingLinkedPurchaseRecaps, ...pendingLinkedSalesRecaps];
    
    // Add filled blank recaps (those with at least product, unit, currency)
    const filledBlanks = [];
    blankPurchaseRecaps.forEach(blank => {
      const values = blankRecapValues[blank.id];
      if (values?.product && values?.quantity_unit && values?.price_currency) {
        filledBlanks.push({
          ...blank,
          product: values.product,
          purchase_contract_quantity_unit: values.quantity_unit,
          purchase_contract_price_currency: values.price_currency,
          type: 'purchase'
        });
      }
    });
    blankSalesRecaps.forEach(blank => {
      const values = blankRecapValues[blank.id];
      if (values?.product && values?.quantity_unit && values?.price_currency) {
        filledBlanks.push({
          ...blank,
          product: values.product,
          sale_contract_quantity_unit: values.quantity_unit,
          sale_contract_price_currency: values.price_currency,
          type: 'sales'
        });
      }
    });
    
    return [...savedPurchase, ...savedSales, ...pendingLinked, ...filledBlanks];
  };

  // === HELPER: Get used products per side (purchase or sales) ===
  // Returns Set of base product names already used on the specified side
  // Used to prevent duplicate products on the same side (purchase/sales stay separate)
  const getUsedProductsForSide = (recapType, excludeBlankId = null) => {
    const usedProducts = new Set();
    
    if (recapType === 'purchase') {
      // Saved purchase recaps
      (formData.purchase_recaps_data || []).forEach(recap => {
        const baseProduct = extractBaseProduct(recap.product);
        if (baseProduct) usedProducts.add(baseProduct);
      });
      
      // Pending linked purchase recaps
      pendingLinkedPurchaseRecaps.forEach(recap => {
        const baseProduct = extractBaseProduct(recap.product);
        if (baseProduct) usedProducts.add(baseProduct);
      });
      
      // Blank purchase recaps with product set
      blankPurchaseRecaps.forEach(blank => {
        if (blank.id !== excludeBlankId) {
          const values = blankRecapValues[blank.id];
          if (values?.product) {
            const baseProduct = extractBaseProduct(values.product);
            if (baseProduct) usedProducts.add(baseProduct);
          }
        }
      });
    } else if (recapType === 'sales') {
      // Saved sales recaps
      (formData.sales_recaps_data || []).forEach(recap => {
        const baseProduct = extractBaseProduct(recap.product);
        if (baseProduct) usedProducts.add(baseProduct);
      });
      
      // Pending linked sales recaps
      pendingLinkedSalesRecaps.forEach(recap => {
        const baseProduct = extractBaseProduct(recap.product);
        if (baseProduct) usedProducts.add(baseProduct);
      });
      
      // Blank sales recaps with product set
      blankSalesRecaps.forEach(blank => {
        if (blank.id !== excludeBlankId) {
          const values = blankRecapValues[blank.id];
          if (values?.product) {
            const baseProduct = extractBaseProduct(values.product);
            if (baseProduct) usedProducts.add(baseProduct);
          }
        }
      });
    }
    
    return usedProducts;
  };
  
  // === RECAP CONSISTENCY VALIDATION ===
  const validateRecapConsistency = (recapType, newRecap) => {
    // Skip validation for blank recaps (they're empty and being created)
    if (newRecap.id && typeof newRecap.id === 'string' && newRecap.id.startsWith('temp-')) {
      return { valid: true };
    }
    
    // CRITICAL: Check ALL recaps (saved, pending linked, AND filled blanks) for consistency
    const allExistingRecaps = getAllRecapsIncludingFilledBlanks();
    
    // Only validate if there's already at least one recap (the first one sets the standard)
    if (allExistingRecaps.length === 0) return { valid: true };
    
    const firstRecap = allExistingRecaps[0];
    
    // Extract and validate product consistency ACROSS ALL TYPES
    const newProduct = extractBaseProduct(newRecap.product);
    const firstProduct = extractBaseProduct(firstRecap.product);
    
    if (newProduct && firstProduct && newProduct !== firstProduct) {
      return {
        valid: false,
        message: `Product mismatch: First recap uses "${firstProduct}". New recap uses "${newProduct}". All recaps must be for the same base product.`
      };
    }
    
    // CROSS-TYPE VALIDATION: Validate currency and unit against THE FIRST RECAP (regardless of type)
    // This ensures purchase and sales recaps must match each other
    
    // Helper to get unit name for display
    const getUnitDisplayName = (unit) => {
      if (!unit) return 'unknown';
      const unitObj = units.find(u => String(u.id) === String(unit) || u.code === String(unit));
      return unitObj ? unitObj.code : String(unit);
    };
    
    // Extract currency and unit from first recap (could be purchase OR sales)
    const firstCurrency = firstRecap.purchase_contract_price_currency || 
                          firstRecap.sale_contract_price_currency || 
                          firstRecap.price_currency;
    const firstUnitRaw = firstRecap.purchase_contract_quantity_unit || 
                         firstRecap.sale_contract_quantity_unit || 
                         firstRecap.quantity_unit;
    const firstUnit = normalizeUnit(firstUnitRaw);
    
    // Extract currency and unit from new recap
    const newCurrency = newRecap.purchase_contract_price_currency || 
                        newRecap.sale_contract_price_currency || 
                        newRecap.price_currency;
    const newUnitRaw = newRecap.purchase_contract_quantity_unit || 
                       newRecap.sale_contract_quantity_unit || 
                       newRecap.quantity_unit;
    const newUnit = normalizeUnit(newUnitRaw);
    
    // Debug logging
    console.log('🔍 CROSS-TYPE VALIDATION:', {
      firstRecapId: firstRecap.id,
      firstRecapType: firstRecap.recap_type || firstRecap.type || 'unknown',
      firstUnitRaw,
      firstUnitNormalized: firstUnit,
      firstCurrency,
      newRecapId: newRecap.id,
      newRecapType: recapType,
      newUnitRaw,
      newUnitNormalized: newUnit,
      newCurrency
    });
    
    // Validate currency
    if (newCurrency && firstCurrency && newCurrency !== firstCurrency) {
      return {
        valid: false,
        message: `Currency mismatch: First recap uses "${firstCurrency}". All recaps (purchase AND sales) must use the same currency.`
      };
    }
    
    // Validate unit
    if (newUnit && firstUnit && newUnit !== firstUnit) {
      const firstUnitName = getUnitDisplayName(firstUnitRaw);
      const newUnitName = getUnitDisplayName(newUnitRaw);
      console.log('❌ UNIT MISMATCH DETECTED:', { firstUnit, newUnit, firstUnitName, newUnitName });
      return {
        valid: false,
        message: `Unit mismatch: First recap uses "${firstUnitName}". All recaps (purchase AND sales) must use the same unit.`
      };
    }
    
    return { valid: true };
  };
  
  // Which legs had recaps the last time the totals below were written, and
  // on which record. A leg of the SAME record that had recaps and now has
  // none carries figures its recap produced, and that recap has just been
  // unlinked or removed: those are cleared, the rule the server applies when
  // a leg's last allocation goes. A leg that never had recaps -- or another
  // record opened in this same screen -- is left exactly as it is.
  const recapLegsSeenRef = useRef({ recordId: undefined, purchase: false, sales: false });

  // === AUTO-UPDATE RECAP TOTALS ===
  useEffect(() => {
    console.log('===== TOTALS USEEFFECT FIRED =====');
    console.log('Pending linked purchase recaps:', pendingLinkedPurchaseRecaps);
    console.log('Pending linked sales recaps:', pendingLinkedSalesRecaps);
    console.log('Blank purchase recaps:', blankPurchaseRecaps);
    console.log('Blank sales recaps:', blankSalesRecaps);
    
    const totals = calculateRecapTotals();
    
    console.log('=== RECAP TOTALS CALCULATED ===');
    console.log('Purchase totals:', totals.purchase);
    console.log('Sales totals:', totals.sales);
    
    // Each leg's totals: only the keys the totals step returned. A leg with
    // no recaps returns none of its saved columns, so what was loaded or
    // typed there stands (see calculateRecapTotals).
    const RECAP_DERIVED_FIELDS = {
      purchase: [
        'total_purchase_product_quantity', 'total_purchase_tolerance',
        'total_purchase_product_unit', 'total_purchase_price_per_ton_adjusted',
        'total_purchase_price_per_ton_adjusted_currency',
      ],
      sales: [
        'total_sales_product_quantity', 'total_sales_tolerance',
        'total_sales_product_unit', 'total_sale_price_per_ton_adjusted',
        'total_sale_price_per_ton_adjusted_currency',
      ],
    };
    const seen = recapLegsSeenRef.current;
    const sameRecord = seen.recordId === formData.id;
    ['purchase', 'sales'].forEach((leg) => {
      const hasRecaps = totals.validation[`${leg}_has_recaps`];
      if (!hasRecaps && sameRecord && seen[leg]) {
        RECAP_DERIVED_FIELDS[leg].forEach((name) => {
          handleInputChange({ target: { name, value: '' } });
        });
      }
      Object.entries(totals[leg]).forEach(([name, value]) => {
        handleInputChange({ target: { name, value } });
      });
    });
    recapLegsSeenRef.current = {
      recordId: formData.id,
      purchase: totals.validation.purchase_has_recaps,
      sales: totals.validation.sales_has_recaps,
    };
    
    // Update validation status
    handleInputChange({ target: { name: 'ranges_overlap_status', value: totals.validation.ranges_overlap } });
    handleInputChange({ target: { name: 'exact_quantity_mismatch_warning', value: totals.validation.exact_quantity_mismatch } });
    handleInputChange({ target: { name: 'sales_exceeds_purchase', value: totals.validation.sales_exceeds_purchase } });
    handleInputChange({ target: { name: 'purchase_exceeds_sales', value: totals.validation.purchase_exceeds_sales } });
    handleInputChange({ target: { name: 'entity_mismatch', value: totals.validation.entity_mismatch } });
    handleInputChange({ target: { name: 'entity_mismatch_entities', value: totals.validation.entity_mismatch_entities } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blankPurchaseRecaps, 
    blankSalesRecaps, 
    blankRecapValues,
    pendingLinkedPurchaseRecaps,
    pendingLinkedSalesRecaps,
    activeBlankPurchaseId,
    activeBlankSalesId,
    formData.purchase_recaps_data, 
    formData.sales_recaps_data, 
    formData.delivery_product_quantity,
    formData.delivery_product_tolerance_lower_value,
    formData.delivery_product_tolerance_lower_unit,
    formData.delivery_product_tolerance_upper_value,
    formData.delivery_product_tolerance_upper_unit,
    formData.sales_product_quantity,
    formData.sales_tolerance_lower_value,
    formData.sales_tolerance_lower_unit,
    formData.sales_tolerance_upper_value,
    formData.sales_tolerance_upper_unit,
    formData.purchase_price_per_ton_adjusted,
    formData.sales_price_per_ton_adjusted,
    formData.id,
    deliveryId,
    calculateRecapTotals,
    savedRecapEdits
  ]);
  
  // === THE STATE THE SAVE-TIME METHODS READ ===
  // DeliveryScreen calls the methods below through the ref during Save,
  // renders after they were built. They read the form's state through this
  // ref, refreshed after every render, never from their own closure: the
  // hook's dependency list once missed savedRecapProductEdits and
  // pendingLinkedFreightRecaps, so Save used an old, empty copy -- a price
  // typed on the What tab never reached the recap, a freight recap picked
  // before Save was never linked, and both reported success.
  // A method that needs something new takes it from here. Reading component
  // state directly inside the hook is what eslint's exhaustive-deps reports,
  // and DeliveryForm.saveHandleLint.test.js fails on that report.
  const saveStateRef = useRef(null);
  useLayoutEffect(() => {
    saveStateRef.current = {
      formData,
      portValidation,
      savedRecapEdits,
      savedRecapProductEdits,
      blankPurchaseRecaps,
      blankSalesRecaps,
      blankRecapValues,
      activeBlankPurchaseId,
      activeBlankSalesId,
      pendingLinkedPurchaseRecaps,
      pendingLinkedSalesRecaps,
      pendingLinkedFreightRecaps,
      sellerContractualList,
      buyerContractualList,
      originalHandleInputChange,
      calculateRecapTotals,
      OLD_createBlankRecapsInDB,
    };
  });

  // Expose methods to parent component through ref
  useImperativeHandle(ref, () => ({
    validatePorts: () => {
      const { portValidation, formData } = saveStateRef.current;
      const portsValid = 
        portValidation.loading_port_location && 
        portValidation.discharging_port_location;
      
      // If ports are required and empty, consider them invalid
      const loadingPortEmpty = !formData.loading_port_location;
      const dischargingPortEmpty = !formData.discharging_port_location;
      
      // If port fields have values, check their validation state
      if ((loadingPortEmpty || portValidation.loading_port_location) && 
          (dischargingPortEmpty || portValidation.discharging_port_location)) {
        return true;
      }
      
      return false;
    },
    
    // PATCH any buffered edits to already-saved TradeRecaps. Called from
    // DeliveryScreen.handleSubmit before the delivery itself is saved so the
    // recap rows are in their final state when the delivery PUT/POST runs.
    // Clears the buffer only on full success so a partial failure can be retried.
    persistSavedRecapEdits: async () => {
      const { savedRecapEdits, savedRecapProductEdits, formData } = saveStateRef.current;
      const results = { success: true, errors: [], count: 0 };
      const recapIds = Array.from(new Set([
        ...Object.keys(savedRecapEdits || {}),
        ...Object.keys(savedRecapProductEdits || {}),
      ]));
      if (recapIds.length === 0) return results;

      const toNum = recapFigureToNumber;

      for (const recapId of recapIds) {
        const payload = { ...(savedRecapEdits[recapId] || {}) };
        const productEdits = savedRecapProductEdits[recapId] || {};

        // Bunkers per-product edits -> send a products[] array (now upserts by
        // id) plus re-derived recap aggregates so the recap totals stay in sync.
        if (Object.keys(productEdits).length > 0) {
          const pRec = (formData.purchase_recaps_data || []).find(r => String(r.id) === String(recapId));
          const sRec = pRec ? null : (formData.sales_recaps_data || []).find(r => String(r.id) === String(recapId));
          const recap = pRec || sRec;
          const side = pRec ? 'purchase' : 'sales';
          const baseProducts = (recap && recap.recap_products) || [];

          // Every existing product id must be present (the backend deletes
          // omitted ids); unedited rows carry just their id.
          payload.products = baseProducts.map(p => {
            const e = productEdits[p.id] || {};
            const row = { id: p.id };
            if (e.sale_quantity !== undefined) row.sale_quantity = e.sale_quantity;
            if (e.buy_price !== undefined) row.buy_price = e.buy_price;
            if (e.sale_price !== undefined) row.sale_price = e.sale_price;
            return row;
          });

          const merged = baseProducts.map(p => ({ ...p, ...(productEdits[p.id] || {}) }));
          const totalQty = merged.reduce((a, p) => a + (Number.isFinite(toNum(p.sale_quantity)) ? toNum(p.sale_quantity) : 0), 0);
          const wavg = (key) => {
            let n = 0, d = 0;
            merged.forEach(p => { const q = toNum(p.sale_quantity), v = toNum(p[key]); if (q > 0 && Number.isFinite(v)) { n += v * q; d += q; } });
            return d > 0 ? n / d : null;
          };
          // Rounded to the chain's precision (utils/precision.js): a float
          // sum/average can carry noise digits the text columns (15 chars)
          // and the price columns would refuse.
          if (side === 'purchase') {
            payload.purchase_contract_quantity = formatFigure(totalQty, 0, QUANTITY_DECIMALS);
            const avg = wavg('buy_price');
            if (avg != null) payload.purchase_contract_price_value = formatFigure(avg, 2, PRICE_DECIMALS);
          } else {
            payload.sale_contract_quantity = formatFigure(totalQty, 0, QUANTITY_DECIMALS);
            const avg = wavg('sale_price');
            if (avg != null) payload.sale_contract_price_value = formatFigure(avg, 2, PRICE_DECIMALS);
          }
          const avgBuy = wavg('buy_price');
          if (avgBuy != null) payload.buy_price_per_ton = formatFigure(avgBuy, 2, PRICE_DECIMALS);
        }

        if (Object.keys(payload).length === 0) continue;
        try {
          await axiosInstance.patch(`/api/offer/trade-recaps/${recapId}/`, payload);
          results.count++;
        } catch (error) {
          console.error(`Error patching saved recap ${recapId}:`, error);
          results.errors.push({ recapId, error });
          results.success = false;
        }
      }

      if (results.success) {
        setSavedRecapEdits({});
        setSavedRecapProductEdits({});
      }
      return results;
    },

    // NEW SIMPLIFIED: Create blank recaps using new API
    createAndLinkBlankRecaps: async (deliveryId) => {
      const {
        blankPurchaseRecaps, blankSalesRecaps, blankRecapValues,
        activeBlankPurchaseId, activeBlankSalesId,
      } = saveStateRef.current;
      try {
        const results = { success: true, errors: [], count: 0 };
        
        // Skip if no blank recaps to create
        if (blankPurchaseRecaps.length === 0 && blankSalesRecaps.length === 0) {
          console.log('No blank recaps to create');
          return results;
        }
        
        // CRITICAL: Log state for debugging ID mismatches
        console.log('=== CREATE AND LINK BLANK RECAPS ===');
        console.log('Blank purchase recaps:', blankPurchaseRecaps.map(r => r.id));
        console.log('Blank sales recaps:', blankSalesRecaps.map(r => r.id));
        console.log('Active blank purchase ID:', activeBlankPurchaseId);
        console.log('Active blank sales ID:', activeBlankSalesId);
        console.log('Available keys in blankRecapValues:', Object.keys(blankRecapValues));
        
        // Create and link each blank purchase recap
        for (const blankRecap of blankPurchaseRecaps) {
          try {
            const recapData = blankRecapValues[blankRecap.id];
            
            // CRITICAL FIX: Log detailed information about missing data
            if (!recapData || Object.keys(recapData).length === 0) {
              console.error(`❌ BLANK RECAP DATA MISMATCH - Purchase`);
              console.error(`   Blank recap ID: ${blankRecap.id}`);
              console.error(`   Data found: ${recapData ? 'YES but empty' : 'NO'}`);
              console.error(`   Available keys in blankRecapValues:`, Object.keys(blankRecapValues));
              console.error(`   Skipping this recap - values were likely stored under a different ID`);
              results.errors.push({ 
                recap: blankRecap, 
                error: new Error(`No data found for blank recap ID ${blankRecap.id}`) 
              });
              results.success = false;
              continue;
            }
            
            console.log(`✓ Creating blank purchase recap ${blankRecap.id} with data:`, recapData);
            
            await axiosInstance.post(
              `/api/deliveries/${deliveryId}/create-and-link-blank-recap/`,
              {
                recap_type: 'purchase',
                recap_data: recapData
              }
            );
            results.count++;
          } catch (error) {
            console.error(`Error creating blank purchase recap ${blankRecap.id}:`, error);
            results.errors.push({ recap: blankRecap, error });
            results.success = false;
          }
        }
        
        // Create and link each blank sales recap
        for (const blankRecap of blankSalesRecaps) {
          try {
            const recapData = blankRecapValues[blankRecap.id];
            
            // CRITICAL FIX: Log detailed information about missing data
            if (!recapData || Object.keys(recapData).length === 0) {
              console.error(`❌ BLANK RECAP DATA MISMATCH - Sales`);
              console.error(`   Blank recap ID: ${blankRecap.id}`);
              console.error(`   Data found: ${recapData ? 'YES but empty' : 'NO'}`);
              console.error(`   Available keys in blankRecapValues:`, Object.keys(blankRecapValues));
              console.error(`   Skipping this recap - values were likely stored under a different ID`);
              results.errors.push({ 
                recap: blankRecap, 
                error: new Error(`No data found for blank recap ID ${blankRecap.id}`) 
              });
              results.success = false;
              continue;
            }
            
            console.log(`✓ Creating blank sales recap ${blankRecap.id} with data:`, recapData);
            
            await axiosInstance.post(
              `/api/deliveries/${deliveryId}/create-and-link-blank-recap/`,
              {
                recap_type: 'sales',
                recap_data: recapData
              }
            );
            results.count++;
          } catch (error) {
            console.error(`Error creating blank sales recap ${blankRecap.id}:`, error);
            results.errors.push({ recap: blankRecap, error });
            results.success = false;
          }
        }
        
        // Clear blank recaps after successful creation
        if (results.success) {
          setBlankPurchaseRecaps([]);
          setBlankSalesRecaps([]);
          setBlankRecapValues({});
          setActiveBlankPurchaseId(null);
          setActiveBlankSalesId(null);
        }
        
        return results;
      } catch (error) {
        console.error('Error in createAndLinkBlankRecaps:', error);
        return { success: false, errors: [error], count: 0 };
      }
    },
    
    // NEW: Link pending linked recaps after delivery save
    linkPendingLinkedRecaps: async (deliveryId) => {
      const {
        pendingLinkedPurchaseRecaps, pendingLinkedSalesRecaps, pendingLinkedFreightRecaps,
      } = saveStateRef.current;
      try {
        const results = { success: true, errors: [], count: 0 };
        
        // Link each pending linked purchase recap
        for (const recap of pendingLinkedPurchaseRecaps) {
          try {
            // Build request body - include allocated_quantity if partial allocation
            const requestBody = { recap_id: recap.id };
            if (recap.is_partial_allocation && recap.allocated_quantity) {
              requestBody.allocated_quantity = recap.allocated_quantity;
            }
            
            await axiosInstance.post(
              `/api/deliveries/${deliveryId}/link-purchase-recap/`,
              requestBody
            );
            results.count++;
          } catch (error) {
            console.error(`Error linking purchase recap ${recap.id}:`, error);
            results.errors.push({ recap, error });
            results.success = false;
          }
        }
        
        // Link each pending linked sales recap
        for (const recap of pendingLinkedSalesRecaps) {
          try {
            const requestBody = { recap_id: recap.id };
            if (recap.is_partial_allocation && recap.allocated_quantity) {
              requestBody.allocated_quantity = recap.allocated_quantity;
            }
            
            await axiosInstance.post(
              `/api/deliveries/${deliveryId}/link-sales-recap/`,
              requestBody
            );
            results.count++;
          } catch (error) {
            console.error(`Error linking sales recap ${recap.id}:`, error);
            results.errors.push({ recap, error });
            results.success = false;
          }
        }

        // Link each pending linked freight recap via assign_recap
        for (const recap of pendingLinkedFreightRecaps) {
          try {
            await axiosInstance.post(
              `/api/deliveries/${deliveryId}/assign_recap/`,
              { recap_transaction_number: recap.transaction_number }
            );
            results.count++;
          } catch (error) {
            console.error(`Error linking freight recap ${recap.transaction_number}:`, error);
            results.errors.push({ recap, error });
            results.success = false;
          }
        }
        
        // Clear pending linked recaps after successful linking
        if (results.success) {
          setPendingLinkedPurchaseRecaps([]);
          setPendingLinkedSalesRecaps([]);
          setPendingLinkedFreightRecaps([]);
        }
        
        return results;
      } catch (error) {
        console.error('Error in linkPendingLinkedRecaps:', error);
        return { success: false, errors: [error], count: 0 };
      }
    },
    
    // OLD: Create blank recaps in database before saving delivery (DEPRECATED - use createAndLinkBlankRecaps)
    createBlankRecaps: (...args) => saveStateRef.current.OLD_createBlankRecapsInDB(...args),
    
    // OLD: Link pending recaps after delivery is created (DEPRECATED - use createAndLinkBlankRecaps)
    linkPendingRecaps: async (newDeliveryId, createdRecapsFromDB = null) => {
      const {
        blankPurchaseRecaps, blankSalesRecaps,
        pendingLinkedPurchaseRecaps, pendingLinkedSalesRecaps,
        originalHandleInputChange,
      } = saveStateRef.current;
      console.log('=== LINKING PENDING RECAPS ===');
      console.log('New Delivery ID:', newDeliveryId);
      console.log('createdRecapsFromDB:', createdRecapsFromDB);
      console.log('Pending Purchase Recaps:', blankPurchaseRecaps);
      console.log('Pending Sales Recaps:', blankSalesRecaps);
      
      const results = { success: true, errors: [] };
      
      // Combine created blank recaps AND saved pending recaps
      let purchaseRecapsToLink = [];
      let salesRecapsToLink = [];
      
      // Add newly created blank recaps (if any)
      if (createdRecapsFromDB) {
        purchaseRecapsToLink = [...createdRecapsFromDB.purchase.map(c => c.recap)];
        salesRecapsToLink = [...createdRecapsFromDB.sales.map(c => c.recap)];
        console.log('📦 Added created blank recaps - Purchase:', purchaseRecapsToLink.length, 'Sales:', salesRecapsToLink.length);
      }
      
      // ALSO add saved recaps from pending state (added via modal)
      // CRITICAL FIX: Use pendingLinkedPurchaseRecaps (from modal) NOT blankPurchaseRecaps (for blank recap creation)
      const savedPendingPurchaseRecaps = pendingLinkedPurchaseRecaps.filter(r => typeof r.id === 'number');
      const savedPendingSalesRecaps = pendingLinkedSalesRecaps.filter(r => typeof r.id === 'number');
      
      console.log('📊 Saved pending recaps - Purchase:', savedPendingPurchaseRecaps.length, 'Sales:', savedPendingSalesRecaps.length);
      
      purchaseRecapsToLink = [...purchaseRecapsToLink, ...savedPendingPurchaseRecaps];
      salesRecapsToLink = [...salesRecapsToLink, ...savedPendingSalesRecaps];
      
      // CRITICAL FIX: Deduplicate by ID to prevent 400 errors from duplicate linking
      const uniquePurchaseRecaps = [];
      const seenPurchaseIds = new Set();
      purchaseRecapsToLink.forEach(recap => {
        if (!seenPurchaseIds.has(recap.id)) {
          seenPurchaseIds.add(recap.id);
          uniquePurchaseRecaps.push(recap);
        } else {
          console.warn(`⚠️ Skipping duplicate purchase recap ID ${recap.id} (${recap.transaction_number})`);
        }
      });
      
      const uniqueSalesRecaps = [];
      const seenSalesIds = new Set();
      salesRecapsToLink.forEach(recap => {
        if (!seenSalesIds.has(recap.id)) {
          seenSalesIds.add(recap.id);
          uniqueSalesRecaps.push(recap);
        } else {
          console.warn(`⚠️ Skipping duplicate sales recap ID ${recap.id} (${recap.transaction_number})`);
        }
      });
      
      purchaseRecapsToLink = uniquePurchaseRecaps;
      salesRecapsToLink = uniqueSalesRecaps;
      
      console.log('✅ Deduplicated recaps - Purchase:', purchaseRecapsToLink.length, 'Sales:', salesRecapsToLink.length);
      
      // Link pending purchase recaps (only those with numeric IDs from DB)
      for (const recap of purchaseRecapsToLink) {
        try {
          console.log(`🔗 Linking purchase recap ${recap.id} (${recap.transaction_number})...`);
          console.log(`   Current tolerance: ${recap.purchase_shipping_tolerance || 'N/A'}`);
          console.log(`   Product: ${recap.product || 'N/A'}`);
          console.log(`   🎯 PARTIAL ALLOCATION DATA:`, {
            allocated_quantity: recap.allocated_quantity,
            is_partial_allocation: recap.is_partial_allocation,
            total_quantity: recap.total_quantity
          });
          
          // Build request body - include allocated_quantity if partial allocation
          const requestBody = { recap_id: recap.id };
          if (recap.is_partial_allocation && recap.allocated_quantity) {
            requestBody.allocated_quantity = recap.allocated_quantity;
            console.log(`   ✅ Including allocated_quantity in request:`, recap.allocated_quantity);
          } else {
            console.log(`   ⚠️ NOT including allocated_quantity - using full quantity`);
          }
          
          console.log(`   📤 Request body:`, requestBody);
          
          const response = await axiosInstance.post(
            `/api/deliveries/${newDeliveryId}/link-purchase-recap/`,
            requestBody
          );
          
          console.log(`   📥 Response purchase_recaps_data:`, response.data.purchase_recaps_data);
          
          console.log(`✅ Purchase recap ${recap.id} linked successfully`);
          console.log(`   Response tolerance fields:`, {
            delivery_product_tolerance_lower_value: response.data.delivery_product_tolerance_lower_value,
            delivery_product_tolerance_lower_unit: response.data.delivery_product_tolerance_lower_unit,
            delivery_product_tolerance_upper_value: response.data.delivery_product_tolerance_upper_value,
            delivery_product_tolerance_upper_unit: response.data.delivery_product_tolerance_upper_unit,
            product_name: response.data.product_name
          });
          
          // CRITICAL FIX: Update form data WITHOUT triggering server sync
          // The response contains delivery data, not recap data, so syncing would
          // overwrite the recap's tolerance with the delivery's tolerance
          // ALSO: Only update PURCHASE fields to prevent overwriting pending SALES values
          const purchaseOnlyFields = [
            'purchase_product_name',
            'delivery_product_quantity',
            'delivery_product_unit',
            'delivery_product_tolerance',
            'delivery_product_tolerance_lower_value',
            'delivery_product_tolerance_lower_unit',
            'delivery_product_tolerance_upper_value',
            'delivery_product_tolerance_upper_unit',
            'purchase_price_per_ton_contract',
            'purchase_price_per_ton_contract_currency',
            'purchase_price_per_ton_adjusted',
            'purchase_price_per_ton_adjusted_currency',
            'purchase_price_basis_type',
            'purchase_price_basis_value',
            'delivery_producer',
            'seller_contractual',
            'active_purchase_recap_id',
            'purchase_recaps_data'
          ];
          
          const salesOnlyFields = [
            'sales_product_name',
            'sales_product_quantity',
            'sales_product_unit',
            'sales_tolerance',
            'sales_tolerance_lower_value',
            'sales_tolerance_lower_unit',
            'sales_tolerance_upper_value',
            'sales_tolerance_upper_unit',
            'sales_price_per_ton_contract',
            'sale_price_per_ton_contract_currency',
            'sales_price_per_ton_adjusted',
            'sale_price_per_ton_adjusted_currency',
            'sales_price_basis_type',
            'sales_price_basis_value',
            'buyer_contractual',
            'delivery_end_user',
            'active_sales_recap_id',
            'sales_recaps_data'
          ];
          
          Object.keys(response.data).forEach(key => {
            if (response.data[key] !== null && response.data[key] !== undefined) {
              const isPurchaseField = purchaseOnlyFields.includes(key);
              const isNeutralField = !purchaseOnlyFields.includes(key) && !salesOnlyFields.includes(key);
              
              if (isPurchaseField || isNeutralField) {
                originalHandleInputChange({ target: { name: key, value: response.data[key] } });
              }
            }
          });
          
        } catch (error) {
          console.error(`❌ Failed to link purchase recap ${recap.id}:`, error);
          console.error(`   Error response:`, error.response?.data);
          console.error(`   Error status:`, error.response?.status);
          results.errors.push(`Purchase recap ${recap.transaction_number}: ${error.message}`);
          results.success = false;
        }
      }
      
      // Link pending sales recaps (only those with numeric IDs from DB)
      for (const recap of salesRecapsToLink) {
        try {
          console.log(`🔗 Linking sales recap ${recap.id} (${recap.transaction_number})...`);
          console.log(`   Current tolerance: ${recap.sale_shipping_tolerance || 'N/A'}`);
          console.log(`   Product: ${recap.product || 'N/A'}`);
          
          // Build request body - include allocated_quantity if partial allocation
          const requestBody = { recap_id: recap.id };
          if (recap.is_partial_allocation && recap.allocated_quantity) {
            requestBody.allocated_quantity = recap.allocated_quantity;
          }
          
          const response = await axiosInstance.post(
            `/api/deliveries/${newDeliveryId}/link-sales-recap/`,
            requestBody
          );
          
          console.log(`✅ Sales recap ${recap.id} linked successfully`);
          console.log(`   Response tolerance fields:`, {
            sales_tolerance_lower_value: response.data.sales_tolerance_lower_value,
            sales_tolerance_lower_unit: response.data.sales_tolerance_lower_unit,
            sales_tolerance_upper_value: response.data.sales_tolerance_upper_value,
            sales_tolerance_upper_unit: response.data.sales_tolerance_upper_unit,
            product_name: response.data.product_name
          });
          
          // CRITICAL FIX: Update form data WITHOUT triggering server sync
          // The response contains delivery data, not recap data, so syncing would
          // overwrite the recap's tolerance with the delivery's tolerance
          // ALSO: Only update SALES fields to prevent overwriting PURCHASE values
          const purchaseOnlyFields = [
            'purchase_product_name',
            'delivery_product_quantity',
            'delivery_product_unit',
            'delivery_product_tolerance',
            'delivery_product_tolerance_lower_value',
            'delivery_product_tolerance_lower_unit',
            'delivery_product_tolerance_upper_value',
            'delivery_product_tolerance_upper_unit',
            'purchase_price_per_ton_contract',
            'purchase_price_per_ton_contract_currency',
            'purchase_price_per_ton_adjusted',
            'purchase_price_per_ton_adjusted_currency',
            'purchase_price_basis_type',
            'purchase_price_basis_value',
            'delivery_producer',
            'seller_contractual',
            'active_purchase_recap_id',
            'purchase_recaps_data'
          ];
          
          const salesOnlyFields = [
            'sales_product_name',
            'sales_product_quantity',
            'sales_product_unit',
            'sales_tolerance',
            'sales_tolerance_lower_value',
            'sales_tolerance_lower_unit',
            'sales_tolerance_upper_value',
            'sales_tolerance_upper_unit',
            'sales_price_per_ton_contract',
            'sale_price_per_ton_contract_currency',
            'sales_price_per_ton_adjusted',
            'sale_price_per_ton_adjusted_currency',
            'sales_price_basis_type',
            'sales_price_basis_value',
            'buyer_contractual',
            'delivery_end_user',
            'active_sales_recap_id',
            'sales_recaps_data'
          ];
          
          Object.keys(response.data).forEach(key => {
            if (response.data[key] !== null && response.data[key] !== undefined) {
              const isSalesField = salesOnlyFields.includes(key);
              const isNeutralField = !purchaseOnlyFields.includes(key) && !salesOnlyFields.includes(key);
              
              if (isSalesField || isNeutralField) {
                originalHandleInputChange({ target: { name: key, value: response.data[key] } });
              }
            }
          });
          
        } catch (error) {
          console.error(`❌ Failed to link sales recap ${recap.id}:`, error);
          console.error(`   Error response:`, error.response?.data);
          console.error(`   Error status:`, error.response?.status);
          results.errors.push(`Sales recap ${recap.transaction_number}: ${error.message}`);
          results.success = false;
        }
      }
      
      // Clear pending recaps and active IDs after linking
      if (results.success) {
        setBlankPurchaseRecaps([]);
        setBlankSalesRecaps([]);
        setActiveBlankPurchaseId(null);
        setActiveBlankSalesId(null);
//        console.log('All pending recaps linked successfully');
      }
      
      return results;
    },
    
    // Get current form data (used after linking recaps to refresh processedFormData)
    getFormData: () => {
      const { formData, sellerContractualList, buyerContractualList } = saveStateRef.current;
      const data = {
        ...formData,
        seller_contractual_list: sellerContractualList,
        buyer_contractual_list: buyerContractualList
      };
      console.log('📦 getFormData() called - Returning contractual lists:', {
        seller_contractual_list: sellerContractualList,
        buyer_contractual_list: buyerContractualList
      });
      return data;
    },
    
    // Update form data from external source (used after reloading delivery from backend)
    updateFormData: (newData) => {
      const { originalHandleInputChange } = saveStateRef.current;
      // CRITICAL: After save, reset to first recap in sorted order for both purchase and sales
      let purchaseRecapsData = newData.purchase_recaps_data;
      let salesRecapsData = newData.sales_recaps_data;
      
      // Sort purchase recaps in ascending order
      if (purchaseRecapsData && Array.isArray(purchaseRecapsData) && purchaseRecapsData.length > 0) {
        purchaseRecapsData = [...purchaseRecapsData].sort((a, b) => {
          if (!a.transaction_number || !b.transaction_number) return 0;
          return a.transaction_number.localeCompare(b.transaction_number);
        });
        
        // Force first recap as active
        const firstPurchaseRecap = purchaseRecapsData[0];
        console.log(`🔄 Resetting to first purchase recap: ${firstPurchaseRecap.transaction_number} (ID: ${firstPurchaseRecap.id})`);
        
        // Override the active_purchase_recap_id to first recap
        newData.active_purchase_recap_id = firstPurchaseRecap.id;
        
        // Load first purchase recap's data into form
        if (firstPurchaseRecap.product) {
          newData.purchase_product_name = firstPurchaseRecap.product;
          setProductSearchTerm(firstPurchaseRecap.product);
          setSelectedProduct({ name: firstPurchaseRecap.product });
        }
      }
      
      // Sort sales recaps in ascending order
      if (salesRecapsData && Array.isArray(salesRecapsData) && salesRecapsData.length > 0) {
        salesRecapsData = [...salesRecapsData].sort((a, b) => {
          if (!a.transaction_number || !b.transaction_number) return 0;
          return a.transaction_number.localeCompare(b.transaction_number);
        });
        
        // Force first recap as active
        const firstSalesRecap = salesRecapsData[0];
        console.log(`🔄 Resetting to first sales recap: ${firstSalesRecap.transaction_number} (ID: ${firstSalesRecap.id})`);
        
        // Override the active_sales_recap_id to first recap
        newData.active_sales_recap_id = firstSalesRecap.id;
        
        // Load first sales recap's data into form (independently)
        if (firstSalesRecap.product) {
          newData.sales_product_name = firstSalesRecap.product;
          setSalesProductSearchTerm(firstSalesRecap.product);
          setSelectedSalesProduct({ name: firstSalesRecap.product });
        }
      }
      
      // Update all form data including overridden active recap IDs
      Object.keys(newData).forEach(key => {
        if (newData[key] !== null && newData[key] !== undefined) {
          // Use original handler to avoid triggering server sync
          originalHandleInputChange({ target: { name: key, value: newData[key] } });
        }
      });
    },
    
    // Expose calculateRecapTotals for workflow validation
    calculateRecapTotals: () => {
      return saveStateRef.current.calculateRecapTotals();
    }
  }), []);

  // Real-time CRM status sync - fetch latest status when form loads with existing CRM profiles
  useEffect(() => {
    const syncCRMStatus = async () => {
      try {
        // Sync buyer CRM profile status
        if (formData.buyer_crm_profile) {
          const buyerProfile = await crmService.getProfile(formData.buyer_crm_profile);
          const buyerSanctionStatus = mapSanctionStatus(buyerProfile);
          const buyerKycStatus = mapKYCStatus(buyerProfile);
          
          // Only update if different to avoid unnecessary re-renders
          if (formData.buyer_sanction_status !== buyerSanctionStatus) {
            handleInputChange({ target: { name: 'buyer_sanction_status', value: buyerSanctionStatus } });
          }
          if (formData.buyer_kyc_status !== buyerKycStatus) {
            handleInputChange({ target: { name: 'buyer_kyc_status', value: buyerKycStatus } });
          }
        }
        
        // Sync seller CRM profile status
        if (formData.seller_crm_profile) {
          const sellerProfile = await crmService.getProfile(formData.seller_crm_profile);
          const sellerSanctionStatus = mapSanctionStatus(sellerProfile);
          const sellerKycStatus = mapKYCStatus(sellerProfile);
          
          // Only update if different to avoid unnecessary re-renders
          if (formData.seller_sanction_status !== sellerSanctionStatus) {
            handleInputChange({ target: { name: 'seller_sanction_status', value: sellerSanctionStatus } });
          }
          if (formData.seller_kyc_status !== sellerKycStatus) {
            handleInputChange({ target: { name: 'seller_kyc_status', value: sellerKycStatus } });
          }
        }
      } catch (error) {
        console.error('Error syncing CRM status:', error);
      }
    };
    
    // Only sync when a delivery is loaded (has deliveryId)
    if (deliveryId && (formData.buyer_crm_profile || formData.seller_crm_profile)) {
      syncCRMStatus();
    }
  }, [deliveryId, formData.buyer_crm_profile, formData.seller_crm_profile]);
  
  // Add state for section field completion tracking
  const [sectionCompletion, setSectionCompletion] = useState({
    deliveryInfo: { filled: 0, total: 0 },
    productInfo: { filled: 0, total: 0 },
    portFreight: { filled: 0, total: 0 },
    banks: { filled: 0, total: 0 },
    blInfo: { filled: 0, total: 0 },
    contractsLegal: { filled: 0, total: 0 }
  });

  // Field groups by section for tracking completion
  const sectionFields = {
    deliveryInfo: [
      // Mode of delivery only applies to (and is only shown for) fuels
      'vessel_name', ...(formData.commodity_type === 'fuels' ? ['delivery_transport_mode'] : []),
      'vessel_position', 'delivery_estimated_time_of_arrival', 'hms_entity',
      'delivery_assigned_hms_office', 'delivery_access', 'financial_access', 
      'delivery_main_operator', 'delivery_backup_operator_1', 'delivery_backup_operator_2',
      'delivery_deal_maker_sale', 'delivery_deal_maker_sale_percentage',
      'delivery_deal_maker_purchase', 'delivery_deal_maker_purchase_percentage',
      'delivery_deal_maker_freight'
    ],
    productInfo: [
      // Purchase price fields
      'purchase_price_per_ton_contract', 'purchase_price_per_ton_contract_currency',
      'purchase_price_per_ton_adjusted', 'purchase_price_per_ton_adjusted_currency',
      
      // Sales price fields - MUST match backend: amount fields use 'sales_', currency fields use 'sale_'
      'sales_price_per_ton_contract', 'sale_price_per_ton_contract_currency',
      'sales_price_per_ton_adjusted', 'sale_price_per_ton_adjusted_currency',
      
      // Price basis fields - Updated to match backend model field names
      'price_basis_purchase_quality', 'price_basis_sale_quality',
      
      // Total price and quantity fields
      'total_purchase_price_per_ton_adjusted', 'total_purchase_price_per_ton_adjusted_currency',
      'total_sale_price_per_ton_adjusted', 'total_sale_price_per_ton_adjusted_currency',
      'total_purchase_tolerance', 'total_sales_tolerance',
      'total_purchase_product_quantity', 'total_purchase_product_unit',
      'total_sales_product_quantity', 'total_sales_product_unit',
      
      // Product details fields
      'delivery_product_quantity', 'delivery_product_unit', 'delivery_product_tolerance',
      'delivery_product_category',
      
      // Business relationship fields - excluding checkboxes
      // Producer
      'delivery_producer',
      
      // End user field
      'delivery_end_user',
      
      // Chain in between Purchase fields
      'delivery_chain_in_between_purchase_1', 'delivery_chain_in_between_purchase_2',
      
      // Chain in between Sale fields
      'delivery_chain_in_between_sale_1', 'delivery_chain_in_between_sale_2',
      
      // Seller and buyer contractual fields
      'seller_contractual',
      'buyer_contractual'
    ],
    portFreight: [
      'loading_port_location', 'discharging_port_location', 'product_origin', 'delivery_destination', 
      'charter_party_date', 'delivery_laycan', 'incoterms_purchase',
      'incoterms_sale', 'freight_per_ton', 'freight_per_ton_currency',
      'vessel_owners', 'vessel_brokers',
      'address_commission', 'mercurius_commission', 'brokerage_commission'
    ],
    banks: [
      'hms_banking_crm_partner_name', 'hms_bank_1', 'hms_bank_2',
      'seller_banking_crm_partner_name', 'seller_bank_1', 'seller_bank_2',
      'buyer_banking_crm_partner_name', 'buyer_bank_1', 'buyer_bank_2'
    ],
    blInfo: [
      'bill_of_lading_date', 'bill_of_lading_date_estimated_or_final',
      'bill_of_lading_quantity', 'bill_of_lading_quantity_estimated_or_final',
      'reporting_month',
      // Sprint 4A: Fuels-only BDN-confirmed FINAL tonnage. Solids
      // deliveries leave this null; the field is hidden in the form
      // for Solids and stripped from Solids serializer payloads.
      'final_tonnage_mt'
    ],
    contractsLegal: [
      'purchase_contract_quality_basis', 'sales_contract_quality_basis',
      'purchase_confirmation_date', 'sale_confirmation_date',
      'kyc_information', 'sanction_check'
    ]
  };

  // Function to check if a field has a valid value
  const isFieldFilled = (fieldName) => {
    const value = formData[fieldName];
    
    // Debug log to help troubleshoot field values during development
    // if (fieldName.includes('currency') || fieldName.includes('price')) {
    //   console.log(`Field ${fieldName} has value:`, value, typeof value);
    // }
    
    // Additional logging for business relationship fields
    // if (fieldName.includes('contractual') || fieldName.includes('chain_in_between') || fieldName.includes('reporting_as_tickbox')) {
    //   console.log(`Business relationship field ${fieldName} has value:`, value, typeof value);
    // }
    
    // Special handling for checkbox fields - they should be considered filled if they're boolean (either true or false)
    if (fieldName.includes('tickbox') || fieldName.includes('reporting_as')) {
      return value !== null && value !== undefined;
    }
    
    // Check for null, undefined, empty string, empty array
    if (value === null || value === undefined || value === '') {
      return false;
    }
    
    // Check for empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    
    // Check for empty objects
    if (typeof value === 'object' && !Array.isArray(value) && 
        Object.keys(value).length === 0) {
      return false;
    }
    
    // Special handling for currency fields - sometimes they might contain only spaces
    if (fieldName.includes('currency') && typeof value === 'string' && value.trim() === '') {
      return false;
    }
    
    // Special handling for date fields - ensure they are valid
    if ((fieldName.includes('date') || fieldName === 'delivery_laycan' || fieldName === 'delivery_estimated_time_of_arrival') && 
        typeof value === 'string') {
      // For date fields, consider them filled if they have a non-empty value
      return value.trim() !== '';
    }
    
    return true;
  };

  // Calculate Business Partners progress (including dynamic contractual fields)
  const businessPartnersProgress = useMemo(() => {
    let filledCount = 0;
    let totalCount = 0;
    
    // Static fields
    const staticFields = [
      'hms_entity',
      'delivery_producer',
      'delivery_chain_in_between_purchase_1',
      'delivery_chain_in_between_purchase_2',
      'delivery_end_user',
      'delivery_chain_in_between_sale_1',
      'delivery_chain_in_between_sale_2'
    ];
    
    staticFields.forEach(field => {
      const value = formData[field];
      if (value !== null && value !== undefined && typeof value === 'string' && value.trim() !== '') {
        filledCount++;
      }
    });
    totalCount += staticFields.length;
    
    // Dynamic seller contractual fields
    sellerContractualList.forEach(seller => {
      if (seller.name && seller.name.trim() !== '') {
        filledCount++;
      }
      totalCount++;
    });
    
    // Dynamic buyer contractual fields
    buyerContractualList.forEach(buyer => {
      if (buyer.name && buyer.name.trim() !== '') {
        filledCount++;
      }
      totalCount++;
    });
    
    const percentage = totalCount > 0 ? Math.floor((filledCount / totalCount) * 100) : 0;
    
    return { filled: filledCount, total: totalCount, percentage };
  }, [formData, sellerContractualList, buyerContractualList]);

  // WHO tab: each side's HMS entity, read off its linked recaps (Marcel,
  // 25.09). Purchase and Sale always show side by side, even when they are
  // the same entity. An unsaved delivery reads the recaps picked so far; a
  // saved one reads what the server linked.
  const recapLegEntities = useMemo(() => {
    const isUnsaved = !deliveryId || deliveryId === 'new' || deliveryId === 'undefined';
    const purchase = (isUnsaved ? pendingLinkedPurchaseRecaps : formData.purchase_recaps_data) || [];
    const sales = (isUnsaved ? pendingLinkedSalesRecaps : formData.sales_recaps_data) || [];
    const names = (recaps) => [...new Set(
      recaps.map(r => (r.hms_entity_display || '').trim()).filter(Boolean)
    )].join(', ');
    return {
      hasRecaps: purchase.length > 0 || sales.length > 0,
      purchase: names(purchase),
      sales: names(sales),
    };
  }, [deliveryId, pendingLinkedPurchaseRecaps, pendingLinkedSalesRecaps,
      formData.purchase_recaps_data, formData.sales_recaps_data]);

  // Calculate Contracts/Legal progress (6 input fields only, excluding status buttons)
  // NOTE: Buyer/Seller inputs removed - status now tracked in Business Partners contractual lists
  const contractsLegalProgress = useMemo(() => {
    // Quality-basis (coal CV) fields are hidden for fuels, so don't count them
    // toward the bunkers progress denominator.
    const contractsLegalFields = formData.commodity_type === 'fuels'
      ? [
          'purchase_confirmation_date',
          'sale_confirmation_date'
        ]
      : [
          'purchase_contract_quality_basis',
          'purchase_confirmation_date',
          'sales_contract_quality_basis',
          'sale_confirmation_date'
        ];

    let filledCount = contractsLegalFields.filter(field => {
      const value = formData[field];
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== null && value !== undefined;
    }).length;

    // Contract numbers are now derived from linked recaps (read-only in UI).
    // Count purchase / sales contract presence based on whether at least one
    // linked recap on each side has a non-empty contract number.
    const purchaseRecapsAll = [
      ...(formData.purchase_recaps_data || []),
      ...pendingLinkedPurchaseRecaps
    ];
    const salesRecapsAll = [
      ...(formData.sales_recaps_data || []),
      ...pendingLinkedSalesRecaps
    ];
    const hasPurchaseContract = purchaseRecapsAll.some(r => r && r.purchase_contract_number && String(r.purchase_contract_number).trim() !== '');
    const hasSalesContract = salesRecapsAll.some(r => r && r.sale_contract_number && String(r.sale_contract_number).trim() !== '');

    if (hasPurchaseContract) filledCount += 1;
    if (hasSalesContract) filledCount += 1;

    const totalCount = contractsLegalFields.length + 2;
    const percentage = totalCount > 0 ? Math.floor((filledCount / totalCount) * 100) : 0;

    return { filled: filledCount, total: totalCount, percentage };
  }, [formData, pendingLinkedPurchaseRecaps, pendingLinkedSalesRecaps]);


  // Effect to calculate section completion when formData changes
  useEffect(() => {
    const newSectionCompletion = {};
    
    // Calculate completion for each section
    Object.keys(sectionFields).forEach(sectionKey => {
      const fields = sectionFields[sectionKey];
      
      // Filter fields to only those that exist in the form (may not all be rendered)
      const existingFields = fields.filter(field => field in formData);
      
      // Log all business relationship fields to help debug issues
      if (sectionKey === 'productInfo') {
        const businessFields = existingFields.filter(field => 
          field.includes('contractual') || 
          field.includes('chain_in_between') || 
          field.includes('reporting_as_tickbox')
        );
        
        // if (businessFields.length > 0) {
        //   console.log('Business relationship fields in formData:', businessFields);
        //   businessFields.forEach(field => {
        //     console.log(`  ${field}: ${formData[field]} (${typeof formData[field]})`);
        //   });
        // }
      }
      
      const totalFields = existingFields.length;
      
      // More detailed tracking
      const filledFields = existingFields.filter(field => {
        const isFilled = isFieldFilled(field);
        
        // Detailed logging for specific field types
        if ((sectionKey === 'productInfo' && field.includes('currency')) ||
            (sectionKey === 'productInfo' && (field.includes('contractual') || field.includes('chain_in_between')))) {
//          console.log(`Field ${field} is ${isFilled ? 'filled' : 'not filled'}`);
        }
        
        return isFilled;
      }).length;
      
      newSectionCompletion[sectionKey] = {
        filled: filledFields,
        total: totalFields
      };
      
      // Debug log for development
      // console.log(`Completion for ${sectionKey}: ${filledFields}/${totalFields}`);
    });
    
    setSectionCompletion(newSectionCompletion);
  }, [formData]);

  // Render completion indicator component
  const renderCompletionIndicator = (sectionKey) => {
    const { filled, total } = sectionCompletion[sectionKey] || { filled: 0, total: 0 };
    
    if (total === 0) return null;
    
    const progressPercentage = Math.floor((filled / total) * 100);
    const isComplete = filled === total;
    
    return (
      <div className="delivery-completion-indicator">
        <div className="delivery-completion-progress">
          <div 
            className="delivery-completion-progress-bar" 
            style={{ 
              width: `${progressPercentage}%`,
              backgroundColor: 'var(--hms-primary-blue)'
            }}
          />
        </div>
        <span className="delivery-completion-counter">{filled}/{total}</span>
        <div className={`delivery-completion-status ${isComplete ? 'complete' : 'incomplete'}`}>
          {isComplete ? (
            <svg viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          )}
        </div>
      </div>
    );
  };

  // Custom wrapper for handleInputChange that prevents duplicate operator selection
  const handleOperatorChange = (e) => {
    const { name, value } = e.target;
    
    // Only apply special handling for operator fields
    if (name === 'delivery_main_operator' || name === 'delivery_backup_operator_1' || name === 'delivery_backup_operator_2') {
      // If value is empty, just process normally
      if (!value) {
        handleInputChange(e);
        return;
      }
      
      // Check if this operator is already selected in another field
      const operatorFields = {
        main: formData.delivery_main_operator,
        backup1: formData.delivery_backup_operator_1,
        backup2: formData.delivery_backup_operator_2
      };
      
      // Determine which fields already have this operator
      const duplicateFields = [];
      if (name !== 'delivery_main_operator' && operatorFields.main === value) {
        duplicateFields.push('Main Operator');
      }
      if (name !== 'delivery_backup_operator_1' && operatorFields.backup1 === value) {
        duplicateFields.push('Backup Operator 1');
      }
      if (name !== 'delivery_backup_operator_2' && operatorFields.backup2 === value) {
        duplicateFields.push('Backup Operator 2');
      }
      
      // If duplicates found, show error and don't update
      if (duplicateFields.length > 0) {
        showError(`This operator is already selected as ${duplicateFields.join(' and ')}`);
        return;
      }
    }
    
    // If no duplicates, process normally
    handleInputChange(e);
  };
  
  // State for dropdown options from API
  const [dropdownOptions, setDropdownOptions] = useState({
    transport_modes: TRANSPORT_MODE_OPTIONS, // Default to local constants
    operators: [], // Start with empty array for operators
    traders: [], // Start with empty array for traders
    all_employees: [], // Start with empty array for all employees
    offices: [], // Add empty offices array
    currencies: CURRENCY_OPTIONS, // Default to local constants
    product_categories: [], // Add empty product_categories array
    countries: [], // Add empty countries array
    months: [], // Add empty months array
    incoterms: INCOTERMS_OPTIONS, // Default to local constants
    hms_entities: [], // Add empty HMS entities array
  });

  // === Units State for B/L Quantity ===
  const [units, setUnits] = useState([]);

  // Pre-linked recaps (Assign Delivery -> Create New) arrive via
  // location.state and are stashed by the mount effect above. This effect
  // runs the WHO/contract auto-populate once reference data (units, HMS
  // entities) is loaded, so the form previews the same fields the backend
  // fills on link/save. Ref guard + blank-only writes make it idempotent.
  // Placed after units/dropdownOptions are declared (its dep array reads
  // them) to avoid a temporal-dead-zone reference at render time.
  const preLinkAutoPopulatedRef = useRef(false);
  useEffect(() => {
    if (preLinkAutoPopulatedRef.current) return;
    if (!location.state?.preLinkedRecaps) return;
    if (!units.length || !dropdownOptions.hms_entities.length) return;

    // Source from the pending recaps (not the raw location.state payload) so the
    // confirmation dates, vessel and quality basis fill from the FULL hydrated
    // detail rather than the stripped list serializer. Wait for the hydration
    // pass to finish (success or failure) before running, so fuels fields are
    // present; on failure we still proceed with the list payload (numbers fill).
    if (!preLinkedHydrationDone) return;

    const firstPurchase = pendingLinkedPurchaseRecaps[0];
    const firstSales = pendingLinkedSalesRecaps[0];
    if (firstPurchase) applyRecapAutoPopulate('purchase', firstPurchase);
    if (firstSales) applyRecapAutoPopulate('sales', firstSales);
    preLinkAutoPopulatedRef.current = true;
  }, [
    units,
    dropdownOptions.hms_entities,
    location.state,
    preLinkedHydrationDone,
    pendingLinkedPurchaseRecaps,
    pendingLinkedSalesRecaps,
  ]);

  // === Product Search State (Purchase) ===
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const productSearchRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // === Product Search State (Sales) ===
  const [salesProductSearchTerm, setSalesProductSearchTerm] = useState('');
  const [debouncedSalesProductSearch, setDebouncedSalesProductSearch] = useState('');
  const [filteredSalesProducts, setFilteredSalesProducts] = useState([]);
  const [showSalesProductDropdown, setShowSalesProductDropdown] = useState(false);
  const [salesProductSearchLoading, setSalesProductSearchLoading] = useState(false);
  const salesProductSearchRef = useRef(null);
  const [selectedSalesProduct, setSelectedSalesProduct] = useState(null);

  // Sprint D — Inline product creator state. Two contexts in this form:
  //   - 'purchase' opens with purchase_price pre-fill, attaches via handleSelectProduct
  //   - 'sales'    opens with sale_price pre-fill, attaches via handleSelectSalesProduct
  // Stored together so we only render one creator at a time.
  const [productCreatorState, setProductCreatorState] = useState({
    isOpen: false,
    context: null, // 'purchase' | 'sales'
    searchTerm: '',
  });

  // Inline CRM partner creator state. Mirrors the product creator but for
  // CRM profiles. The picker that triggered the flow attaches the new
  // profile via the same onChange contract DeliveryPartnerSearch already
  // exposes (so seller / buyer / chain / producer / end user / vessel
  // owner / vessel broker pickers all share one creator). For contractual
  // multi-row pickers we remember (kind, index) so the right row is
  // updated on success.
  const [crmCreatorState, setCrmCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
    targetFieldName: null,
    targetFieldNameId: null,
    contractualKind: null, // 'seller' | 'buyer' | null
    contractualIndex: null,
    initialPartnerType: 'Seller (Creditor)',
    title: 'Create CRM Partner',
    onCreated: null, // a picker that writes the partner itself (Pit to Port)
  });
  const [pendingCreatedPartners, setPendingCreatedPartners] = useState([]);

  // Inline CRM partner creation is only allowed while the delivery is
  // still a DRAFT (or hasn't been saved yet). Once it transitions to
  // ACTIVE via Save & Workflow, the partner pickers should refuse to
  // mutate CRM — historical / finalized deliveries are read-only for
  // CRM purposes; counterparty edits must go through the CRM dashboard.
  // The delivery status is exposed on ``formData.status`` (Delivery
  // model: choices DRAFT / ACTIVE, default DRAFT). Any unrecognized /
  // missing value defaults to DRAFT to stay permissive in the new-flow
  // path where ``deliveryId === 'new'`` and ``formData.status`` is
  // undefined.
  const isDraftDelivery = (
    !deliveryId
    || deliveryId === 'new'
    || deliveryId === 'undefined'
    || (formData?.status || 'DRAFT') === 'DRAFT'
  );
  
  // === INITIAL LOAD: Populate each input from its respective active recap ===
  useEffect(() => {
    // CRITICAL: Only populate from DB recaps if there's NO active PENDING recap
    // This prevents saved DB recap values from overwriting unsaved pending recap values
    
    // Check if we have purchase recap data to load
    // BUT ONLY if there's no active pending purchase recap taking precedence
    if (!activeBlankPurchaseId && 
        formData.purchase_recaps_data && 
        formData.purchase_recaps_data.length > 0 && 
        formData.active_purchase_recap_id) {
      const activePurchaseRecap = formData.purchase_recaps_data.find(
        r => r.id === formData.active_purchase_recap_id
      );
      if (activePurchaseRecap && activePurchaseRecap.product && !productSearchTerm) {
        setProductSearchTerm(activePurchaseRecap.product);
        setSelectedProduct({ name: activePurchaseRecap.product });
      }
    }
    
    // Check if we have sales recap data to load (independent of purchase)
    // BUT ONLY if there's no active pending sales recap taking precedence
    if (!activeBlankSalesId &&
        formData.sales_recaps_data && 
        formData.sales_recaps_data.length > 0 && 
        formData.active_sales_recap_id) {
      const activeSalesRecap = formData.sales_recaps_data.find(
        r => r.id === formData.active_sales_recap_id
      );
      if (activeSalesRecap && activeSalesRecap.product && !salesProductSearchTerm) {
        setSalesProductSearchTerm(activeSalesRecap.product);
        setSelectedSalesProduct({ name: activeSalesRecap.product });
      }
    }
  }, [
    formData.purchase_recaps_data, 
    formData.sales_recaps_data, 
    formData.active_purchase_recap_id, 
    formData.active_sales_recap_id,
    activeBlankPurchaseId,
    activeBlankSalesId
  ]);

  // === Vessel Info Progress Calculation (out of 10 fields) ===
  const vesselInfoProgress = useMemo(() => {
    const fields = [
      formData.vessel_name,
      formData.vessel_imo_number,
      formData.delivery_estimated_time_of_arrival,
      // Mode of delivery only applies to (and is only shown for) fuels
      ...(formData.commodity_type === 'fuels' ? [formData.delivery_transport_mode] : []),
      formData.bill_of_lading_date,
      formData.bill_of_lading_date_estimated_or_final,
      formData.bill_of_lading_quantity_unit,
      formData.bill_of_lading_quantity,
      formData.bill_of_lading_quantity_estimated_or_final,
      formData.reporting_month
    ];

    const completed = fields.filter(field => field !== null && field !== undefined && field !== '').length;
    const total = fields.length;
    const percentage = (completed / total) * 100;

    return { completed, total, percentage };
  }, [
    formData.vessel_name,
    formData.vessel_imo_number,
    formData.delivery_estimated_time_of_arrival,
    formData.delivery_transport_mode,
    formData.commodity_type,
    formData.bill_of_lading_date,
    formData.bill_of_lading_date_estimated_or_final,
    formData.bill_of_lading_quantity_unit,
    formData.bill_of_lading_quantity,
    formData.bill_of_lading_quantity_estimated_or_final,
    formData.reporting_month
  ]);

  // === Port and Freight Progress Calculation (out of 4 fields, 3 for fuels) ===
  const portAndFreightProgress = useMemo(() => {
    // Bunkers hides the Discharging Port field (single dispatch port), so it
    // must not count against the fuels Port and Freight completion.
    const fields = formData.commodity_type === 'fuels'
      ? [
          formData.loading_port_location,
          formData.product_origin,
          formData.delivery_destination
        ]
      : [
          formData.loading_port_location,
          formData.discharging_port_location,
          formData.product_origin,
          formData.delivery_destination
        ];

    const completed = fields.filter(field => field !== null && field !== undefined && field !== '').length;
    const total = fields.length;
    const percentage = (completed / total) * 100;

    return { completed, total, percentage };
  }, [
    formData.commodity_type,
    formData.loading_port_location,
    formData.discharging_port_location,
    formData.product_origin,
    formData.delivery_destination
  ]);

  // === Commissions Progress Calculation (out of 6 fields) ===
  const commissionsProgress = useMemo(() => {
    const fields = [
      formData.freight_per_ton_unit,
      formData.freight_per_ton,
      formData.freight_per_ton_currency,
      formData.address_commission,
      formData.mercurius_commission,
      formData.brokerage_commission
    ];
    
    const completed = fields.filter(field => field !== null && field !== undefined && field !== '').length;
    const total = 6;
    const percentage = (completed / total) * 100;
    
    return { completed, total, percentage };
  }, [
    formData.freight_per_ton_unit,
    formData.freight_per_ton,
    formData.freight_per_ton_currency,
    formData.address_commission,
    formData.mercurius_commission,
    formData.brokerage_commission
  ]);

  // === Terms and Dates Progress Calculation (out of 4 fields) ===
  const termsAndDatesProgress = useMemo(() => {
    // Laycan counts as 1 field if either start or end date is filled
    const laycanCompleted = (formData.laycan_start_date && formData.laycan_end_date) ? 1 : 0;
    
    const fields = [
      formData.charter_party_date,
      formData.incoterms_purchase,
      formData.incoterms_sale
    ];
    
    const completed = fields.filter(field => field !== null && field !== undefined && field !== '').length + laycanCompleted;
    const total = 4;
    const percentage = (completed / total) * 100;
    
    return { completed, total, percentage };
  }, [
    formData.charter_party_date,
    formData.laycan_start_date,
    formData.laycan_end_date,
    formData.incoterms_purchase,
    formData.incoterms_sale
  ]);

  // === Vessel Parties Progress Calculation (out of 2 fields) ===
  const vesselPartiesProgress = useMemo(() => {
    const fields = [
      formData.vessel_owners,
      formData.vessel_brokers
    ];
    
    const completed = fields.filter(field => field !== null && field !== undefined && field !== '').length;
    const total = 2;
    const percentage = (completed / total) * 100;
    
    return { completed, total, percentage };
  }, [
    formData.vessel_owners,
    formData.vessel_brokers
  ]);

  // Add state for custom quality rows in the product quality table
  const [customQualityRows, setCustomQualityRows] = useState([]);


  // === Contractual Partner Management Functions ===
  
  const addSellerContractual = () => {
    setSellerContractualList([...sellerContractualList, { name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' }]);
    // Auto-scroll to bottom after adding
    setTimeout(() => {
      const container = document.getElementById('seller-contractual-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  };

  const removeSellerContractual = (index) => {
    const updated = sellerContractualList.filter((_, i) => i !== index);
    // Ensure at least one entry remains
    if (updated.length === 0) {
      updated.push({ name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' });
    }
    setSellerContractualList(updated);
  };

  const updateSellerContractual = (index, field, value) => {
    const updated = [...sellerContractualList];
    updated[index] = { ...updated[index], [field]: value };
    setSellerContractualList(updated);
    console.log(`✅ Updated seller contractual [${index}].${field} = ${value}:`, updated);
  };

  const addBuyerContractual = () => {
    setBuyerContractualList([...buyerContractualList, { name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' }]);
    // Auto-scroll to bottom after adding
    setTimeout(() => {
      const container = document.getElementById('buyer-contractual-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  };

  const removeBuyerContractual = (index) => {
    const updated = buyerContractualList.filter((_, i) => i !== index);
    // Ensure at least one entry remains
    if (updated.length === 0) {
      updated.push({ name: '', crm_id: null, reporting_as: false, sanction_status: 'pending', kyc_status: 'pending' });
    }
    setBuyerContractualList(updated);
  };

  const updateBuyerContractual = (index, field, value) => {
    const updated = [...buyerContractualList];
    updated[index] = { ...updated[index], [field]: value };
    setBuyerContractualList(updated);
    console.log(`✅ Updated buyer contractual [${index}].${field} = ${value}:`, updated);
  };

  // Special handler for contractual partner search (integrates with DeliveryPartnerSearch)
  // Signature: onChange(fieldName, fieldNameId, partnerId, partnerName, statusData)
  const handleContractualPartnerChange = (type, index) => (fieldName, fieldNameId, partnerId, partnerName, statusData) => {
    console.log(`👥 Contractual partner change - Type: ${type}, Index: ${index}, Partner: ${partnerName} (ID: ${partnerId})`);
    console.log(`📊 Status data received:`, statusData);
    
    if (type === 'seller') {
      const updated = [...sellerContractualList];
      updated[index] = { 
        ...updated[index], 
        name: partnerName || '', 
        crm_id: partnerId || null,
        // Update status from CRM if available, otherwise keep existing or default to pending
        sanction_status: statusData?.sanctionStatus || updated[index].sanction_status || 'pending',
        kyc_status: statusData?.kycStatus || updated[index].kyc_status || 'pending'
      };
      setSellerContractualList(updated);
      console.log(`✅ Updated seller contractual list with status:`, updated);
    } else if (type === 'buyer') {
      const updated = [...buyerContractualList];
      updated[index] = { 
        ...updated[index], 
        name: partnerName || '', 
        crm_id: partnerId || null,
        // Update status from CRM if available, otherwise keep existing or default to pending
        sanction_status: statusData?.sanctionStatus || updated[index].sanction_status || 'pending',
        kyc_status: statusData?.kycStatus || updated[index].kyc_status || 'pending'
      };
      setBuyerContractualList(updated);
      console.log(`✅ Updated buyer contractual list with status:`, updated);
    }
  };

  // Function to add a new custom quality row
  const addCustomQualityRow = () => {
    const newRow = {
      id: Date.now(), // Use timestamp as a simple unique ID
      propertyName: '',
      loadingPortValue: '',
      dischargingPortValue: '',
      purchaseContractValue: '',
      salesContractValue: '',
      hmsQualityReferenceValue: ''
    };
    setCustomQualityRows([...customQualityRows, newRow]);
  };

  // Function to delete a custom quality row
  const deleteCustomQualityRow = (id) => {
//    console.log(`Deleting custom quality row with id: ${id}`);
    
    // Remove the row from the state
    const updatedRows = customQualityRows.filter(row => row.id !== id);
    setCustomQualityRows(updatedRows);
    
    // Remove the custom quality values from form data
    // We'll create empty synthetic events to clear out the values
    const fieldsToRemove = ['propertyName', 'loadingPortValue', 'dischargingPortValue', 'purchaseContractValue', 'salesContractValue', 'hmsQualityReferenceValue'];
    
    fieldsToRemove.forEach(field => {
      const fieldName = `custom_quality_${id}_${field}`;
//      console.log(`Clearing field from form data: ${fieldName}`);
      
      // First try to completely remove the property from formData
      if (formData[fieldName] !== undefined) {
        const newFormData = { ...formData };
        delete newFormData[fieldName];
        
        // If handleInputChange takes a modified formData object directly, use this instead
        // handleInputChange(newFormData);
        
        // Otherwise use synthetic events as before
        const syntheticEvent = {
          target: {
            name: fieldName,
            value: ''
          }
        };
        handleInputChange(syntheticEvent);
      }
    });
    
    // Also ensure the row is removed from savedCustomRows if it exists there
    if (savedCustomRows.includes(id)) {
      unsaveCustomQualityRow(id);
    }
  };

  // --- Fill quality from a Certificate of Analysis --------------------
  // The API only PROPOSES values; this is where they land. We merge through
  // the same channels the operator's own typing uses -- handleInputChange for
  // the eight standard rows, and a new customQualityRows entry plus synthetic
  // events for anything the certificate reported that isn't a standard row.
  //
  // PREMISE: DeliveryViewSet.update stays the ONLY writer of the quality
  // fields, because it owns the custom-row __metadata__ id map that
  // DeliverySerializer.get_custom_quality_fields reads rows out of (and it
  // reads that map from the LOADING-PORT blob alone). Do not "optimise" this
  // into a direct PATCH from the modal. (2026-08-10)
  const [showQualityCertificateModal, setShowQualityCertificateModal] = useState(false);
  // The app-wide tray links back here with ?certificate_reading=<id> for a
  // certificate still being read: open the window so it shows the progress.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('certificate_reading')) {
      setShowQualityCertificateModal(true);
    }
  }, []);

  const applyCertificateProposals = ({ standard, custom, column }) => {
    (standard || []).forEach(({ field, value }) => {
      handleInputChange({ target: { name: field, value } });
    });

    if (custom && custom.length > 0) {
      // Reuse an existing row when the certificate names a property the
      // operator already has on the table -- otherwise a second COA would
      // create a duplicate "Chlorine" row alongside the first.
      const newRows = [];
      custom.forEach((entry, index) => {
        const existing = customQualityRows.find(
          row => (row.propertyName || '').trim().toLowerCase() === entry.propertyName.trim().toLowerCase()
        );
        const rowId = existing ? existing.id : Date.now() + index;
        if (!existing) {
          newRows.push({
            id: rowId,
            propertyName: entry.propertyName,
            loadingPortValue: '',
            dischargingPortValue: '',
            salesContractValue: '',
            hmsQualityReferenceValue: '',
            [entry.customKey]: entry.value
          });
        }
        handleInputChange({
          target: { name: `custom_quality_${rowId}_propertyName`, value: entry.propertyName }
        });
        handleInputChange({
          target: { name: `custom_quality_${rowId}_${entry.customKey}`, value: entry.value }
        });
      });
      if (newRows.length > 0) {
        setCustomQualityRows([...customQualityRows, ...newRows]);
      }
    }

    if (markAsChanged) {
      markAsChanged();
    }
    setShowQualityCertificateModal(false);

    const total = (standard || []).length + (custom || []).length;
    const columnLabel = column === 'discharging_port' ? 'Discharging Port' : 'Loading Port';
    showSuccess(
      `${total} value${total === 1 ? '' : 's'} filled into ${columnLabel} — save the delivery to keep them.`
    );
  };

  // Function to update a custom row property
  const updateCustomQualityRow = (id, field, value) => {
//    console.log(`Updating custom quality row: id=${id}, field=${field}, value=${value}`);
    
    const updatedRows = customQualityRows.map(row => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setCustomQualityRows(updatedRows);
    
    // Update the form data with the custom property values
    const syntheticEvent = {
      target: {
        name: `custom_quality_${id}_${field}`,
        value: value
      }
    };
//    console.log(`Sending synthetic event for custom quality field: ${syntheticEvent.target.name}=${syntheticEvent.target.value}`);
    handleInputChange(syntheticEvent);
  };

  // Add state for multi-select dropdowns
  const [multiSelectOpen, setMultiSelectOpen] = useState({
    delivery_access: false,
    financial_access: false
  });
  
  // Add state for selected options in multi-select dropdowns
  const [selectedOptions, setSelectedOptions] = useState({
    delivery_access: [],
    financial_access: []
  });

  // Refs for handling outside clicks
  const deliveryAccessRef = useRef(null);
  const financialAccessRef = useRef(null);
  
  // Filtered operators for main operator and backup operator dropdowns
  // Only show operators that are selected in delivery_access
  const filteredOperators = useMemo(() => {
    // console.log('Filtering operators for dropdowns:');
    // console.log('- Available operators:', dropdownOptions.operators?.length || 0);
    // console.log('- Delivery access selections:', selectedOptions.delivery_access?.length || 0);
    
    // Check data format of delivery_access
    if (selectedOptions.delivery_access && selectedOptions.delivery_access.length > 0) {
      // Check if delivery_access is an array as expected
      if (!Array.isArray(selectedOptions.delivery_access)) {
        console.error('CRITICAL: delivery_access is not an array:', selectedOptions.delivery_access);
      } else {
        // Check the format of the first few elements to ensure they're strings
        const samples = selectedOptions.delivery_access.slice(0, 3);
//        console.log('Sample delivery_access values:', samples);
        samples.forEach((value, index) => {
          if (typeof value !== 'string') {
            console.error(`CRITICAL: delivery_access[${index}] is not a string:`, value);
          }
        });
      }
    }
    
    // Check data format of operators
    if (dropdownOptions.operators && dropdownOptions.operators.length > 0) {
      // Check if operators is an array as expected
      if (!Array.isArray(dropdownOptions.operators)) {
        console.error('CRITICAL: operators is not an array:', dropdownOptions.operators);
      } else {
        // Check the format of the first few elements to ensure they have value and label
        const samples = dropdownOptions.operators.slice(0, 3);
//        console.log('Sample operator values:', samples);
        samples.forEach((op, index) => {
          if (!op.value || !op.label) {
            console.error(`CRITICAL: operators[${index}] missing value or label:`, op);
          }
          
          // Fix for the "– 24779005" format issue
          if (typeof op.value !== 'string') {
            console.error(`CRITICAL: operators[${index}].value is not a string:`, op.value);
            
            // Try to convert the operator value to a proper string format
            if (op.value !== null && op.value !== undefined) {
              // Convert to string and handle special case with dash prefix
              const strValue = String(op.value).trim();
              // Check if it starts with a dash and has numbers after it
              if (strValue.match(/^[\u2013\u2014\-]\s*\d+$/)) {
                // Extract just the numeric part
                dropdownOptions.operators[index].value = strValue.replace(/^[\u2013\u2014\-]\s*/, '');
//                console.log(`Fixed operator[${index}].value format from "${strValue}" to "${dropdownOptions.operators[index].value}"`);
              } else {
                dropdownOptions.operators[index].value = strValue;
              }
            }
          }
        });
        
        // Fix all operator values, not just the samples
        dropdownOptions.operators.forEach((op, index) => {
          if (typeof op.value !== 'string') {
            // Convert to string and handle special case with dash prefix
            const strValue = String(op.value).trim();
            // Check if it starts with a dash and has numbers after it
            if (strValue.match(/^[\u2013\u2014\-]\s*\d+$/)) {
              // Extract just the numeric part
              dropdownOptions.operators[index].value = strValue.replace(/^[\u2013\u2014\-]\s*/, '');
            } else {
              dropdownOptions.operators[index].value = strValue;
            }
          }
        });
      }
    }
    
    if (!dropdownOptions.operators || !selectedOptions.delivery_access) {
//      console.log('No operators or delivery_access available, returning empty list');
      return [];
    }
    
    // Filter operators based on delivery_access selection
    const filtered = dropdownOptions.operators.filter(op => {
      // Ensure the value is a string for comparison
      const opValue = typeof op.value === 'string' ? op.value : String(op.value).trim().replace(/^[\u2013\u2014\-]\s*/, '');
      
      // Check if this operator ID exists in the delivery_access array
      const isIncluded = selectedOptions.delivery_access.some(accessId => {
        // Normalize the access ID for comparison
        const normalizedAccessId = typeof accessId === 'string' ? 
          accessId : String(accessId).trim().replace(/^[\u2013\u2014\-]\s*/, '');
        
        return normalizedAccessId === opValue;
      });
      
      if (!isIncluded) {
//        console.log(`Operator ${op.label} (${opValue}) excluded from dropdown because not in delivery_access`);
      }
      
      return isIncluded;
    });
    
//    console.log(`Filtered ${filtered.length} operators for dropdowns:`, 
//      filtered.map(op => `${op.label} (${op.value})`));
    
    return filtered;
  }, [dropdownOptions.operators, selectedOptions.delivery_access]);
  
  // Initialize selected options from formData when dropdownOptions change
  useEffect(() => {
    if (dropdownOptions.operators && dropdownOptions.operators.length > 0) {
      // Initialize with all operators selected by default
      const initializeSelected = (field) => {
//        console.log(`Initializing ${field} selections:`, selectedOptions[field]);
        
        if (!selectedOptions[field].length) {
          // Get all operator IDs
          const allOperators = dropdownOptions.operators.map(op => op.value);
//          console.log(`All available operator IDs (${allOperators.length}):`, allOperators);
          
          // Ensure Steffen Unger is included initially, but can be removed later
          const steffenUngerId = "24778979";
          let selectedOperators = allOperators;
          
          // If Steffen is not in the operators list from API, add his ID for initial setup
          if (!selectedOperators.includes(steffenUngerId)) {
            // Check if this user exists in the system
            const steffenExists = dropdownOptions.operators.some(op => op.value === steffenUngerId);
//            console.log(`Steffen Unger (${steffenUngerId}) exists in operators list:`, steffenExists);
            
            if (!steffenExists) {
              // Only add if not already present in data
              selectedOperators.push(steffenUngerId);
//              console.log(`Added Steffen Unger (${steffenUngerId}) to selected operators`);
            }
          }
          
//          console.log(`Setting ${field} selections to ${selectedOperators.length} operators`);
          setSelectedOptions(prev => ({
            ...prev,
            [field]: selectedOperators
          }));
          
          // Also update the formData with all operators (as comma-separated values)
          const syntheticEvent = {
            target: {
              name: field,
              value: selectedOperators.join(',')
            }
          };
          handleInputChange(syntheticEvent);
//          console.log(`Updated formData.${field} with ${selectedOperators.length} operators`);
        } else {
//          console.log(`${field} already initialized with ${selectedOptions[field].length} operators`);
        }
      };
      
      initializeSelected('delivery_access');
      initializeSelected('financial_access');
    }
  }, [dropdownOptions.operators]);

  // Effect to handle validation when delivery_access changes
  // Ensure main operator and backup operators are cleared if they're not in delivery_access
  useEffect(() => {
    // console.log('Validating operators against delivery_access:', {
    //   delivery_access: selectedOptions.delivery_access?.length || 0,
    //   main_operator: formData.delivery_main_operator || 'not set',
    //   backup_operator_1: formData.delivery_backup_operator_1 || 'not set',
    //   backup_operator_2: formData.delivery_backup_operator_2 || 'not set'
    // });
    
    // Helper function to normalize IDs for comparison
    const normalizeId = (id) => {
      if (!id) return '';
      const strId = typeof id === 'string' ? id : String(id);
      // Remove any dash prefix that might exist
      return strId.trim().replace(/^[\u2013\u2014\-]\s*/, '');
    };
    
    // Helper function to check if an operator ID is in the delivery_access list
    const isInDeliveryAccess = (operatorId) => {
      if (!operatorId || !selectedOptions.delivery_access || !selectedOptions.delivery_access.length) {
        return false;
      }
      
      const normalizedOpId = normalizeId(operatorId);
      
      return selectedOptions.delivery_access.some(accessId => {
        return normalizeId(accessId) === normalizedOpId;
      });
    };
    
    if (selectedOptions.delivery_access && selectedOptions.delivery_access.length > 0) {
      // Check if main operator is still in filtered list
      if (formData.delivery_main_operator && !isInDeliveryAccess(formData.delivery_main_operator)) {
//        console.log(`Main Operator (${formData.delivery_main_operator}) was reset as it was removed from Delivery Access`);
        
        // Clear main operator silently without showing an error
        const syntheticEvent = {
          target: {
            name: 'delivery_main_operator',
            value: ''
          }
        };
        handleInputChange(syntheticEvent);
        // Error message removed for better user experience
      } else if (formData.delivery_main_operator) {
//        console.log(`Main Operator (${formData.delivery_main_operator}) is valid and included in delivery_access`);
      }
      
      // Check backup operator 1
      if (formData.delivery_backup_operator_1 && !isInDeliveryAccess(formData.delivery_backup_operator_1)) {
//        console.log(`Backup Operator 1 (${formData.delivery_backup_operator_1}) was reset as it was removed from Delivery Access`);
        
        const syntheticEvent = {
          target: {
            name: 'delivery_backup_operator_1',
            value: ''
          }
        };
        handleInputChange(syntheticEvent);
        // Error message removed for better user experience
      }
      
      // Check backup operator 2
      if (formData.delivery_backup_operator_2 && !isInDeliveryAccess(formData.delivery_backup_operator_2)) {
//        console.log(`Backup Operator 2 (${formData.delivery_backup_operator_2}) was reset as it was removed from Delivery Access`);
        
        const syntheticEvent = {
          target: {
            name: 'delivery_backup_operator_2',
            value: ''
          }
        };
        handleInputChange(syntheticEvent);
        // Error message removed for better user experience
      }
    }
  }, [selectedOptions.delivery_access, formData.delivery_main_operator, formData.delivery_backup_operator_1, formData.delivery_backup_operator_2]);

  // Handle outside clicks to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (deliveryAccessRef.current && !deliveryAccessRef.current.contains(event.target)) {
        setMultiSelectOpen(prev => ({ ...prev, delivery_access: false }));
      }
      if (financialAccessRef.current && !financialAccessRef.current.contains(event.target)) {
        setMultiSelectOpen(prev => ({ ...prev, financial_access: false }));
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle a specific dropdown
  const toggleMultiSelect = (name) => {
    setMultiSelectOpen(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Handle checkbox changes in multi-select
  const handleMultiSelectChange = (name, value) => {
//    console.log(`Multi-select change for ${name}:`, {
//      action: selectedOptions[name].includes(value) ? 'removing' : 'adding',
//      value: value
//    });
    
    // Check if we're removing an operator from delivery_access
    if (name === 'delivery_access' && selectedOptions[name].includes(value)) {
      // We're removing an operator, check if they're currently selected
      if (formData.delivery_main_operator === value) {
//        console.log(`Operator ${value} was removed from Delivery Access and will be unselected from Main Operator`);
        // Clear main operator field
        handleInputChange({
          target: {
            name: 'delivery_main_operator',
            value: ''
          }
        });
      }
      if (formData.delivery_backup_operator_1 === value) {
//        console.log(`Operator ${value} was removed from Delivery Access and will be unselected from Backup Operator 1`);
        // Clear backup operator 1 field
        handleInputChange({
          target: {
            name: 'delivery_backup_operator_1',
            value: ''
          }
        });
      }
      if (formData.delivery_backup_operator_2 === value) {
//        console.log(`Operator ${value} was removed from Delivery Access and will be unselected from Backup Operator 2`);
        // Clear backup operator 2 field
        handleInputChange({
          target: {
            name: 'delivery_backup_operator_2',
            value: ''
          }
        });
      }
    }
    
    const newSelected = selectedOptions[name].includes(value)
      ? selectedOptions[name].filter(item => item !== value)
      : [...selectedOptions[name], value];
    
//    console.log(`New ${name} selections:`, {
//      before: selectedOptions[name].length,
//      after: newSelected.length,
//      selection: newSelected
//    });
    
    setSelectedOptions(prev => ({
      ...prev,
      [name]: newSelected
    }));
    
    // Update formData with comma-separated values
    const syntheticEvent = {
      target: {
        name: name,
        value: newSelected.join(',')
      }
    };
    handleInputChange(syntheticEvent);
  };

  // Select all options in a multi-select
  const selectAllOptions = (name) => {
    const allValues = dropdownOptions.operators.map(op => op.value);
    setSelectedOptions(prev => ({
      ...prev,
      [name]: allValues
    }));
    
    // Update formData with all options
    const syntheticEvent = {
      target: {
        name: name,
        value: allValues.join(',')
      }
    };
    handleInputChange(syntheticEvent);
  };

  // Deselect all options in a multi-select
  const deselectAllOptions = (name) => {
    // Deselect all operators including Steffen Unger
    setSelectedOptions(prev => ({
      ...prev,
      [name]: []
    }));
    
    // Update formData with empty value
    const syntheticEvent = {
      target: {
        name: name,
        value: ''
      }
    };
    handleInputChange(syntheticEvent);
    
    // If deselecting from delivery_access, also clear main and backup operators
    if (name === 'delivery_access') {
      // Clear main operator
      handleInputChange({
        target: {
          name: 'delivery_main_operator',
          value: ''
        }
      });
      
      // Clear backup operator 1
      handleInputChange({
        target: {
          name: 'delivery_backup_operator_1',
          value: ''
        }
      });
      
      // Clear backup operator 2
      handleInputChange({
        target: {
          name: 'delivery_backup_operator_2',
          value: ''
        }
      });
    }
  };

  // Older deliveries stored the HMS entity as its id in text ("4"). Resolve
  // that to the name once the options are known, so the box shows the company
  // and a later Save writes the name like every other writer does. Uses the
  // no-sync handler: this is a display normalisation, not a user edit.
  useEffect(() => {
    const raw = formData.hms_entity;
    const entities = dropdownOptions.hms_entities || [];
    if (!raw || entities.length === 0) return;
    if (entities.some(e => e.value === raw)) return;
    const byId = entities.find(e => String(e.id) === String(raw));
    if (byId) {
      originalHandleInputChange({ target: { name: 'hms_entity', value: byId.value } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.hms_entity, dropdownOptions.hms_entities]);

  // Fetch dropdown options from API on component mount
  useEffect(() => {
    const getDropdownOptions = async () => {
      try {
        const options = await fetchDeliveryDropdowns();
//        console.log('Received dropdown options:', options);
        
        // Fetch HMS entities
        const hmsEntities = await fetchHMSEntities();
//        console.log('Fetched HMS entities:', hmsEntities);

        // Delivery.hms_entity holds the entity NAME. That is what the recap
        // form stores, what the bunker enquiry conversion stores, what the
        // link-recap step copies onto the delivery, and what
        // /api/deliveries/dropdowns/ speaks. fetchHMSEntities() is keyed on
        // the HMSEntity id because the invoice screens need it for a foreign
        // key -- and a <select> whose option values are ids cannot display a
        // name, so this box read "Select HMS Entity" on every bunker delivery
        // the server had just filled in. Key the options on the name here and
        // keep the id alongside for rows that stored the id as text.
        setDropdownOptions({
          ...options,
          hms_entities: (hmsEntities || []).map(e => ({
            value: e.label,
            label: e.label,
            id: e.value,
          }))
        });
        
        // DEBUG: Log countries loaded
//        console.log('📍 Countries loaded for dropdowns:', options.countries?.length || 0, 'countries');
        if (options.countries && options.countries.length > 0) {
//          console.log('📍 First 3 countries:', options.countries.slice(0, 3));
        }
      } catch (error) {
        console.error('Failed to load dropdown options:', error);
        // No fallbacks needed, empty arrays already in initial state
      }
    };
    
    getDropdownOptions();
  }, []);

  // Fetch units for B/L quantity dropdown
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const unitsData = await systemDataService.getUnits();
        const activeUnits = unitsData.filter(unit => unit.is_active);
        setUnits(activeUnits);
//        console.log('Loaded units for B/L quantity:', activeUnits);
      } catch (error) {
        console.error('Error fetching units:', error);
        showError('Failed to load units');
      }
    };
    fetchUnits();
  }, []);
  
  // One answer to "is the B/L unit and quantity recap-driven?": the boxes
  // grey out on it (renderBLQuantityField) and the effect that fills them
  // keys off it, so the two cannot disagree.
  const hasSalesRecaps = (formData.sales_recaps_data?.length > 0) ||
                         (pendingLinkedSalesRecaps?.length > 0) ||
                         (blankSalesRecaps?.some(r => r.product && r.sale_contract_quantity_unit));

  // === THE B/L QUANTITY OF A DELIVERY WITH BILLS OF LADING ROWS ===
  // With rows, the B/L quantity IS their sum: the server stores exactly that
  // whatever is sent (DeliverySerializer._derive_bill_of_lading_quantity). So
  // the box shows it, read-only, with or without sales recaps, and the form
  // holds it too, so Save sends the figure that will be stored. The rows are
  // the loaded delivery's, or -- once the B/L table has saved its own rows --
  // the ones it just saved. null = no rows: the box behaves as it always did.
  const [billsOfLadingJustSaved, setBillsOfLadingJustSaved] = useState(null);
  useEffect(() => {
    // A reload brings the stored rows; the table's own copy is then old news.
    setBillsOfLadingJustSaved(null);
  }, [formData.bills_of_lading]);
  const billOfLadingRowsTotal = useMemo(
    () => billOfLadingRowsSum(billsOfLadingJustSaved ?? formData.bills_of_lading),
    [billsOfLadingJustSaved, formData.bills_of_lading],
  );
  useEffect(() => {
    if (billOfLadingRowsTotal === null) return;
    if (String(formData.bill_of_lading_quantity ?? '') !== billOfLadingRowsTotal) {
      handleInputChange({ target: { name: 'bill_of_lading_quantity', value: billOfLadingRowsTotal } });
    }
    // handleInputChange is the parent's plain setter; its identity is not a reason to rerun.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billOfLadingRowsTotal, formData.bill_of_lading_quantity]);

  // === DYNAMIC BL UNIT AND QUANTITY POPULATION ===
  // Populate BL Unit from FIRST sales recap (dynamically updates when recaps change)
  // Populate BL Quantity from Total Sales Quantity field in WHAT tab
  useEffect(() => {
    // Skip if units haven't loaded yet
    if (!units || units.length === 0) {
      return;
    }

    // Recap-driven ONLY while the delivery has sales recaps. Without them the
    // unit and the quantity belong to the person -- typed, or written by the
    // B/L table as its rows' sum -- and nothing here clears or replaces them.
    // This used to blank the quantity whenever Total Sales Quantity was
    // empty, and overwrite it with that total otherwise, so a B/L figure on
    // a delivery with no recaps never survived the next render.
    if (!hasSalesRecaps) {
      return;
    }
    
    // Get all sales recaps (saved + pending linked + filled blank recaps)
    const savedSalesRecaps = formData.sales_recaps_data || [];
    const allSalesRecaps = [
      ...savedSalesRecaps,
      ...pendingLinkedSalesRecaps,
      ...blankSalesRecaps.filter(r => r.product && r.sale_contract_quantity_unit)
    ];
    
    // Sort by transaction number to get the first recap consistently
    const sortedSalesRecaps = allSalesRecaps.sort((a, b) => {
      const numA = a.transaction_number || '';
      const numB = b.transaction_number || '';
      return numA.localeCompare(numB);
    });
    
    const firstSalesRecap = sortedSalesRecaps[0];
    
    // Update BL Unit from first sales recap's unit
    if (firstSalesRecap && firstSalesRecap.sale_contract_quantity_unit) {
      const unitValueFromRecap = firstSalesRecap.sale_contract_quantity_unit;
      
      // CRITICAL: Convert unit code to unit ID if needed
      // sale_contract_quantity_unit can be either a code (string like "MT") or ID (number)
      let unitIdToSet;
      
      if (typeof unitValueFromRecap === 'string' && isNaN(unitValueFromRecap)) {
        // It's a code like "MT" - find the matching unit ID
        const matchingUnit = units.find(u => u.code === unitValueFromRecap);
        if (matchingUnit) {
          unitIdToSet = matchingUnit.id;
          console.log('🔄 Converted unit code to ID:', { code: unitValueFromRecap, id: unitIdToSet });
        } else {
          console.error('❌ Unit code not found in units array:', unitValueFromRecap, 'Available:', units.map(u => u.code));
          return;
        }
      } else {
        // It's already an ID
        unitIdToSet = unitValueFromRecap;
      }
      
      // Check if the unit is different from current value to avoid unnecessary updates
      if (String(formData.bill_of_lading_quantity_unit) !== String(unitIdToSet)) {
        console.log('🔄 Auto-populating BL Unit from first sales recap:', {
          recap: firstSalesRecap.transaction_number,
          unitId: unitIdToSet
        });
        
        handleInputChange({
          target: {
            name: 'bill_of_lading_quantity_unit',
            value: unitIdToSet
          }
        });
      }
    }
    
    // Update BL Quantity from Total Sales Quantity field -- unless the
    // delivery has Bills of Lading rows: then it is their sum (above).
    const totalSalesQuantity = formData.total_sales_product_quantity;
    if (billOfLadingRowsTotal === null && totalSalesQuantity && totalSalesQuantity !== formData.bill_of_lading_quantity) {
      console.log('🔄 Auto-populating BL Quantity from Total Sales Quantity:', totalSalesQuantity);
      
      handleInputChange({
        target: {
          name: 'bill_of_lading_quantity',
          value: totalSalesQuantity
        }
      });
    }
  }, [
    units,
    hasSalesRecaps,
    billOfLadingRowsTotal,
    formData.sales_recaps_data,
    pendingLinkedSalesRecaps,
    blankSalesRecaps,
    formData.total_sales_product_quantity,
    formData.bill_of_lading_quantity_unit,
    formData.bill_of_lading_quantity
  ]);
  
  // Log dropdown options whenever they change
  // useEffect(() => {
  //   console.log('Current dropdown options:', dropdownOptions);
  //   console.log('HMS entities available:', dropdownOptions.hms_entities);
  //   console.log('All employees for Deal Maker Freight:', dropdownOptions.all_employees);
  // }, [dropdownOptions]);

  // === Product Search Effects ===
  
  // Debounce product search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductSearch(productSearchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [productSearchTerm]);
  
  // Fetch products when debounced search changes
  useEffect(() => {
    if (debouncedProductSearch && debouncedProductSearch.trim().length >= 2) {
      fetchProducts(debouncedProductSearch);
    } else {
      setFilteredProducts([]);
    }
  }, [debouncedProductSearch]);
  
  // Click outside to close product dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Load product details when editing existing delivery
  useEffect(() => {
    if (formData.delivery_product_category) {
//      console.log('🔍 PURCHASE: Attempting to load product for ID:', formData.delivery_product_category);
      const loadProductDetails = async () => {
        try {
          const variant = await stockService.getVariantById(formData.delivery_product_category);
//          console.log('✅ PURCHASE: Loaded product variant:', variant);
          if (variant) {
            // Use display_name from backend (now returned by getVariantById)
            // Backend constructs: "Steam Coal | Australia | Acme Trading - Steam Coal Richards (prices)"
            // Strip pricing info if present
            let cleanDescription = variant.display_name 
              ? variant.display_name.split(' (Sale:')[0].split(' (Purchase:')[0] 
              : variant.shortname || variant.full_description || 'Product';
            
//            console.log('✅ PURCHASE: Setting product search term to:', cleanDescription);
            setProductSearchTerm(cleanDescription);
            setSelectedProduct(variant);
          }
        } catch (error) {
          console.error('❌ PURCHASE: Error loading product details:', error);
        }
      };
      loadProductDetails();
    } else {
//      console.log('⚠️ PURCHASE: No delivery_product_category in formData');
    }
  }, [formData.delivery_product_category]);
  
  // API call to fetch products
  const fetchProducts = async (searchTerm) => {
    try {
      setProductSearchLoading(true);
      const results = await stockService.searchVariantsForInvoice(searchTerm);
      setFilteredProducts(results);
    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    } finally {
      setProductSearchLoading(false);
    }
  };
  
  // Product selection handler (Purchase)
  const handleSelectProduct = (variant) => {
    console.log('Selected product variant:', variant);
    console.log('Variant unit value:', variant.unit, 'Type:', typeof variant.unit);
    
    // Store selected product
    setSelectedProduct(variant);
    
    // Update form data with product ID
    handleInputChange({
      target: {
        name: 'delivery_product_category',
        value: variant.id
      }
    });
    
    // AUTO-FILL: Populate quantity, unit, and purchase price from product
    if (variant.quantity) {
      handleInputChange({
        target: {
          name: 'delivery_product_quantity',
          value: variant.quantity
        }
      });
    }
    
    // Unit field expects ID (integer or string number)
    if (variant.unit_id) {
      console.log('Setting unit to:', variant.unit_id);
      handleInputChange({
        target: {
          name: 'delivery_product_unit',
          value: variant.unit_id
        }
      });
    }
    
    if (variant.purchase_price) {
      handleInputChange({
        target: {
          name: 'purchase_price_per_ton_contract',
          value: variant.purchase_price
        }
      });
    }
    
    if (variant.price_currency) {
      handleInputChange({
        target: {
          name: 'purchase_price_per_ton_contract_currency',
          value: variant.price_currency
        }
      });
    }
    
    // Auto-fill tolerance from variant (NEW)
    if (variant.tolerance_lower_value || variant.tolerance_upper_value) {
      console.log('Auto-filling delivery tolerance from variant');
      handleInputChange({
        target: {
          name: 'delivery_product_tolerance_lower_value',
          value: variant.tolerance_lower_value || ''
        }
      });
      handleInputChange({
        target: {
          name: 'delivery_product_tolerance_lower_unit',
          value: variant.tolerance_lower_unit || '%'
        }
      });
      handleInputChange({
        target: {
          name: 'delivery_product_tolerance_upper_value',
          value: variant.tolerance_upper_value || ''
        }
      });
      handleInputChange({
        target: {
          name: 'delivery_product_tolerance_upper_unit',
          value: variant.tolerance_upper_unit || '%'
        }
      });
    }
    
    // Update search term to show selected item
    // CRITICAL FIX: Reconstruct the exact display format that user sees in dropdown
    let cleanDescription = '';
    if (variant.display_name) {
      cleanDescription = variant.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
    } else {
      // Reconstruct from variant properties
      const productType = variant.base_stock_item_data?.product_type_display || '';
      const country = variant.base_stock_item_data?.country_of_origin || '';
      const supplier = variant.base_stock_item_data?.supplier_name || '';
      const shortname = variant.shortname || '';
      
      if (productType && country && supplier && shortname) {
        cleanDescription = `${productType} | ${country} | ${supplier} - ${shortname}`;
      } else {
        cleanDescription = shortname || variant.full_description || 'Product';
      }
    }
    
    // CRITICAL: Save product name to formData so it can be saved to recap
    handleInputChange({
      target: {
        name: 'purchase_product_name',
        value: cleanDescription
      }
    });
    
    setProductSearchTerm(cleanDescription);
    
    // Close dropdown
    setShowProductDropdown(false);
  };

  // === Sales Product Search Effects ===
  
  // Debounce sales product search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSalesProductSearch(salesProductSearchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [salesProductSearchTerm]);
  
  // Fetch sales products when debounced search changes
  useEffect(() => {
    if (debouncedSalesProductSearch && debouncedSalesProductSearch.trim().length >= 2) {
      fetchSalesProducts(debouncedSalesProductSearch);
    } else {
      setFilteredSalesProducts([]);
    }
  }, [debouncedSalesProductSearch]);
  
  // Click outside to close sales product dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (salesProductSearchRef.current && !salesProductSearchRef.current.contains(event.target)) {
        setShowSalesProductDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Load sales product details when editing existing delivery
  useEffect(() => {
    if (formData.sales_product_category) {
//      console.log('🔍 SALES: Attempting to load product for ID:', formData.sales_product_category);
      const loadSalesProductDetails = async () => {
        try {
          const variant = await stockService.getVariantById(formData.sales_product_category);
//          console.log('✅ SALES: Loaded product variant:', variant);
          if (variant) {
            // CRITICAL FIX: Reconstruct the exact display format that user sees in dropdown
            // Format: "Steam Coal | Australia | Acme Trading - Steam Coal Richards"
            let cleanDescription = '';
            if (variant.display_name) {
              cleanDescription = variant.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
            } else {
              // Reconstruct from variant properties
              const productType = variant.base_stock_item_data?.product_type_display || '';
              const country = variant.base_stock_item_data?.country_of_origin || '';
              const supplier = variant.base_stock_item_data?.supplier_name || '';
              const shortname = variant.shortname || '';
              
              if (productType && country && supplier && shortname) {
                cleanDescription = `${productType} | ${country} | ${supplier} - ${shortname}`;
              } else {
                cleanDescription = shortname || variant.full_description || 'Product';
              }
            }
//            console.log('✅ SALES: Setting product search term to:', cleanDescription);
            setSalesProductSearchTerm(cleanDescription);
            setSelectedSalesProduct(variant);
          }
        } catch (error) {
          console.error('❌ SALES: Error loading sales product details:', error);
        }
      };
      loadSalesProductDetails();
    } else {
//      console.log('⚠️ SALES: No sales_product_category in formData');
    }
  }, [formData.sales_product_category]);
  
  // API call to fetch sales products
  const fetchSalesProducts = async (searchTerm) => {
    try {
      setSalesProductSearchLoading(true);
      const results = await stockService.searchVariantsForInvoice(searchTerm);
      setFilteredSalesProducts(results);
    } catch (error) {
      console.error('Error searching sales products:', error);
      setFilteredSalesProducts([]);
    } finally {
      setSalesProductSearchLoading(false);
    }
  };
  
  // Sales product selection handler
  const handleSelectSalesProduct = (variant) => {
    console.log('Selected sales product variant:', variant);
    console.log('Variant unit value:', variant.unit, 'Type:', typeof variant.unit);
    
    // Store selected product
    setSelectedSalesProduct(variant);
    
    // Update form data with product ID
    handleInputChange({
      target: {
        name: 'sales_product_category',
        value: variant.id
      }
    });
    
    // AUTO-FILL: Populate quantity, unit, and sale price from product
    if (variant.quantity) {
      handleInputChange({
        target: {
          name: 'sales_product_quantity',
          value: variant.quantity
        }
      });
    }
    
    // Unit field expects ID (integer or string number)
    if (variant.unit_id) {
      console.log('Setting sales unit to:', variant.unit_id);
      handleInputChange({
        target: {
          name: 'sales_product_unit',
          value: variant.unit_id
        }
      });
    }
    
    if (variant.sale_price) {
      handleInputChange({
        target: {
          name: 'sales_price_per_ton_contract',
          value: variant.sale_price
        }
      });
    }
    
    if (variant.price_currency) {
      handleInputChange({
        target: {
          name: 'sale_price_per_ton_contract_currency',
          value: variant.price_currency
        }
      });
    }
    
    // Auto-fill sales tolerance from variant (NEW)
    if (variant.tolerance_lower_value || variant.tolerance_upper_value) {
      console.log('Auto-filling sales tolerance from variant');
      handleInputChange({
        target: {
          name: 'sales_tolerance_lower_value',
          value: variant.tolerance_lower_value || ''
        }
      });
      handleInputChange({
        target: {
          name: 'sales_tolerance_lower_unit',
          value: variant.tolerance_lower_unit || '%'
        }
      });
      handleInputChange({
        target: {
          name: 'sales_tolerance_upper_value',
          value: variant.tolerance_upper_value || ''
        }
      });
      handleInputChange({
        target: {
          name: 'sales_tolerance_upper_unit',
          value: variant.tolerance_upper_unit || '%'
        }
      });
    }
    
    // Update search term to show selected item
    // CRITICAL FIX: Reconstruct the exact display format that user sees in dropdown
    let cleanDescription = '';
    if (variant.display_name) {
      cleanDescription = variant.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
    } else {
      // Reconstruct from variant properties
      const productType = variant.base_stock_item_data?.product_type_display || '';
      const country = variant.base_stock_item_data?.country_of_origin || '';
      const supplier = variant.base_stock_item_data?.supplier_name || '';
      const shortname = variant.shortname || '';
      
      if (productType && country && supplier && shortname) {
        cleanDescription = `${productType} | ${country} | ${supplier} - ${shortname}`;
      } else {
        cleanDescription = shortname || variant.full_description || 'Product';
      }
    }
    
    // CRITICAL: Save product name to formData so it can be saved to recap
    handleInputChange({
      target: {
        name: 'sales_product_name',
        value: cleanDescription
      }
    });
    
    setSalesProductSearchTerm(cleanDescription);
    
    // Close dropdown
    setShowSalesProductDropdown(false);
  };

  // Sprint D — open / close / complete handlers for the inline product creator.
  // The "context" key picks which of the two attach paths runs when the
  // variant is created (purchase ⇒ handleSelectProduct, sales ⇒ handleSelectSalesProduct).
  const openProductCreator = (context, searchTerm) => {
    setProductCreatorState({
      isOpen: true,
      context,
      searchTerm: searchTerm || '',
    });
    if (context === 'purchase') setShowProductDropdown(false);
    if (context === 'sales') setShowSalesProductDropdown(false);
  };

  const closeProductCreator = () => {
    setProductCreatorState({ isOpen: false, context: null, searchTerm: '' });
  };

  // Inline CRM partner creator helpers. ``openCrmCreator`` is called
  // from each DeliveryPartnerSearch's "+ Create CRM partner" CTA.
  // ``contractualKind`` / ``contractualIndex`` are non-null only for the
  // multi-row Seller / Buyer Contractual sections; everything else is a
  // single-field picker that uses the regular handlePartnerChange path.
  //
  // ``inlineCrmCreate(opts)`` is the canonical wrapper used at every
  // DeliveryPartnerSearch callsite. It returns a click handler that
  // opens the CRM creator pre-seeded with ``opts``, OR ``undefined``
  // when the delivery is no longer DRAFT (Option A status gate). When
  // the prop is undefined, the picker hides its "+ Create CRM partner"
  // CTA entirely — historical / ACTIVE deliveries are read-only for
  // CRM purposes; counterparty edits must go through the CRM dashboard
  // approval flow rather than being mutated mid-edit on a finalized
  // delivery.
  const inlineCrmCreate = (opts) => (
    isDraftDelivery
      ? (searchTerm) => openCrmCreator({ ...opts, searchTerm })
      : undefined
  );
  const openCrmCreator = (opts) => {
    const {
      fieldName,
      fieldNameId,
      contractualKind = null,
      contractualIndex = null,
      initialPartnerType = 'Seller (Creditor)',
      title = 'Create CRM Partner',
      searchTerm = '',
      onCreated = null,
    } = opts || {};
    setCrmCreatorState({
      isOpen: true,
      searchTerm: searchTerm || '',
      targetFieldName: fieldName || null,
      targetFieldNameId: fieldNameId || null,
      contractualKind,
      contractualIndex,
      initialPartnerType,
      title,
      onCreated,
    });
  };
  const closeCrmCreator = () => {
    setCrmCreatorState({
      isOpen: false,
      searchTerm: '',
      targetFieldName: null,
      targetFieldNameId: null,
      contractualKind: null,
      contractualIndex: null,
      initialPartnerType: 'Seller (Creditor)',
      title: 'Create CRM Partner',
      onCreated: null,
    });
  };
  const handleInlineCrmPartnerCreated = (profile) => {
    if (!profile) {
      closeCrmCreator();
      return;
    }
    setPendingCreatedPartners((prev) => {
      if (prev.some((p) => p.id === profile.id)) return prev;
      return [...prev, profile];
    });

    const {
      targetFieldName, targetFieldNameId, contractualKind, contractualIndex, onCreated,
    } = crmCreatorState;
    // New stub profiles have no sanction/KYC data yet — the existing
    // pending defaults are correct for both the contractual list and the
    // single-field path.
    const statusData = { sanctionStatus: 'pending', kycStatus: 'pending' };

    if (onCreated) {
      onCreated(profile);
    } else if (contractualKind && contractualIndex !== null && contractualIndex !== undefined) {
      // Reuse the existing multi-row updater so list items + form state
      // stay in sync exactly as if a real picker selection had happened.
      handleContractualPartnerChange(contractualKind, contractualIndex)(
        targetFieldName,
        targetFieldNameId,
        profile.id,
        profile.legal_entity_name,
        statusData,
      );
    } else if (targetFieldName && targetFieldNameId) {
      handlePartnerChange(
        targetFieldName,
        targetFieldNameId,
        profile.id,
        profile.legal_entity_name,
        statusData,
      );
    }
    closeCrmCreator();
  };

  const handleInlineProductCreated = (variant) => {
    if (!variant) {
      closeProductCreator();
      return;
    }
    const ctx = productCreatorState.context;
    if (ctx === 'sales') {
      handleSelectSalesProduct(variant);
    } else {
      handleSelectProduct(variant);
    }
    closeProductCreator();
  };

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState({
    deliveryInfo: false,
    productInfo: false,
    portFreight: false,
    blInfo: false,
    banks: false,
    contractsLegal: false,
    productQualities: false
  });

  // Add validation states for price fields
  const [isPriceValid, setIsPriceValid] = useState({
    purchase_price_per_ton_contract: true,
    purchase_price_per_ton_adjusted: true,
    sales_price_per_ton_contract: true,
    sales_price_per_ton_adjusted: true,
    total_purchase_price_per_ton_adjusted: true,
    total_sale_price_per_ton_adjusted: true,
    freight_per_ton: true
  });

  // Add validation states for quantity and tolerance fields
  const [isQuantityValid, setIsQuantityValid] = useState({
    delivery_product_quantity: true,
    sales_product_quantity: true,
    total_purchase_product_quantity: true,
    total_sales_product_quantity: true,
    purchase_price_basis_value: true,
    sales_price_basis_value: true,
    bill_of_lading_quantity: true
  });
  const [isToleranceValid, setIsToleranceValid] = useState(true);
  
  // Add validation states for commission percentage fields
  const [isPercentageValid, setIsPercentageValid] = useState({
    address_commission: true,
    mercurius_commission: true,
    brokerage_commission: true,
    delivery_product_tolerance: true,
    sales_tolerance: true,
    total_purchase_tolerance: true,
    total_sales_tolerance: true
  });

  // Country options are now provided by the API via dropdownOptions.countries
  // const countryOptions = [
  //   { value: 'Australia', label: 'Australia' },
  //   { value: 'Brazil', label: 'Brazil' },
  //   { value: 'Canada', label: 'Canada' },
  //   { value: 'China', label: 'China' },
  //   { value: 'Colombia', label: 'Colombia' },
  //   { value: 'India', label: 'India' },
  //   { value: 'Indonesia', label: 'Indonesia' },
  //   { value: 'Kazakhstan', label: 'Kazakhstan' },
  //   { value: 'Mongolia', label: 'Mongolia' },
  //   { value: 'Mozambique', label: 'Mozambique' },
  //   { value: 'Poland', label: 'Poland' },
  //   { value: 'Russia', label: 'Russia' },
  //   { value: 'South Africa', label: 'South Africa' },
  //   { value: 'Ukraine', label: 'Ukraine' },
  //   { value: 'United States', label: 'United States' },
  //   { value: 'Vietnam', label: 'Vietnam' }
  // ];

  // Add producer options - would be populated from backend in a real scenario
  const producerOptions = [
    { value: 'Producer A', label: 'Producer A' },
    { value: 'Producer B', label: 'Producer B' },
    { value: 'Producer C', label: 'Producer C' },
    { value: 'Producer D', label: 'Producer D' },
    { value: 'Producer E', label: 'Producer E' }
  ];

  // Add end user options - would be populated from backend in a real scenario
  const endUserOptions = [
    { value: 'End User A', label: 'End User A' },
    { value: 'End User B', label: 'End User B' },
    { value: 'End User C', label: 'End User C' },
    { value: 'End User D', label: 'End User D' },
    { value: 'End User E', label: 'End User E' }
  ];

  // Add chain in between options - would be populated from backend in a real scenario
  const chainOptions = [
    { value: 'Chain Company A', label: 'Chain Company A' },
    { value: 'Chain Company B', label: 'Chain Company B' },
    { value: 'Chain Company C', label: 'Chain Company C' },
    { value: 'Chain Company D', label: 'Chain Company D' },
    { value: 'Chain Company E', label: 'Chain Company E' }
  ];

  // Add seller and buyer contractual options - would be populated from backend in a real scenario
  const contractualOptions = [
    { value: 'Contractual Company A', label: 'Contractual Company A' },
    { value: 'Contractual Company B', label: 'Contractual Company B' },
    { value: 'Contractual Company C', label: 'Contractual Company C' },
    { value: 'Contractual Company D', label: 'Contractual Company D' },
    { value: 'Contractual Company E', label: 'Contractual Company E' }
  ];

  // Add bank options after the contractualOptions array, around line 109
  const bankOptions = [
    { value: 'Bank A', label: 'Bank A' },
    { value: 'Bank B', label: 'Bank B' },
    { value: 'Bank C', label: 'Bank C' },
    { value: 'Bank D', label: 'Bank D' },
    { value: 'Bank E', label: 'Bank E' }
  ];

  // Add CRM partner options after the bankOptions array
  const crmPartnerOptions = [
    { value: 'CRM Partner A', label: 'CRM Partner A' },
    { value: 'CRM Partner B', label: 'CRM Partner B' },
    { value: 'CRM Partner C', label: 'CRM Partner C' },
    { value: 'CRM Partner D', label: 'CRM Partner D' },
    { value: 'CRM Partner E', label: 'CRM Partner E' }
  ];

  // Toggle collapsed state of sections
  const toggleSection = (section) => {
    setCollapsedSections({
      ...collapsedSections,
      [section]: !collapsedSections[section]
    });
  };

  // Validation function for price inputs
  const validatePrice = (value) => {
    if (!value) return true;
    // Remove commas for validation
    const cleanValue = value.replace(/,/g, '');
    // Allow decimal numbers with up to PRICE_DECIMALS decimal places
    return isTypeableNumber(cleanValue, PRICE_DECIMALS);
  };

  // Tidy a typed price when its box loses focus: grouping commas go (the
  // server refuses them) and the figure is kept exactly as typed. This used
  // to round to 2 decimals, silently rewriting a longer price.
  const formatPriceValue = (value) => {
    if (!value) return '';
    return canonicalizeAmount(value);
  };

  // Format numeric value to always show 2 decimal places
  const formatNumericValue = (value) => {
    if (!value) return '';
    // Remove commas for parsing
    const cleanValue = value.replace(/,/g, '');
    // Parse as float and format with 2 decimal places
    const numericValue = parseFloat(cleanValue);
    if (isNaN(numericValue)) return '';
    return numericValue.toFixed(2);
  };

  // Validation function for quantity inputs
  const validateQuantity = (value) => {
    // Allow any text input
    return true;
  };

  // Parse tolerance string into separate lower and upper components
  const parseToleranceString = (toleranceString) => {
    if (!toleranceString) {
      return {
        lowerValue: '',
        lowerUnit: '%',
        upperValue: '',
        upperUnit: '%'
      };
    }

    // Check if tolerance has a range format like "5% to 10%" or "5min to 10max"
    if (toleranceString.includes('to')) {
      const [lower, upper] = toleranceString.split('to').map(part => part.trim());
      
      // Extract value and unit for lower
      const lowerMatch = lower.match(/^(\d*\.?\d*)(.*)$/);
      const lowerValue = lowerMatch ? lowerMatch[1] : '';
      const lowerUnit = (lowerMatch && lowerMatch[2]) ? lowerMatch[2] : '%';
      
      // Extract value and unit for upper
      const upperMatch = upper.match(/^(\d*\.?\d*)(.*)$/);
      const upperValue = upperMatch ? upperMatch[1] : '';
      const upperUnit = (upperMatch && upperMatch[2]) ? upperMatch[2] : '%';
      
      return {
        lowerValue,
        lowerUnit,
        upperValue,
        upperUnit
      };
    } else {
      // Single value - check if it's upper (max) or lower (min/%)
      const match = toleranceString.match(/^(\d*\.?\d*)(.*)$/);
      if (match) {
        const value = match[1];
        const unit = match[2] || '%';
        
        // CRITICAL FIX: If unit is 'max', it's an UPPER tolerance, not lower
        if (unit === 'max') {
          return {
            lowerValue: '',
            lowerUnit: '%',
            upperValue: value,
            upperUnit: 'max'
          };
        }
        
        // Otherwise it's a lower tolerance (min or %)
        return {
          lowerValue: value,
          lowerUnit: unit,
          upperValue: '',
          upperUnit: '%'
        };
      }
    }

    return {
      lowerValue: '',
      lowerUnit: '%',
      upperValue: '',
      upperUnit: '%'
    };
  };

  // Validation function for percentage inputs
  const validatePercentage = (value) => {
    if (!value) return true;
    // Remove % sign and spaces for validation
    const cleanValue = value.replace(/[%\s]/g, '');
    // Allow decimal numbers with up to 2 decimal places
    return /^\d*\.?\d{0,2}$/.test(cleanValue);
  };

  // Custom handler for price field changes with validation
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    
    if (validatePrice(value)) {
      setIsPriceValid(prev => ({
        ...prev,
        [name]: true
      }));
      // handleInputChange is now the wrapped version that auto-syncs to pending recaps
      handleInputChange(e);
    } else {
      setIsPriceValid(prev => ({
        ...prev,
        [name]: false
      }));
      showError(`Please enter a valid number for the price field (at most ${PRICE_DECIMALS} decimals)`, DEFAULT_DURATION);
    }
  };

  // Handler for when price field loses focus - drop grouping commas, keep the figure
  const handlePriceBlur = (e) => {
    const { name, value } = e.target;
    
    if (value && isPriceValid[name]) {
      const formattedValue = formatPriceValue(value);
      // Create a synthetic event object to pass to handleInputChange
      const syntheticEvent = {
        target: {
          name: name,
          value: formattedValue
        }
      };
      handleInputChange(syntheticEvent);
    }
  };

  // Custom handler for quantity field changes with validation
  const handleQuantityChange = (e) => {
    const { name, value } = e.target;
    
    // Any text is still accepted; only a decimal beyond QUANTITY_DECIMALS is
    // refused, so the save cannot come back refused for it.
    if (exceedsDecimals(value, QUANTITY_DECIMALS)) {
      showError(`A quantity carries at most ${QUANTITY_DECIMALS} decimals`, DEFAULT_DURATION);
      return;
    }
    // All text input is valid now
    setIsQuantityValid(prev => ({
      ...prev,
      [name]: true
    }));
    handleInputChange(e);
  };

  // Handler for when quantity field loses focus - no formatting needed
  const handleQuantityBlur = (e) => {
    // No validation or formatting needed
  };

  // === UNIT/CURRENCY CONSISTENCY VALIDATION FOR RECAPS ===
  // Helper to get unit name from ID
  const getUnitName = (unitId) => {
    if (!unitId) return '';
    const unit = units.find(u => String(u.id) === String(unitId));
    return unit ? unit.name : unitId;
  };
  
  // Helper to get currency label from code
  const getCurrencyLabel = (currencyCode) => {
    if (!currencyCode) return '';
    const currency = dropdownOptions.currencies?.find(c => c.value === currencyCode);
    return currency ? (currency.label || currency.value) : currencyCode;
  };

  // Handle purchase unit change - validation happens in validateRecapConsistency
  const handlePurchaseUnitChange = (e) => {
    const { value } = e.target;
    console.log('📊 PURCHASE UNIT CHANGE:', {
      selectedUnitId: value,
      activePendingRecapId: activeBlankPurchaseId,
      isBlankRecap: activeBlankPurchaseId && typeof activeBlankPurchaseId === 'string' && activeBlankPurchaseId.startsWith('temp-'),
      deliveryId,
      pendingRecapsCount: blankPurchaseRecaps.length,
      savedRecapsCount: formData.purchase_recaps_data?.length || 0
    });
    handleInputChange(e);
  };

  // Handle purchase currency change - validation happens in validateRecapConsistency
  const handlePurchaseCurrencyChange = (e) => {
    handleInputChange(e);
  };

  // Handle sales unit change - validation happens in validateRecapConsistency
  const handleSalesUnitChange = (e) => {
    const { value } = e.target;
    console.log('📊 SALES UNIT CHANGE:', {
      selectedUnitId: value,
      activeBlankSalesId,
      isBlankRecap: activeBlankSalesId && typeof activeBlankSalesId === 'string' && activeBlankSalesId.startsWith('temp-'),
      deliveryId,
      pendingRecapsCount: blankSalesRecaps.length,
      savedRecapsCount: formData.sales_recaps_data?.length || 0
    });
    handleInputChange(e);
  };

  // Handle sales currency change - validation happens in validateRecapConsistency
  const handleSalesCurrencyChange = (e) => {
    handleInputChange(e);
  };

  // Handler for percentage commission field changes with validation
  const handlePercentageChange = (e) => {
    const { name, value } = e.target;
    
    // Remove any % signs for validation
    const valueForValidation = value.replace(/[%\s]/g, '');
    
    if (validatePercentage(valueForValidation)) {
      setIsPercentageValid(prev => ({
        ...prev,
        [name]: true
      }));
      
      // Allow the input without the % symbol
      const syntheticEvent = {
        target: {
          name: name,
          value: valueForValidation
        }
      };
      handleInputChange(syntheticEvent);
    } else {
      setIsPercentageValid(prev => ({
        ...prev,
        [name]: false
      }));
      showError("Please enter a valid percentage value", DEFAULT_DURATION);
    }
  };
  
  // Handler for when percentage field loses focus - format with % symbol
  const handlePercentageBlur = (e) => {
    const { name, value } = e.target;
    
    if (value && isPercentageValid[name]) {
      // Format to up to 2 decimal places and add % symbol
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return;
      
      const formattedValue = `${numericValue.toFixed(2)}%`;
      
      // Create a synthetic event object to pass to handleInputChange
      const syntheticEvent = {
        target: {
          name: name,
          value: formattedValue
        }
      };
      handleInputChange(syntheticEvent);
    }
  };

  // Custom handler for tolerance field changes with validation
  const handleToleranceChange = (e) => {
    const { name, value } = e.target;
    
    // Remove any % signs for validation
    const valueForValidation = value.replace(/[%\s]/g, '');
    
    if (validatePercentage(valueForValidation)) {
      setIsPercentageValid(prev => ({
        ...prev,
        [name]: true
      }));
      setIsToleranceValid(true);
      
      // Allow the input without the % symbol
      const syntheticEvent = {
        target: {
          name: name,
          value: valueForValidation
        }
      };
      // handleInputChange is now the wrapped version that auto-syncs to pending recaps
      handleInputChange(syntheticEvent);
    } else {
      setIsPercentageValid(prev => ({
        ...prev,
        [name]: false
      }));
      setIsToleranceValid(false);
      showError("Please enter a valid percentage value for tolerance", DEFAULT_DURATION);
    }
  };

  // Handler for when tolerance field loses focus - format with % symbol
  const handleToleranceBlur = (e) => {
    const { name, value } = e.target;
    
    if (value && isPercentageValid[name] && isToleranceValid) {
      // Format to up to 2 decimal places and add % symbol
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return;
      
      const formattedValue = `${numericValue.toFixed(2)}%`;
      
      // Create a synthetic event object to pass to handleInputChange
      const syntheticEvent = {
        target: {
          name: name,
          value: formattedValue
        }
      };
      handleInputChange(syntheticEvent);
    }
  };

  // Validation function for number inputs: up to `decimals` decimal places
  const validateNumber = (value, decimals) => isTypeableNumber(value, decimals);

  // Custom handler for number field changes with validation. `decimals` is
  // what the field keeps: QUANTITY_DECIMALS for the quantities,
  // PRICE_BASIS_DECIMALS for the quality price basis.
  const handleNumberChange = (decimals) => (e) => {
    const { name, value } = e.target;
    
    if (validateNumber(value, decimals)) {
      setIsQuantityValid(prev => ({
        ...prev,
        [name]: true
      }));
      // handleInputChange is now the wrapped version that auto-syncs to pending recaps
      handleInputChange(e);
    } else {
      setIsQuantityValid(prev => ({
        ...prev,
        [name]: false
      }));
      showError(`Please enter a valid number (at most ${decimals} decimals)`, DEFAULT_DURATION);
    }
  };

  // Handler for when the price basis loses focus - format with 2 decimal
  // places. Quantities do not use it: a typed quantity is never rounded or
  // padded when the trader clicks away.
  const handleNumberBlur = (e) => {
    const { name, value } = e.target;
    
    if (value && isQuantityValid[name]) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return;
      
      const formattedValue = numericValue.toFixed(PRICE_BASIS_DECIMALS);
      
      const syntheticEvent = {
        target: {
          name: name,
          value: formattedValue
        }
      };
      handleInputChange(syntheticEvent);
    }
  };

  // Handler for blocking non-numeric input in quality table fields
  // Allows: digits (0-9), decimal point (.), minus sign (-) for negative values
  const handleNumericKeyPress = (e) => {
    // Allow control keys like backspace, delete, arrow keys
    if (e.key.length > 1) return;
    
    // Allow digits, decimal point, and minus sign
    if (!/[0-9.\-]/.test(e.key)) {
      e.preventDefault();
      showError("Only numeric values (0-9, ., -) are allowed in quality fields", DEFAULT_DURATION);
    }
  };

  // Helper rendering functions
  const renderInputField = (name, label, type = "text", required = false, placeholder = "") => {
    // Special handling for vessel name field
    if (name === 'vessel_name') {
      return (
        <div className="form-group">
          <label htmlFor={name}>{label}</label>
          <div className="input-wrapper">
            <input
              type={type}
              id={name}
              name={name}
              value={formData[name] || ''}
              onChange={handleInputChange}
            />

          </div>
        </div>
      );
    }

    // Special handling for delivery number field
    if (name === 'delivery_unique_number') {
      return (
        <div className="form-group">
          <label htmlFor={name}>{label}</label>
          <input
            type="text"
            id={name}
            name={name}
            value={formData[name] || ''}
            readOnly
            placeholder="Auto Generated"
            className="read-only-field"
            style={{ backgroundColor: '#e6f7ff', cursor: 'not-allowed' }}
          />
        </div>
      );
    }
    
    
    // Special handling for vessel position field
    if (name === 'vessel_position') {
      return (
        <div className="form-group">
          <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
          <input
            type={type}
            id={name}
            name={name}
            value={formData[name] || ''}
            onChange={handleInputChange}
            required={required}
            placeholder="Vessel Position"
          />
        </div>
      );
    }
    
    // Special handling for price basis purchase quality field
    if (name === 'price_basis_purchase_quality') {
      return (
        <div className="form-group">
          <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
          <input
            type={type}
            id={name}
            name={name}
            value={formData[name] || ''}
            onChange={handleInputChange}
            required={required}
            placeholder="Quality"
          />
        </div>
      );
    }
    
    // Special handling for price basis sale quality field
    if (name === 'price_basis_sale_quality') {
      return (
        <div className="form-group">
          <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
          <input
            type={type}
            id={name}
            name={name}
            value={formData[name] || ''}
            onChange={handleInputChange}
            required={required}
            placeholder="Quality"
          />
        </div>
      );
    }

    return (
      <div className="form-group">
        <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          required={required}
          placeholder={placeholder}
        />
      </div>
    );
  };

  const renderSelectField = (name, label, options = [], required = false) => {
    // Normalize IDs for comparison
    const normalizeId = (id) => {
      if (!id) return '';
      const strId = typeof id === 'string' ? id : String(id);
      // Remove any dash prefix that might exist
      return strId.trim().replace(/^[\u2013\u2014\-]\s*/, '');
    };
    
    // Determine if this is an operator field
    const isOperatorField = name === 'delivery_main_operator' || 
                           name === 'delivery_backup_operator_1' || 
                           name === 'delivery_backup_operator_2';
    
    // Use the appropriate handler
    const changeHandler = isOperatorField ? handleOperatorChange : handleInputChange;
    
    return (
      <div className="form-group">
        <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
        <select
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={changeHandler}
          required={required}
        >
          <option value="">Select {label}</option>
          {name === 'hms_entity' && formData.hms_entity &&
            !(options || []).some(o => o.value === formData.hms_entity) && (
              <option value={formData.hms_entity}>
                {formData.hms_entity_name || formData.hms_entity}
              </option>
          )}
          {options && options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderDecimalField = (name, label, required = false) => (
    <div className="form-group">
      <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
      <input
        type="text"
        id={name}
        name={name}
        value={formData[name] || ''}
        onChange={handleInputChange}
        required={required}
      />
    </div>
  );

  // Render percentage field with validation and auto-formatting
  const renderPercentageField = (name, label, required = false) => (
    <div className="form-group">
      <h3 className="delivery-subsection-title">{label} {required && <span className="required-field">*</span>}</h3>
      <input
        type="text"
        id={name}
        name={name}
        value={formData[name] || ''}
        onChange={handlePercentageChange}
        onBlur={handlePercentageBlur}
        className={!isPercentageValid[name] ? 'error' : ''}
        placeholder="0.00%"
        required={required}
        inputMode="decimal"
      />
    </div>
  );

  // Render quantity field with unit dropdown (matching Sales Quantity structure)
  const renderQuantityField = () => (
    <div className="form-group">
      <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
        <input
          type="text"
          id="delivery_product_quantity"
          name="delivery_product_quantity"
          value={formData.delivery_product_quantity || ''}
          onChange={handleNumberChange(QUANTITY_DECIMALS)}
          className={!isQuantityValid.delivery_product_quantity ? 'error' : ''}
          placeholder="0.00"
          style={{ flex: 1 }}
          inputMode="decimal"
        />
        <select
          name="delivery_product_unit"
          value={formData.delivery_product_unit || ''}
          onChange={handlePurchaseUnitChange}
          style={{ width: '140px' }}
        >
          <option value="">Unit</option>
          {units.map(unit => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  // Render B/L quantity field with validation and metric tons subtext
  // BL Unit and Quantity are auto-populated from first sales recap and Total Sales Quantity
  const renderBLQuantityField = () => {
    // hasSalesRecaps (defined above the B/L effect) decides whether these
    // fields are disabled, the same flag that decides whether they are filled.
    // With Bills of Lading rows the quantity is their sum and read-only either way.
    const blQuantityFromRows = billOfLadingRowsTotal !== null;
    const isFuels = formData.commodity_type === 'fuels';

    // Option A (display-only, no persistence): when a fuels delivery's linked
    // sales recap(s) carry more than one product, break the BDN quantity out
    // per product — each row pulls that product's recap quantity + unit,
    // read-only. Single-product fuels and solids keep the existing aggregate
    // row bound to bill_of_lading_quantity. Same data source the
    // LinkedProductsBDNTable consumes (sales_recaps_data[].recap_products).
    const perProductRows = isFuels
      ? (formData.sales_recaps_data || [])
          .filter(r => r.commodity_type === 'fuels' && Array.isArray(r.recap_products))
          .flatMap(r => r.recap_products.map(p => ({ ...p, _recapId: r.id })))
      : [];
    const showPerProduct = perProductRows.length > 1;
    const resolveUnitName = (unitId) => {
      const match = units.find(u => String(u.id) === String(unitId));
      return match ? match.name : '';
    };
    const readonlyFieldStyle = { backgroundColor: '#f5f5f5', cursor: 'not-allowed' };

    return (
      <div className="form-group">
        <h3 className="delivery-subsection-title">
          {isFuels ? 'BDN quantity' : 'B/L quantity'}
          {(hasSalesRecaps || blQuantityFromRows) && (
            <span style={{
              fontSize: '0.75rem',
              color: '#666',
              fontWeight: 'normal',
              marginLeft: '8px',
              fontStyle: 'italic'
            }}>
              {blQuantityFromRows ? '(Sum of the Bills of Lading below)' : '(Auto-populated from Sales Recap)'}
            </span>
          )}
        </h3>
        {showPerProduct ? (
          <>
            {perProductRows.map((p, idx) => {
              const unitName = resolveUnitName(p.measuring_unit) || resolveUnitName(formData.bill_of_lading_quantity_unit);
              const productLabel = p.product_label || `Product ${idx + 1}`;
              return (
                <div className="delivery-bl-quantity-row" key={`bdn-qty-${p._recapId}-${p.id}`} style={{ marginBottom: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value={unitName || 'Unit'}
                    className="delivery-bl-unit-select"
                    style={readonlyFieldStyle}
                    title="Auto-populated from product unit"
                  />
                  <input
                    type="text"
                    readOnly
                    value={p.sale_quantity != null ? p.sale_quantity : ''}
                    className="delivery-bl-quantity-input"
                    style={readonlyFieldStyle}
                    title={`${productLabel} — auto-populated from Sales Recap`}
                  />
                  <span
                    className="delivery-bl-status-select"
                    // display:block (NOT flex): text-overflow ellipsis never
                    // renders inside a flex container, so long product names
                    // hard-clipped with no visual cue. flex 2 gives the name
                    // real room; the full text stays on the title tooltip.
                    style={{ display: 'block', alignSelf: 'center', flex: '2 1 0%', minWidth: 0, background: 'transparent', border: 'none', cursor: 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, color: '#444' }}
                    title={productLabel}
                  >
                    {productLabel}
                  </span>
                </div>
              );
            })}
            {/* Status gets the standard field treatment (label + full-width
                select), same as Mode of delivery — one status applies to all
                BDN quantities above. */}
            <div className="form-group" style={{ marginTop: '12px' }}>
              <h3 className="delivery-subsection-title">Status</h3>
              <select
                id="bill_of_lading_quantity_estimated_or_final"
                name="bill_of_lading_quantity_estimated_or_final"
                value={formData.bill_of_lading_quantity_estimated_or_final || ''}
                onChange={handleInputChange}
                title="Estimated or Final — applies to all BDN quantities above"
              >
                <option value="">Select</option>
                <option value="Estimated">Estimated</option>
                <option value="Final">Final</option>
              </select>
            </div>
          </>
        ) : (
          <div className="delivery-bl-quantity-row">
            <select
              id="bill_of_lading_quantity_unit"
              name="bill_of_lading_quantity_unit"
              value={formData.bill_of_lading_quantity_unit || ''}
              onChange={handleInputChange}
              className="delivery-bl-unit-select"
              disabled={hasSalesRecaps}
              style={hasSalesRecaps ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              title={hasSalesRecaps ? 'Auto-populated from first Sales Recap unit' : ''}
            >
              <option value="">Unit</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              id="bill_of_lading_quantity"
              name="bill_of_lading_quantity"
              value={blQuantityFromRows ? billOfLadingRowsTotal : (formData.bill_of_lading_quantity || '')}
              onChange={handleQuantityChange}
              onBlur={handleQuantityBlur}
              className={`delivery-bl-quantity-input ${!isQuantityValid.bill_of_lading_quantity ? 'error' : ''}`}
              placeholder="Amount"
              inputMode="text"
              disabled={hasSalesRecaps || blQuantityFromRows}
              style={(hasSalesRecaps || blQuantityFromRows) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              title={blQuantityFromRows
                ? 'Sum of the Bills of Lading below -- change the rows to change it'
                : (hasSalesRecaps ? 'Auto-populated from Total Sales Quantity in WHAT tab' : '')}
            />
            <select
              id="bill_of_lading_quantity_estimated_or_final"
              name="bill_of_lading_quantity_estimated_or_final"
              value={formData.bill_of_lading_quantity_estimated_or_final || ''}
              onChange={handleInputChange}
              className="delivery-bl-status-select"
            >
              <option value="">Status</option>
              <option value="Estimated">Estimated</option>
              <option value="Final">Final</option>
            </select>
          </div>
        )}
      </div>
    );
  };

  // Sprint 4A: Fuels-only FINAL Tonnage row (BDN-confirmed). Captured
  // manually by the trader at delivery handover. Solids deliveries leave
  // this null and continue to use bill_of_lading_quantity as the
  // final-quantity field. Reuses the existing .form-group +
  // .delivery-subsection-title chrome so no new CSS is required.
  //
  // Sprint 6 multi-product: when a linked sales recap is multi-product
  // (has ``recap_products`` non-empty), the trader gets an additional
  // per-product BDN table below so each product line can capture its
  // own final tonnage + BDN document id. Saved via the new
  // ``set-sales-product-allocations`` endpoint.
  const renderFinalTonnageField = () => {
    if (formData.commodity_type !== 'fuels') {
      return null;
    }

    // Option B: when per-product BDN capture exists (Linked products has at
    // least one sales line), this delivery-level field becomes a READ-ONLY
    // total = Σ per-product Final Tonnages. The per-product rows are the
    // single source of truth (and what invoicing bills on); this just totals
    // them so it pairs with the planned "BDN quantity" above. Only when there
    // are no linked sales products do we fall back to a free editable field.
    const hasLinkedProducts = linkedBdnSummary && linkedBdnSummary.count > 0;
    if (hasLinkedProducts) {
      const total = linkedBdnSummary.total;
      const display = total != null
        ? total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: QUANTITY_DECIMALS })
        : '—';
      return (
        <div className="form-group">
          <h3 className="delivery-subsection-title">FINAL Tonnage (BDN, MT)</h3>
          <input
            type="text"
            readOnly
            value={display}
            className="delivery-bl-quantity-input linked-products-total-input"
            title="Sum of the per-product BDN tonnages captured under Linked products"
          />
          <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
            Total of the per-product BDN tonnages captured under Linked products below.
          </small>
        </div>
      );
    }

    return (
      <div className="form-group">
        <h3 className="delivery-subsection-title">FINAL Tonnage (BDN, MT)</h3>
        <AmountInput
          id="final_tonnage_mt"
          name="final_tonnage_mt"
          value={formData.final_tonnage_mt || ''}
          onChange={(v) => handleInputChange({ target: { name: 'final_tonnage_mt', value: v } })}
          maxDecimals={QUANTITY_DECIMALS}
          className="delivery-bl-quantity-input"
          placeholder="e.g. 224.503125"
        />
        <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
          BDN-confirmed final delivered tonnage. Captured at handover.
        </small>
      </div>
    );
  };

  // HMS brief: "show all linked products here". Full-width list of every
  // product linked to this delivery via its sales recaps, rendered below
  // the two-column grid so it can grow across the whole width. Replaces
  // the stale Funding/Interest/ROCE financial panel that used to render
  // here with empty dashes.
  const renderLinkedProducts = () => {
    if (formData.commodity_type !== 'fuels') {
      // Cargo counterpart (Jonas feedback 19.08): multiple Bills of Lading
      // per delivery. Existing deliveries only — the rows need a delivery id
      // to save against; a new delivery adds them after its first save.
      if (!isExistingDelivery) return null;
      return (
        <BillsOfLadingTable
          deliveryId={deliveryId}
          bills={formData.bills_of_lading || []}
          units={units}
          onSavedFlash={(msg, ok) => (ok ? showSuccess(msg) : showError(msg))}
          onSumSaved={(_sum, rows) => {
            // What the table just saved is what the delivery now has. The
            // single B/L quantity field becomes those rows' sum (see
            // billOfLadingRowsTotal) so every existing reader (serializer,
            // shipment overview widget, delivery screen) stays correct.
            setBillsOfLadingJustSaved(rows || []);
            // Every row removed: the figure was their sum and goes with them.
            if (billOfLadingRowsSum(rows) === null) {
              handleInputChange({ target: { name: 'bill_of_lading_quantity', value: '' } });
            }
            if (markAsChanged) markAsChanged();
          }}
        />
      );
    }
    return (
      <LinkedProductsBDNTable
        deliveryId={formData.id}
        salesRecapsData={formData.sales_recaps_data || []}
        purchaseRecapsData={formData.purchase_recaps_data || []}
        onSavedFlash={(msg, ok) => (ok ? showSuccess(msg) : showError(msg))}
        onTotalsChange={handleLinkedBdnTotals}
      />
    );
  };

  // Render tolerance field with validation (kept for backward compatibility)
  const renderToleranceField = () => (
    <div className="form-group">
      <input
        type="text"
        id="delivery_product_tolerance"
        name="delivery_product_tolerance"
        value={formData.delivery_product_tolerance || ''}
        onChange={handleToleranceChange}
        onBlur={handleToleranceBlur}
        className={!isPercentageValid.delivery_product_tolerance ? 'error' : ''}
        placeholder="0.00%"
        inputMode="decimal"
      />
    </div>
  );

  // Render purchase tolerance field with Upper/Lower inputs and unit dropdowns
  const renderPurchaseToleranceField = () => (
    <div className="form-group">
      <div className="tolerance-inputs-container">
        <div className="tolerance-input-group">
          <div className="tolerance-input-with-dropdown">
            <AmountInput
              id="delivery_product_tolerance_lower_value"
              name="delivery_product_tolerance_lower_value"
              maxDecimals={2}
              onChange={(v) => {
                console.log('✅ DELIVERY PURCHASE TOLERANCE LOWER:', { value: v });
                handleInputChange({ target: { name: 'delivery_product_tolerance_lower_value', value: v } });
              }}
              onBlur={() => {
                const value = formData.delivery_product_tolerance_lower_value;
                const unit = formData.delivery_product_tolerance_lower_unit || '%';
                const baseQty = recapFigureToNumber(formData.delivery_product_quantity) || 0;
                if (unit === 'min' && value && baseQty && parseFloat(value) > baseQty) {
                  showError(`Lower min (${value}) cannot exceed base quantity (${baseQty})`, DEFAULT_DURATION);
                }
              }}
              value={formData.delivery_product_tolerance_lower_value || ''}
              placeholder="0"
            />
            <select
              name="delivery_product_tolerance_lower_unit"
              value={formData.delivery_product_tolerance_lower_unit || '%'}
              onChange={(e) => {
                console.log('🔄 DELIVERY PURCHASE TOLERANCE LOWER UNIT:', e.target.value);
                handleInputChange(e);
              }}
              className="tolerance-unit-dropdown"
            >
              <option value="%">%</option>
              <option value="min">min</option>
            </select>
          </div>
          <small className="field-description">Lower</small>
        </div>
        
        <div className="tolerance-input-group">
          <div className="tolerance-input-with-dropdown">
            <AmountInput
              id="delivery_product_tolerance_upper_value"
              name="delivery_product_tolerance_upper_value"
              maxDecimals={2}
              onChange={(v) => {
                console.log('✅ DELIVERY PURCHASE TOLERANCE UPPER:', { value: v });
                handleInputChange({ target: { name: 'delivery_product_tolerance_upper_value', value: v } });
              }}
              onBlur={() => {
                const value = formData.delivery_product_tolerance_upper_value;
                const unit = formData.delivery_product_tolerance_upper_unit || '%';
                const baseQty = recapFigureToNumber(formData.delivery_product_quantity) || 0;
                if (unit === 'max' && value && baseQty && parseFloat(value) < baseQty) {
                  showError(`Upper max (${value}) cannot be less than base quantity (${baseQty})`, DEFAULT_DURATION);
                }
              }}
              value={formData.delivery_product_tolerance_upper_value || ''}
              placeholder="0"
            />
            <select
              name="delivery_product_tolerance_upper_unit"
              value={formData.delivery_product_tolerance_upper_unit || '%'}
              onChange={(e) => {
                console.log('🔄 DELIVERY PURCHASE TOLERANCE UPPER UNIT:', e.target.value);
                handleInputChange(e);
              }}
              className="tolerance-unit-dropdown"
            >
              <option value="%">%</option>
              <option value="max">max</option>
            </select>
          </div>
          <small className="field-description">Upper</small>
        </div>
      </div>
    </div>
  );

  // Render sales tolerance field with Upper/Lower inputs and unit dropdowns
  const renderSalesToleranceField = () => (
    <div className="form-group">
      <div className="tolerance-inputs-container">
        <div className="tolerance-input-group">
          <div className="tolerance-input-with-dropdown">
            <AmountInput
              id="sales_tolerance_lower_value"
              name="sales_tolerance_lower_value"
              maxDecimals={2}
              onChange={(v) => {
                console.log('✅ DELIVERY SALES TOLERANCE LOWER:', { value: v });
                handleInputChange({ target: { name: 'sales_tolerance_lower_value', value: v } });
              }}
              onBlur={() => {
                const value = formData.sales_tolerance_lower_value;
                const unit = formData.sales_tolerance_lower_unit || '%';
                const baseQty = recapFigureToNumber(formData.sales_product_quantity) || 0;
                if (unit === 'min' && value && baseQty && parseFloat(value) > baseQty) {
                  showError(`Lower min (${value}) cannot exceed base quantity (${baseQty})`, DEFAULT_DURATION);
                }
              }}
              value={formData.sales_tolerance_lower_value || ''}
              placeholder="0"
            />
            <select
              name="sales_tolerance_lower_unit"
              value={formData.sales_tolerance_lower_unit || '%'}
              onChange={(e) => {
                console.log('🔄 DELIVERY SALES TOLERANCE LOWER UNIT:', e.target.value);
                handleInputChange(e);
              }}
              className="tolerance-unit-dropdown"
            >
              <option value="%">%</option>
              <option value="min">min</option>
            </select>
          </div>
          <small className="field-description">Lower</small>
        </div>
        
        <div className="tolerance-input-group">
          <div className="tolerance-input-with-dropdown">
            <AmountInput
              id="sales_tolerance_upper_value"
              name="sales_tolerance_upper_value"
              maxDecimals={2}
              onChange={(v) => {
                console.log('✅ DELIVERY SALES TOLERANCE UPPER:', { value: v });
                handleInputChange({ target: { name: 'sales_tolerance_upper_value', value: v } });
              }}
              onBlur={() => {
                const value = formData.sales_tolerance_upper_value;
                const unit = formData.sales_tolerance_upper_unit || '%';
                const baseQty = recapFigureToNumber(formData.sales_product_quantity) || 0;
                if (unit === 'max' && value && baseQty && parseFloat(value) < baseQty) {
                  showError(`Upper max (${value}) cannot be less than base quantity (${baseQty})`, DEFAULT_DURATION);
                }
              }}
              value={formData.sales_tolerance_upper_value || ''}
              placeholder="0"
            />
            <select
              name="sales_tolerance_upper_unit"
              value={formData.sales_tolerance_upper_unit || '%'}
              onChange={(e) => {
                console.log('🔄 DELIVERY SALES TOLERANCE UPPER UNIT:', e.target.value);
                handleInputChange(e);
              }}
              className="tolerance-unit-dropdown"
            >
              <option value="%">%</option>
              <option value="max">max</option>
            </select>
          </div>
          <small className="field-description">Upper</small>
        </div>
      </div>
    </div>
  );

  const renderCurrencyField = (name, currencyName, label, required = false) => (
    <div className="form-group">
      <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
      <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
        <input
          type="number"
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          required={required}
          style={{ flex: 2 }}
          step="0.01"
          min="0"
          placeholder="0.00"
        />
        <select
          id={currencyName}
          name={currencyName}
          value={formData[currencyName] || ''}
          onChange={handleInputChange}
          style={currencySelectStyle}
        >
          <option value="">Currency</option>
          {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label || option.value} {option.symbol ? `- ${option.symbol}` : ''}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  // Render a modern, improved checkbox field with a styled appearance
  const renderModernCheckbox = (name, label) => (
    <div className="delivery-modern-checkbox-row">
      <div className="delivery-modern-checkbox-container">
        <input
          type="checkbox"
          id={name}
          name={name}
          checked={formData[name] || false}
          onChange={handleInputChange}
          className="delivery-modern-checkbox"
        />
        <span className="delivery-modern-checkbox-custom">
          {/* Let CSS handle the checkmark display */}
        </span>
      </div>
      <label htmlFor={name} className="delivery-modern-checkbox-label">{label}</label>
    </div>
  );

  // Render a field with an inline checkbox next to it
  const renderFieldWithInlineCheckbox = (fieldName, fieldLabel, checkboxName, checkboxLabel, options = [], required = false) => (
    <div className="form-group">
      <label htmlFor={fieldName}>{fieldLabel} {required && <span className="required-field">*</span>}</label>
      <div className="delivery-field-with-checkbox">
        <select
          id={fieldName}
          name={fieldName}
          value={formData[fieldName] || ''}
          onChange={handleInputChange}
          className="delivery-select-with-checkbox"
          required={required}
        >
          <option value="">Select {fieldLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="delivery-inline-checkbox">
          <div className="delivery-modern-checkbox-container">
            <input
              type="checkbox"
              id={checkboxName}
              name={checkboxName}
              checked={formData[checkboxName] || false}
              onChange={handleInputChange}
              className="delivery-modern-checkbox"
            />
            <span className="delivery-modern-checkbox-custom">
              {/* Let CSS handle the checkmark display */}
            </span>
          </div>
          <label htmlFor={checkboxName} className="delivery-modern-checkbox-label">{checkboxLabel}</label>
        </div>
      </div>
    </div>
  );

  // Modified function for Business Relationships section
  const renderBusinessRelationshipField = (fieldName, fieldLabel, checkboxName, checkboxLabel, options = [], required = false) => (
    <div className="form-group business-relationship-field">
      <label htmlFor={fieldName}>{fieldLabel} {required && <span className="required-field">*</span>}</label>
      <div className="delivery-field-with-checkbox">
        <select
          id={fieldName}
          name={fieldName}
          value={formData[fieldName] || ''}
          onChange={handleInputChange}
          className="delivery-select-with-checkbox"
          required={required}
        >
          <option value="">Select {fieldLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="delivery-inline-checkbox">
          <div className="delivery-modern-checkbox-container">
            <input
              type="checkbox"
              id={checkboxName}
              name={checkboxName}
              checked={formData[checkboxName] || false}
              onChange={handleInputChange}
              className="delivery-modern-checkbox"
            />
            <span className="delivery-modern-checkbox-custom">
              {/* Let CSS handle the checkmark display */}
            </span>
          </div>
          <label htmlFor={checkboxName} className="delivery-modern-checkbox-label">{checkboxLabel}</label>
        </div>
      </div>
    </div>
  );

  // Render two dropdowns on a single line with a shared label
  const renderTwoDropdownsWithLabel = (label, field1Name, field1Placeholder, field2Name, field2Placeholder, options = [], required = false) => (
    <div className="form-group">
      <label htmlFor={field1Name}>{label} {required && <span className="required-field">*</span>}</label>
      <div className="delivery-dual-dropdown-container">
        <div className="delivery-dual-dropdown">
          <select
            id={field1Name}
            name={field1Name}
            value={formData[field1Name] || ''}
            onChange={handleInputChange}
            required={required}
          >
            <option value="">{field1Placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            id={field2Name}
            name={field2Name}
            value={formData[field2Name] || ''}
            onChange={handleInputChange}
            required={required}
          >
            <option value="">{field2Placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  // Render two dropdowns with a single checkbox aligned right in Business Relationships
  const renderBusinessRelationshipChain = (label, field1Name, field1Placeholder, field2Name, field2Placeholder, checkbox1Name, checkbox1Label, checkbox2Name, checkbox2Label, options = [], required = false) => {
    return (
      <div className="form-group business-relationship-field">
        <label htmlFor={field1Name}>{label} {required && <span className="required-field">*</span>}</label>
        <div className="delivery-chain-container" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          {/* Partner 1 Dropdown */}
          <select
            id={field1Name}
            name={field1Name}
            value={formData[field1Name] || ''}
            onChange={handleInputChange}
            required={required}
            style={{ marginBottom: '8px' }}
          >
            <option value="">{field1Placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Checkbox 1 - Reporting as for Partner 1 */}
          <div className="delivery-right-checkbox-container" style={{ marginLeft: '0', marginTop: '0px', marginBottom: '12px' }}>
            <div className="delivery-right-checkbox">
              <div className="delivery-modern-checkbox-container">
                <input
                  type="checkbox"
                  id={checkbox1Name}
                  name={checkbox1Name}
                  checked={formData[checkbox1Name] || false}
                  onChange={handleInputChange}
                  className="delivery-modern-checkbox"
                />
                <span className="delivery-modern-checkbox-custom">
                  {/* Let CSS handle the checkmark display */}
                </span>
              </div>
              <label htmlFor={checkbox1Name} className="delivery-modern-checkbox-label">{checkbox1Label}</label>
            </div>
          </div>
          
          {/* Partner 2 Dropdown */}
          <select
            id={field2Name}
            name={field2Name}
            value={formData[field2Name] || ''}
            onChange={handleInputChange}
            required={required}
            style={{ marginBottom: '8px' }}
          >
            <option value="">{field2Placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Checkbox 2 - Reporting as for Partner 2 */}
          <div className="delivery-right-checkbox-container" style={{ marginLeft: '0', marginTop: '0px' }}>
            <div className="delivery-right-checkbox">
              <div className="delivery-modern-checkbox-container">
                <input
                  type="checkbox"
                  id={checkbox2Name}
                  name={checkbox2Name}
                  checked={formData[checkbox2Name] || false}
                  onChange={handleInputChange}
                  className="delivery-modern-checkbox"
                />
                <span className="delivery-modern-checkbox-custom">
                  {/* Let CSS handle the checkmark display */}
                </span>
              </div>
              <label htmlFor={checkbox2Name} className="delivery-modern-checkbox-label">{checkbox2Label}</label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handler for partner selection from CRM search
  const handlePartnerChange = (fieldName, fieldNameId, partnerId, partnerName, statusData) => {
    // Update both name and ID fields
    handleInputChange({ 
      target: { 
        name: fieldName, 
        value: partnerName 
      } 
    });
    handleInputChange({ 
      target: { 
        name: fieldNameId, 
        value: partnerId 
      } 
    });
    
    // Update sanction and KYC status based on CRM profile data
    if (statusData) {
      // Determine which partner (buyer or seller) based on fieldName
      if (fieldName === 'buyer_crm_profile_name') {
        handleInputChange({ target: { name: 'buyer_sanction_status', value: statusData.sanctionStatus } });
        handleInputChange({ target: { name: 'buyer_kyc_status', value: statusData.kycStatus } });
      } else if (fieldName === 'seller_crm_profile_name') {
        handleInputChange({ target: { name: 'seller_sanction_status', value: statusData.sanctionStatus } });
        handleInputChange({ target: { name: 'seller_kyc_status', value: statusData.kycStatus } });
      }
    }
  };

  // Handler for banking/HMS entity CRM fields (simpler, no status updates)

  // Render standalone checkbox for reporting as
  const renderCheckbox = (checkboxName, checkboxLabel) => (
    <div style={{ width: '100%', marginTop: '4px', marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className="delivery-modern-checkbox-container">
          <input
            type="checkbox"
            id={checkboxName}
            name={checkboxName}
            checked={formData[checkboxName] || false}
            onChange={handleInputChange}
            className="delivery-modern-checkbox"
          />
          <span className="delivery-modern-checkbox-custom">
            {/* Let CSS handle the checkmark display */}
          </span>
        </div>
        <label htmlFor={checkboxName} className="delivery-modern-checkbox-label" style={{ marginLeft: '8px' }}>{checkboxLabel}</label>
      </div>
    </div>
  );

  // Paired fields helper (for name + percentage fields)
  const renderPairedFields = (nameName, percentName, label) => (
    <div className="form-group">
      <label htmlFor={nameName}>{label}</label>
      <div className="delivery-paired-field">
        <input
          type="text"
          id={nameName}
          name={nameName}
          value={formData[nameName] || ''}
          onChange={handleInputChange}
          className="delivery-paired-field-main"
        />
        <input
          type="text"
          id={percentName}
          name={percentName}
          value={formData[percentName] || ''}
          onChange={handleInputChange}
          className="delivery-paired-field-secondary"
          placeholder="%"
        />
      </div>
    </div>
  );

  // Render an input field with a checkbox aligned to the right
  const renderInputWithRightCheckbox = (name, label, checkboxName, checkboxLabel, type = "text", required = false) => (
    <div className="form-group">
      <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
      <div className="delivery-chain-container">
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          required={required}
        />
        <div className="delivery-right-checkbox">
          <div className="delivery-modern-checkbox-container">
            <input
              type="checkbox"
              id={checkboxName}
              name={checkboxName}
              checked={formData[checkboxName] || false}
              onChange={handleInputChange}
              className="delivery-modern-checkbox"
            />
            <span className="delivery-modern-checkbox-custom">
              {/* Let CSS handle the checkmark display */}
            </span>
          </div>
          <label htmlFor={checkboxName} className="delivery-modern-checkbox-label">{checkboxLabel}</label>
        </div>
      </div>
    </div>
  );

  // Add new renderDateField function to use our DatePicker component
  const renderDateField = (name, label, required = false, placeholder = "") => (
    <div className="form-group">
      <h3 className="delivery-subsection-title">{label} {required && <span className="required-field">*</span>}</h3>
      <DatePicker
        id={name}
        name={name}
        value={formData[name] || ''}
        onChange={handleInputChange}
        required={required}
        format="YYYY-MM-DD" // ISO format for backend compatibility
        placeholder={placeholder || "Select date"}
      />
    </div>
  );

  // Add new function for B/L date with status dropdown
  const renderBLDateField = () => (
    <div className="form-group">
      <h3 className="delivery-subsection-title">
        {formData.commodity_type === 'fuels' ? 'BDN date' : 'B/L date'}
      </h3>
      <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
        <InvoiceDatePicker
          name="bill_of_lading_date"
          value={formData.bill_of_lading_date || ''}
          onChange={(e) => {
            handleInputChange(e);
            // Reporting month follows the B/L (BDN) date; stays manually editable
            const blDate = e.target.value;
            if (blDate && /^\d{4}-\d{2}/.test(blDate)) {
              handleInputChange({ target: { name: 'reporting_month', value: blDate.substring(0, 7) } });
            }
          }}
          placeholder="Select date"
          style={{ flex: 2 }}
        />
        <select
          id="bill_of_lading_date_estimated_or_final"
          name="bill_of_lading_date_estimated_or_final"
          value={formData.bill_of_lading_date_estimated_or_final || ''}
          onChange={handleInputChange}
          style={currencySelectStyle}
        >
          <option value="">Status</option>
          <option value="Estimated">Estimated</option>
          <option value="Final">Final</option>
        </select>
      </div>
    </div>
  );

  // Add new renderDateRangeField function specifically for laycan
  const renderDateRangeField = (startDateName, endDateName, label, required = false) => (
    <div className="form-group">
      <h3 className="delivery-subsection-title">{label} {required && <span className="required-field">*</span>}</h3>
      <DateRangePicker
        startDateName={startDateName}
        endDateName={endDateName}
        startDateValue={formData[startDateName] || ''}
        endDateValue={formData[endDateName] || ''}
        onChange={handleInputChange}
        required={required}
        format="YYYY-MM-DD" // ISO format for backend compatibility
        placeholder="Select laycan date range"
      />
    </div>
  );

  // Render a multi-select dropdown with checkboxes
  const renderMultiSelectField = (name, label, options = [], ref) => {
    // Get selected values array or empty array if no value
    const selectedValues = selectedOptions[name] || [];
    
    // Check if this is the delivery_access field to add special indicators
    const isDeliveryAccess = name === 'delivery_access';
    
    return (
      <div className="form-group" ref={ref}>
        <label htmlFor={name}>{label}</label>
        <div className="delivery-multi-select-container">
          <div 
            className={`delivery-multi-select-header ${multiSelectOpen[name] ? 'active' : ''}`}
            onClick={() => toggleMultiSelect(name)}
          >
            <span>
              {selectedValues.length === 0 
                ? `Select ${label}` 
                : `${selectedValues.length} selected`}
            </span>
            {/* Arrow is now handled via CSS background image */}
          </div>
          
          {multiSelectOpen[name] && (
            <div className="delivery-multi-select-dropdown">
              <div className="delivery-multi-select-actions">
                <button 
                  type="button" 
                  className="delivery-multi-select-action-btn"
                  onClick={() => selectAllOptions(name)}
                >
                  Select All
                </button>
                <button 
                  type="button" 
                  className="delivery-multi-select-action-btn"
                  onClick={() => deselectAllOptions(name)}
                >
                  Deselect All
                </button>
              </div>
              
              {isDeliveryAccess && (
                <div className="delivery-multi-select-info" style={{ 
                  padding: '4px 8px', 
                  fontSize: '12px', 
                  color: '#666', 
                  background: '#f5f5f5',
                  borderBottom: '1px solid #eee'
                }}>
                  Note: Operators currently selected in Main/Backup fields will be automatically unselected if removed from Delivery Access
                </div>
              )}
              
              <div className="delivery-multi-select-options">
                {options.map((option) => {
                  // Check if this operator is assigned to any operator fields
                  const isMainOperator = isDeliveryAccess && formData.delivery_main_operator === option.value;
                  const isBackupOperator1 = isDeliveryAccess && formData.delivery_backup_operator_1 === option.value;
                  const isBackupOperator2 = isDeliveryAccess && formData.delivery_backup_operator_2 === option.value;
                  
                  // Determine if we need to show an indicator
                  const showIndicator = isMainOperator || isBackupOperator1 || isBackupOperator2;
                  
                  // Get text for the indicator
                  let indicatorText = '';
                  if (isMainOperator) indicatorText = 'Main';
                  else if (isBackupOperator1) indicatorText = 'Backup 1';
                  else if (isBackupOperator2) indicatorText = 'Backup 2';
                  
                  return (
                    <div key={option.value} className="delivery-multi-select-option" style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: showIndicator ? '#f0f7ff' : 'transparent'
                    }}>
                      <label className="delivery-checkbox-container">
                        <input
                          type="checkbox"
                          checked={selectedValues.includes(option.value)}
                          onChange={() => handleMultiSelectChange(name, option.value)}
                        />
                        <span className="delivery-checkbox-checkmark"></span>
                        {option.label}
                      </label>
                      
                      {showIndicator && (
                        <span style={{ 
                          fontSize: '11px', 
                          background: '#4a90e2', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '10px',
                          marginLeft: '8px'
                        }}>
                          {indicatorText}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Hidden input to store the actual value for form submission */}
          <input 
            type="hidden" 
            name={name} 
            value={selectedValues.join(',')} 
          />
        </div>
      </div>
    );
  };

  // Modified function for the product qualities section
  const renderProductQualitiesSection = () => {
    return (
      <div className="delivery-grid-item-full">
            {/* Action buttons for adding and deleting rows */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '10px',
              gap: '8px'
            }}>
              {/* Fill from a Certificate of Analysis. Needs a saved delivery --
                  the endpoint is /api/deliveries/<id>/..., so there is nothing
                  to extract against until the delivery exists. Say that rather
                  than showing a button that 404s. */}
              {!deleteModeActive && (
                <button
                  type="button"
                  className="delivery-fill-from-certificate-button"
                  onClick={() => setShowQualityCertificateModal(true)}
                  disabled={!deliveryId || deliveryId === 'new' || deliveryId === 'undefined'}
                  title={
                    !deliveryId || deliveryId === 'new' || deliveryId === 'undefined'
                      ? 'Save the delivery first, then fill quality from a certificate'
                      : 'Read a Certificate of Analysis and fill these values'
                  }
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    background: '#f3f0ff',
                    border: '1px solid #ddd6fe',
                    borderRadius: '4px',
                    cursor: (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined')
                      ? 'not-allowed' : 'pointer',
                    opacity: (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined')
                      ? 0.55 : 1,
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Fill from Certificate
                </button>
              )}

              {/* Delete row button - only shows if there are saved rows */}
              {savedCustomRows.length > 0 && (
                deleteModeActive ? (
                  <button 
                    type="button" 
                    className="delivery-cancel-delete-button"
                    onClick={toggleDeleteMode}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      background: '#f5f5f5',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Cancel
                  </button>
                ) : (
                  <button 
                    type="button" 
                    className="delivery-delete-row-button"
                    onClick={toggleDeleteMode}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      background: '#fff0f0',
                      border: '1px solid #ffd0d0',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>-</span> Delete row
                  </button>
                )
              )}
              
              {/* Add row button - hide when in delete mode */}
              {!deleteModeActive && (
                <button 
                  type="button" 
                  className="delivery-add-row-button"
                  onClick={addCustomQualityRow}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    background: '#f0f7ff',
                    border: '1px solid #d0e3ff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '14px' }}>+</span> Add row
                </button>
              )}
            </div>
            
            {/* Delete mode instructions - only show when delete mode is active */}
            {deleteModeActive && savedCustomRows.length > 0 && (
              <div style={{
                background: '#fff5f5',
                padding: '8px 12px',
                marginBottom: '10px',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#e53935'
              }}>
                Please select a row to delete
              </div>
            )}
            
            <div className="delivery-quality-container">
              <table className="delivery-quality-table">
                <thead>
                  <tr>
                    <th>
                      <span>Property</span>
                    </th>
                    <th>
                      <span>Loading Port Actual Quality</span>
                    </th>
                    <th>
                      <span>Discharging Port Actual Quality</span>
                    </th>
                    <th>
                      <span>Purchase Contract</span>
                    </th>
                    <th>
                      <span>Sales Contract</span>
                    </th>
                    <th>
                      <span>IMT Quality Reference Value</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Standard fixed rows */}
                  <tr>
                    <td className="delivery-quality-row-label">
                      <div>Net Calorific Value</div>
                      <div style={{ fontSize: '11px', fontWeight: 'normal', opacity: '0.7' }}>(NAR)</div>
                    </td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_net_calorific_value_NAR"
                        value={formData.loading_port_actual_quality_net_calorific_value_NAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_net_calorific_value_NAR"
                        value={formData.discharging_port_actual_quality_net_calorific_value_NAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_net_calorific_value_NAR"
                        value={formData.purchase_contract_net_calorific_value_NAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_net_calorific_value_NAR"
                        value={formData.sales_contract_net_calorific_value_NAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_net_calorific_value_NAR"
                        value={formData.HMS_quality_reference_value_net_calorific_value_NAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">
                      <div>Gross Calorific Value</div>
                      <div style={{ fontSize: '11px', fontWeight: 'normal', opacity: '0.7' }}>(GAR)</div>
                    </td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_gross_calorific_value_GAR"
                        value={formData.loading_port_actual_quality_gross_calorific_value_GAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_net_calorific_value_NAR"
                        value={formData.purchase_contract_net_calorific_value_NAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_gross_calorific_value_GAR"
                        value={formData.discharging_port_actual_quality_gross_calorific_value_GAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_gross_calorific_value_GAR"
                        value={formData.purchase_contract_gross_calorific_value_GAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_gross_calorific_value_GAR"
                        value={formData.sales_contract_gross_calorific_value_GAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_gross_calorific_value_GAR"
                        value={formData.HMS_quality_reference_value_gross_calorific_value_GAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">
                      <div>Total Moisture</div> 
                      <div style={{ fontSize: '11px', fontWeight: 'normal', opacity: '0.7' }}>(ARB)</div>
                    </td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_total_moisture_ARB"
                        value={formData.loading_port_actual_quality_total_moisture_ARB || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_total_moisture_ARB"
                        value={formData.discharging_port_actual_quality_total_moisture_ARB || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_total_moisture_ARB"
                        value={formData.purchase_contract_total_moisture_ARB || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_total_moisture_ARB"
                        value={formData.sales_contract_total_moisture_ARB || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_total_moisture_ARB"
                        value={formData.HMS_quality_reference_value_total_moisture_ARB || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">Inherent Moisture</td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_inherent_moisture"
                        value={formData.loading_port_actual_quality_inherent_moisture || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_inherent_moisture"
                        value={formData.discharging_port_actual_quality_inherent_moisture || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_inherent_moisture"
                        value={formData.purchase_contract_inherent_moisture || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_inherent_moisture"
                        value={formData.sales_contract_inherent_moisture || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_inherent_moisture"
                        value={formData.HMS_quality_reference_value_inherent_moisture || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">Ash Content</td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_ash_content"
                        value={formData.loading_port_actual_quality_ash_content || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_ash_content"
                        value={formData.discharging_port_actual_quality_ash_content || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_ash_content"
                        value={formData.purchase_contract_ash_content || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_ash_content"
                        value={formData.sales_contract_ash_content || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_ash_content"
                        value={formData.HMS_quality_reference_value_ash_content || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">Volatile Matter</td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_volatile_matter"
                        value={formData.loading_port_actual_quality_volatile_matter || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_gross_calorific_value_GAR"
                        value={formData.purchase_contract_gross_calorific_value_GAR || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_volatile_matter"
                        value={formData.discharging_port_actual_quality_volatile_matter || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_volatile_matter"
                        value={formData.purchase_contract_volatile_matter || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_volatile_matter"
                        value={formData.sales_contract_volatile_matter || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_volatile_matter"
                        value={formData.HMS_quality_reference_value_volatile_matter || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">Total Sulphur</td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_total_sulphur"
                        value={formData.loading_port_actual_quality_total_sulphur || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_total_moisture_ARB"
                        value={formData.purchase_contract_total_moisture_ARB || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_total_sulphur"
                        value={formData.discharging_port_actual_quality_total_sulphur || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_total_sulphur"
                        value={formData.purchase_contract_total_sulphur || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_total_sulphur"
                        value={formData.sales_contract_total_sulphur || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_total_sulphur"
                        value={formData.HMS_quality_reference_value_total_sulphur || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="delivery-quality-row-label">
                      <div>Hard Grove Index</div>
                      <div style={{ fontSize: '11px', fontWeight: 'normal', opacity: '0.7' }}>(HGI)</div>
                    </td>
                    <td>
                      <input
                        type="text"
                        name="loading_port_actual_quality_hard_grove_index_HGI"
                        value={formData.loading_port_actual_quality_hard_grove_index_HGI || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_inherent_moisture"
                        value={formData.purchase_contract_inherent_moisture || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="discharging_port_actual_quality_hard_grove_index_HGI"
                        value={formData.discharging_port_actual_quality_hard_grove_index_HGI || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_hard_grove_index_HGI"
                        value={formData.purchase_contract_hard_grove_index_HGI || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="sales_contract_hard_grove_index_HGI"
                        value={formData.sales_contract_hard_grove_index_HGI || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_ash_content"
                        value={formData.purchase_contract_ash_content || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_volatile_matter"
                        value={formData.purchase_contract_volatile_matter || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_total_sulphur"
                        value={formData.purchase_contract_total_sulphur || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="purchase_contract_hard_grove_index_HGI"
                        value={formData.purchase_contract_hard_grove_index_HGI || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="HMS_quality_reference_value_hard_grove_index_HGI"
                        value={formData.HMS_quality_reference_value_hard_grove_index_HGI || ''}
                        onChange={handleInputChange}
                        onKeyPress={handleNumericKeyPress}
                        placeholder="Enter value"
                      />
                    </td>
                  </tr>
                  
                  {/* Render custom quality rows */}
                  {customQualityRows.map((row) => {
                    // Check if this row is in saved state
                    const isSaved = savedCustomRows.includes(row.id);
                    
                    // Function to handle row click in delete mode
                    const handleRowClick = () => {
                      if (deleteModeActive && isSaved) {
                        deleteSavedRow(row.id);
                      }
                    };
                    
                    return (
                      <React.Fragment key={row.id}>
                        <tr 
                          className={deleteModeActive && isSaved ? 'deletable-row' : ''}
                          onClick={handleRowClick}
                        >
                          <td className="delivery-quality-row-label">
                            {isSaved ? (
                              // Saved row format - similar to standard rows
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                position: 'relative'
                              }}>
                                {/* Delete button shown in delete mode */}
                                {deleteModeActive && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent row click
                                      deleteSavedRow(row.id);
                                    }}
                                    style={{
                                      marginRight: '8px',
                                      padding: '2px 6px',
                                      fontSize: '11px',
                                      background: '#fff0f0',
                                      border: '1px solid #ffd0d0',
                                      borderRadius: '3px',
                                      color: '#d32f2f',
                                      cursor: 'pointer',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    Delete
                                  </button>
                                )}
                                {row.propertyName}
                              </div>
                            ) : (
                              // Editable row format with input
                              <div style={{ display: 'flex', position: 'relative' }}>
                                <input
                                  type="text"
                                  value={row.propertyName}
                                  onChange={(e) => updateCustomQualityRow(row.id, 'propertyName', e.target.value)}
                                  placeholder="Enter property name"
                                  style={{ 
                                    width: '100%',
                                    fontWeight: 'bold',
                                    border: '1px solid #e0e0e0',
                                    padding: '6px 10px'
                                  }}
                                />
                              </div>
                            )}
                          </td>
                          <td>
                            {isSaved ? (
                              // Saved row input format
                              <input
                                type="text"
                                value={row.loadingPortValue}
                                onChange={(e) => updateCustomQualityRow(row.id, 'loadingPortValue', e.target.value)}
                                onKeyPress={handleNumericKeyPress}
                                placeholder="Enter value"
                              />
                            ) : (
                              // Editable row input format
                              <input
                                type="text"
                                value={row.loadingPortValue}
                                onChange={(e) => updateCustomQualityRow(row.id, 'loadingPortValue', e.target.value)}
                                onKeyPress={handleNumericKeyPress}
                                placeholder="Enter value"
                              />
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.dischargingPortValue}
                              onChange={(e) => updateCustomQualityRow(row.id, 'dischargingPortValue', e.target.value)}
                              onKeyPress={handleNumericKeyPress}
                              placeholder="Enter value"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.purchaseContractValue || ''}
                              onChange={(e) => updateCustomQualityRow(row.id, 'purchaseContractValue', e.target.value)}
                              onKeyPress={handleNumericKeyPress}
                              placeholder="Enter value"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.salesContractValue}
                              onChange={(e) => updateCustomQualityRow(row.id, 'salesContractValue', e.target.value)}
                              onKeyPress={handleNumericKeyPress}
                              placeholder="Enter value"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.purchaseContractValue || ''}
                              onChange={(e) => updateCustomQualityRow(row.id, 'purchaseContractValue', e.target.value)}
                              onKeyPress={handleNumericKeyPress}
                              placeholder="Enter value"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.hmsQualityReferenceValue}
                              onChange={(e) => updateCustomQualityRow(row.id, 'hmsQualityReferenceValue', e.target.value)}
                              onKeyPress={handleNumericKeyPress}
                              placeholder="Enter value"
                            />
                          </td>
                        </tr>
                        
                        {/* Action buttons row - only for non-saved rows */}
                        {!isSaved && !deleteModeActive && (
                          <tr className="custom-quality-actions">
                            <td colSpan={6} style={{
                              padding: '0 0 10px 0',
                              textAlign: 'right',
                              borderBottom: '1px dashed #eaeaea'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                gap: '8px', 
                                paddingRight: '10px' 
                              }}>
                                <button
                                  type="button"
                                  onClick={() => deleteCustomQualityRow(row.id)}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: '11px',
                                    background: '#fff0f0',
                                    border: '1px solid #ffd0d0',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveCustomQualityRow(row.id)}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: '11px',
                                    background: '#f0ffeb',
                                    border: '1px solid #d0ffba',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Save
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Info text about saving custom rows */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              marginTop: '8px',
              paddingRight: '10px'
            }}>
              <span style={{ 
                fontSize: '12px', 
                color: '#4a90e2',
                fontStyle: 'italic'
              }}>
                * Only rows with values will be saved
              </span>
            </div>

            {showQualityCertificateModal && (
              <DeliveryQualityCertificateModal
                deliveryId={deliveryId}
                onClose={() => setShowQualityCertificateModal(false)}
                onApply={applyCertificateProposals}
              />
            )}
      </div>
    );
  };

  // Add the missing renderTextareaField function
  const renderTextareaField = (name, label, required = false) => (
    <div className="form-group">
      <label htmlFor={name}>{label} {required && <span className="required-field">*</span>}</label>
      <textarea
        id={name}
        name={name}
        value={formData[name] || ''}
        onChange={handleInputChange}
        className="delivery-textarea"
        required={required}
        rows={4}
      />
    </div>
  );

  // When the formData changes, process custom quality fields and populate customQualityRows
  useEffect(() => {
    // Extract custom quality fields from formData
    const extractCustomQualityFields = () => {
//      console.log("Extracting custom quality fields from formData");
      
      // Find all custom quality property name fields
      const customFields = Object.entries(formData).filter(([key]) => 
        key.startsWith('custom_quality_') && key.endsWith('_propertyName') && formData[key] !== ''
      );
      
//      console.log(`Found ${customFields.length} custom quality fields:`, customFields);
      
      if (customFields.length > 0) {
        // Create rows for each custom property
        const newRows = customFields.map(([key, propertyName]) => {
          // Extract the ID part from the key (e.g., 'custom_quality_custom_1_propertyName' -> 'custom_1')
          // or 'custom_quality_1621234567890_propertyName' -> '1621234567890'
          const idMatch = key.match(/custom_quality_([^_]+)_propertyName/);
          if (!idMatch) return null;
          
          const id = idMatch[1];
//          console.log(`Processing custom quality with ID: ${id}, Name: ${propertyName}`);
          
          // Get corresponding values
          const loadingPortKey = `custom_quality_${id}_loadingPortValue`;
          const dischargingPortKey = `custom_quality_${id}_dischargingPortValue`;
          const purchaseContractKey = `custom_quality_${id}_purchaseContractValue`;
          const salesContractKey = `custom_quality_${id}_salesContractValue`;
          const hmsQualityReferenceKey = `custom_quality_${id}_hmsQualityReferenceValue`;

          // Skip this row if all values are empty
          if (!propertyName && !formData[loadingPortKey] && !formData[dischargingPortKey] && !formData[purchaseContractKey] && !formData[salesContractKey] && !formData[hmsQualityReferenceKey]) {
//            console.log(`Skipping empty custom quality row with ID: ${id}`);
            return null;
          }
          
          // Log the values we're extracting
//          console.log(`Loading Port Value: ${formData[loadingPortKey] || 'empty'}`);
//          console.log(`Discharging Port Value: ${formData[dischargingPortKey] || 'empty'}`);
//          console.log(`Sales Contract Value: ${formData[salesContractKey] || 'empty'}`);
//          console.log(`HMS Quality Reference Value: ${formData[hmsQualityReferenceKey] || 'empty'}`);
          
          return {
            id,
            propertyName: propertyName || '',
            loadingPortValue: formData[loadingPortKey] || '',
            dischargingPortValue: formData[dischargingPortKey] || '',
            purchaseContractValue: formData[purchaseContractKey] || '',
            salesContractValue: formData[salesContractKey] || '',
            hmsQualityReferenceValue: formData[hmsQualityReferenceKey] || ''
          };
        }).filter(Boolean); // Remove any null entries
        
        // Update customQualityRows if there are valid rows and it's not already set to these values
        if (newRows.length > 0) {
//          console.log(`Setting ${newRows.length} custom quality rows:`, newRows);
          setCustomQualityRows(newRows);
          
          // Only set savedCustomRows when loading data from backend
          // Check if this is data from the backend based on the presence of delivery_unique_number
          // AND this is the initial load (not when a user is actively typing)
          if (formData.delivery_unique_number && !customQualityRows.length) {
            // Get all row IDs
            const rowIds = newRows.map(row => row.id);
//            console.log(`Initializing saved rows from backend data:`, rowIds);
            setSavedCustomRows(rowIds);
          }
        } else {
//          console.log("No valid custom quality rows extracted");
        }
      } else {
//        console.log("No custom quality fields found in formData");
      }
    };
    
    extractCustomQualityFields();
  }, [formData]);

  // Add initialization for delivery_access and financial_access from formData
  useEffect(() => {
//    console.log('Initializing from formData:', {
//      delivery_access: formData.delivery_access || '(empty)',
//      financial_access: formData.financial_access || '(empty)'
//    });
    
    // Helper function to normalize IDs
    const normalizeIds = (idsArray) => {
      if (!idsArray || !idsArray.length) return [];
      
      return idsArray.map(id => {
        if (!id) return '';
        const strId = typeof id === 'string' ? id : String(id);
        // Remove any dash prefix that might exist
        return strId.trim().replace(/^[\u2013\u2014\-]\s*/, '');
      }).filter(Boolean); // Remove any empty strings
    };
    
    // Initialize delivery_access from formData
    if (formData.delivery_access && typeof formData.delivery_access === 'string') {
      // Split the comma-separated string into an array
      const accessArray = formData.delivery_access.split(',')
        .map(id => id.trim())
        .filter(Boolean);
      
      // Normalize the IDs to handle any dash prefixes
      const normalizedArray = normalizeIds(accessArray);
      
//      console.log(`Parsed ${normalizedArray.length} operators from formData.delivery_access`);
      
      if (normalizedArray.length > 0) {
        setSelectedOptions(prev => ({
          ...prev,
          delivery_access: normalizedArray
        }));
      }
    }
    
    // Initialize financial_access from formData
    if (formData.financial_access && typeof formData.financial_access === 'string') {
      // Split the comma-separated string into an array
      const accessArray = formData.financial_access.split(',')
        .map(id => id.trim())
        .filter(Boolean);
      
      // Normalize the IDs to handle any dash prefixes
      const normalizedArray = normalizeIds(accessArray);
      
//      console.log(`Parsed ${normalizedArray.length} operators from formData.financial_access`);
      
      if (normalizedArray.length > 0) {
        setSelectedOptions(prev => ({
          ...prev,
          financial_access: normalizedArray
        }));
      }
    }
  }, [formData.delivery_access, formData.financial_access]);

  // Function to handle port validation change
  const handlePortValidationChange = (field, isValid) => {
    setPortValidation(prev => ({
      ...prev,
      [field]: isValid
    }));
  };

  // Turn a picked NavAPI port suggestion into the canonical stored string
  // "PortName, Country, PortCode". The existing parseManualPort/extractUnlocode
  // pipeline reads the trailing UN/LOCODE token off this string to resolve the
  // marker, so the format matters (name first, code last). Missing pieces are
  // simply dropped so we never emit dangling commas.
  const buildCanonicalPortString = (port) => {
    if (!port) return '';
    const parts = [port.port_name, port.country, port.port_code]
      .map(p => (p == null ? '' : String(p).trim()))
      .filter(Boolean);
    return parts.join(', ');
  };

  // Add state for tracking saved custom rows
  const [savedCustomRows, setSavedCustomRows] = useState([]);
  
  // Function to save a custom quality row (marks it as permanent)
  const saveCustomQualityRow = (id) => {
//    console.log(`Saving custom quality row with id: ${id}`);
    
    // Find the row to save
    const rowToSave = customQualityRows.find(row => row.id === id);
    
    // If row exists and has a non-empty property name (at least 2 characters)
    if (rowToSave && rowToSave.propertyName && rowToSave.propertyName.trim().length >= 2) {
      // Add to saved rows list
      setSavedCustomRows(prev => [...prev, id]);
    } else {
      // Show a warning to the user that the property name is too short
      console.warn('Cannot save row with empty or too short property name (min 2 characters)');
      
      // Keep a reference to the showError function from useFlashMessage
      if (typeof showError === 'function') {
        showError('Please enter a property name with at least 2 characters before saving');
      }
    }
  };
  
  // Function to toggle a row from saved back to editable
  const unsaveCustomQualityRow = (id) => {
//    console.log(`Unsaving custom quality row with id: ${id}`);
    setSavedCustomRows(prev => prev.filter(rowId => rowId !== id));
  };

  // Add state for tracking row deletion UI
  const [deleteModeActive, setDeleteModeActive] = useState(false);
  
  // Function to toggle delete mode
  const toggleDeleteMode = () => {
    setDeleteModeActive(!deleteModeActive);
  };
  
  // Function to delete a selected saved row
  const deleteSavedRow = (id) => {
    // Unsave and delete the row
    unsaveCustomQualityRow(id);
    deleteCustomQualityRow(id);
    // Exit delete mode
    setDeleteModeActive(false);
  };

  // Add styling for delete mode
  const deleteStyles = `
    .deletable-row {
      transition: background-color 0.2s;
    }
    .deletable-row:hover {
      background-color: #ffebee;
      cursor: pointer;
    }
  `;

  return (
    <>
      {/* Add styles for delete mode */}
      {deleteModeActive && <style>{deleteStyles}</style>}
      
      {/* === PHASE 1: Tab Navigation === */}
      <div className="delivery-tabs-and-subnav-container">
        <DeliveryTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Ship details are kept in parity with the linked recap (HMS,
         * 2026-09-09: "full parity flow"), which means a vessel an operator
         * typed here CAN be replaced by a correction made upstream. Say so —
         * a silent overwrite on the screen where the real ship shows up is
         * the thing that would erode trust in the whole chain. Twin of the
         * recap's own "updated from the enquiry" banner. Below the tabs, so
         * the tabs stay the first thing under the title. */}
        {formData?.recap_sync_note ? (
          <div
            className="delivery-recap-sync-note"
            data-testid="recap-sync-note"
            style={{
              margin: '0 auto 14px', padding: '10px 14px', maxWidth: 1280,
              background: 'var(--hms-info-bg, #EEF4FB)',
              border: '1px solid var(--hms-info-border, #C7DAF0)',
              borderRadius: 6, fontSize: 13,
              color: 'var(--hms-info-text, #234E7D)',
            }}
          >
            <strong>Updated from the recap.</strong> {formData.recap_sync_note}
          </div>
        ) : null}
        
        {/* Section Navigation - Only visible on WHO tab */}
        {activeTab === 'who' && (
          <DeliverySectionNav 
            activeSection={activeSection} 
            onSectionClick={handleSectionClick}
            visible={true}
          />
        )}
      </div>
      
      {/* System Information - Hidden but maintained for backend compatibility */}
      <div className="delivery-section" style={{ display: 'none' }}>
        <h2 className="delivery-section-title">System Information</h2>
        {renderInputField('delivery_unique_number', 'Delivery Number', 'text')}
        {renderInputField('employee_user_identifier', 'Employee ID', 'text')}
        {/* Associated trade recap numbers JSON field */}
      </div>

      {/* ============================================== */}
      {/* WHO TAB CONTENT */}
      {/* ============================================== */}
      {activeTab === 'who' && (
        <div className="delivery-tab-content delivery-tab-who">
          {/* BUSINESS PARTNERS Section */}
          {activeSection === 'business-partners' && (
          <div className="delivery-section-card" id="business-partners">
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Business Partners</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${businessPartnersProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{businessPartnersProgress.filled}/{businessPartnersProgress.total}</span>
              </div>
            </div>
            
            {/* HMS Entity - Centered at top */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <div style={{ 
                width: '50%', 
                maxWidth: recapLegEntities.hasRecaps ? '640px' : '400px', 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                padding: '15px',
                backgroundColor: '#fafafa'
              }}>
                <h3 className="delivery-subsection-title" style={{ marginBottom: '10px' }}>IMT Entity</h3>
                {recapLegEntities.hasRecaps ? (
                  /* Display only: each side's entity comes from its recap
                     and is changed there. */
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {[['Purchase', recapLegEntities.purchase], ['Sale', recapLegEntities.sales]].map(([side, name]) => (
                      <div className="form-group" key={side}>
                        <label>{side}</label>
                        <input
                          type="text"
                          value={name}
                          placeholder="Not set on the recap"
                          readOnly
                          className="read-only-field"
                          title={`${side} entity, from the linked ${side.toLowerCase()} recap`}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="form-group">
                  <select
                    name="hms_entity"
                    value={formData.hms_entity || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select IMT Entity</option>
                    {/* The server can hold an entity that is not in the
                        office-backed list (or not yet loaded). Show it rather
                        than a blank that reads as "nothing set". */}
                    {formData.hms_entity &&
                      !dropdownOptions.hms_entities.some(e => e.value === formData.hms_entity) && (
                        <option value={formData.hms_entity}>
                          {formData.hms_entity_name || formData.hms_entity}
                        </option>
                    )}
                    {dropdownOptions.hms_entities.map((entity, index) => (
                      <option key={entity.value || index} value={entity.value}>
                        {entity.label}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                {formData.is_intercompany && (
                  <div style={{
                    padding: '10px 12px',
                    marginTop: '10px',
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <i className="fa fa-exclamation-triangle" style={{ color: '#ff9800', fontSize: '16px', marginTop: '2px' }}></i>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      <strong>Intercompany Transaction</strong>
                      {formData.intercompany_entities && (
                        <div style={{ marginTop: '4px' }}>
                          {formData.intercompany_entities.purchase && (
                            <div>Purchase: {formData.intercompany_entities.purchase.join(', ')}</div>
                          )}
                          {formData.intercompany_entities.sales && (
                            <div>Sales: {formData.intercompany_entities.sales.join(', ')}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 className="delivery-subsection-title" style={{ marginBottom: '0' }}>Seller contractual</h3>
                    <button
                      type="button"
                      onClick={addSellerContractual}
                      style={{
                        background: '#4CAF50',
                        color: 'white',
                        border: '1px solid #45a049',
                        borderRadius: '4px',
                        padding: '0',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1'
                      }}
                      title="Add another seller contractual"
                    >
                      +
                    </button>
                  </div>
                  <div 
                    id="seller-contractual-container"
                    style={{
                      maxHeight: sellerContractualList.length >= 4 ? '400px' : 'none',
                      overflowY: sellerContractualList.length >= 4 ? 'auto' : 'visible',
                      paddingRight: sellerContractualList.length >= 4 ? '8px' : '0',
                      position: 'relative',
                      scrollBehavior: 'smooth',
                      borderBottom: sellerContractualList.length >= 4 ? '3px solid #4a6b25' : 'none'
                    }}
                  >
                    {sellerContractualList.map((seller, index) => (
                    <div key={index}>
                      {index > 0 && (
                        <div style={{ 
                          borderTop: '1px solid #e0e0e0', 
                          margin: '8px 0',
                          paddingTop: '8px'
                        }}></div>
                      )}
                      <div className="form-group" style={{ width: '100%', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <DeliveryPartnerSearch
                              fieldName={`seller_contractual_${index}`}
                              fieldNameId={`seller_contractual_crm_id_${index}`}
                              value={seller.name || ''}
                              onChange={handleContractualPartnerChange('seller', index)}
                              onCreatePartner={inlineCrmCreate({
                                fieldName: `seller_contractual_${index}`,
                                fieldNameId: `seller_contractual_crm_id_${index}`,
                                contractualKind: 'seller',
                                contractualIndex: index,
                                initialPartnerType: 'Seller (Creditor)',
                                title: 'Create Seller (CRM)',
                              })}
                              extraVisibleProfiles={pendingCreatedPartners}
                            />
                          </div>
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => removeSellerContractual(index)}
                              style={{
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '0',
                                fontSize: '14px',
                                cursor: 'pointer',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: '1',
                                marginTop: '0'
                              }}
                              title="Remove seller"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div style={{ width: '100%', marginTop: '4px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div className="delivery-modern-checkbox-container">
                              <input
                                type="checkbox"
                                id={`seller_contractual_reporting_${index}`}
                                checked={seller.reporting_as || false}
                                onChange={(e) => updateSellerContractual(index, 'reporting_as', e.target.checked)}
                                className="delivery-modern-checkbox"
                              />
                              <span className="delivery-modern-checkbox-custom">
                                {/* Let CSS handle the checkmark display */}
                              </span>
                            </div>
                            <label htmlFor={`seller_contractual_reporting_${index}`} className="delivery-modern-checkbox-label" style={{ marginLeft: '8px' }}>Reporting as</label>
                          </div>
                        </div>
                        <div className="delivery-status-row" style={{ marginTop: '8px' }}>
                          <StatusButton
                            status={seller.sanction_status || 'pending'}
                            onChange={(newStatus) => updateSellerContractual(index, 'sanction_status', newStatus)}
                            label="Sanction status"
                            readOnly={!!seller.crm_id}
                          />
                          <StatusButton
                            status={seller.kyc_status || 'pending'}
                            onChange={(newStatus) => updateSellerContractual(index, 'kyc_status', newStatus)}
                            label="KYC status"
                            readOnly={!!seller.crm_id}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Purchase - chain in between</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <DeliveryPartnerSearch
                      label="Partner 1"
                      fieldName="delivery_chain_in_between_purchase_1"
                      fieldNameId="delivery_chain_in_between_purchase_1_crm_id"
                      value={formData.delivery_chain_in_between_purchase_1 || ''}
                      onChange={handlePartnerChange}
                      onCreatePartner={inlineCrmCreate({
                        fieldName: 'delivery_chain_in_between_purchase_1',
                        fieldNameId: 'delivery_chain_in_between_purchase_1_crm_id',
                        initialPartnerType: 'Seller (Creditor)',
                        title: 'Create Chain Partner (CRM)',
                      })}
                      extraVisibleProfiles={pendingCreatedPartners}
                    />
                    {renderCheckbox('delivery_chain_in_between_reporting_as_tickbox_1', 'Reporting as')}
                  </div>
                  
                  <div className="form-group full-width">
                    <DeliveryPartnerSearch
                      label="Partner 2"
                      fieldName="delivery_chain_in_between_purchase_2"
                      fieldNameId="delivery_chain_in_between_purchase_2_crm_id"
                      value={formData.delivery_chain_in_between_purchase_2 || ''}
                      onChange={handlePartnerChange}
                      onCreatePartner={inlineCrmCreate({
                        fieldName: 'delivery_chain_in_between_purchase_2',
                        fieldNameId: 'delivery_chain_in_between_purchase_2_crm_id',
                        initialPartnerType: 'Seller (Creditor)',
                        title: 'Create Chain Partner (CRM)',
                      })}
                      extraVisibleProfiles={pendingCreatedPartners}
                    />
                    {renderCheckbox('delivery_chain_in_between_reporting_as_tickbox_2', 'Reporting as')}
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Producer</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <DeliveryPartnerSearch
                      fieldName="delivery_producer"
                      fieldNameId="delivery_producer_crm_id"
                      value={formData.delivery_producer || ''}
                      onChange={handlePartnerChange}
                      onCreatePartner={inlineCrmCreate({
                        fieldName: 'delivery_producer',
                        fieldNameId: 'delivery_producer_crm_id',
                        initialPartnerType: 'Producer',
                        title: 'Create Producer (CRM)',
                      })}
                      extraVisibleProfiles={pendingCreatedPartners}
                    />
                    {renderCheckbox('delivery_producer_reporting_as_tickbox', 'Reporting as')}
                  </div>
                </div>
              </div>
              
              <div className="delivery-column">
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 className="delivery-subsection-title" style={{ marginBottom: '0' }}>Buyer contractual</h3>
                    <button
                      type="button"
                      onClick={addBuyerContractual}
                      style={{
                        background: '#4CAF50',
                        color: 'white',
                        border: '1px solid #45a049',
                        borderRadius: '4px',
                        padding: '0',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1'
                      }}
                      title="Add another buyer contractual"
                    >
                      +
                    </button>
                  </div>
                  <div 
                    id="buyer-contractual-container"
                    style={{
                      maxHeight: buyerContractualList.length >= 4 ? '400px' : 'none',
                      overflowY: buyerContractualList.length >= 4 ? 'auto' : 'visible',
                      paddingRight: buyerContractualList.length >= 4 ? '8px' : '0',
                      position: 'relative',
                      scrollBehavior: 'smooth',
                      borderBottom: buyerContractualList.length >= 4 ? '3px solid #4a6b25' : 'none'
                    }}
                  >
                    {buyerContractualList.map((buyer, index) => (
                    <div key={index}>
                      {index > 0 && (
                        <div style={{ 
                          borderTop: '1px solid #e0e0e0', 
                          margin: '8px 0',
                          paddingTop: '8px'
                        }}></div>
                      )}
                      <div className="form-group" style={{ width: '100%', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <DeliveryPartnerSearch
                              fieldName={`buyer_contractual_${index}`}
                              fieldNameId={`buyer_contractual_crm_id_${index}`}
                              value={buyer.name || ''}
                              onChange={handleContractualPartnerChange('buyer', index)}
                              onCreatePartner={inlineCrmCreate({
                                fieldName: `buyer_contractual_${index}`,
                                fieldNameId: `buyer_contractual_crm_id_${index}`,
                                contractualKind: 'buyer',
                                contractualIndex: index,
                                initialPartnerType: 'Buyer (Debtor)',
                                title: 'Create Buyer (CRM)',
                              })}
                              extraVisibleProfiles={pendingCreatedPartners}
                            />
                          </div>
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => removeBuyerContractual(index)}
                              style={{
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '0',
                                fontSize: '14px',
                                cursor: 'pointer',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: '1',
                                marginTop: '0'
                              }}
                              title="Remove buyer"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div style={{ width: '100%', marginTop: '4px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div className="delivery-modern-checkbox-container">
                              <input
                                type="checkbox"
                                id={`buyer_contractual_reporting_${index}`}
                                checked={buyer.reporting_as || false}
                                onChange={(e) => updateBuyerContractual(index, 'reporting_as', e.target.checked)}
                                className="delivery-modern-checkbox"
                              />
                              <span className="delivery-modern-checkbox-custom">
                                {/* Let CSS handle the checkmark display */}
                              </span>
                            </div>
                            <label htmlFor={`buyer_contractual_reporting_${index}`} className="delivery-modern-checkbox-label" style={{ marginLeft: '8px' }}>Reporting as</label>
                          </div>
                        </div>
                        <div className="delivery-status-row" style={{ marginTop: '8px' }}>
                          <StatusButton
                            status={buyer.sanction_status || 'pending'}
                            onChange={(newStatus) => updateBuyerContractual(index, 'sanction_status', newStatus)}
                            label="Sanction status"
                            readOnly={!!buyer.crm_id}
                          />
                          <StatusButton
                            status={buyer.kyc_status || 'pending'}
                            onChange={(newStatus) => updateBuyerContractual(index, 'kyc_status', newStatus)}
                            label="KYC status"
                            readOnly={!!buyer.crm_id}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Sale - chain in between</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <DeliveryPartnerSearch
                      label="Partner 1"
                      fieldName="delivery_chain_in_between_sale_1"
                      fieldNameId="delivery_chain_in_between_sale_1_crm_id"
                      value={formData.delivery_chain_in_between_sale_1 || ''}
                      onChange={handlePartnerChange}
                      onCreatePartner={inlineCrmCreate({
                        fieldName: 'delivery_chain_in_between_sale_1',
                        fieldNameId: 'delivery_chain_in_between_sale_1_crm_id',
                        initialPartnerType: 'Buyer (Debtor)',
                        title: 'Create Chain Partner (CRM)',
                      })}
                      extraVisibleProfiles={pendingCreatedPartners}
                    />
                    {renderCheckbox('delivery_chain_in_between_sale_reporting_as_tickbox_1', 'Reporting as')}
                  </div>
                  
                  <div className="form-group full-width">
                    <DeliveryPartnerSearch
                      label="Partner 2"
                      fieldName="delivery_chain_in_between_sale_2"
                      fieldNameId="delivery_chain_in_between_sale_2_crm_id"
                      value={formData.delivery_chain_in_between_sale_2 || ''}
                      onChange={handlePartnerChange}
                      onCreatePartner={inlineCrmCreate({
                        fieldName: 'delivery_chain_in_between_sale_2',
                        fieldNameId: 'delivery_chain_in_between_sale_2_crm_id',
                        initialPartnerType: 'Buyer (Debtor)',
                        title: 'Create Chain Partner (CRM)',
                      })}
                      extraVisibleProfiles={pendingCreatedPartners}
                    />
                    {renderCheckbox('delivery_chain_in_between_sale_reporting_as_tickbox_2', 'Reporting as')}
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">End User</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <DeliveryPartnerSearch
                      fieldName="delivery_end_user"
                      fieldNameId="delivery_end_user_crm_id"
                      value={formData.delivery_end_user || ''}
                      onChange={handlePartnerChange}
                      onCreatePartner={inlineCrmCreate({
                        fieldName: 'delivery_end_user',
                        fieldNameId: 'delivery_end_user_crm_id',
                        initialPartnerType: 'End User',
                        title: 'Create End User (CRM)',
                      })}
                      extraVisibleProfiles={pendingCreatedPartners}
                    />
                    {renderCheckbox('delivery_end_user_reporting_as_tickbox', 'Reporting as')}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* CONTRACTS/LEGAL Section */}
          {activeSection === 'contract-legal' && (
          <div className="delivery-section-card" id="contract-legal">
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Contracts/Legal</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${contractsLegalProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{contractsLegalProgress.filled}/{contractsLegalProgress.total}</span>
              </div>
            </div>
            
            <div className="delivery-two-column-grid">
              {/* Left Column */}
              <div className="delivery-column">
                {/* Quality basis is the coal CV (kcal/kg) basis -- inapplicable
                    to bunkers, whose spec is ISO 8217 / grade / sulfur on the
                    WHAT tab. Shown disabled for fuels with a helper note so the
                    field is discoverable but clearly marked solids-only. */}
                <h3 className="delivery-subsection-title">Purchase contract quality basis</h3>
                <input
                  type="text"
                  name="purchase_contract_quality_basis"
                  value={formData.purchase_contract_quality_basis || ''}
                  onChange={handleInputChange}
                  placeholder={formData.commodity_type === 'fuels' ? 'Solids only — not applicable to bunkers' : 'Enter quality basis'}
                  className="form-input"
                  disabled={formData.commodity_type === 'fuels'}
                />
                {formData.commodity_type === 'fuels' && (
                  <small style={{ color: '#888', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                    Quality basis (calorific value) applies to solids only — bunkers spec is set on the What tab.
                  </small>
                )}

                <h3 className="delivery-subsection-title">Purchase confirmation date</h3>
                <InvoiceDatePicker
                  name="purchase_confirmation_date"
                  value={formData.purchase_confirmation_date || ''}
                  onChange={handleInputChange}
                  placeholder="Select date"
                />
                
                <h3 className="delivery-subsection-title">Sales contract No.</h3>
                {/*
                  Bound to seller_contract_number, which is auto-populated from
                  the linked SALE recap's sale_contract_number (the C-number
                  minted by the legal contracts module). Editable so a trader
                  can override; blank when no contract number exists yet.
                */}
                <input
                  type="text"
                  name="seller_contract_number"
                  value={formData.seller_contract_number || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Auto-filled from linked sales recap/contract"
                />
              </div>
              
              {/* Right Column */}
              <div className="delivery-column">
                {/* Coal CV quality basis -- disabled for fuels (see left column). */}
                <h3 className="delivery-subsection-title">Sales contract quality basis</h3>
                <input
                  type="text"
                  name="sales_contract_quality_basis"
                  value={formData.sales_contract_quality_basis || ''}
                  onChange={handleInputChange}
                  placeholder={formData.commodity_type === 'fuels' ? 'Solids only — not applicable to bunkers' : 'Enter quality basis'}
                  className="form-input"
                  disabled={formData.commodity_type === 'fuels'}
                />
                {formData.commodity_type === 'fuels' && (
                  <small style={{ color: '#888', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                    Quality basis (calorific value) applies to solids only — bunkers spec is set on the What tab.
                  </small>
                )}

                <h3 className="delivery-subsection-title">Sales confirmation date</h3>
                <InvoiceDatePicker
                  name="sale_confirmation_date"
                  value={formData.sale_confirmation_date || ''}
                  onChange={handleInputChange}
                  placeholder="Select date"
                />
                
                <h3 className="delivery-subsection-title">Purchase contract No.</h3>
                {/*
                  Bound to buyer_contract_number, which is auto-populated from
                  the linked PURCHASE recap's purchase_contract_number (the
                  C-number minted by the legal contracts module). Editable so a
                  trader can override; blank when no contract number exists yet.
                */}
                <input
                  type="text"
                  name="buyer_contract_number"
                  value={formData.buyer_contract_number || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Auto-filled from linked purchase recap/contract"
                />
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* ============================================== */}
      {/* HOW TAB CONTENT */}
      {/* ============================================== */}
      {activeTab === 'how' && (
        <div className="delivery-tab-content delivery-tab-how">
          {/* PIT TO PORT Section - first on cargo deliveries: the mines and
              inland delivery places, ahead of the vessel and the ports */}
          {effectiveCommodityType !== 'fuels' && (
            <DeliveryPitToPortSection
              formData={formData}
              handleInputChange={handleInputChange}
              units={units}
              inlineCrmCreate={inlineCrmCreate}
              pendingCreatedPartners={pendingCreatedPartners}
            />
          )}

          {/* VESSEL INFO Section - At the top */}
          <div className="delivery-section-card">
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Vessel Info</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${vesselInfoProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{vesselInfoProgress.completed}/{vesselInfoProgress.total}</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <div style={{
                width: '50%',
                maxWidth: '400px',
                border: '1px solid #4a6b25',
                borderLeft: '4px solid #4a6b25',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#e8f4fd'
              }}>
                <h3 className="delivery-subsection-title" style={{ marginBottom: '10px', color: '#4a6b25' }}>Search Vessel</h3>
                <div className="form-group">
                  <VesselSearchAutocomplete
                    onSelect={handleVesselSearchSelect}
                    initialValue={formData.vessel_name || ''}
                  />
                </div>
              </div>
            </div>

            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div className="form-group">
                  <h3 className="delivery-subsection-title">ETA (Contract)</h3>
                  <InvoiceDatePicker
                    name="delivery_estimated_time_of_arrival"
                    value={formData.delivery_estimated_time_of_arrival || ''}
                    onChange={handleInputChange}
                    placeholder="Select Date"
                  />
                </div>
                {formData.commodity_type === 'fuels' && (
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Mode of delivery</h3>
                  <select
                    name="delivery_transport_mode"
                    value={formData.delivery_transport_mode || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select</option>
                    {dropdownOptions.transport_modes && dropdownOptions.transport_modes.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Reporting month</h3>
                  <ReportingMonthInput
                    id="reporting_month_input"
                    value={formData.reporting_month || ''}
                    onChange={(value) => handleInputChange({ target: { name: 'reporting_month', value } })}
                  />
                </div>
              </div>

              <div className="delivery-column">
                {renderBLDateField()}
                {renderBLQuantityField()}
                {renderFinalTonnageField()}
              </div>
            </div>

            {/* Full-width list of all products linked via sales recaps */}
            {renderLinkedProducts()}

            {/* Vessel Tracking Info Panels */}
            <div className="delivery-vessel-tracking-section">
              {(vesselPositionData || vesselDetailsData) && (
                <VesselTrackingPanel
                  position={vesselPositionData}
                  details={vesselDetailsData}
                  contractEta={formData.delivery_estimated_time_of_arrival}
                  resolvedPorts={resolvedPortMarkers}
                />
              )}

              {isTrackingVessel && (
                <div className="delivery-vessel-loading">
                  Loading vessel data...
                </div>
              )}

              <div className="delivery-vessel-globe">
                <h3 className="delivery-subsection-title">Live Vessel Tracking</h3>
                <FlatMapComponent
                  vesselData={vesselPositionData}
                  portMarkers={resolvedPortMarkers}
                  routeWaypoints={routeWaypoints}
                  sailedWaypoints={sailedRouteWaypoints}
                />
              </div>
            </div>

            <div className="delivery-vessel-data-info">
              Data powered by NavAPI maritime intelligence
            </div>
          </div>

          {/* PORT AND FREIGHT Section - Full Width */}
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Port and Freight</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${portAndFreightProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{portAndFreightProgress.completed}/{portAndFreightProgress.total}</span>
              </div>
            </div>
            
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div className="form-group">
                  <h3 className="delivery-subsection-title">{formData.commodity_type === 'fuels' ? 'Dispatch Port' : 'Loading Port'}</h3>
                  <PortSearchAutocomplete
                    value={formData.loading_port_location || ''}
                    onChange={(value) => {
                      // Free-text keystrokes flow straight through so a user who
                      // never picks a suggestion keeps the old save behavior.
                      handleInputChange({
                        target: { name: 'loading_port_location', value },
                      });
                    }}
                    onSelect={(port) => {
                      // A picked suggestion is NavAPI-canonical: store
                      // "PortName, Country, PortCode" so extractUnlocode/resolve
                      // reads the code, and keep the coords for the map marker.
                      const canonical = buildCanonicalPortString(port);
                      handleInputChange({
                        target: { name: 'loading_port_location', value: canonical },
                      });
                      handlePortValidationChange('loading_port_location', true);
                    }}
                    placeholder="Search port by name or UN/LOCODE…"
                  />
                  {renderAisSuggestChip('loading_port_location', vesselPositionData?.origin)}
                </div>

                <div className="form-group">
                  <h3 className="delivery-subsection-title">Origin (Country)</h3>
                  <select
                    name="product_origin"
                    value={formData.product_origin || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select country</option>
                    {dropdownOptions.countries && dropdownOptions.countries.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="delivery-column">
                {/* Bunkers has a single dispatch port; discharging port hidden for fuels. */}
                {formData.commodity_type !== 'fuels' && (
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Discharging Port</h3>
                  <PortSearchAutocomplete
                    value={formData.discharging_port_location || ''}
                    onChange={(value) => {
                      handleInputChange({
                        target: { name: 'discharging_port_location', value },
                      });
                    }}
                    onSelect={(port) => {
                      const canonical = buildCanonicalPortString(port);
                      handleInputChange({
                        target: { name: 'discharging_port_location', value: canonical },
                      });
                      handlePortValidationChange('discharging_port_location', true);
                    }}
                    placeholder="Search port by name or UN/LOCODE…"
                  />
                  {renderAisSuggestChip('discharging_port_location', vesselPositionData?.destination)}
                </div>
                )}

                <div className="form-group">
                  <h3 className="delivery-subsection-title">Destination (Country)</h3>
                  <select
                    name="delivery_destination"
                    value={formData.delivery_destination || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select country</option>
                    {dropdownOptions.countries && dropdownOptions.countries.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* Freight recap linking (Jonas feedback 19.08) — cargo only.
                Linked rows render in the "Linked Freight Recap" strip below
                (freight_recaps_data); this block adds the missing ability to
                ATTACH one to an existing delivery, plus the pending queue for
                unsaved deliveries. */}
            {formData.commodity_type !== 'fuels' && (
              <div className="form-group" style={{ marginTop: '15px' }}>
                <h3 className="delivery-subsection-title">Freight Recaps</h3>
                {pendingLinkedFreightRecaps.map((recap) => (
                  <div
                    key={recap.transaction_number}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 10px', marginBottom: '6px',
                      background: '#fffbe8', border: '1px solid #f0e0a0', borderRadius: '4px',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {recap.transaction_number}
                      <span style={{ fontWeight: 400, fontSize: '12px', color: '#888', marginLeft: '6px' }}>
                        links on save
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleUnlinkFreightRecap(recap.transaction_number)}
                      style={{
                        padding: '3px 8px', fontSize: '11px', background: '#fff0f0',
                        border: '1px solid #ffd0d0', borderRadius: '3px', cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setShowFreightRecapModal(true)}
                  style={{
                    padding: '6px 12px', fontSize: '13px', background: '#f0f7ff',
                    border: '1px solid #d0e3ff', borderRadius: '4px', cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  + Link Freight Recap
                </button>
              </div>
            )}
          </div>

          {/* EXPECTED DELIVERY WINDOW (Bunkers only) — laycan lifted out of Terms and Dates */}
          {formData.commodity_type === 'fuels' && (
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Expected Delivery Window</h2>
            </div>
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div className="form-group">
                  <DateRangePicker
                    startDateName="laycan_start_date"
                    endDateName="laycan_end_date"
                    startDateValue={formData.laycan_start_date || ''}
                    endDateValue={formData.laycan_end_date || ''}
                    onChange={handleInputChange}
                    placeholder="Select expected delivery window"
                  />
                </div>
              </div>
              <div className="delivery-column"></div>
            </div>
          </div>
          )}

          {/* Commissions, Terms and Dates, Vessel Parties — hidden for Bunkers (fuels) */}
          {formData.commodity_type !== 'fuels' && (
          <>
          {/* COMMISSIONS Section - Full Width */}
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Commissions</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${commissionsProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{commissionsProgress.completed}/{commissionsProgress.total}</span>
              </div>
            </div>
            
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Freight per unit</h3>
                  <div className="delivery-freight-unit-row">
                    <select
                      id="freight_per_ton_unit"
                      name="freight_per_ton_unit"
                      value={formData.freight_per_ton_unit || ''}
                      onChange={handleInputChange}
                      className="delivery-freight-unit-select"
                    >
                      <option value="">Unit</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      id="freight_per_ton"
                      name="freight_per_ton"
                      value={formData.freight_per_ton || ''}
                      onChange={handlePriceChange}
                      onBlur={handlePriceBlur}
                      className={`delivery-freight-amount-input ${!isPriceValid.freight_per_ton ? 'error' : ''}`}
                      placeholder="Amount"
                      inputMode="decimal"
                    />
                    <select
                      id="freight_per_ton_currency"
                      name="freight_per_ton_currency"
                      value={formData.freight_per_ton_currency || ''}
                      onChange={handleInputChange}
                      className="delivery-freight-currency-select"
                    >
                      <option value="">Currency</option>
                      {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label || option.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {renderPercentageField('address_commission', 'Address Commission')}
              </div>
              
              <div className="delivery-column">
                {renderPercentageField('mercurius_commission', 'Mercurius Commission')}
                {renderPercentageField('brokerage_commission', 'Brokerage Commission')}
              </div>
            </div>
          </div>

          {/* TERMS AND DATES Section - Full Width */}
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Terms and Dates</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${termsAndDatesProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{termsAndDatesProgress.completed}/{termsAndDatesProgress.total}</span>
              </div>
            </div>
            
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Charter Party Date</h3>
                  <InvoiceDatePicker
                    name="charter_party_date"
                    value={formData.charter_party_date || ''}
                    onChange={handleInputChange}
                    placeholder="Select date"
                  />
                </div>
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Incoterms Purchase</h3>
                  <select
                    name="incoterms_purchase"
                    value={formData.incoterms_purchase || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Incoterms</option>
                    {dropdownOptions.incoterms && dropdownOptions.incoterms.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const purchaseIncoterms = [
                      ...(formData.purchase_recaps_data || []),
                      ...pendingLinkedPurchaseRecaps
                    ]
                      .map(r => r && r.purchase_incoterm ? String(r.purchase_incoterm).trim() : '')
                      .filter(Boolean);
                    const uniqueIncoterms = [...new Set(purchaseIncoterms)];
                    if (uniqueIncoterms.length < 2) return null;
                    return (
                      <div style={{
                        padding: '10px 12px',
                        marginTop: '10px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}>
                        <i className="fa fa-exclamation-triangle" style={{ color: '#ff9800', fontSize: '16px', marginTop: '2px' }}></i>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          <strong>Incoterms Conflict</strong>
                          <div style={{ marginTop: '4px' }}>
                            Linked purchase recaps have different incoterms: {uniqueIncoterms.join(', ')}. Field left empty - select the correct value manually.
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              <div className="delivery-column">
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Laycan</h3>
                  <DateRangePicker
                    startDateName="laycan_start_date"
                    endDateName="laycan_end_date"
                    startDateValue={formData.laycan_start_date || ''}
                    endDateValue={formData.laycan_end_date || ''}
                    onChange={handleInputChange}
                    placeholder="Select laycan date range"
                  />
                </div>
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Incoterms Sale</h3>
                  <select
                    name="incoterms_sale"
                    value={formData.incoterms_sale || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Incoterms</option>
                    {dropdownOptions.incoterms && dropdownOptions.incoterms.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const saleIncoterms = [
                      ...(formData.sales_recaps_data || []),
                      ...pendingLinkedSalesRecaps
                    ]
                      .map(r => r && r.sale_incoterm ? String(r.sale_incoterm).trim() : '')
                      .filter(Boolean);
                    const uniqueIncoterms = [...new Set(saleIncoterms)];
                    if (uniqueIncoterms.length < 2) return null;
                    return (
                      <div style={{
                        padding: '10px 12px',
                        marginTop: '10px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}>
                        <i className="fa fa-exclamation-triangle" style={{ color: '#ff9800', fontSize: '16px', marginTop: '2px' }}></i>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          <strong>Incoterms Conflict</strong>
                          <div style={{ marginTop: '4px' }}>
                            Linked sales recaps have different incoterms: {uniqueIncoterms.join(', ')}. Field left empty - select the correct value manually.
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <h3 className="delivery-subsection-title">Industry</h3>
                  <select
                    name="industry"
                    value={formData.industry || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Industry</option>
                    {INDUSTRY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* VESSEL PARTIES Section - Full Width */}
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Vessel Parties</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: `${vesselPartiesProgress.percentage}%` }}></div>
                </div>
                <span className="delivery-progress-text">{vesselPartiesProgress.completed}/{vesselPartiesProgress.total}</span>
              </div>
            </div>
            
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <h3 className="delivery-subsection-title">Owners</h3>
                <DeliveryPartnerSearch
                  fieldName="vessel_owners"
                  fieldNameId="vessel_owners_crm_id"
                  value={formData.vessel_owners || ''}
                  onChange={handlePartnerChange}
                  onCreatePartner={inlineCrmCreate({
                    fieldName: 'vessel_owners',
                    fieldNameId: 'vessel_owners_crm_id',
                    initialPartnerType: 'Ship Owners',
                    title: 'Create Vessel Owner (CRM)',
                  })}
                  extraVisibleProfiles={pendingCreatedPartners}
                />
              </div>
              
              <div className="delivery-column">
                <h3 className="delivery-subsection-title">Brokers</h3>
                <DeliveryPartnerSearch
                  fieldName="vessel_brokers"
                  fieldNameId="vessel_brokers_crm_id"
                  value={formData.vessel_brokers || ''}
                  onChange={handlePartnerChange}
                  onCreatePartner={inlineCrmCreate({
                    fieldName: 'vessel_brokers',
                    fieldNameId: 'vessel_brokers_crm_id',
                    initialPartnerType: 'Brokers',
                    title: 'Create Vessel Broker (CRM)',
                  })}
                  extraVisibleProfiles={pendingCreatedPartners}
                />
              </div>
            </div>
          </div>
          </>
          )}

          {/* OPERATORS Section - TEMPORARILY REMOVED (Backend fields preserved for future reinstatement) */}
          {/* 
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Operators</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: '0%' }}></div>
                </div>
                <span className="delivery-progress-text">0/25</span>
              </div>
            </div>
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <h3 className="delivery-subsection-title">IMT Assignment</h3>
                {renderSelectField('hms_entity', 'IMT Entity', dropdownOptions.hms_entities || [])}
                {renderSelectField('delivery_assigned_hms_office', 'IMT Office', dropdownOptions.offices || [])}
                {renderMultiSelectField('delivery_access', 'Delivery Access', dropdownOptions.operators || [], deliveryAccessRef)}
                {renderMultiSelectField('financial_access', 'Financial Access', dropdownOptions.operators || [], financialAccessRef)}
              </div>
              
              <div className="delivery-column">
                <h3 className="delivery-subsection-title">Operator Assignment</h3>
                {renderSelectField('delivery_main_operator', 'Main Operator', filteredOperators)}
                {renderSelectField('delivery_backup_operator_1', 'Back-up Operator 1', filteredOperators)}
                {renderSelectField('delivery_backup_operator_2', 'Back-up Operator 2', filteredOperators)}
              </div>
            </div>
          </div>
          */}

          {/* DEAL MAKERS Section - TEMPORARILY REMOVED (Backend fields preserved for future reinstatement) */}
          {/*
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Deal Makers</h2>
              <div className="delivery-progress-indicator">
                <div className="delivery-progress-bar">
                  <div className="delivery-progress-fill" style={{ width: '0%' }}></div>
                </div>
                <span className="delivery-progress-text">0/25</span>
              </div>
            </div>
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                <div className="form-group">
                  <label htmlFor="delivery_deal_maker_sale">Deal maker Sale</label>
                  <div className="delivery-paired-field">
                    <select
                      id="delivery_deal_maker_sale"
                      name="delivery_deal_maker_sale"
                      value={formData.delivery_deal_maker_sale || ''}
                      onChange={handleInputChange}
                      className="delivery-paired-field-main"
                    >
                      <option value="">Select Deal maker</option>
                      {dropdownOptions.traders && dropdownOptions.traders.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      id="delivery_deal_maker_sale_percentage"
                      name="delivery_deal_maker_sale_percentage"
                      value={formData.delivery_deal_maker_sale_percentage || ''}
                      onChange={handleInputChange}
                      className="delivery-paired-field-secondary"
                    >
                      <option value="">%</option>
                      <option value="50">50/50</option>
                      <option value="70">70/30</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="delivery_deal_maker_purchase">Deal maker Purchase</label>
                  <div className="delivery-paired-field">
                    <select
                      id="delivery_deal_maker_purchase"
                      name="delivery_deal_maker_purchase"
                      value={formData.delivery_deal_maker_purchase || ''}
                      onChange={handleInputChange}
                      className="delivery-paired-field-main"
                    >
                      <option value="">Select Deal maker</option>
                      {dropdownOptions.traders && dropdownOptions.traders.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      id="delivery_deal_maker_purchase_percentage"
                      name="delivery_deal_maker_purchase_percentage"
                      value={formData.delivery_deal_maker_purchase_percentage || ''}
                      onChange={handleInputChange}
                      className="delivery-paired-field-secondary"
                    >
                      <option value="">%</option>
                      <option value="50">50/50</option>
                      <option value="70">70/30</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="delivery-column">
                {renderSelectField('delivery_deal_maker_freight', 'Deal maker Freight', dropdownOptions.all_employees || [])}
              </div>
            </div>
          </div>
          */}
        </div>
      )}

      {/* ============================================== */}
      {/* WHAT TAB CONTENT */}
      {/* ============================================== */}
      {activeTab === 'what' && (
        <div className="delivery-tab-content delivery-tab-what">
          {/* PRODUCT INFORMATION Section */}
          <div className="delivery-section-card">
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Product Information</h2>
            </div>
            
            {/* Product Details Grid */}
            <div className="delivery-two-column-grid">
              <div className="delivery-column">
                {/* Purchase Recap Card Flip Carousel */}
                {(() => {
                  // SIMPLIFIED: Merge saved recaps + pending linked recaps + blank recaps for display
                  const savedPurchaseRecaps = (formData.purchase_recaps_data || []);
                  const pendingLinkedMapped = pendingLinkedPurchaseRecaps.map(r => ({
                    ...r,
                    isPendingLink: true // Mark as pending so we can style it differently if needed
                  }));
                  const blankPurchaseMapped = blankPurchaseRecaps.map(r => ({
                    ...r,
                    isBlank: true
                  }));
                  
                  const purchaseDisplayRecaps = [...savedPurchaseRecaps, ...pendingLinkedMapped, ...blankPurchaseMapped]
                    .sort((a, b) => {
                      if (!a.transaction_number || !b.transaction_number) return 0;
                      return a.transaction_number.localeCompare(b.transaction_number);
                    });
                  
                  // Determine active recap and if it's blank
                  // CRITICAL FIX: Only use formData.active_purchase_recap_id if it exists in current recaps
                  // This prevents using stale IDs after unlinking all recaps
                  let activePurchaseRecapId = activeBlankPurchaseId;
                  if (!activePurchaseRecapId && formData.active_purchase_recap_id) {
                    // Only use formData ID if it exists in current recaps (prevents stale state)
                    const existsInRecaps = purchaseDisplayRecaps.some(r => r.id === formData.active_purchase_recap_id);
                    if (existsInRecaps) {
                      activePurchaseRecapId = formData.active_purchase_recap_id;
                    }
                  }
                  // Final fallback to first available recap
                  if (!activePurchaseRecapId) {
                    activePurchaseRecapId = purchaseDisplayRecaps[0]?.id;
                  }
                  
                  const activePurchaseRecap = purchaseDisplayRecaps.find(r => r.id === activePurchaseRecapId);
                  const isPurchaseActiveBlank = activePurchaseRecap?.isBlank || false;
                  
                  return (
                    <RecapCardFlip
                      recaps={purchaseDisplayRecaps}
                      activeRecapId={activePurchaseRecapId}
                      onRecapChange={(recapId) => handleRecapNavigation('purchase', recapId)}
                      onUnlink={(recapId) => {
                        const isBlankRecap = typeof recapId === 'string' && recapId.startsWith('blank-');
                        if (isBlankRecap) {
                          setBlankPurchaseRecaps(prev => prev.filter(r => r.id !== recapId));
                          delete blankRecapValues[recapId];
                          if (recapId === activeBlankPurchaseId) {
                            setActiveBlankPurchaseId(null);
                          }
                          showSuccess('Blank recap removed');
                        } else {
                          handleUnlinkRecap('purchase', recapId);
                        }
                      }}
                      onAddNew={() => setShowPurchaseRecapModal(true)}
                      recapType="purchase"
                    >
                      {/* PURCHASE FORM FIELDS */}
                      
                      {/* For SAVED recaps: All fields EDITABLE, edits buffered in savedRecapEdits and PATCHed on delivery save */}
                      {!isPurchaseActiveBlank && (() => {
                        const editedRecap = getEditedRecap(activePurchaseRecap);
                        const recapId = editedRecap?.id;
                        // Tolerance: prefer explicit edited components, else fall back to parsed legacy string
                        const parsedTolerance = parseToleranceString(editedRecap?.purchase_shipping_tolerance || '');
                        const tolLowerValue = editedRecap?.purchase_shipping_tolerance_lower_value ?? parsedTolerance.lowerValue ?? '';
                        const tolLowerUnit = editedRecap?.purchase_shipping_tolerance_lower_unit ?? parsedTolerance.lowerUnit ?? '%';
                        const tolUpperValue = editedRecap?.purchase_shipping_tolerance_upper_value ?? parsedTolerance.upperValue ?? '';
                        const tolUpperUnit = editedRecap?.purchase_shipping_tolerance_upper_unit ?? parsedTolerance.upperUnit ?? '%';
                        const purchaseToleranceCols = {
                          lowerValueCol: 'purchase_shipping_tolerance_lower_value',
                          lowerUnitCol: 'purchase_shipping_tolerance_lower_unit',
                          upperValueCol: 'purchase_shipping_tolerance_upper_value',
                          upperUnitCol: 'purchase_shipping_tolerance_upper_unit',
                          stringCol: 'purchase_shipping_tolerance',
                        };
                        // Unit dropdown stores code on the recap; display uses unit.id
                        const unitCode = editedRecap?.purchase_contract_quantity_unit;
                        const unitSelectValue = (() => {
                          if (!unitCode) return '';
                          const m = units.find(u => u.code === unitCode || String(u.id) === String(unitCode));
                          return m ? m.id : '';
                        })();
                        // BUNKERS (fuels): per-product picker card. Cargo keeps the layout below.
                        if (editedRecap?.commodity_type === 'fuels') {
                          return (
                            <BunkersRecapProductCard
                              key={recapId}
                              recap={editedRecap}
                              side="purchase"
                              units={units}
                              currencies={dropdownOptions.currencies}
                              productEdits={savedRecapProductEdits[recapId]}
                              onProductFieldChange={(pid, field, val) => handleProductFieldChange(recapId, pid, field, val)}
                              onRecapFieldChange={(field, val) => handleSavedRecapFieldChange(recapId, field, val)}
                            />
                          );
                        }
                        return (
                          <div className="recap-editable-container">
                            <h3 className="delivery-subsection-title">Product</h3>
                            <div className="form-group">
                              <RecapProductSearch
                                label=""
                                value={editedRecap?.product || ''}
                                variantId={editedRecap?.stock_item_variant || null}
                                // A recap with several products sets them on the recap and below.
                                disabled={hasSeveralProducts(editedRecap)}
                                onChange={(variantId, displayName, variant) => {
                                  if (!displayName && !variantId) {
                                    handleSavedRecapFieldChange(recapId, 'product', '');
                                    handleSavedRecapFieldChange(recapId, 'stock_item_variant', null);
                                    return;
                                  }
                                  const baseProduct = variant?.base_stock_item_data?.product_type_display || extractBaseProduct(displayName) || displayName;
                                  handleSavedRecapFieldChange(recapId, 'product', baseProduct);
                                  handleSavedRecapFieldChange(recapId, 'stock_item_variant', variantId || null);
                                }}
                              />
                            </div>

                            {hasSeveralProducts(editedRecap) && (
                              <SeveralProductsNote
                                recap={editedRecap} side="purchase" units={units}
                                deliveryId={isExistingDelivery ? deliveryId : null}
                                onSaved={refreshRecapFigures}
                                onSavedFlash={(msg, ok) => (ok ? showSuccess(msg) : showError(msg))}
                              />
                            )}

                            <h3 className="delivery-subsection-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              Purchase Quantity
                              {activePurchaseRecap?.is_partial_allocation && (
                                <span
                                  className="allocation-badge"
                                  style={{
                                    backgroundColor: '#9b59b6',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'normal',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={`Using ${activePurchaseRecap.allocated_quantity} of ${activePurchaseRecap.total_quantity || activePurchaseRecap.purchase_contract_quantity}`}
                                >
                                  <FontAwesomeIcon icon={faBalanceScale} style={{ marginRight: '4px', fontSize: '10px' }} />
                                  {activePurchaseRecap.allocated_quantity} / {activePurchaseRecap.total_quantity || activePurchaseRecap.purchase_contract_quantity} {(() => {
                                    const code = activePurchaseRecap.purchase_contract_quantity_unit;
                                    const matchingUnit = units.find(u => u.code === code);
                                    return matchingUnit ? matchingUnit.name : code;
                                  })()}
                                </span>
                              )}
                            </h3>
                            {(() => {
                              // Lock Quantity + Unit when this recap is partially allocated.
                              // Reason: editing recap qty here would silently mutate the contract
                              // for every other delivery sharing this recap. To change THIS
                              // delivery's slice, the user should unlink + relink via the modal;
                              // to amend the recap itself, they should do it on the recap screen.
                              // A deal with several products: each one's quantity on
                              // this delivery is set in its products table (above).
                              const isPartial = !!activePurchaseRecap?.is_partial_allocation || hasSeveralProducts(editedRecap);
                              const lockedTooltip = hasSeveralProducts(editedRecap)
                                ? 'This deal has several products: set how much of each this delivery carries in the table above.'
                                : isPartial
                                ? 'Recap is partially allocated. Unlink + relink to change this delivery\u2019s slice, or edit the recap directly on the recap screen.'
                                : undefined;
                              const lockedStyle = isPartial
                                ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }
                                : {};
                              return (
                                <div className="form-group">
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                      type="text"
                                      value={editedRecap?.purchase_contract_quantity ?? ''}
                                      onChange={refuseExtraDecimals(QUANTITY_DECIMALS, (e) => handleSavedRecapFieldChange(recapId, 'purchase_contract_quantity', e.target.value))}
                                      placeholder="Quantity"
                                      readOnly={isPartial}
                                      disabled={isPartial}
                                      title={lockedTooltip}
                                      style={{ flex: 1, ...lockedStyle }}
                                    />
                                    <select
                                      value={unitSelectValue}
                                      onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const u = units.find(unit => String(unit.id) === String(selectedId));
                                        handleSavedRecapFieldChange(recapId, 'purchase_contract_quantity_unit', u?.code || selectedId);
                                      }}
                                      disabled={isPartial}
                                      title={lockedTooltip}
                                      style={{ width: '140px', ...lockedStyle }}
                                    >
                                      <option value="">Unit</option>
                                      {units.map(unit => (
                                        <option key={unit.id} value={unit.id}>{unit.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              );
                            })()}

                            <h3 className="delivery-subsection-title">Tolerance</h3>
                            <div className="form-group">
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={tolLowerValue || ''}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, purchaseToleranceCols, 'purchase_shipping_tolerance_lower_value', e.target.value)}
                                    placeholder="Lower"
                                    style={{ flex: 1 }}
                                  />
                                  <select
                                    value={tolLowerUnit || '%'}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, purchaseToleranceCols, 'purchase_shipping_tolerance_lower_unit', e.target.value)}
                                    style={{ width: '70px' }}
                                  >
                                    <option value="%">%</option>
                                    <option value="min">min</option>
                                    <option value="MT">MT</option>
                                  </select>
                                </div>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={tolUpperValue || ''}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, purchaseToleranceCols, 'purchase_shipping_tolerance_upper_value', e.target.value)}
                                    placeholder="Upper"
                                    style={{ flex: 1 }}
                                  />
                                  <select
                                    value={tolUpperUnit || '%'}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, purchaseToleranceCols, 'purchase_shipping_tolerance_upper_unit', e.target.value)}
                                    style={{ width: '70px' }}
                                  >
                                    <option value="%">%</option>
                                    <option value="max">max</option>
                                    <option value="MT">MT</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            <h3 className="delivery-subsection-title">Purchase Price per Unit (contract)</h3>
                            <div className="form-group">
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                  type="text"
                                  value={editedRecap?.purchase_contract_price_value ?? ''}
                                  onChange={refuseExtraDecimals(PRICE_DECIMALS, (e) => handleSavedRecapFieldChange(recapId, 'purchase_contract_price_value', e.target.value))}
                                  placeholder="Price"
                                  readOnly={hasSeveralProducts(editedRecap)}
                                  disabled={hasSeveralProducts(editedRecap)}
                                  title={hasSeveralProducts(editedRecap) ? 'This deal has several products: each one\u2019s price on this delivery is in the table above.' : undefined}
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={editedRecap?.purchase_contract_price_currency || ''}
                                  onChange={(e) => handleSavedRecapFieldChange(recapId, 'purchase_contract_price_currency', e.target.value)}
                                  style={{ width: '140px' }}
                                >
                                  <option value="">Currency</option>
                                  {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label || option.value}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <h3 className="delivery-subsection-title">Price Basis Purchase (Quality)</h3>
                            <div className="form-group">
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                  type="text"
                                  value={editedRecap?.purchase_price_basis_value ?? ''}
                                  onChange={(e) => handleSavedRecapFieldChange(recapId, 'purchase_price_basis_value', e.target.value)}
                                  placeholder="Value"
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={editedRecap?.purchase_price_basis_option || ''}
                                  onChange={(e) => handleSavedRecapFieldChange(recapId, 'purchase_price_basis_option', e.target.value)}
                                  style={{ width: '160px' }}
                                >
                                  <option value="">CV basis option</option>
                                  <option value="NCV(ARB)">NCV(ARB)</option>
                                  <option value="GCV(ARB)">GCV(ARB)</option>
                                  <option value="GCV(ADB)">GCV(ADB)</option>
                                  <option value="GCV(DB)">GCV(DB)</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* For BLANK recaps: Show editable fields that update blankRecapValues */}
                      {isPurchaseActiveBlank && (
                        <div className="recap-editable-container">
                          <h3 className="delivery-subsection-title">Product</h3>
                          <div className="form-group">
                            <RecapProductSearch
                              label=""
                              value={blankRecapValues[activePurchaseRecapId]?.product || ''}
                              variantId={blankRecapValues[activePurchaseRecapId]?.stock_item_variant}
                              onCreateProduct={(searchTerm) => openProductCreator('purchase', searchTerm)}
                              filterFunction={(variant) => {
                                // NEW: Get used products for purchase side (excluding current blank)
                                const usedPurchaseProducts = getUsedProductsForSide('purchase', activePurchaseRecapId);
                                
                                // Check if this variant's product is already used
                                const variantProduct = extractBaseProduct(variant.display_name || '');
                                if (variantProduct && usedPurchaseProducts.has(variantProduct)) {
                                  return false; // Product already used by another purchase recap - hide
                                }
                                
                                // Keep existing unit/currency validation (optional - can be removed if not needed)
                                const allExisting = getAllRecapsIncludingFilledBlanks().filter(r => r.id !== activePurchaseRecapId);
                                if (allExisting.length > 0) {
                                  const firstRecap = allExisting[0];
                                  
                                  // Check unit (if variant has one)
                                  if (variant.unit_id) {
                                    const firstUnit = firstRecap.purchase_contract_quantity_unit || firstRecap.sale_contract_quantity_unit;
                                    const normalizedFirst = normalizeUnit(firstUnit);
                                    const normalizedNew = normalizeUnit(variant.unit_id);
                                    if (normalizedFirst && normalizedNew && normalizedFirst !== normalizedNew) {
                                      return false; // Different unit - hide
                                    }
                                  }
                                  
                                  // Check currency (if variant has one)
                                  if (variant.price_currency) {
                                    const firstCurrency = firstRecap.purchase_contract_price_currency || firstRecap.sale_contract_price_currency;
                                    if (firstCurrency && variant.price_currency !== firstCurrency) {
                                      return false; // Different currency - hide
                                    }
                                  }
                                }
                                
                                return true; // All checks passed - show this product
                              }}
                              onChange={(variantId, displayName, variant) => {
                                // If product is being cleared, clear ALL related fields
                                if (!displayName && !variantId) {
                                  setBlankRecapValues(prev => ({
                                    ...prev,
                                    [activePurchaseRecapId]: {
                                      product: '',
                                      stock_item_variant: null,
                                      quantity: '',
                                      quantity_unit: '',
                                      price_value: '',
                                      price_currency: '',
                                      price_basis_option: '',
                                      price_basis_value: '',
                                      tolerance_lower_value: '',
                                      tolerance_lower_unit: '%',
                                      tolerance_upper_value: '',
                                      tolerance_upper_unit: '%'
                                    }
                                  }));
                                  return;
                                }
                                
                                // CRITICAL FIX: Extract base product type for storage
                                // displayName is like "Steam Coal | south_africa | Dotcloud Services - Steam Coal 4"
                                // We only want to store "Steam Coal" in the recap product field
                                const baseProduct = variant?.base_stock_item_data?.product_type_display || extractBaseProduct(displayName) || displayName;
                                
                                // No validation needed - dropdown only shows compatible products
                                // Update product name with ONLY the base product
                                handleBlankRecapFieldChange(activePurchaseRecapId, 'product', baseProduct);
                                
                                // CRITICAL: Always update stock_item_variant, even when clearing (null)
                                // Otherwise old variant ID stays and triggers product detail loading
                                handleBlankRecapFieldChange(activePurchaseRecapId, 'stock_item_variant', variantId || null);
                                
                                // Auto-fill fields from variant if available
                                if (variant) {
                                  if (variant.quantity) {
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'quantity', variant.quantity);
                                  }
                                  if (variant.unit_id) {
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'quantity_unit', variant.unit_id);
                                  }
                                  if (variant.purchase_price) {
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'price_value', variant.purchase_price);
                                  }
                                  if (variant.price_currency) {
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'price_currency', variant.price_currency);
                                  }
                                  // Auto-fill tolerance into blank recap (NEW)
                                  if (variant.tolerance_lower_value || variant.tolerance_upper_value) {
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_lower_value', variant.tolerance_lower_value || '');
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_lower_unit', variant.tolerance_lower_unit || '%');
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_upper_value', variant.tolerance_upper_value || '');
                                    handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_upper_unit', variant.tolerance_upper_unit || '%');
                                  }
                                }
                              }}
                            />
                          </div>
                          
                          <h3 className="delivery-subsection-title">Purchase Quantity</h3>
                          <div className="form-group">
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={blankRecapValues[activePurchaseRecapId]?.quantity || ''}
                                onChange={refuseExtraDecimals(QUANTITY_DECIMALS, (e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'quantity', e.target.value))}
                                placeholder="Quantity"
                                style={{ flex: 1 }}
                              />
                              <select
                                value={blankRecapValues[activePurchaseRecapId]?.quantity_unit || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'quantity_unit', e.target.value)}
                                style={{ width: '140px' }}
                              >
                                <option value="">Unit</option>
                                {units.map(unit => (
                                  <option key={unit.id} value={unit.id}>{unit.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <h3 className="delivery-subsection-title">Tolerance</h3>
                          <div className="form-group">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={blankRecapValues[activePurchaseRecapId]?.tolerance_lower_value || ''}
                                  onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_lower_value', e.target.value)}
                                  placeholder="Lower"
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={blankRecapValues[activePurchaseRecapId]?.tolerance_lower_unit || '%'}
                                  onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_lower_unit', e.target.value)}
                                  style={{ width: '70px' }}
                                >
                                  <option value="%">%</option>
                                  <option value="min">min</option>
                                  <option value="MT">MT</option>
                                </select>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={blankRecapValues[activePurchaseRecapId]?.tolerance_upper_value || ''}
                                  onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_upper_value', e.target.value)}
                                  placeholder="Upper"
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={blankRecapValues[activePurchaseRecapId]?.tolerance_upper_unit || '%'}
                                  onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'tolerance_upper_unit', e.target.value)}
                                  style={{ width: '70px' }}
                                >
                                  <option value="%">%</option>
                                  <option value="max">max</option>
                                  <option value="MT">MT</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          
                          <h3 className="delivery-subsection-title">Purchase Price per Unit (contract)</h3>
                          <div className="form-group">
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={blankRecapValues[activePurchaseRecapId]?.price_value || ''}
                                onChange={refuseExtraDecimals(PRICE_DECIMALS, (e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'price_value', e.target.value))}
                                placeholder="Price"
                                style={{ flex: 1 }}
                              />
                              <select
                                value={blankRecapValues[activePurchaseRecapId]?.price_currency || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'price_currency', e.target.value)}
                                style={{ width: '140px' }}
                              >
                                <option value="">Currency</option>
                                {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label || option.value}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <h3 className="delivery-subsection-title">Price Basis Purchase (Quality)</h3>
                          <div className="form-group">
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={blankRecapValues[activePurchaseRecapId]?.price_basis_value || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'price_basis_value', e.target.value)}
                                placeholder="Value"
                                style={{ flex: 1 }}
                              />
                              <select
                                value={blankRecapValues[activePurchaseRecapId]?.price_basis_option || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activePurchaseRecapId, 'price_basis_option', e.target.value)}
                                style={{ width: '160px' }}
                              >
                                <option value="">CV basis option</option>
                                <option value="NCV(ARB)">NCV(ARB)</option>
                                <option value="GCV(ARB)">GCV(ARB)</option>
                                <option value="GCV(ADB)">GCV(ADB)</option>
                                <option value="GCV(DB)">GCV(DB)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                      
                    </RecapCardFlip>
                  );
                })()}
              </div>
              
              {/* OLD PURCHASE FIELDS - HIDDEN TEMPORARILY */}
              <div style={{ display: 'none' }}>
                {/* OLD COMPLEX LOGIC BELOW - TO BE REMOVED */}
                {/*
                          // CRITICAL: Save current form values to the active recap BEFORE switching
                    if (activeBlankPurchaseId && activeBlankPurchaseId !== recapId) {
                            // Format tolerance from component fields
                            const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
                              const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
                              const upper = upperValue ? `${upperValue}${upperUnit}` : '';
                              if (lower && upper) return `${lower} to ${upper}`;
                              else if (lower) return lower;
                              else if (upper) return upper;
                              return '';
                            };
                            
                            const formattedTolerance = formatToleranceField(
                              formData.delivery_product_tolerance_lower_value,
                              formData.delivery_product_tolerance_lower_unit,
                              formData.delivery_product_tolerance_upper_value,
                              formData.delivery_product_tolerance_upper_unit
                            );
                            
          setBlankPurchaseRecaps(prev => 
            prev.map(r => {
              if (r.id === activeBlankPurchaseId) {
                return {
                  ...r,
                  product: formData.purchase_product_name,
                  purchase_contract_quantity: formData.delivery_product_quantity,
                  purchase_contract_quantity_unit: formData.delivery_product_unit,
                  purchase_contract_price_value: formData.purchase_price_per_ton_contract,
                  purchase_contract_price_currency: formData.purchase_price_per_ton_contract_currency,
                  purchase_adjusted_price_value: formData.purchase_price_per_ton_adjusted,
                  purchase_adjusted_price_currency: formData.purchase_price_per_ton_adjusted_currency,
                  purchase_shipping_tolerance: formattedTolerance,
                  purchase_price_basis_option: formData.purchase_price_basis_type,
                  purchase_price_basis_value: formData.purchase_price_basis_value,
                };
              }
              return r;
            })
          );
        }
        
        // Update active pending recap ID
        setActiveBlankPurchaseId(recapId);
                          
                          const recap = blankPurchaseRecaps.find(r => r.id === recapId);
                          if (recap) {
                            // Parse tolerance string into component fields
                            const parsedTolerance = parseToleranceString(recap.purchase_shipping_tolerance);
                            
                            // Use originalHandleInputChange to populate fields without syncing back
                            // (we're reading FROM the recap, not writing TO it)
                            const fieldMappings = {
                              'purchase_product_name': recap.product,
                              'delivery_product_quantity': recap.purchase_contract_quantity,
                              'delivery_product_unit': recap.purchase_contract_quantity_unit,
                              'purchase_price_per_ton_contract': recap.purchase_contract_price_value,
                              'purchase_price_per_ton_contract_currency': recap.purchase_contract_price_currency,
                              'purchase_price_per_ton_adjusted': recap.purchase_adjusted_price_value,
                              'purchase_price_per_ton_adjusted_currency': recap.purchase_adjusted_price_currency,
                              'delivery_product_tolerance': recap.purchase_shipping_tolerance,
                              'delivery_product_tolerance_lower_value': parsedTolerance.lowerValue,
                              'delivery_product_tolerance_lower_unit': parsedTolerance.lowerUnit,
                              'delivery_product_tolerance_upper_value': parsedTolerance.upperValue,
                              'delivery_product_tolerance_upper_unit': parsedTolerance.upperUnit,
                              'purchase_price_basis_type': recap.purchase_price_basis_option,
                              'purchase_price_basis_value': recap.purchase_price_basis_value,
                              'price_basis_purchase_quality': (recap.purchase_price_basis_option || recap.purchase_price_basis_value)
                                ? [recap.purchase_price_basis_option, recap.purchase_price_basis_value ? `${recap.purchase_price_basis_value} kcal/kg` : ''].filter(Boolean).join(' ')
                                : '',
                            };
                            Object.entries(fieldMappings).forEach(([fieldName, fieldValue]) => {
                              originalHandleInputChange({ target: { name: fieldName, value: fieldValue ?? '' } });
                            });
                            // Also set product search term to display the product
                            if (recap.product) {
                              setProductSearchTerm(recap.product);
                            } else {
                              setProductSearchTerm('');
                            }
                          }
                        } else {
                          // Navigating to a saved recap on a saved delivery
                          // CRITICAL: Save BOTH pending purchase AND sales recap values BEFORE API call
                          // (API response will overwrite form fields, so we must preserve pending values)
                          
                          const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
                            const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
                            const upper = upperValue ? `${upperValue}${upperUnit}` : '';
                            if (lower && upper) return `${lower} to ${upper}`;
                            else if (lower) return lower;
                            else if (upper) return upper;
                            return '';
                          };
                          
                          // Save pending purchase recap if active
                          if (activeBlankPurchaseId) {
                            const formattedTolerance = formatToleranceField(
                              formData.delivery_product_tolerance_lower_value,
                              formData.delivery_product_tolerance_lower_unit,
                              formData.delivery_product_tolerance_upper_value,
                              formData.delivery_product_tolerance_upper_unit
                            );
                            
                            setBlankPurchaseRecaps(prev => 
                              prev.map(r => {
                                if (r.id === activeBlankPurchaseId) {
                                  return {
                                    ...r,
                                    product: formData.purchase_product_name,
                                    purchase_contract_quantity: formData.delivery_product_quantity,
                                    purchase_contract_quantity_unit: formData.delivery_product_unit,
                                    purchase_contract_price_value: formData.purchase_price_per_ton_contract,
                                    purchase_contract_price_currency: formData.purchase_price_per_ton_contract_currency,
                                    purchase_adjusted_price_value: formData.purchase_price_per_ton_adjusted,
                                    purchase_adjusted_price_currency: formData.purchase_price_per_ton_adjusted_currency,
                                    purchase_shipping_tolerance: formattedTolerance,
                                    purchase_price_basis_option: formData.purchase_price_basis_type,
                                    purchase_price_basis_value: formData.purchase_price_basis_value,
                                  };
                                }
                                return r;
                              })
                            );
                          }
                          
                          // ALSO save pending sales recap if active (prevents cross-side wipe)
                          if (activeBlankSalesId) {
                            const formattedSalesTolerance = formatToleranceField(
                              formData.sales_tolerance_lower_value,
                              formData.sales_tolerance_lower_unit,
                              formData.sales_tolerance_upper_value,
                              formData.sales_tolerance_upper_unit
                            );
                            
                            setBlankSalesRecaps(prev => 
                              prev.map(r => {
                                if (r.id === activeBlankSalesId) {
                                  return {
                                    ...r,
                                    product: formData.sales_product_name,
                                    sale_contract_quantity: formData.sales_product_quantity,
                                    sale_contract_quantity_unit: formData.sales_product_unit,
                                    sale_contract_price_value: formData.sales_price_per_ton_contract,
                                    sale_contract_price_currency: formData.sale_price_per_ton_contract_currency,
                                    sale_adjusted_price_value: formData.sales_price_per_ton_adjusted,
                                    sale_adjusted_price_currency: formData.sale_price_per_ton_adjusted_currency,
                                    sale_shipping_tolerance: formattedSalesTolerance,
                                    sale_cv_basis_option: formData.sales_price_basis_type,
                                    sale_cv_basis_value: formData.sales_price_basis_value,
                                  };
                                }
                                return r;
                              })
                            );
                          }
                          
                          // Clear pending active ID so it doesn't take precedence
                          setActiveBlankPurchaseId(null);
                          handleSetActiveRecap('purchase', recapId);
                        }
                      }}
                      onUnlink={(recapId) => {
                        // Check if unlinking pending (temp-) or saved (numeric) recap
                        const isPendingRecap = typeof recapId === 'string' && recapId.startsWith('temp-');
                        
                        if (isPendingRecap) {
                          setBlankPurchaseRecaps(prev => {
                            const filtered = prev.filter(r => r.id !== recapId);
                            
                            // If removing the active pending recap, clear active ID
                            if (recapId === activeBlankPurchaseId) {
                              setActiveBlankPurchaseId(null);
                              
                              // If there are no more recaps (pending or saved), clear form
                              const totalRecaps = (formData.purchase_recaps_data || []).length + filtered.length;
                              if (totalRecaps === 0) {
                                const fieldsToClear = [
                                  'purchase_product_name',
                                  'delivery_product_quantity',
                                  'delivery_product_unit',
                                  'delivery_product_tolerance',
                                  'delivery_product_tolerance_lower_value',
                                  'delivery_product_tolerance_lower_unit',
                                  'delivery_product_tolerance_upper_value',
                                  'delivery_product_tolerance_upper_unit',
                                  'purchase_price_per_ton_contract',
                                  'purchase_price_per_ton_contract_currency',
                                  'purchase_price_per_ton_adjusted',
                                  'purchase_price_per_ton_adjusted_currency',
                                  'purchase_price_basis_type',
                                  'purchase_price_basis_value',
                                  'delivery_producer',
                                  'seller_contractual'
                                ];
                                fieldsToClear.forEach(field => {
                                  originalHandleInputChange({ target: { name: field, value: '' } });
                                });
                                setProductSearchTerm('');
                              }
                            }
                            
                            return filtered;
                          });
                          showSuccess('Pending recap removed');
                        } else {
                          handleUnlinkRecap('purchase', recapId);
                        }
                      }}
                      {/* OLD onAddNew and RecapCardFlip - HIDDEN */}
                      {/* OLD PURCHASE RECAP CARD BELOW - HIDDEN */}
                      {/*
                <div className="form-group" ref={productSearchRef} style={{ position: 'relative', display: 'none' }}>
                  <div style={{ position: 'relative' }}>
                    {!selectedProduct && (
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        style={{ 
                          position: 'absolute', 
                          left: '12px', 
                          top: '14px', 
                          color: '#666',
                          pointerEvents: 'none',
                          fontSize: '14px',
                          zIndex: 1
                        }} 
                      />
                    )}
                    <input
                      type="text"
                      className="delivery-product-search-input"
                      style={{ 
                        paddingLeft: selectedProduct ? '12px' : '36px',
                        paddingRight: productSearchTerm ? '36px' : '12px',
                        width: '100%',
                        height: '44px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: selectedProduct ? '700' : '400',
                        transition: 'border-color 0.3s ease'
                      }}
                      value={productSearchTerm}
                      onChange={(e) => {
                        setProductSearchTerm(e.target.value);
                        setShowProductDropdown(true);
                        setSelectedProduct(null); // Reset selected state when typing
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Search products or enter manually..."
                    />
                    {productSearchTerm && (
                      <FontAwesomeIcon 
                        icon={faTimes} 
                        onClick={(e) => {
                          e.stopPropagation();
                          setProductSearchTerm('');
                          setSelectedProduct(null);
                          handleInputChange({
                            target: { name: 'delivery_product_category', value: '' }
                          });
                          setShowProductDropdown(false);
                        }}
                        style={{ 
                          position: 'absolute', 
                          right: '12px', 
                          top: '14px', 
                          color: '#999',
                          cursor: 'pointer',
                          fontSize: '14px',
                          zIndex: 1
                        }}
                      />
                    )}
                  </div>
                  
                  {showProductDropdown && (productSearchLoading || filteredProducts.length > 0) && (
                    <div 
                      className="delivery-product-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        zIndex: 9999,
                        marginTop: '4px'
                      }}
                    >
                      {productSearchLoading && (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                          Searching products...
                        </div>
                      )}
                      
                      {!productSearchLoading && filteredProducts.length > 0 && (
                        filteredProducts.map((variant) => (
                          <div
                            key={variant.id}
                            className="delivery-product-dropdown-item"
                            onClick={() => handleSelectProduct(variant)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>
                              {variant.display_name}
                            </div>
                            {variant.quality && (
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Quality: {variant.quality.substring(0, 80)}{variant.quality.length > 80 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      
                      {!productSearchLoading && filteredProducts.length === 0 && debouncedProductSearch.length >= 2 && (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                          <div style={{ marginBottom: '8px' }}>No products found.</div>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openProductCreator('purchase', debouncedProductSearch);
                              }}
                              style={{
                                padding: '6px 12px',
                                fontSize: '13px',
                                backgroundColor: '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 500,
                              }}
                            >
                              + Create product
                            </button>
                            <span style={{ fontSize: '12px', color: '#999' }}>or enter manually above</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <h3 className="delivery-subsection-title">Purchase Quantity</h3>
                {renderQuantityField()}
                
                <h3 className="delivery-subsection-title">Tolerance</h3>
                {renderPurchaseToleranceField()}
                
                <h3 className="delivery-subsection-title">Purchase Price per Unit (contract)</h3>
                {/* maxDecimals matches the price scale (utils/precision.js); AmountInput's
                    keystroke guard replaces validatePrice/handlePriceChange
                    (routing through them would desync the visible draft
                    from state on rejected keystrokes). */}
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    <AmountInput
                      name="purchase_price_per_ton_contract"
                      value={formData.purchase_price_per_ton_contract || ''}
                      onChange={(v) => handleInputChange({ target: { name: 'purchase_price_per_ton_contract', value: v } })}
                      maxDecimals={PRICE_DECIMALS}
                      style={{ flex: 1 }}
                      placeholder="number field"
                    />
                    <select
                      name="purchase_price_per_ton_contract_currency"
                      value={formData.purchase_price_per_ton_contract_currency || ''}
                      onChange={handlePurchaseCurrencyChange}
                      style={{ width: '140px' }}
                    >
                      <option value="">currency</option>
                      {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                        <option key={option.value} value={option.value}>{option.label || option.value}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Purchase Price per Unit (adjusted)</h3>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    <input
                      type="text"
                      name="purchase_price_per_ton_adjusted"
                      value={formData.purchase_price_per_ton_adjusted || ''}
                      onChange={handlePriceChange}
                      onBlur={handlePriceBlur}
                      className={!isPriceValid.purchase_price_per_ton_adjusted ? 'error' : ''}
                      placeholder="number field"
                      style={{ flex: 1 }}
                    />
                    <select
                      name="purchase_price_per_ton_adjusted_currency"
                      value={formData.purchase_price_per_ton_adjusted_currency || ''}
                      onChange={handlePurchaseCurrencyChange}
                      style={{ width: '140px' }}
                    >
                      <option value="">currency</option>
                      {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                        <option key={option.value} value={option.value}>{option.label || option.value}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Price basis purchase (Quality)</h3>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    <input
                      type="text"
                      name="purchase_price_basis_value"
                      value={formData.purchase_price_basis_value || ''}
                      onChange={handleNumberChange(PRICE_BASIS_DECIMALS)}
                      onBlur={handleNumberBlur}
                      className={!isQuantityValid.purchase_price_basis_value ? 'error' : ''}
                      placeholder="0.00"
                      style={{ flex: 1 }}
                      inputMode="decimal"
                    />
                    <select
                      name="purchase_price_basis_type"
                      value={formData.purchase_price_basis_type || ''}
                      onChange={handleInputChange}
                      style={{ width: '160px' }}
                    >
                      <option value="">CV basis option</option>
                      <option value="NCV(ARB)">NCV(ARB)</option>
                      <option value="GCV(ARB)">GCV(ARB)</option>
                      <option value="GCV(ADB)">GCV(ADB)</option>
                      <option value="GCV(DB)">GCV(DB)</option>
                    </select>
                  </div>
                </div>
                */}
              {/* END OLD PURCHASE RECAP CARD COMMENT */}
              </div>
              
              <div className="delivery-column">
                {/* Sales Recap Card Flip Carousel */}
                {(() => {
                  // SIMPLIFIED: Merge saved recaps + pending linked recaps + blank recaps for display
                  const savedRecaps = (formData.sales_recaps_data || []);
                  const pendingLinkedMapped = pendingLinkedSalesRecaps.map(r => ({
                    ...r,
                    isPendingLink: true // Mark as pending so we can style it differently if needed
                  }));
                  const blankSalesMapped = blankSalesRecaps.map(r => ({
                    ...r,
                    isBlank: true
                  }));
                  
                  const salesDisplayRecaps = [...savedRecaps, ...pendingLinkedMapped, ...blankSalesMapped]
                    .sort((a, b) => {
                      if (!a.transaction_number || !b.transaction_number) return 0;
                      return a.transaction_number.localeCompare(b.transaction_number);
                    });
                  
                  // Determine active recap and if it's blank
                  // CRITICAL FIX: Only use formData.active_sales_recap_id if it exists in current recaps
                  // This prevents using stale IDs after unlinking all recaps
                  let activeSalesRecapId = activeBlankSalesId;
                  if (!activeSalesRecapId && formData.active_sales_recap_id) {
                    // Only use formData ID if it exists in current recaps (prevents stale state)
                    const existsInRecaps = salesDisplayRecaps.some(r => r.id === formData.active_sales_recap_id);
                    if (existsInRecaps) {
                      activeSalesRecapId = formData.active_sales_recap_id;
                    }
                  }
                  // Final fallback to first available recap
                  if (!activeSalesRecapId) {
                    activeSalesRecapId = salesDisplayRecaps[0]?.id;
                  }
                  
                  const activeSalesRecap = salesDisplayRecaps.find(r => r.id === activeSalesRecapId);
                  const isSalesActiveBlank = activeSalesRecap?.isBlank || false;
                  
                  return (
                    <RecapCardFlip
                      recaps={salesDisplayRecaps}
                      activeRecapId={activeSalesRecapId}
                      onRecapChange={(recapId) => handleRecapNavigation('sales', recapId)}
                      onUnlink={(recapId) => {
                        const isBlankRecap = typeof recapId === 'string' && recapId.startsWith('blank-');
                        if (isBlankRecap) {
                          setBlankSalesRecaps(prev => prev.filter(r => r.id !== recapId));
                          delete blankRecapValues[recapId];
                          if (recapId === activeBlankSalesId) {
                            setActiveBlankSalesId(null);
                          }
                          showSuccess('Blank recap removed');
                        } else {
                          handleUnlinkRecap('sales', recapId);
                        }
                      }}
                      onAddNew={() => setShowSalesRecapModal(true)}
                      recapType="sales"
                    >
                      {/* SALES FORM FIELDS */}
                      
                      {/* For SAVED recaps: All fields EDITABLE, edits buffered in savedRecapEdits and PATCHed on delivery save */}
                      {!isSalesActiveBlank && (() => {
                        const editedRecap = getEditedRecap(activeSalesRecap);
                        const recapId = editedRecap?.id;
                        const parsedTolerance = parseToleranceString(editedRecap?.sale_shipping_tolerance || '');
                        const tolLowerValue = editedRecap?.sale_shipping_tolerance_lower_value ?? parsedTolerance.lowerValue ?? '';
                        const tolLowerUnit = editedRecap?.sale_shipping_tolerance_lower_unit ?? parsedTolerance.lowerUnit ?? '%';
                        const tolUpperValue = editedRecap?.sale_shipping_tolerance_upper_value ?? parsedTolerance.upperValue ?? '';
                        const tolUpperUnit = editedRecap?.sale_shipping_tolerance_upper_unit ?? parsedTolerance.upperUnit ?? '%';
                        const saleToleranceCols = {
                          lowerValueCol: 'sale_shipping_tolerance_lower_value',
                          lowerUnitCol: 'sale_shipping_tolerance_lower_unit',
                          upperValueCol: 'sale_shipping_tolerance_upper_value',
                          upperUnitCol: 'sale_shipping_tolerance_upper_unit',
                          stringCol: 'sale_shipping_tolerance',
                        };
                        const unitCode = editedRecap?.sale_contract_quantity_unit;
                        const unitSelectValue = (() => {
                          if (!unitCode) return '';
                          const m = units.find(u => u.code === unitCode || String(u.id) === String(unitCode));
                          return m ? m.id : '';
                        })();
                        // BUNKERS (fuels): per-product picker card. Cargo keeps the layout below.
                        if (editedRecap?.commodity_type === 'fuels') {
                          return (
                            <BunkersRecapProductCard
                              key={recapId}
                              recap={editedRecap}
                              side="sales"
                              units={units}
                              currencies={dropdownOptions.currencies}
                              productEdits={savedRecapProductEdits[recapId]}
                              onProductFieldChange={(pid, field, val) => handleProductFieldChange(recapId, pid, field, val)}
                              onRecapFieldChange={(field, val) => handleSavedRecapFieldChange(recapId, field, val)}
                            />
                          );
                        }
                        return (
                          <div className="recap-editable-container">
                            <h3 className="delivery-subsection-title">Product</h3>
                            <div className="form-group">
                              <RecapProductSearch
                                label=""
                                value={editedRecap?.product || ''}
                                variantId={editedRecap?.stock_item_variant || null}
                                // A recap with several products sets them on the recap and below.
                                disabled={hasSeveralProducts(editedRecap)}
                                onChange={(variantId, displayName, variant) => {
                                  if (!displayName && !variantId) {
                                    handleSavedRecapFieldChange(recapId, 'product', '');
                                    handleSavedRecapFieldChange(recapId, 'stock_item_variant', null);
                                    return;
                                  }
                                  const baseProduct = variant?.base_stock_item_data?.product_type_display || extractBaseProduct(displayName) || displayName;
                                  handleSavedRecapFieldChange(recapId, 'product', baseProduct);
                                  handleSavedRecapFieldChange(recapId, 'stock_item_variant', variantId || null);
                                }}
                              />
                            </div>

                            {hasSeveralProducts(editedRecap) && (
                              <SeveralProductsNote
                                recap={editedRecap} side="sales" units={units}
                                deliveryId={isExistingDelivery ? deliveryId : null}
                                onSaved={refreshRecapFigures}
                                onSavedFlash={(msg, ok) => (ok ? showSuccess(msg) : showError(msg))}
                              />
                            )}

                            <h3 className="delivery-subsection-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              Sales Quantity
                              {activeSalesRecap?.is_partial_allocation && (
                                <span
                                  className="allocation-badge"
                                  style={{
                                    backgroundColor: '#9b59b6',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'normal',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={`Using ${activeSalesRecap.allocated_quantity} of ${activeSalesRecap.total_quantity || activeSalesRecap.sale_contract_quantity}`}
                                >
                                  <FontAwesomeIcon icon={faBalanceScale} style={{ marginRight: '4px', fontSize: '10px' }} />
                                  {activeSalesRecap.allocated_quantity} / {activeSalesRecap.total_quantity || activeSalesRecap.sale_contract_quantity} {(() => {
                                    const code = activeSalesRecap.sale_contract_quantity_unit;
                                    const matchingUnit = units.find(u => u.code === code);
                                    return matchingUnit ? matchingUnit.name : code;
                                  })()}
                                </span>
                              )}
                            </h3>
                            {(() => {
                              // Lock Quantity + Unit when this recap is partially allocated.
                              // Reason: editing recap qty here would silently mutate the contract
                              // for every other delivery sharing this recap. To change THIS
                              // delivery's slice, the user should unlink + relink via the modal;
                              // to amend the recap itself, they should do it on the recap screen.
                              // A deal with several products: each one's quantity on
                              // this delivery is set in its products table (above).
                              const isPartial = !!activeSalesRecap?.is_partial_allocation || hasSeveralProducts(editedRecap);
                              const lockedTooltip = hasSeveralProducts(editedRecap)
                                ? 'This deal has several products: set how much of each this delivery carries in the table above.'
                                : isPartial
                                ? 'Recap is partially allocated. Unlink + relink to change this delivery\u2019s slice, or edit the recap directly on the recap screen.'
                                : undefined;
                              const lockedStyle = isPartial
                                ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }
                                : {};
                              return (
                                <div className="form-group">
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                      type="text"
                                      value={editedRecap?.sale_contract_quantity ?? ''}
                                      onChange={refuseExtraDecimals(QUANTITY_DECIMALS, (e) => handleSavedRecapFieldChange(recapId, 'sale_contract_quantity', e.target.value))}
                                      placeholder="Quantity"
                                      readOnly={isPartial}
                                      disabled={isPartial}
                                      title={lockedTooltip}
                                      style={{ flex: 1, ...lockedStyle }}
                                    />
                                    <select
                                      value={unitSelectValue}
                                      onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const u = units.find(unit => String(unit.id) === String(selectedId));
                                        handleSavedRecapFieldChange(recapId, 'sale_contract_quantity_unit', u?.code || selectedId);
                                      }}
                                      disabled={isPartial}
                                      title={lockedTooltip}
                                      style={{ width: '140px', ...lockedStyle }}
                                    >
                                      <option value="">Unit</option>
                                      {units.map(unit => (
                                        <option key={unit.id} value={unit.id}>{unit.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              );
                            })()}

                            <h3 className="delivery-subsection-title">Tolerance</h3>
                            <div className="form-group">
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={tolLowerValue || ''}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, saleToleranceCols, 'sale_shipping_tolerance_lower_value', e.target.value)}
                                    placeholder="Lower"
                                    style={{ flex: 1 }}
                                  />
                                  <select
                                    value={tolLowerUnit || '%'}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, saleToleranceCols, 'sale_shipping_tolerance_lower_unit', e.target.value)}
                                    style={{ width: '70px' }}
                                  >
                                    <option value="%">%</option>
                                    <option value="min">min</option>
                                    <option value="MT">MT</option>
                                  </select>
                                </div>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={tolUpperValue || ''}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, saleToleranceCols, 'sale_shipping_tolerance_upper_value', e.target.value)}
                                    placeholder="Upper"
                                    style={{ flex: 1 }}
                                  />
                                  <select
                                    value={tolUpperUnit || '%'}
                                    onChange={(e) => updateSavedRecapTolerance(recapId, editedRecap, saleToleranceCols, 'sale_shipping_tolerance_upper_unit', e.target.value)}
                                    style={{ width: '70px' }}
                                  >
                                    <option value="%">%</option>
                                    <option value="max">max</option>
                                    <option value="MT">MT</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            <h3 className="delivery-subsection-title">Sales Price per Unit (contract)</h3>
                            <div className="form-group">
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                  type="text"
                                  value={editedRecap?.sale_contract_price_value ?? ''}
                                  onChange={refuseExtraDecimals(PRICE_DECIMALS, (e) => handleSavedRecapFieldChange(recapId, 'sale_contract_price_value', e.target.value))}
                                  placeholder="Price"
                                  readOnly={hasSeveralProducts(editedRecap)}
                                  disabled={hasSeveralProducts(editedRecap)}
                                  title={hasSeveralProducts(editedRecap) ? 'This deal has several products: each one\u2019s price on this delivery is in the table above.' : undefined}
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={editedRecap?.sale_contract_price_currency || ''}
                                  onChange={(e) => handleSavedRecapFieldChange(recapId, 'sale_contract_price_currency', e.target.value)}
                                  style={{ width: '140px' }}
                                >
                                  <option value="">Currency</option>
                                  {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label || option.value}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <h3 className="delivery-subsection-title">Price Basis Sales (Quality)</h3>
                            <div className="form-group">
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                  type="text"
                                  value={editedRecap?.sale_cv_basis_value ?? ''}
                                  onChange={(e) => handleSavedRecapFieldChange(recapId, 'sale_cv_basis_value', e.target.value)}
                                  placeholder="Value"
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={editedRecap?.sale_cv_basis_option || ''}
                                  onChange={(e) => handleSavedRecapFieldChange(recapId, 'sale_cv_basis_option', e.target.value)}
                                  style={{ width: '160px' }}
                                >
                                  <option value="">CV basis option</option>
                                  <option value="NCV(ARB)">NCV(ARB)</option>
                                  <option value="GCV(ARB)">GCV(ARB)</option>
                                  <option value="GCV(ADB)">GCV(ADB)</option>
                                  <option value="GCV(DB)">GCV(DB)</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* For BLANK recaps: Show editable fields that update blankRecapValues */}
                      {isSalesActiveBlank && (
                        <div className="recap-editable-container">
                          <h3 className="delivery-subsection-title">Product</h3>
                          <div className="form-group">
                            <RecapProductSearch
                              label=""
                              value={blankRecapValues[activeSalesRecapId]?.product || ''}
                              variantId={blankRecapValues[activeSalesRecapId]?.stock_item_variant}
                              onCreateProduct={(searchTerm) => openProductCreator('sales', searchTerm)}
                              filterFunction={(variant) => {
                                // NEW: Get used products for sales side (excluding current blank)
                                const usedSalesProducts = getUsedProductsForSide('sales', activeSalesRecapId);
                                
                                // Check if this variant's product is already used
                                const variantProduct = extractBaseProduct(variant.display_name || '');
                                if (variantProduct && usedSalesProducts.has(variantProduct)) {
                                  return false; // Product already used by another sales recap - hide
                                }
                                
                                // Keep existing unit/currency validation (optional - can be removed if not needed)
                                const allExisting = getAllRecapsIncludingFilledBlanks().filter(r => r.id !== activeSalesRecapId);
                                if (allExisting.length > 0) {
                                  const firstRecap = allExisting[0];
                                  
                                  // Check unit (if variant has one)
                                  if (variant.unit_id) {
                                    const firstUnit = firstRecap.purchase_contract_quantity_unit || firstRecap.sale_contract_quantity_unit;
                                    const normalizedFirst = normalizeUnit(firstUnit);
                                    const normalizedNew = normalizeUnit(variant.unit_id);
                                    if (normalizedFirst && normalizedNew && normalizedFirst !== normalizedNew) {
                                      return false; // Different unit - hide
                                    }
                                  }
                                  
                                  // Check currency (if variant has one)
                                  if (variant.price_currency) {
                                    const firstCurrency = firstRecap.purchase_contract_price_currency || firstRecap.sale_contract_price_currency;
                                    if (firstCurrency && variant.price_currency !== firstCurrency) {
                                      return false; // Different currency - hide
                                    }
                                  }
                                }
                                
                                return true; // All checks passed - show this product
                              }}
                              onChange={(variantId, displayName, variant) => {
                                // If product is being cleared, clear ALL related fields
                                if (!displayName && !variantId) {
                                  setBlankRecapValues(prev => ({
                                    ...prev,
                                    [activeSalesRecapId]: {
                                      product: '',
                                      stock_item_variant: null,
                                      quantity: '',
                                      quantity_unit: '',
                                      price_value: '',
                                      price_currency: '',
                                      cv_basis_option: '',
                                      cv_basis_value: '',
                                      tolerance_lower_value: '',
                                      tolerance_lower_unit: '%',
                                      tolerance_upper_value: '',
                                      tolerance_upper_unit: '%'
                                    }
                                  }));
                                  return;
                                }
                                
                                // CRITICAL FIX: Extract base product type for storage
                                // displayName is like "Steam Coal | south_africa | Dotcloud Services - Steam Coal 4"
                                // We only want to store "Steam Coal" in the recap product field
                                const baseProduct = variant?.base_stock_item_data?.product_type_display || extractBaseProduct(displayName) || displayName;
                                
                                // No validation needed - dropdown only shows compatible products
                                // Update product name with ONLY the base product
                                handleBlankRecapFieldChange(activeSalesRecapId, 'product', baseProduct);
                                
                                // CRITICAL: Always update stock_item_variant, even when clearing (null)
                                // Otherwise old variant ID stays and triggers product detail loading
                                handleBlankRecapFieldChange(activeSalesRecapId, 'stock_item_variant', variantId || null);
                                
                                // Auto-fill fields from variant if available
                                if (variant) {
                                  if (variant.quantity) {
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'quantity', variant.quantity);
                                  }
                                  if (variant.unit_id) {
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'quantity_unit', variant.unit_id);
                                  }
                                  if (variant.sale_price) {
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'price_value', variant.sale_price);
                                  }
                                  if (variant.price_currency) {
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'price_currency', variant.price_currency);
                                  }
                                  // Auto-fill tolerance into blank recap (NEW)
                                  if (variant.tolerance_lower_value || variant.tolerance_upper_value) {
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_lower_value', variant.tolerance_lower_value || '');
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_lower_unit', variant.tolerance_lower_unit || '%');
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_upper_value', variant.tolerance_upper_value || '');
                                    handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_upper_unit', variant.tolerance_upper_unit || '%');
                                  }
                                }
                              }}
                            />
                          </div>
                          
                          <h3 className="delivery-subsection-title">Sales Quantity</h3>
                          <div className="form-group">
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={blankRecapValues[activeSalesRecapId]?.quantity || ''}
                                onChange={refuseExtraDecimals(QUANTITY_DECIMALS, (e) => handleBlankRecapFieldChange(activeSalesRecapId, 'quantity', e.target.value))}
                                placeholder="Quantity"
                                style={{ flex: 1 }}
                              />
                              <select
                                value={blankRecapValues[activeSalesRecapId]?.quantity_unit || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'quantity_unit', e.target.value)}
                                style={{ width: '140px' }}
                              >
                                <option value="">Unit</option>
                                {units.map(unit => (
                                  <option key={unit.id} value={unit.id}>{unit.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <h3 className="delivery-subsection-title">Tolerance</h3>
                          <div className="form-group">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={blankRecapValues[activeSalesRecapId]?.tolerance_lower_value || ''}
                                  onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_lower_value', e.target.value)}
                                  placeholder="Lower"
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={blankRecapValues[activeSalesRecapId]?.tolerance_lower_unit || '%'}
                                  onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_lower_unit', e.target.value)}
                                  style={{ width: '70px' }}
                                >
                                  <option value="%">%</option>
                                  <option value="min">min</option>
                                  <option value="MT">MT</option>
                                </select>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={blankRecapValues[activeSalesRecapId]?.tolerance_upper_value || ''}
                                  onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_upper_value', e.target.value)}
                                  placeholder="Upper"
                                  style={{ flex: 1 }}
                                />
                                <select
                                  value={blankRecapValues[activeSalesRecapId]?.tolerance_upper_unit || '%'}
                                  onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'tolerance_upper_unit', e.target.value)}
                                  style={{ width: '70px' }}
                                >
                                  <option value="%">%</option>
                                  <option value="max">max</option>
                                  <option value="MT">MT</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          
                          <h3 className="delivery-subsection-title">Sales Price per Unit (contract)</h3>
                          <div className="form-group">
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={blankRecapValues[activeSalesRecapId]?.price_value || ''}
                                onChange={refuseExtraDecimals(PRICE_DECIMALS, (e) => handleBlankRecapFieldChange(activeSalesRecapId, 'price_value', e.target.value))}
                                placeholder="Price"
                                style={{ flex: 1 }}
                              />
                              <select
                                value={blankRecapValues[activeSalesRecapId]?.price_currency || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'price_currency', e.target.value)}
                                style={{ width: '140px' }}
                              >
                                <option value="">Currency</option>
                                {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label || option.value}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <h3 className="delivery-subsection-title">Price Basis Sales (Quality)</h3>
                          <div className="form-group">
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="text"
                                value={blankRecapValues[activeSalesRecapId]?.cv_basis_value || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'cv_basis_value', e.target.value)}
                                placeholder="Value"
                                style={{ flex: 1 }}
                              />
                              <select
                                value={blankRecapValues[activeSalesRecapId]?.cv_basis_option || ''}
                                onChange={(e) => handleBlankRecapFieldChange(activeSalesRecapId, 'cv_basis_option', e.target.value)}
                                style={{ width: '160px' }}
                              >
                                <option value="">CV basis option</option>
                                <option value="NCV(ARB)">NCV(ARB)</option>
                                <option value="GCV(ARB)">GCV(ARB)</option>
                                <option value="GCV(ADB)">GCV(ADB)</option>
                                <option value="GCV(DB)">GCV(DB)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* OLD COMPLEX SALES LOGIC - TO BE REMOVED LATER */}
                      {/*
                      onRecapChange={(recapId) => {
                        // Check if navigating to pending (temp-) or saved (numeric) recap
                        const isPendingRecap = typeof recapId === 'string' && recapId.startsWith('temp-');
                        const isUnsavedDelivery = !deliveryId || deliveryId === 'new' || deliveryId === 'undefined';
                        
                        // Use frontend logic if: pending recap OR unsaved delivery (regardless of recap type)
                        if (isPendingRecap || isUnsavedDelivery) {
                          // CRITICAL: Save current form values to the active recap BEFORE switching
                          if (activeBlankSalesId && activeBlankSalesId !== recapId) {
                            // Format tolerance from component fields
                            const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
                              const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
                              const upper = upperValue ? `${upperValue}${upperUnit}` : '';
                              if (lower && upper) return `${lower} to ${upper}`;
                              else if (lower) return lower;
                              else if (upper) return upper;
                              return '';
                            };
                            
                            const formattedTolerance = formatToleranceField(
                              formData.sales_tolerance_lower_value,
                              formData.sales_tolerance_lower_unit,
                              formData.sales_tolerance_upper_value,
                              formData.sales_tolerance_upper_unit
                            );
                            
          setBlankSalesRecaps(prev => 
            prev.map(r => {
              if (r.id === activeBlankSalesId) {
                return {
                  ...r,
                  product: formData.sales_product_name,
                  sale_contract_quantity: formData.sales_product_quantity,
                  sale_contract_quantity_unit: formData.sales_product_unit,
                  sale_contract_price_value: formData.sales_price_per_ton_contract,
                  sale_contract_price_currency: formData.sale_price_per_ton_contract_currency,
                  sale_adjusted_price_value: formData.sales_price_per_ton_adjusted,
                  sale_adjusted_price_currency: formData.sale_price_per_ton_adjusted_currency,
                  sale_shipping_tolerance: formattedTolerance,
                  sale_cv_basis_option: formData.sales_price_basis_type,
                  sale_cv_basis_value: formData.sales_price_basis_value,
                };
              }
              return r;
            })
          );
        }
        
        // Update active pending recap ID
        setActiveBlankSalesId(recapId);
                          
                          const recap = blankSalesRecaps.find(r => r.id === recapId);
                          if (recap) {
                            // Parse tolerance string into component fields
                            const parsedTolerance = parseToleranceString(recap.sale_shipping_tolerance);
                            
                            // Use originalHandleInputChange to populate fields without syncing back
                            // (we're reading FROM the recap, not writing TO it)
                            const fieldMappings = {
                              'sales_product_name': recap.product,
                              'sales_product_quantity': recap.sale_contract_quantity,
                              'sales_product_unit': recap.sale_contract_quantity_unit,
                              'sales_price_per_ton_contract': recap.sale_contract_price_value,
                              'sale_price_per_ton_contract_currency': recap.sale_contract_price_currency,
                              'sales_price_per_ton_adjusted': recap.sale_adjusted_price_value,
                              'sale_price_per_ton_adjusted_currency': recap.sale_adjusted_price_currency,
                              'sales_tolerance': recap.sale_shipping_tolerance,
                              'sales_tolerance_lower_value': parsedTolerance.lowerValue,
                              'sales_tolerance_lower_unit': parsedTolerance.lowerUnit,
                              'sales_tolerance_upper_value': parsedTolerance.upperValue,
                              'sales_tolerance_upper_unit': parsedTolerance.upperUnit,
                              'sales_price_basis_type': recap.sale_cv_basis_option,
                              'sales_price_basis_value': recap.sale_cv_basis_value,
                              'price_basis_sale_quality': (recap.sale_cv_basis_option || recap.sale_cv_basis_value)
                                ? [recap.sale_cv_basis_option, recap.sale_cv_basis_value ? `${recap.sale_cv_basis_value} kcal/kg` : ''].filter(Boolean).join(' ')
                                : '',
                            };
                            Object.entries(fieldMappings).forEach(([fieldName, fieldValue]) => {
                              originalHandleInputChange({ target: { name: fieldName, value: fieldValue ?? '' } });
                            });
                            // Also set sales product search term to display the product
                            if (recap.product) {
                              setSalesProductSearchTerm(recap.product);
                            } else {
                              setSalesProductSearchTerm('');
                            }
                          }
                        } else {
                          // Navigating to a saved recap on a saved delivery
                          // CRITICAL: Save BOTH pending purchase AND sales recap values BEFORE API call
                          // (API response will overwrite form fields, so we must preserve pending values)
                          
                          const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
                            const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
                            const upper = upperValue ? `${upperValue}${upperUnit}` : '';
                            if (lower && upper) return `${lower} to ${upper}`;
                            else if (lower) return lower;
                            else if (upper) return upper;
                            return '';
                          };
                          
                          // Save pending sales recap if active
                          if (activeBlankSalesId) {
                            const formattedTolerance = formatToleranceField(
                              formData.sales_tolerance_lower_value,
                              formData.sales_tolerance_lower_unit,
                              formData.sales_tolerance_upper_value,
                              formData.sales_tolerance_upper_unit
                            );
                            
                            setBlankSalesRecaps(prev => 
                              prev.map(r => {
                                if (r.id === activeBlankSalesId) {
                                  return {
                                    ...r,
                                    product: formData.sales_product_name,
                                    sale_contract_quantity: formData.sales_product_quantity,
                                    sale_contract_quantity_unit: formData.sales_product_unit,
                                    sale_contract_price_value: formData.sales_price_per_ton_contract,
                                    sale_contract_price_currency: formData.sale_price_per_ton_contract_currency,
                                    sale_adjusted_price_value: formData.sales_price_per_ton_adjusted,
                                    sale_adjusted_price_currency: formData.sale_price_per_ton_adjusted_currency,
                                    sale_shipping_tolerance: formattedTolerance,
                                    sale_cv_basis_option: formData.sales_price_basis_type,
                                    sale_cv_basis_value: formData.sales_price_basis_value,
                                  };
                                }
                                return r;
                              })
                            );
                          }
                          
                          // ALSO save pending purchase recap if active (prevents cross-side wipe)
                          if (activeBlankPurchaseId) {
                            const formattedPurchaseTolerance = formatToleranceField(
                              formData.delivery_product_tolerance_lower_value,
                              formData.delivery_product_tolerance_lower_unit,
                              formData.delivery_product_tolerance_upper_value,
                              formData.delivery_product_tolerance_upper_unit
                            );
                            
                            setBlankPurchaseRecaps(prev => 
                              prev.map(r => {
                                if (r.id === activeBlankPurchaseId) {
                                  return {
                                    ...r,
                                    product: formData.purchase_product_name,
                                    purchase_contract_quantity: formData.delivery_product_quantity,
                                    purchase_contract_quantity_unit: formData.delivery_product_unit,
                                    purchase_contract_price_value: formData.purchase_price_per_ton_contract,
                                    purchase_contract_price_currency: formData.purchase_price_per_ton_contract_currency,
                                    purchase_adjusted_price_value: formData.purchase_price_per_ton_adjusted,
                                    purchase_adjusted_price_currency: formData.purchase_price_per_ton_adjusted_currency,
                                    purchase_shipping_tolerance: formattedPurchaseTolerance,
                                    purchase_price_basis_option: formData.purchase_price_basis_type,
                                    purchase_price_basis_value: formData.purchase_price_basis_value,
                                  };
                                }
                                return r;
                              })
                            );
                          }
                          
                          // Clear pending active ID so it doesn't take precedence
                          setActiveBlankSalesId(null);
                          handleSetActiveRecap('sales', recapId);
                        }
                      }}
                      onUnlink={(recapId) => {
                        // Check if unlinking pending (temp-) or saved (numeric) recap
                        const isPendingRecap = typeof recapId === 'string' && recapId.startsWith('temp-');
                        
                        if (isPendingRecap) {
                          setBlankSalesRecaps(prev => {
                            const filtered = prev.filter(r => r.id !== recapId);
                            
                            // If removing the active pending recap, clear active ID
                            if (recapId === activeBlankSalesId) {
                              setActiveBlankSalesId(null);
                              
                              // If there are no more recaps (pending or saved), clear form
                              const totalRecaps = (formData.sales_recaps_data || []).length + filtered.length;
                              if (totalRecaps === 0) {
                                const fieldsToClear = [
                                  'sales_product_name',
                                  'sales_product_quantity',
                                  'sales_product_unit',
                                  'sales_tolerance',
                                  'sales_tolerance_lower_value',
                                  'sales_tolerance_lower_unit',
                                  'sales_tolerance_upper_value',
                                  'sales_tolerance_upper_unit',
                                  'sales_price_per_ton_contract',
                                  'sales_price_per_ton_contract_currency',
                                  'sales_price_per_ton_adjusted',
                                  'sale_price_per_ton_adjusted_currency',
                                  'sales_price_basis_type',
                                  'sales_price_basis_value',
                                  'buyer_contractual',
                                  'delivery_end_user'
                                ];
                                fieldsToClear.forEach(field => {
                                  originalHandleInputChange({ target: { name: field, value: '' } });
                                });
                                setSalesProductSearchTerm('');
                              }
                            }
                            
                            return filtered;
                          });
                          showSuccess('Pending recap removed');
                        } else {
                          handleUnlinkRecap('sales', recapId);
                        }
                      }}
                      onAddNew={() => {}}
                      recapType="sales"
                    >
                      {/* OLD DUPLICATE SALES FORM - COMPLETELY HIDDEN */}
                      <div style={{ display: 'none' }}>
                      <h3 className="delivery-subsection-title">Product</h3>
                <div className="form-group" ref={salesProductSearchRef} style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    {!selectedSalesProduct && (
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        style={{ 
                          position: 'absolute', 
                          left: '12px', 
                          top: '14px', 
                          color: '#666',
                          pointerEvents: 'none',
                          fontSize: '14px',
                          zIndex: 1
                        }} 
                      />
                    )}
                    <input
                      type="text"
                      className="delivery-product-search-input"
                      style={{ 
                        paddingLeft: selectedSalesProduct ? '12px' : '36px',
                        paddingRight: salesProductSearchTerm ? '36px' : '12px',
                        width: '100%',
                        height: '44px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: selectedSalesProduct ? '700' : '400',
                        transition: 'border-color 0.3s ease'
                      }}
                      value={salesProductSearchTerm}
                      onChange={(e) => {
                        setSalesProductSearchTerm(e.target.value);
                        setShowSalesProductDropdown(true);
                        setSelectedSalesProduct(null); // Reset selected state when typing
                      }}
                      onFocus={() => setShowSalesProductDropdown(true)}
                      placeholder="Search products or enter manually..."
                    />
                    {salesProductSearchTerm && (
                      <FontAwesomeIcon 
                        icon={faTimes} 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSalesProductSearchTerm('');
                          setSelectedSalesProduct(null);
                          handleInputChange({
                            target: { name: 'sales_product_category', value: '' }
                          });
                          setShowSalesProductDropdown(false);
                        }}
                        style={{ 
                          position: 'absolute', 
                          right: '12px', 
                          top: '14px', 
                          color: '#999',
                          cursor: 'pointer',
                          fontSize: '14px',
                          zIndex: 1
                        }}
                      />
                    )}
                  </div>
                  
                  {showSalesProductDropdown && (salesProductSearchLoading || filteredSalesProducts.length > 0) && (
                    <div 
                      className="delivery-product-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        zIndex: 9999,
                        marginTop: '4px'
                      }}
                    >
                      {salesProductSearchLoading && (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                          Searching products...
                        </div>
                      )}
                      
                      {!salesProductSearchLoading && filteredSalesProducts.length > 0 && (
                        filteredSalesProducts.map((variant) => (
                          <div
                            key={variant.id}
                            className="delivery-product-dropdown-item"
                            onClick={() => handleSelectSalesProduct(variant)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>
                              {variant.display_name}
                            </div>
                            {variant.quality && (
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                Quality: {variant.quality.substring(0, 80)}{variant.quality.length > 80 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      
                      {!salesProductSearchLoading && filteredSalesProducts.length === 0 && debouncedSalesProductSearch.length >= 2 && (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                          <div style={{ marginBottom: '8px' }}>No products found.</div>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openProductCreator('sales', debouncedSalesProductSearch);
                              }}
                              style={{
                                padding: '6px 12px',
                                fontSize: '13px',
                                backgroundColor: '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 500,
                              }}
                            >
                              + Create product
                            </button>
                            <span style={{ fontSize: '12px', color: '#999' }}>or enter manually above</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <h3 className="delivery-subsection-title">Sales Quantity</h3>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    <input
                      type="text"
                      name="sales_product_quantity"
                      value={formData.sales_product_quantity || ''}
                      onChange={handleNumberChange(QUANTITY_DECIMALS)}
                      className={!isQuantityValid.sales_product_quantity ? 'error' : ''}
                      placeholder="0.00"
                      style={{ flex: 1 }}
                      inputMode="decimal"
                    />
                    <select
                      name="sales_product_unit"
                      value={formData.sales_product_unit || ''}
                      onChange={handleSalesUnitChange}
                      style={{ width: '140px' }}
                    >
                      <option value="">Unit</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Tolerance</h3>
                {renderSalesToleranceField()}
                
                <h3 className="delivery-subsection-title">Sales Price per Unit (contract)</h3>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    {/* Same rationale as the purchase-price AmountInput above. */}
                    <AmountInput
                      name="sales_price_per_ton_contract"
                      value={formData.sales_price_per_ton_contract || ''}
                      onChange={(v) => handleInputChange({ target: { name: 'sales_price_per_ton_contract', value: v } })}
                      maxDecimals={PRICE_DECIMALS}
                      style={{ flex: 1 }}
                      placeholder="number field"
                    />
                    <select
                      name="sale_price_per_ton_contract_currency"
                      value={formData.sale_price_per_ton_contract_currency || ''}
                      onChange={handleSalesCurrencyChange}
                      style={{ width: '140px' }}
                    >
                      <option value="">currency</option>
                      {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                        <option key={option.value} value={option.value}>{option.label || option.value}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Sales Price per Unit (adjusted)</h3>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    <input
                      type="text"
                      name="sales_price_per_ton_adjusted"
                      value={formData.sales_price_per_ton_adjusted || ''}
                      onChange={handlePriceChange}
                      onBlur={handlePriceBlur}
                      className={!isPriceValid.sales_price_per_ton_adjusted ? 'error' : ''}
                      placeholder="number field"
                      style={{ flex: 1 }}
                    />
                    <select
                      name="sale_price_per_ton_adjusted_currency"
                      value={formData.sale_price_per_ton_adjusted_currency || ''}
                      onChange={handleSalesCurrencyChange}
                      style={{ width: '140px' }}
                    >
                      <option value="">currency</option>
                      {dropdownOptions.currencies && dropdownOptions.currencies.map((option) => (
                        <option key={option.value} value={option.value}>{option.label || option.value}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <h3 className="delivery-subsection-title">Price basis sales (Quality)</h3>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                    <input
                      type="text"
                      name="sales_price_basis_value"
                      value={formData.sales_price_basis_value || ''}
                      onChange={handleNumberChange(PRICE_BASIS_DECIMALS)}
                      onBlur={handleNumberBlur}
                      className={!isQuantityValid.sales_price_basis_value ? 'error' : ''}
                      placeholder="0.00"
                      style={{ flex: 1 }}
                      inputMode="decimal"
                    />
                    <select
                      name="sales_price_basis_type"
                      value={formData.sales_price_basis_type || ''}
                      onChange={handleInputChange}
                      style={{ width: '160px' }}
                    >
                      <option value="">CV basis option</option>
                      <option value="NCV(ARB)">NCV(ARB)</option>
                      <option value="GCV(ARB)">GCV(ARB)</option>
                      <option value="GCV(ADB)">GCV(ADB)</option>
                      <option value="GCV(DB)">GCV(DB)</option>
                    </select>
                  </div>
                </div>
                    </div>
                    {/* END OLD DUPLICATE SALES FORM */}
                    </RecapCardFlip>
                  );
                })()}
              </div>
            </div>
            
            {/* Linked Freight Recap strip — read-only fixture summary + Unlink.
                Freight links live in the associated JSON (no M2M); rows come
                from freight_recaps_data. The delivery's freight fields on the
                HOW tab were prefilled from this fixture at link time (blanks
                only) and stay independently editable. */}
            {(formData.freight_recaps_data || []).length > 0 && (
              <div className="delivery-recap-totals-container">
                <h3 className="delivery-recap-totals-header">Linked Freight Recap</h3>
                {(formData.freight_recaps_data || []).map((fr) => (
                  <div
                    key={fr.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '16px',
                      flexWrap: 'wrap', padding: '10px 14px',
                      border: '1px solid #d0d5dd', borderRadius: '8px',
                      background: '#f8fafc', marginBottom: '8px',
                    }}
                  >
                    <span style={{ fontWeight: 700, color: '#003B5C' }}>
                      {fr.transaction_number}
                    </span>
                    {fr.freight_price && (
                      <span title="Fixture freight rate (prefilled the HOW tab where blank)">
                        {fr.currency || 'USD'} {fr.freight_price}
                        {fr.quantity_unit ? ` / ${fr.quantity_unit}` : ''}
                      </span>
                    )}
                    {(fr.load_port || fr.discharge_port) && (() => {
                      // A fixture may call at several ports. The card has room
                      // for the route ends only, so show first load → last
                      // discharge with a "+N more" hint (and the full
                      // itinerary on hover) rather than silently hiding calls.
                      const extra = Math.max(
                        0,
                        (fr.load_ports?.length || 0) + (fr.discharge_ports?.length || 0) - 2,
                      );
                      const full = [...(fr.load_ports || []), ...(fr.discharge_ports || [])]
                        .join(' → ');
                      return (
                        <span title={full || undefined}>
                          {fr.load_port || '—'} → {fr.discharge_port || '—'}
                          {extra > 0 ? ` (+${extra} more)` : ''}
                        </span>
                      );
                    })()}
                    {fr.vessel && <span>{fr.vessel}</span>}
                    {fr.owners && <span>Owners: {fr.owners}</span>}
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleUnlinkFreight(fr)}
                      style={{ marginLeft: 'auto' }}
                      title="Unlink this freight recap (releases it back to ACTIVE)"
                    >
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Recap Totals Section */}
            <div className="delivery-recap-totals-container">
              <h3 className="delivery-recap-totals-header">Recap Totals</h3>
              
              <div className="delivery-two-column-grid">
                <div className="delivery-column">
                  <h3 className="delivery-subsection-title">Average Purchase price per unit (adjusted)</h3>
                  <div className="form-group">
                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                      <input
                        type="text"
                        name="total_purchase_price_per_ton_adjusted"
                        value={formData.total_purchase_price_per_ton_adjusted || ''}
                        onChange={handlePriceChange}
                        onBlur={handlePriceBlur}
                        className={!isPriceValid.total_purchase_price_per_ton_adjusted ? 'error' : ''}
                        placeholder="0.00"
                        style={{ flex: 1 }}
                        inputMode="decimal"
                      />
                      <div
                        style={{ 
                          width: '140px',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'not-allowed',
                          color: '#666',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '38px'
                        }}
                        title="Currency is automatically set from recap data"
                      >
                        {formData.total_purchase_price_per_ton_adjusted_currency || 'currency'}
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="delivery-subsection-title">Total Purchase Quantity Range</h3>
                  <div className="form-group">
                    {formData.purchase_range_is_exact ? (
                      <div style={{ 
                        padding: '8px 12px', 
                        backgroundColor: '#f0f7ff', 
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '14px',
                        border: '1px solid #d0e3ff'
                      }}>
                        N/A (Exact quantity - no tolerance)
                      </div>
                    ) : (
                      <div className="quantity-range-display">
                        <span className="range-value">{formData.purchase_quantity_range_min || '0.00'}</span>
                        <span className="range-separator">to</span>
                        <span className="range-value">{formData.purchase_quantity_range_max || '0.00'}</span>
                        <span className="range-unit">{(() => {
                          const unitId = formData.total_purchase_product_unit;
                          if (!unitId) return 'MT';
                          const unit = units.find(u => String(u.id) === String(unitId));
                          return unit ? unit.name : 'MT';
                        })()}</span>
                      </div>
                    )}
                  </div>
                  
                  <h3 className="delivery-subsection-title">Total purchase quantity</h3>
                  <div className="form-group">
                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                      <input
                        type="text"
                        name="total_purchase_product_quantity"
                        value={formData.total_purchase_product_quantity || ''}
                        onChange={handleNumberChange(QUANTITY_DECIMALS)}
                        className={!isQuantityValid.total_purchase_product_quantity ? 'error' : ''}
                        placeholder="0.00"
                        style={{ flex: 1 }}
                        inputMode="decimal"
                      />
                      <div
                        style={{ 
                          width: '140px',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'not-allowed',
                          color: '#666',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '38px'
                        }}
                        title="Unit is automatically set from recap data"
                      >
                        {(() => {
                          const unitCode = formData.total_purchase_product_unit;
                          if (!unitCode) return 'Unit';
                          // If it's an ID, find the unit name
                          if (!isNaN(unitCode)) {
                            const unit = units.find(u => u.id === parseInt(unitCode));
                            return unit ? unit.name : unitCode;
                          }
                          // If it's a code, find the unit name
                          const matchingUnit = units.find(u => u.code === unitCode);
                          return matchingUnit ? matchingUnit.name : unitCode;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="delivery-column">
                  <h3 className="delivery-subsection-title">Average Sales price per unit (adjusted)</h3>
                  <div className="form-group">
                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                      <input
                        type="text"
                        name="total_sale_price_per_ton_adjusted"
                        value={formData.total_sale_price_per_ton_adjusted || ''}
                        onChange={handlePriceChange}
                        onBlur={handlePriceBlur}
                        className={!isPriceValid.total_sale_price_per_ton_adjusted ? 'error' : ''}
                        placeholder="0.00"
                        style={{ flex: 1 }}
                        inputMode="decimal"
                      />
                      <div
                        style={{ 
                          width: '140px',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'not-allowed',
                          color: '#666',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '38px'
                        }}
                        title="Currency is automatically set from recap data"
                      >
                        {formData.total_sale_price_per_ton_adjusted_currency || 'currency'}
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="delivery-subsection-title">Total Sales Quantity Range</h3>
                  <div className="form-group">
                    {formData.sales_range_is_exact ? (
                      <div style={{ 
                        padding: '8px 12px', 
                        backgroundColor: '#f0f7ff', 
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '14px',
                        border: '1px solid #d0e3ff'
                      }}>
                        N/A (Exact quantity - no tolerance)
                      </div>
                    ) : (
                      <div className="quantity-range-display">
                        <span className="range-value">{formData.sales_quantity_range_min || '0.00'}</span>
                        <span className="range-separator">to</span>
                        <span className="range-value">{formData.sales_quantity_range_max || '0.00'}</span>
                        <span className="range-unit">{(() => {
                          const unitId = formData.total_sales_product_unit;
                          if (!unitId) return 'MT';
                          const unit = units.find(u => String(u.id) === String(unitId));
                          return unit ? unit.name : 'MT';
                        })()}</span>
                      </div>
                    )}
                  </div>
                  
                  <h3 className="delivery-subsection-title">Total sales quantity</h3>
                  <div className="form-group">
                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                      <input
                        type="text"
                        name="total_sales_product_quantity"
                        value={formData.total_sales_product_quantity || ''}
                        onChange={handleNumberChange(QUANTITY_DECIMALS)}
                        className={!isQuantityValid.total_sales_product_quantity ? 'error' : ''}
                        placeholder="0.00"
                        style={{ flex: 1 }}
                        inputMode="decimal"
                      />
                      <div
                        style={{ 
                          width: '140px',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'not-allowed',
                          color: '#666',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '38px'
                        }}
                        title="Unit is automatically set from recap data"
                      >
                        {(() => {
                          const unitCode = formData.total_sales_product_unit;
                          if (!unitCode) return 'Unit';
                          // If it's an ID, find the unit name
                          if (!isNaN(unitCode)) {
                            const unit = units.find(u => u.id === parseInt(unitCode));
                            return unit ? unit.name : unitCode;
                          }
                          // If it's a code, find the unit name
                          const matchingUnit = units.find(u => u.code === unitCode);
                          return matchingUnit ? matchingUnit.name : unitCode;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sales exceeds purchase - WARNING */}
              {(formData.sales_exceeds_purchase || formData.exact_quantity_mismatch_warning) && (
                <div style={{
                  padding: '12px 16px',
                  marginTop: '15px',
                  backgroundColor: '#fff8e1',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <i className="fa fa-exclamation-triangle" style={{ color: '#ff9800', fontSize: '18px', marginTop: '2px' }}></i>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    <strong>Warning:</strong> Sales quantity exceeds purchase quantity. You are committed to delivering more than currently sourced.
                    <div style={{ marginTop: '6px', paddingLeft: '4px', fontSize: '13px', color: '#888' }}>
                      <i className="fa fa-arrow-right" style={{ marginRight: '6px', fontSize: '11px' }}></i>Add additional purchase recaps to cover the shortfall, or reduce the sales quantity.
                    </div>
                  </div>
                </div>
              )}

              {/* Purchase exceeds sales - WARNING (allowed) */}
              {formData.purchase_exceeds_sales && !formData.sales_exceeds_purchase && !formData.exact_quantity_mismatch_warning && (
                <div style={{
                  padding: '12px 16px',
                  marginTop: '15px',
                  backgroundColor: '#fff8e1',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <i className="fa fa-info-circle" style={{ color: '#ff9800', fontSize: '18px', marginTop: '2px' }}></i>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    <strong>Note:</strong> Purchase quantity exceeds sales quantity. The purchase order will remain partially open for the unsold balance.
                    <div style={{ marginTop: '6px', paddingLeft: '4px', fontSize: '13px', color: '#888' }}>
                      <i className="fa fa-arrow-right" style={{ marginRight: '6px', fontSize: '11px' }}></i>Add additional sales recaps to allocate the remaining stock, or reduce the purchase quantity.
                    </div>
                  </div>
                </div>
              )}
              
              {/* Valid configuration */}
              {formData.purchase_quantity_range_min && formData.sales_quantity_range_min && 
               !formData.sales_exceeds_purchase && !formData.purchase_exceeds_sales && !formData.exact_quantity_mismatch_warning && (
                <div className="range-validation-status valid">
                  <i className="fa fa-check-circle"></i>
                  <span>Valid delivery configuration</span>
                </div>
              )}
            </div>
          </div>

          {/* PRODUCT QUALITY TABLE Section — Cargo only. Hidden for Bunkers
              (commodity_type === 'fuels') per HMS team; stays for Cargo. */}
          {formData.commodity_type !== 'fuels' && (
            <div className="delivery-section-card" style={{ marginTop: '15px' }}>
              <h2 className="delivery-section-header" style={{ marginBottom: '0px' }}>Delivery Product Quality Table</h2>
              {renderProductQualitiesSection()}
            </div>
          )}

          {/* MARGIN & INTERNAL NOTES Section */}
          {/*
            Read-only Gross/Net margin per unit. A linked Budget Calculator
            (Delivery.budget -> BudgetHeader margin rows) still wins; otherwise
            the backend computes the pair from the delivery's own quantity, its
            recap-rolled-up prices and the third-party costs recorded anywhere
            on the deal. Unit + currency come with the figures so this stays
            correct across MT / lbs / BBL / etc.
            Every number is accompanied by where it was read from — this card
            once printed a dash for a sales price that was on the same screen.
            Below it: free-text internal notes (max 10000 chars) saved on the
            Delivery model.
          */}
          <div className="delivery-section-card" style={{ marginTop: '15px' }}>
            <div className="delivery-section-header-row">
              <h2 className="delivery-section-header">Margin &amp; Internal Notes</h2>
            </div>

            {(() => {
              /*
                Aug 2026: this pair used to read "No budget linked" and stop
                there. The backend (deliveries/services/margin_service.py) now
                falls back to computing margin from the delivery's own quantity,
                prices and third-party costs, so the box shows real numbers on
                deliveries that never had a budget.

                Two things the trader must never have to guess: WHICH source
                produced the number (budget vs this delivery), and whether a
                figure is per-unit or a total. Both are on screen. When the
                delivery can't support a calculation the backend returns a
                reason and no number — we render the reason, never a zero.
              */
              const margin = formData.budget_margin_per_unit || null;
              const unitLabel = margin?.unit || 'unit';
              const currency = margin?.currency || '';
              const reason = margin?.unavailable_reason || null;

              const fmt = (v) => {
                if (v === null || v === undefined || v === '') return null;
                const num = parseFloat(v);
                if (Number.isNaN(num)) return null;
                return num.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              };
              const perUnit = (v) => {
                const f = fmt(v);
                if (f === null) return '—';
                return currency
                  ? `${f} ${currency} / ${unitLabel}`
                  : `${f} / ${unitLabel}`;
              };
              const total = (v) => {
                const f = fmt(v);
                if (f === null) return null;
                return currency ? `${f} ${currency} total` : `${f} total`;
              };

              const sourceLabel = !margin
                ? null
                : margin.source === 'budget'
                  ? 'from linked budget'
                  : 'calculated from this delivery';

              /*
                Sept 2026 — provenance. This card used to show two numbers and
                nothing about where they came from, so when it printed a dash
                next to an "Average Sales price per unit (adjusted) 955.00" a
                few centimetres higher up the same screen, there was no way to
                tell which of the two the delivery was actually holding.

                The backend now sends an `inputs` block naming, per figure,
                which column answered — the recap roll-up, a price somebody
                typed on this delivery, the Bill of Lading. We print it under
                the boxes so a trader can tell a pulled-through number from a
                typed one without opening anything else.
              */
              const inputs = margin?.inputs || {};
              const amount = (v, ccy) => {
                const f = fmt(v);
                if (f === null) return null;
                return ccy ? `${f} ${ccy}` : f;
              };
              const provenanceRows = [
                inputs.quantity && {
                  key: 'quantity',
                  name: 'Quantity',
                  value: (() => {
                    const f = fmt(inputs.quantity.value);
                    if (f === null) return null;
                    return `${f} ${inputs.quantity.unit || unitLabel}`;
                  })(),
                  from: inputs.quantity.label,
                },
                inputs.sale_price && {
                  key: 'sale_price',
                  name: 'Sales price',
                  value: amount(inputs.sale_price.value,
                    inputs.sale_price.currency || currency),
                  from: inputs.sale_price.label,
                },
                inputs.purchase_price && {
                  key: 'purchase_price',
                  name: 'Purchase price',
                  value: amount(inputs.purchase_price.value,
                    inputs.purchase_price.currency || currency),
                  from: inputs.purchase_price.label,
                },
                inputs.third_party_costs && {
                  key: 'third_party_costs',
                  name: 'Third-party costs',
                  /*
                    Two numbers, never one. The deal's total is what the recap
                    card prints, so the two screens can be read side by side;
                    the "not recharged" figure is the only part Net actually
                    subtracts. Showing one without the other is how "why isn't
                    Net just Gross minus the barging bill?" starts.
                  */
                  value: (() => {
                    const totalCost = amount(
                      inputs.third_party_costs.total, currency);
                    if (totalCost === null) return null;
                    const unrecovered = amount(
                      inputs.third_party_costs.unrecovered, currency);
                    return `${totalCost} on the deal · ${unrecovered} not recharged`;
                  })(),
                  from: inputs.third_party_costs.row_count
                    ? `${inputs.third_party_costs.row_count} cost `
                      + `${inputs.third_party_costs.row_count === 1 ? 'row' : 'rows'}`
                      + ` on the ${(inputs.third_party_costs.found_on || [])
                        .map((m) => ({
                          FuelEnquiry: 'enquiry',
                          TradeRecap: 'recaps',
                          Delivery: 'delivery',
                        }[m] || m))
                        .join(', ')}`
                    : 'No third-party costs recorded on this deal',
                },
                // The budget branch names its own two rows instead of the
                // delivery's price/cost inputs — the budget IS the source.
                inputs.gross_margin && {
                  key: 'gross_margin',
                  name: 'Gross margin',
                  value: amount(inputs.gross_margin.value, currency),
                  from: inputs.gross_margin.label,
                },
                inputs.net_margin && {
                  key: 'net_margin',
                  name: 'Net margin',
                  value: amount(inputs.net_margin.value, currency),
                  from: inputs.net_margin.label,
                },
              ].filter(Boolean).filter((row) => row.value !== null || row.from);

              const renderBox = (label, perUnitValue, totalValue) => (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{label} per {unitLabel}</label>
                  <input
                    type="text"
                    value={reason ? '—' : perUnit(perUnitValue)}
                    readOnly
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  {!reason && total(totalValue) && (
                    <small
                      style={{
                        display: 'block',
                        marginTop: '4px',
                        color: '#666',
                        fontSize: '0.75rem',
                      }}
                    >
                      {total(totalValue)}
                    </small>
                  )}
                </div>
              );

              return (
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                    }}
                  >
                    {renderBox(
                      'Budget Gross Margin',
                      margin?.gross_per_unit,
                      margin?.gross_total,
                    )}
                    {renderBox(
                      'Budget Net Margin',
                      margin?.net_per_unit,
                      margin?.net_total,
                    )}
                  </div>

                  {reason && (
                    <div
                      style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        background: '#fffbeb',
                        border: '1px solid #fcd34d',
                        color: '#92400e',
                        fontSize: '0.75rem',
                      }}
                    >
                      {reason}
                    </div>
                  )}

                  {!reason && sourceLabel && (
                    <small
                      style={{
                        display: 'block',
                        marginTop: '6px',
                        color: '#888',
                        fontSize: '0.72rem',
                      }}
                    >
                      {sourceLabel}
                      {margin.source === 'delivery'
                        && ' — sale minus purchase price, net of third-party costs'
                          + ' not recharged to the customer'}
                    </small>
                  )}

                  {/*
                    Where each figure came from. Rendered on a refusal too: when
                    the card cannot produce a number, the fastest thing a trader
                    can be told is which of the four inputs is the missing one.
                  */}
                  {provenanceRows.length > 0 && (
                    <div
                      style={{
                        marginTop: '10px',
                        paddingTop: '8px',
                        borderTop: '1px solid #eee',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: '#999',
                          marginBottom: '4px',
                        }}
                      >
                        What these figures were built from
                      </div>
                      {provenanceRows.map((row) => (
                        <div
                          key={row.key}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            alignItems: 'baseline',
                            fontSize: '0.72rem',
                            color: '#666',
                            lineHeight: 1.6,
                          }}
                        >
                          <span style={{ minWidth: '110px', color: '#888' }}>
                            {row.name}
                          </span>
                          <span style={{ fontWeight: 600, color: '#333' }}>
                            {row.value || '—'}
                          </span>
                          <span style={{ color: '#999' }}>
                            {row.from}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="internal_notes">Internal Notes</label>
              {/* Rich text (Jonas feedback 19.08): bold / italic / bullets.
                  Stores sanitized HTML; existing plain-text notes load
                  unchanged. */}
              <RichTextField
                value={formData.internal_notes || ''}
                onChange={(html) =>
                  handleInputChange({ target: { name: 'internal_notes', value: html } })
                }
                placeholder="Free-text internal notes for this delivery"
                maxTextLength={10000}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
                {richTextLength(formData.internal_notes || '')} / 10000 characters
              </small>
            </div>
          </div>
        </div>
      )}


      {/* === Recap Link Modals === */}
      {/* Purchase Recap Modal */}
      {(() => {
        const purchaseRecaps = availableRecaps.purchase_recaps || [];
        // CRITICAL: Include ALL recaps (both purchase AND sales) for cross-type validation
        const linkedPurchaseRecaps = [
          ...(formData.purchase_recaps_data || []), 
          ...blankPurchaseRecaps,
          ...pendingLinkedPurchaseRecaps
        ];
        const linkedSalesRecaps = [
          ...(formData.sales_recaps_data || []), 
          ...blankSalesRecaps,
          ...pendingLinkedSalesRecaps
        ];
        const allLinkedRecaps = [...linkedPurchaseRecaps, ...linkedSalesRecaps];
        
        return (
          <RecapLinkModal
            isOpen={showPurchaseRecapModal}
            onClose={() => setShowPurchaseRecapModal(false)}
            recapType="purchase"
            availableRecaps={purchaseRecaps}
            linkedRecaps={allLinkedRecaps}
            onLink={handleLinkPurchaseRecap}
            onCreateBlank={(partnerName, partnerId) => handleCreateBlankRecap('purchase', partnerName, partnerId)}
            units={units}
          />
        );
      })()}
      
      {/* Sales Recap Modal */}
      {(() => {
        const salesRecaps = availableRecaps.sales_recaps || [];
        // CRITICAL: Include ALL recaps (both purchase AND sales) for cross-type validation
        const linkedSalesRecaps = [...(formData.sales_recaps_data || []), ...blankSalesRecaps];
        const linkedPurchaseRecaps = [
          ...(formData.purchase_recaps_data || []), 
          ...blankPurchaseRecaps,
          ...pendingLinkedPurchaseRecaps
        ];
        const allLinkedRecaps = [...linkedPurchaseRecaps, ...linkedSalesRecaps];
        
        return (
          <RecapLinkModal
            isOpen={showSalesRecapModal}
            onClose={() => setShowSalesRecapModal(false)}
            recapType="sales"
            availableRecaps={salesRecaps}
            linkedRecaps={allLinkedRecaps}
            onLink={handleLinkSalesRecap}
            onCreateBlank={(partnerName, partnerId) => handleCreateBlankRecap('sales', partnerName, partnerId)}
            units={units}
          />
        );
      })()}

      {/* Freight Recap Modal (Jonas feedback 19.08) */}
      <FreightRecapLinkModal
        isOpen={showFreightRecapModal}
        onClose={() => setShowFreightRecapModal(false)}
        availableRecaps={availableRecaps.freight_recaps || []}
        linkedNumbers={[
          ...linkedFreightNumbers,
          ...pendingLinkedFreightRecaps.map((r) => r.transaction_number),
        ]}
        onLink={handleLinkFreightRecaps}
      />

      {/* Sprint D — Inline product creator. Pre-fill price field depends on
          which search field triggered the flow (purchase vs sales).
          On success the new variant is auto-attached via the matching
          handleSelect* path so the form is left in the same state as if the
          user had clicked an existing variant in the dropdown. */}
      {productCreatorState.isOpen && (
        <InlineProductCreator
          isOpen={true}
          onClose={closeProductCreator}
          onProductCreated={handleInlineProductCreated}
          initialSearchTerm={productCreatorState.searchTerm}
          initialPriceField={productCreatorState.context === 'sales' ? 'sale_price' : 'purchase_price'}
          initialUnitPrice={
            productCreatorState.context === 'sales'
              ? (formData?.sales_price_per_ton_contract || '')
              : (formData?.purchase_price_per_ton_contract || '')
          }
          initialCurrency={
            productCreatorState.context === 'sales'
              ? (formData?.sales_price_per_ton_contract_currency || 'USD')
              : (formData?.purchase_price_per_ton_contract_currency || 'USD')
          }
          initialQuantity={
            productCreatorState.context === 'sales'
              ? (formData?.delivery_sales_product_quantity || '')
              : (formData?.delivery_product_quantity || '')
          }
        />
      )}

      {/* Inline CRM partner creator. Surfaced from any DeliveryPartnerSearch
          on this form (Seller / Buyer Contractual rows, chain partners,
          producer, end user, vessel owners / brokers). The new profile
          lands as a Pending stub via /api/crm/quick-create-profile/ and
          is surfaced on every picker on this form via
          pendingCreatedPartners until a CRM approver completes the full
          record. */}
      <InlineCrmCreator
        isOpen={crmCreatorState.isOpen}
        onClose={closeCrmCreator}
        onPartnerCreated={handleInlineCrmPartnerCreated}
        initialLegalEntityName={crmCreatorState.searchTerm}
        initialPartnerType={crmCreatorState.initialPartnerType}
        title={crmCreatorState.title}
      />
    </>
  );
});

export default DeliveryForm; 